import { z } from 'zod';
import { Prisma } from '@prisma/client';

/////////////////////////////////////////
// HELPER FUNCTIONS
/////////////////////////////////////////

// JSON
//------------------------------------------------------

export type NullableJsonInput = Prisma.JsonValue | null | 'JsonNull' | 'DbNull' | Prisma.NullTypes.DbNull | Prisma.NullTypes.JsonNull;

export const transformJsonNull = (v?: NullableJsonInput) => {
  if (!v || v === 'DbNull') return Prisma.DbNull;
  if (v === 'JsonNull') return Prisma.JsonNull;
  return v;
};

export const JsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
  ])
);

export type JsonValueType = z.infer<typeof JsonValueSchema>;

export const NullableJsonValue = z
  .union([JsonValueSchema, z.literal('DbNull'), z.literal('JsonNull')])
  .nullable()
  .transform((v) => transformJsonNull(v));

export type NullableJsonValueType = z.infer<typeof NullableJsonValue>;

export const InputJsonValueSchema: z.ZodType<Prisma.InputJsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: z.function(z.tuple([]), z.any()) }),
    z.record(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
  ])
);

export type InputJsonValueType = z.infer<typeof InputJsonValueSchema>;


/////////////////////////////////////////
// ENUMS
/////////////////////////////////////////

export const UserScalarFieldEnumSchema = z.enum(['id','name','email','emailVerified','image','createdAt','updatedAt','username','role','banned','banReason','banExpires','onboardingComplete','paymentsCustomerId','locale','phoneNumber','phoneVerified','smsVerificationCode','smsVerificationExpiresAt','smsVerificationAttempts']);

export const SessionScalarFieldEnumSchema = z.enum(['id','expiresAt','ipAddress','userAgent','userId','impersonatedBy','activeOrganizationId','token','createdAt','updatedAt']);

export const AccountScalarFieldEnumSchema = z.enum(['id','accountId','providerId','userId','accessToken','refreshToken','idToken','expiresAt','password','accessTokenExpiresAt','refreshTokenExpiresAt','scope','createdAt','updatedAt']);

export const VerificationScalarFieldEnumSchema = z.enum(['id','identifier','value','expiresAt','createdAt','updatedAt']);

export const PasskeyScalarFieldEnumSchema = z.enum(['id','name','publicKey','userId','credentialID','counter','deviceType','backedUp','transports','createdAt']);

export const TwoFactorScalarFieldEnumSchema = z.enum(['id','secret','backupCodes','userId']);

export const OrganizationScalarFieldEnumSchema = z.enum(['id','name','slug','logo','createdAt','metadata','paymentsCustomerId','AutoArchiveDurationDays']);

export const MemberScalarFieldEnumSchema = z.enum(['id','organizationId','userId','role','createdAt']);

export const InvitationScalarFieldEnumSchema = z.enum(['id','organizationId','email','role','status','expiresAt','inviterId']);

export const PurchaseScalarFieldEnumSchema = z.enum(['id','organizationId','userId','type','customerId','isDeleted','subscriptionId','credit','productId','status','startDate','createdAt','updatedAt']);

export const AiChatScalarFieldEnumSchema = z.enum(['id','organizationId','userId','title','messages','createdAt','updatedAt']);

export const ApiKeyScalarFieldEnumSchema = z.enum(['id','userId','organizationId','name','key','isDeleted','createdAt']);

export const JobScalarFieldEnumSchema = z.enum(['id','title','description','userId','organizationId','keyId','purchaseId','cost','creditUsageStatus','status','errorMessage','functionName','source','createdAt','startTime','endTime','isDeleted','archivedAt']);

export const VideoScalarFieldEnumSchema = z.enum(['id','jobId','sourceId','videoLength','title','choosedDefinition','status','createdAt']);

export const VideoProjectScalarFieldEnumSchema = z.enum(['id','jobId','organizationId','createdByUserId','title','status','inputBrief','document','validation','errorMessage','cost','publishedAt','publishTag','episodeSummary','createdAt','updatedAt','isDeleted']);

export const VideoDesignScalarFieldEnumSchema = z.enum(['id','videoProjectId','jobId','organizationId','status','design','fps','durationMs','width','height','latestRenderHistoryId','latestMp4Url','compiledAt','createdAt','updatedAt']);

export const AppFileScalarFieldEnumSchema = z.enum(['id','name','userId','organizationId','jobId','type','status','description','tags','size','length','key','thumbnailUrl','createdAt']);

export const CaptionStyleScalarFieldEnumSchema = z.enum(['id','name','description','organizationId','style','isActive','createdAt','updatedAt']);

export const OrganizationBrandingScalarFieldEnumSchema = z.enum(['id','organizationId','name','description','preset','size','options','isActive','isDefault','createdAt','updatedAt']);

export const OrganizationNarratorProfileScalarFieldEnumSchema = z.enum(['id','organizationId','name','description','narratorName','gender','provider','voiceId','personality','styleNotes','ttsStyle','ttsPace','ttsAccent','ttsNotes','channelName','channelSignOff','overlayText','overlayTextDelayMs','overlayTextDurationMs','visualDefaults','speechSpeed','avatarFileAppId','avatarPhotos','avatarMinDurationMs','avatarMaxDurationMs','avatarAppearEveryMs','avatarIntroHoldMs','isActive','isDefault','createdAt','updatedAt']);

export const OrganizationSocialSettingsScalarFieldEnumSchema = z.enum(['id','organizationId','provider','providerProfileId','isEnabled','approveApiPosts','defaultTimezone','defaultPlatforms','queueSlots','maxPostsPerSlot','maxShortsOfSameVideo','maxShortsPerWeek','minimumVideoDurationSeconds','processOnlyPlaylistId','createdAt','updatedAt']);

export const OrganizationSocialPostScalarFieldEnumSchema = z.enum(['id','organizationId','jobId','provider','status','providerJobId','platforms','title','caption','videoUrl','scheduledFor','publishedAt','lastError','metadata','createdAt','updatedAt']);

export const RenderHistoryScalarFieldEnumSchema = z.enum(['id','renderId','organizationId','userId','keyId','jobId','appFileId','target','data','fileUrl','fileSize','cost','status','errorMessage','priority','retryCount','maxRetries','webhookReceived','webhookReceivedAt','createdAt','updatedAt']);

export const ExternalKeysScalarFieldEnumSchema = z.enum(['id','key','name','status','type','errors','currency','usage','usageCount','lastResetDate','monthlyUsageCount','lastUsedDate','cooldownUntil','monthlyUsage','createdAt','updatedAt']);

export const ProxyScalarFieldEnumSchema = z.enum(['id','name','proxyUrl','createdAt','updatedAt']);

export const CookieIdentityScalarFieldEnumSchema = z.enum(['id','name','isActive','lastRequestTime','proxyId','cookieContent','useCount','errors','createdAt','updatedAt']);

export const SortOrderSchema = z.enum(['asc','desc']);

export const QueryModeSchema = z.enum(['default','insensitive']);

export const PurchaseTypeSchema = z.enum(['SUBSCRIPTION','ONE_TIME']);

export type PurchaseTypeType = `${z.infer<typeof PurchaseTypeSchema>}`

export const ShortStatusSchema = z.enum(['PENDING','COMPLETED','FAILED']);

export type ShortStatusType = `${z.infer<typeof ShortStatusSchema>}`

export const BRollFileTypeSchema = z.enum(['video','image']);

export type BRollFileTypeType = `${z.infer<typeof BRollFileTypeSchema>}`

export const VideoProjectStatusSchema = z.enum(['PROCESSING','VALIDATED','ASSETS_READY','DESIGN_READY','RENDERED','FAILED']);

export type VideoProjectStatusType = `${z.infer<typeof VideoProjectStatusSchema>}`

export const VideoDesignStatusSchema = z.enum(['DRAFT','COMPILED','RENDERED','FAILED']);

export type VideoDesignStatusType = `${z.infer<typeof VideoDesignStatusSchema>}`

export const VideoStatusSchema = z.enum(['INITIATING_SERVICES','DOWNLOADING_VIDEO','TRANSCRIBING','GENERTING_SHORTS','GETTING_ASD','SUCCESS','FAILED']);

export type VideoStatusType = `${z.infer<typeof VideoStatusSchema>}`

export const AppFileTypeSchema = z.enum(['audio','video','image']);

export type AppFileTypeType = `${z.infer<typeof AppFileTypeSchema>}`

export const JobFunctionNameSchema = z.enum(['VideoShorts','VideoProject','DynamicExplainer','Production']);

export type JobFunctionNameType = `${z.infer<typeof JobFunctionNameSchema>}`

export const JobStatusSchema = z.enum(['PROCESSING','COMPLETED','FAILED']);

export type JobStatusType = `${z.infer<typeof JobStatusSchema>}`

export const JobSourceSchema = z.enum(['WEB_APP','API']);

export type JobSourceType = `${z.infer<typeof JobSourceSchema>}`

export const CreditUsageStatusSchema = z.enum(['ACTIVE','PENDING']);

export type CreditUsageStatusType = `${z.infer<typeof CreditUsageStatusSchema>}`

export const AppFileStatusSchema = z.enum(['pending','completed']);

export type AppFileStatusType = `${z.infer<typeof AppFileStatusSchema>}`

export const OrganizationSocialProviderSchema = z.enum(['zernio']);

export type OrganizationSocialProviderType = `${z.infer<typeof OrganizationSocialProviderSchema>}`

export const OrganizationSocialPostStatusSchema = z.enum(['draft','scheduled','published','failed','partial','cancelled','recycled']);

export type OrganizationSocialPostStatusType = `${z.infer<typeof OrganizationSocialPostStatusSchema>}`

export const RenderHistoryStatusSchema = z.enum(['QUEUED','PROCESSING','COMPLETED','FAILED']);

export type RenderHistoryStatusType = `${z.infer<typeof RenderHistoryStatusSchema>}`

export const ExternalKeysTypeSchema = z.enum(['SIEVE']);

export type ExternalKeysTypeType = `${z.infer<typeof ExternalKeysTypeSchema>}`

export const ExternalKeysStatusSchema = z.enum(['ACTIVE','DEACTIVATED']);

export type ExternalKeysStatusType = `${z.infer<typeof ExternalKeysStatusSchema>}`

/////////////////////////////////////////
// MODELS
/////////////////////////////////////////

/////////////////////////////////////////
// USER SCHEMA
/////////////////////////////////////////

export const UserSchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().nullable(),
  role: z.string().nullable(),
  banned: z.boolean().nullable(),
  banReason: z.string().nullable(),
  banExpires: z.coerce.date().nullable(),
  onboardingComplete: z.boolean(),
  paymentsCustomerId: z.string().nullable(),
  locale: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  phoneVerified: z.boolean(),
  smsVerificationCode: z.string().nullable(),
  smsVerificationExpiresAt: z.coerce.date().nullable(),
  smsVerificationAttempts: z.number().int(),
})

export type User = z.infer<typeof UserSchema>

/////////////////////////////////////////
// SESSION SCHEMA
/////////////////////////////////////////

export const SessionSchema = z.object({
  id: z.string().cuid(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  userId: z.string(),
  impersonatedBy: z.string().nullable(),
  activeOrganizationId: z.string().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Session = z.infer<typeof SessionSchema>

/////////////////////////////////////////
// ACCOUNT SCHEMA
/////////////////////////////////////////

export const AccountSchema = z.object({
  id: z.string().cuid(),
  accountId: z.string(),
  providerId: z.string(),
  userId: z.string(),
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  idToken: z.string().nullable(),
  expiresAt: z.coerce.date().nullable(),
  password: z.string().nullable(),
  accessTokenExpiresAt: z.coerce.date().nullable(),
  refreshTokenExpiresAt: z.coerce.date().nullable(),
  scope: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Account = z.infer<typeof AccountSchema>

/////////////////////////////////////////
// VERIFICATION SCHEMA
/////////////////////////////////////////

export const VerificationSchema = z.object({
  id: z.string().cuid(),
  identifier: z.string(),
  value: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
})

export type Verification = z.infer<typeof VerificationSchema>

/////////////////////////////////////////
// PASSKEY SCHEMA
/////////////////////////////////////////

export const PasskeySchema = z.object({
  id: z.string().cuid(),
  name: z.string().nullable(),
  publicKey: z.string(),
  userId: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().nullable(),
  createdAt: z.coerce.date().nullable(),
})

export type Passkey = z.infer<typeof PasskeySchema>

/////////////////////////////////////////
// TWO FACTOR SCHEMA
/////////////////////////////////////////

export const TwoFactorSchema = z.object({
  id: z.string().cuid(),
  secret: z.string(),
  backupCodes: z.string(),
  userId: z.string(),
})

export type TwoFactor = z.infer<typeof TwoFactorSchema>

/////////////////////////////////////////
// ORGANIZATION SCHEMA
/////////////////////////////////////////

export const OrganizationSchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  slug: z.string().nullable(),
  logo: z.string().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().nullable(),
  paymentsCustomerId: z.string().nullable(),
  AutoArchiveDurationDays: z.number().int().nullable(),
})

export type Organization = z.infer<typeof OrganizationSchema>

/////////////////////////////////////////
// MEMBER SCHEMA
/////////////////////////////////////////

export const MemberSchema = z.object({
  id: z.string().cuid(),
  organizationId: z.string(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date(),
})

export type Member = z.infer<typeof MemberSchema>

/////////////////////////////////////////
// INVITATION SCHEMA
/////////////////////////////////////////

export const InvitationSchema = z.object({
  id: z.string().cuid(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  inviterId: z.string(),
})

export type Invitation = z.infer<typeof InvitationSchema>

/////////////////////////////////////////
// PURCHASE SCHEMA
/////////////////////////////////////////

export const PurchaseSchema = z.object({
  type: PurchaseTypeSchema,
  id: z.string().cuid(),
  organizationId: z.string().nullable(),
  userId: z.string().nullable(),
  customerId: z.string(),
  isDeleted: z.boolean(),
  subscriptionId: z.string().nullable(),
  credit: z.number().nullable(),
  productId: z.string(),
  status: z.string().nullable(),
  startDate: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Purchase = z.infer<typeof PurchaseSchema>

/////////////////////////////////////////
// AI CHAT SCHEMA
/////////////////////////////////////////

export const AiChatSchema = z.object({
  id: z.string().cuid(),
  organizationId: z.string().nullable(),
  userId: z.string().nullable(),
  title: z.string().nullable(),
  /**
   * [Array<{role: "user" | "assistant"; content: string;}>]
   */
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type AiChat = z.infer<typeof AiChatSchema>

/////////////////////////////////////////
// API KEY SCHEMA
/////////////////////////////////////////

export const ApiKeySchema = z.object({
  id: z.string().cuid(),
  userId: z.string(),
  organizationId: z.string().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean(),
  createdAt: z.coerce.date().nullable(),
})

export type ApiKey = z.infer<typeof ApiKeySchema>

/////////////////////////////////////////
// JOB SCHEMA
/////////////////////////////////////////

export const JobSchema = z.object({
  creditUsageStatus: CreditUsageStatusSchema,
  status: JobStatusSchema.nullable(),
  functionName: JobFunctionNameSchema,
  source: JobSourceSchema,
  id: z.string().cuid(),
  title: z.string(),
  description: z.string().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().nullable(),
  purchaseId: z.string().nullable(),
  cost: z.number(),
  errorMessage: z.string().array(),
  createdAt: z.coerce.date(),
  startTime: z.coerce.date().nullable(),
  endTime: z.coerce.date().nullable(),
  isDeleted: z.boolean(),
  archivedAt: z.coerce.date().nullable(),
})

export type Job = z.infer<typeof JobSchema>

/////////////////////////////////////////
// VIDEO SCHEMA
/////////////////////////////////////////

export const VideoSchema = z.object({
  status: VideoStatusSchema,
  id: z.string().cuid(),
  jobId: z.string(),
  sourceId: z.string(),
  videoLength: z.number(),
  title: z.string(),
  choosedDefinition: z.string(),
  createdAt: z.coerce.date(),
})

export type Video = z.infer<typeof VideoSchema>

/////////////////////////////////////////
// VIDEO PROJECT SCHEMA
/////////////////////////////////////////

export const VideoProjectSchema = z.object({
  status: VideoProjectStatusSchema,
  id: z.string().cuid(),
  jobId: z.string(),
  organizationId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  /**
   * Locked create-form inputs (format, narrator choice, brief, duration, aspect, language)
   * [Object]
   */
  inputBrief: z.record(z.string(), z.any()),
  /**
   * Full VideoProjectDocument JSON (authoring bible). See @repo/types video-project schemas.
   * [Object]
   */
  document: z.record(z.string(), z.any()).nullable().nullable(),
  /**
   * Last validation report (isValid, errors, fixIterations, …)
   * [Object]
   */
  validation: z.record(z.string(), z.any()).nullable().nullable(),
  errorMessage: z.string().array(),
  cost: z.number().nullable(),
  /**
   * When set, this story episode is published and visible to future story generation.
   */
  publishedAt: z.coerce.date().nullable(),
  /**
   * { type: "story", episodeNumber } | { type: "channel_video", aspect, format, ... }
   */
  publishTag: JsonValueSchema.nullable(),
  /**
   * Short summary for prior-episode context in later story generation.
   */
  episodeSummary: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
})

export type VideoProject = z.infer<typeof VideoProjectSchema>

/////////////////////////////////////////
// VIDEO DESIGN SCHEMA
/////////////////////////////////////////

/**
 * Compiled IDesign timeline for a VideoProject (1:1, overwrite on recompile — no versioning).
 */
export const VideoDesignSchema = z.object({
  status: VideoDesignStatusSchema,
  id: z.string().cuid(),
  videoProjectId: z.string(),
  jobId: z.string(),
  organizationId: z.string(),
  /**
   * Full DesignCombo IDesign JSON
   * [Object]
   */
  design: z.record(z.string(), z.any()),
  fps: z.number().int(),
  durationMs: z.number().int(),
  width: z.number().int(),
  height: z.number().int(),
  latestRenderHistoryId: z.string().nullable(),
  latestMp4Url: z.string().nullable(),
  compiledAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type VideoDesign = z.infer<typeof VideoDesignSchema>

/////////////////////////////////////////
// APP FILE SCHEMA
/////////////////////////////////////////

export const AppFileSchema = z.object({
  type: AppFileTypeSchema,
  status: AppFileStatusSchema,
  id: z.string().cuid(),
  name: z.string(),
  userId: z.string(),
  organizationId: z.string().nullable(),
  jobId: z.string().nullable(),
  description: z.string().nullable(),
  tags: z.string().array(),
  size: z.number().int(),
  length: z.number().int(),
  key: z.string(),
  thumbnailUrl: z.string().nullable(),
  createdAt: z.coerce.date(),
})

export type AppFile = z.infer<typeof AppFileSchema>

/////////////////////////////////////////
// CAPTION STYLE SCHEMA
/////////////////////////////////////////

export const CaptionStyleSchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  description: z.string().nullable(),
  organizationId: z.string(),
  /**
   * [Object]
   */
  style: z.record(z.string(), z.any()),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CaptionStyle = z.infer<typeof CaptionStyleSchema>

/////////////////////////////////////////
// ORGANIZATION BRANDING SCHEMA
/////////////////////////////////////////

export const OrganizationBrandingSchema = z.object({
  id: z.string().cuid(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  preset: z.string().nullable(),
  size: JsonValueSchema.nullable(),
  options: JsonValueSchema.nullable(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrganizationBranding = z.infer<typeof OrganizationBrandingSchema>

/////////////////////////////////////////
// ORGANIZATION NARRATOR PROFILE SCHEMA
/////////////////////////////////////////

/**
 * Reusable narrator persona for video projects (name + voice + personality).
 */
export const OrganizationNarratorProfileSchema = z.object({
  id: z.string().cuid(),
  organizationId: z.string(),
  /**
   * Profile label in settings UI (e.g. "Sarcastic host")
   */
  name: z.string(),
  description: z.string().nullable(),
  /**
   * Display / TTS speaker name snapshotted into document.narrator.name
   */
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  /**
   * e.g. sarcastic, poetic, aggressive, funny — script generation only (not TTS)
   */
  personality: z.string(),
  /**
   * Authoring / LLM delivery notes — script generation only (not TTS)
   */
  styleNotes: z.string().nullable(),
  /**
   * Gemini Director's Notes — Style preset (neutral, empathetic, warm, …)
   */
  ttsStyle: z.string(),
  /**
   * Gemini Director's Notes — Pace preset (natural, slow, fast, measured)
   */
  ttsPace: z.string(),
  /**
   * Gemini Director's Notes — Accent preset (american_neutral, british_rp, auto, …)
   */
  ttsAccent: z.string(),
  /**
   * Optional extra TTS director notes (custom accent detail, etc.)
   */
  ttsNotes: z.string().nullable(),
  /**
   * Public channel / show name for landscape story host mode (e.g. "Midnight Cases")
   */
  channelName: z.string().nullable(),
  /**
   * Optional channel sign-off phrase for script outros
   */
  channelSignOff: z.string().nullable(),
  /**
   * Optional on-screen text overlay compiled into the design (empty = skip)
   */
  overlayText: z.string().nullable(),
  /**
   * Delay before overlay text appears (ms from timeline start)
   */
  overlayTextDelayMs: z.number().int().nullable(),
  /**
   * How long overlay text stays on screen (ms). Null = until end of video.
   */
  overlayTextDurationMs: z.number().int().nullable(),
  /**
   * Default Plyven-style visual look for AI plates (style, lighting, mood, etc.)
   */
  visualDefaults: JsonValueSchema.nullable(),
  /**
   * Narrator TTS playback speed multiplier (0.75–1.35, default 1)
   */
  speechSpeed: z.number(),
  /**
   * Optional still image (AppFile id) shown as a PIP avatar in video projects
   */
  avatarFileAppId: z.string().nullable(),
  /**
   * Collection of avatar photos with aspect ratio: [{ fileAppId, aspectRatio, name? }]
   */
  avatarPhotos: JsonValueSchema.nullable(),
  /**
   * Default PIP cadence for video projects (ms). Project overview can override.
   */
  avatarMinDurationMs: z.number().int().nullable(),
  avatarMaxDurationMs: z.number().int().nullable(),
  avatarAppearEveryMs: z.number().int().nullable(),
  avatarIntroHoldMs: z.number().int().nullable(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrganizationNarratorProfile = z.infer<typeof OrganizationNarratorProfileSchema>

/////////////////////////////////////////
// ORGANIZATION SOCIAL SETTINGS SCHEMA
/////////////////////////////////////////

export const OrganizationSocialSettingsSchema = z.object({
  provider: OrganizationSocialProviderSchema,
  id: z.string().cuid(),
  organizationId: z.string(),
  providerProfileId: z.string().nullable(),
  isEnabled: z.boolean(),
  approveApiPosts: z.boolean(),
  defaultTimezone: z.string().nullable(),
  defaultPlatforms: z.string().array(),
  /**
   * Local schedule entries managed by Swiftia: [{ dayOfWeek: 0..6, time: "HH:mm" }]
   */
  queueSlots: JsonValueSchema,
  /**
   * Queue capacity per time slot (Upload-Post queue `max_posts_per_slot`)
   */
  maxPostsPerSlot: z.number().int(),
  /**
   * Managed video generation service settings
   */
  maxShortsOfSameVideo: z.number().int(),
  maxShortsPerWeek: z.number().int(),
  /**
   * Minimum source video duration in seconds (0 = no minimum)
   */
  minimumVideoDurationSeconds: z.number().int(),
  /**
   * Optional playlist id to restrict processing scope
   */
  processOnlyPlaylistId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrganizationSocialSettings = z.infer<typeof OrganizationSocialSettingsSchema>

/////////////////////////////////////////
// ORGANIZATION SOCIAL POST SCHEMA
/////////////////////////////////////////

export const OrganizationSocialPostSchema = z.object({
  provider: OrganizationSocialProviderSchema,
  status: OrganizationSocialPostStatusSchema,
  id: z.string().cuid(),
  organizationId: z.string(),
  jobId: z.string().nullable(),
  providerJobId: z.string().nullable(),
  platforms: z.string().array(),
  title: z.string().nullable(),
  caption: z.string().nullable(),
  videoUrl: z.string().nullable(),
  scheduledFor: z.coerce.date().nullable(),
  publishedAt: z.coerce.date().nullable(),
  lastError: z.string().nullable(),
  /**
   * Provider payload snapshot, approval metadata, etc.
   */
  metadata: JsonValueSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrganizationSocialPost = z.infer<typeof OrganizationSocialPostSchema>

/////////////////////////////////////////
// RENDER HISTORY SCHEMA
/////////////////////////////////////////

export const RenderHistorySchema = z.object({
  status: RenderHistoryStatusSchema,
  id: z.string().cuid(),
  renderId: z.string().nullable(),
  organizationId: z.string(),
  userId: z.string().nullable(),
  keyId: z.string().nullable(),
  jobId: z.string(),
  appFileId: z.string().nullable(),
  target: z.number().int(),
  /**
   * [Object]
   */
  data: z.record(z.string(), z.any()),
  fileUrl: z.string().nullable(),
  fileSize: z.string().nullable(),
  cost: z.number().nullable(),
  errorMessage: z.string().nullable(),
  priority: z.number().int().nullable(),
  retryCount: z.number().int(),
  maxRetries: z.number().int(),
  webhookReceived: z.boolean(),
  webhookReceivedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type RenderHistory = z.infer<typeof RenderHistorySchema>

/////////////////////////////////////////
// EXTERNAL KEYS SCHEMA
/////////////////////////////////////////

export const ExternalKeysSchema = z.object({
  status: ExternalKeysStatusSchema,
  type: ExternalKeysTypeSchema,
  id: z.string().cuid(),
  key: z.string(),
  name: z.string(),
  errors: z.string().array(),
  currency: z.string(),
  usage: z.number(),
  usageCount: z.number().int(),
  lastResetDate: z.coerce.date().nullable(),
  monthlyUsageCount: z.number().int(),
  lastUsedDate: z.coerce.date().nullable(),
  cooldownUntil: z.coerce.date().nullable(),
  monthlyUsage: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type ExternalKeys = z.infer<typeof ExternalKeysSchema>

/////////////////////////////////////////
// PROXY SCHEMA
/////////////////////////////////////////

export const ProxySchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  proxyUrl: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Proxy = z.infer<typeof ProxySchema>

/////////////////////////////////////////
// COOKIE IDENTITY SCHEMA
/////////////////////////////////////////

export const CookieIdentitySchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  isActive: z.boolean(),
  lastRequestTime: z.coerce.date().nullable(),
  proxyId: z.string(),
  cookieContent: z.string(),
  useCount: z.number().int(),
  /**
   * [Array<{message: string; timestamp: string}>]
   */
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CookieIdentity = z.infer<typeof CookieIdentitySchema>

/////////////////////////////////////////
// COMPOSITE TYPES
/////////////////////////////////////////
// COST BREAKDOWN
//------------------------------------------------------


/////////////////////////////////////////
// COST BREAKDOWN SCHEMA
/////////////////////////////////////////

export const CostBreakdownSchema = z.object({
  shortGen: z.number().nullable(),
  youtubeProcessing: z.number().nullable(),
  render: z.number().nullable(),
  bRoll: z.number().nullable(),
})

export type CostBreakdown = z.infer<typeof CostBreakdownSchema>
// PREDEFINED SHORTS
//------------------------------------------------------


/////////////////////////////////////////
// PREDEFINED SHORTS SCHEMA
/////////////////////////////////////////

export const PredefinedShortsSchema = z.object({
  title: z.string(),
  start: z.number(),
  end: z.number(),
})

export type PredefinedShorts = z.infer<typeof PredefinedShortsSchema>
// VIDEO SHORTS OPTIONS
//------------------------------------------------------


/////////////////////////////////////////
// VIDEO SHORTS OPTIONS SCHEMA
/////////////////////////////////////////

export const VideoShortsOptionsSchema = z.object({
  isRTL: z.boolean().nullable(),
})

export type VideoShortsOptions = z.infer<typeof VideoShortsOptionsSchema>
// SHORT SCORE
//------------------------------------------------------


/////////////////////////////////////////
// SHORT SCORE SCHEMA
/////////////////////////////////////////

export const ShortScoreSchema = z.object({
  final_creative_score: z.number(),
  narrative_payoff_score: z.number(),
  hook_quality_score: z.number(),
  engagement_potential_score: z.number(),
  brand_alignment_score: z.number(),
})

export type ShortScore = z.infer<typeof ShortScoreSchema>
// B ROLL ITEM
//------------------------------------------------------


/////////////////////////////////////////
// B ROLL ITEM SCHEMA
/////////////////////////////////////////

export const BRollItemSchema = z.object({
  fileType: BRollFileTypeSchema,
  id: z.string(),
  sentenceIds: z.number().int().array(),
  fileUrl: z.string(),
  pexelsId: z.string().nullable(),
  startTime: z.number(),
  endTime: z.number(),
  position: z.string().nullable(),
  metadata: JsonValueSchema.nullable(),
})

export type BRollItem = z.infer<typeof BRollItemSchema>
// SHORT
//------------------------------------------------------


/////////////////////////////////////////
// SHORT SCHEMA
/////////////////////////////////////////

export const ShortSchema = z.object({
  status: ShortStatusSchema,
  id: z.number().int(),
  title: z.string(),
  reason: z.string(),
  startId: z.number().int(),
  endId: z.number().int(),
  startTime: z.number(),
  endTime: z.number(),
  text: z.string(),
  cropFileUrl: z.string().nullable(),
  cropFilekey: z.string().nullable(),
  description: z.string().nullable(),
  appFileId: z.string().nullable(),
})

export type Short = z.infer<typeof ShortSchema>
// SOURCE FILES
//------------------------------------------------------


/////////////////////////////////////////
// SOURCE FILES SCHEMA
/////////////////////////////////////////

export const SourceFilesSchema = z.object({
  isInS3: z.boolean().nullable(),
  target: z.string().nullable(),
  targetFPS: z.number().nullable(),
  preview: z.string().nullable(),
  previewFPS: z.number().nullable(),
})

export type SourceFiles = z.infer<typeof SourceFilesSchema>
// STATUS HISTORY
//------------------------------------------------------


/////////////////////////////////////////
// STATUS HISTORY SCHEMA
/////////////////////////////////////////

export const StatusHistorySchema = z.object({
  status: z.string(),
  startTime: z.string(),
  endTime: z.string().nullable(),
})

export type StatusHistory = z.infer<typeof StatusHistorySchema>
// ASD CAMERA CONFIG
//------------------------------------------------------


/////////////////////////////////////////
// ASD CAMERA CONFIG SCHEMA
/////////////////////////////////////////

export const AsdCameraConfigSchema = z.object({
  facePaddingFactor: z.number().nullable(),
  maxZoomFactor: z.number().nullable(),
  smoothingFactor: z.number().nullable(),
  predictionFrames: z.number().int().nullable(),
  predictionWeight: z.number().nullable(),
  movementThresholdFactor: z.number().nullable(),
})

export type AsdCameraConfig = z.infer<typeof AsdCameraConfigSchema>
// ASD LAYOUT CONFIG
//------------------------------------------------------


/////////////////////////////////////////
// ASD LAYOUT CONFIG SCHEMA
/////////////////////////////////////////

export const AsdLayoutConfigSchema = z.object({
  minIndicatorDurationFrames: z.number().int().nullable(),
  speakerLingerFrames: z.number().int().nullable(),
})

export type AsdLayoutConfig = z.infer<typeof AsdLayoutConfigSchema>
// ASD INTELLIGENCE CONFIG
//------------------------------------------------------


/////////////////////////////////////////
// ASD INTELLIGENCE CONFIG SCHEMA
/////////////////////////////////////////

export const AsdIntelligenceConfigSchema = z.object({
  focusSpeakingScoreThreshold: z.number().nullable(),
  maxSpeakersToFocus: z.number().int().nullable(),
  disableFocusThreshold: z.number().int().nullable(),
  speakerDominanceWindowFrames: z.number().int().nullable(),
})

export type AsdIntelligenceConfig = z.infer<typeof AsdIntelligenceConfigSchema>
// ASD OVERLAYS AND TIMING CONFIG
//------------------------------------------------------


/////////////////////////////////////////
// ASD OVERLAYS AND TIMING CONFIG SCHEMA
/////////////////////////////////////////

export const AsdOverlaysAndTimingConfigSchema = z.object({
  showFaceBoundingBoxes: z.boolean().nullable(),
  asdFrameOffset: z.number().int().nullable(),
})

export type AsdOverlaysAndTimingConfig = z.infer<typeof AsdOverlaysAndTimingConfigSchema>
// ASD CONFIG
//------------------------------------------------------


/////////////////////////////////////////
// ASD CONFIG SCHEMA
/////////////////////////////////////////

export const AsdConfigSchema = z.object({
})

export type AsdConfig = z.infer<typeof AsdConfigSchema>
// WORD
//------------------------------------------------------


/////////////////////////////////////////
// WORD SCHEMA
/////////////////////////////////////////

export const WordSchema = z.object({
  word: z.string(),
  time_begin: z.number(),
  time_end: z.number(),
  confidence: z.number(),
})

export type Word = z.infer<typeof WordSchema>
// PREDICTION
//------------------------------------------------------


/////////////////////////////////////////
// PREDICTION SCHEMA
/////////////////////////////////////////

export const PredictionSchema = z.object({
  language: z.string().nullable(),
  transcription: z.string(),
  confidence: z.number(),
  time_begin: z.number(),
  time_end: z.number(),
  speaker: z.string(),
  channel: z.string(),
})

export type Prediction = z.infer<typeof PredictionSchema>
// I TRANSCRIPTION
//------------------------------------------------------


/////////////////////////////////////////
// I TRANSCRIPTION SCHEMA
/////////////////////////////////////////

export const ITranscriptionSchema = z.object({
})

export type ITranscription = z.infer<typeof ITranscriptionSchema>
// WEBHOOK
//------------------------------------------------------


/////////////////////////////////////////
// WEBHOOK SCHEMA
/////////////////////////////////////////

export const WebhookSchema = z.object({
  url: z.string(),
  status: z.number().int().nullable(),
  retryCount: z.number().int().nullable(),
  error: z.string().nullable(),
})

export type Webhook = z.infer<typeof WebhookSchema>
// IDENTITY ERROR
//------------------------------------------------------


/////////////////////////////////////////
// IDENTITY ERROR SCHEMA
/////////////////////////////////////////

export const IdentityErrorSchema = z.object({
  message: z.string(),
  timestamp: z.string(),
})

export type IdentityError = z.infer<typeof IdentityErrorSchema>
// ASD RESOLUTION
//------------------------------------------------------


/////////////////////////////////////////
// ASD RESOLUTION SCHEMA
/////////////////////////////////////////

export const AsdResolutionSchema = z.object({
  width: z.number().int(),
  height: z.number().int(),
})

export type AsdResolution = z.infer<typeof AsdResolutionSchema>
// ASD VIDEO INFO
//------------------------------------------------------


/////////////////////////////////////////
// ASD VIDEO INFO SCHEMA
/////////////////////////////////////////

export const AsdVideoInfoSchema = z.object({
  fps: z.number(),
  processing_fps: z.number(),
  total_frames: z.number().int(),
  duration: z.number(),
  start_time: z.number(),
  end_time: z.number(),
  frame_numbers_start_from: z.number(),
})

export type AsdVideoInfo = z.infer<typeof AsdVideoInfoSchema>
// ASD FACE
//------------------------------------------------------


/////////////////////////////////////////
// ASD FACE SCHEMA
/////////////////////////////////////////

export const AsdFaceSchema = z.object({
  x1: z.number(),
  y1: z.number(),
  x2: z.number(),
  y2: z.number(),
  speaking_score: z.number(),
  active: z.boolean(),
  track_id: z.number().int(),
})

export type AsdFace = z.infer<typeof AsdFaceSchema>
// ASD DETECTION RESULT
//------------------------------------------------------


/////////////////////////////////////////
// ASD DETECTION RESULT SCHEMA
/////////////////////////////////////////

export const AsdDetectionResultSchema = z.object({
  frame_number: z.number().int(),
})

export type AsdDetectionResult = z.infer<typeof AsdDetectionResultSchema>
// ASD SCENE
//------------------------------------------------------


/////////////////////////////////////////
// ASD SCENE SCHEMA
/////////////////////////////////////////

export const AsdSceneSchema = z.object({
  scene_number: z.number().int(),
  start_seconds: z.number(),
  end_seconds: z.number(),
  start_timecode: z.string(),
  end_timecode: z.string(),
  start_frame: z.number().int(),
  end_frame: z.number().int(),
})

export type AsdScene = z.infer<typeof AsdSceneSchema>
// ASD RESULT
//------------------------------------------------------


/////////////////////////////////////////
// ASD RESULT SCHEMA
/////////////////////////////////////////

export const AsdResultSchema = z.object({
  rendered_video_path: z.string().nullable(),
  rendered_video_url: z.string().nullable(),
})

export type AsdResult = z.infer<typeof AsdResultSchema>
// ASD ROOT
//------------------------------------------------------


/////////////////////////////////////////
// ASD ROOT SCHEMA
/////////////////////////////////////////

export const AsdRootSchema = z.object({
  status: z.string(),
  success: z.boolean(),
  segment_id: z.number().int(),
  timestamp: z.string(),
  detection_count: z.number().int(),
  scenes_count: z.number().int(),
  has_rendered_video: z.boolean(),
})

export type AsdRoot = z.infer<typeof AsdRootSchema>

/////////////////////////////////////////
// SELECT & INCLUDE
/////////////////////////////////////////

// USER
//------------------------------------------------------

export const UserIncludeSchema: z.ZodType<Prisma.UserInclude> = z.object({
}).strict()

export const UserArgsSchema: z.ZodType<Prisma.UserDefaultArgs> = z.object({
  select: z.lazy(() => UserSelectSchema).optional(),
  include: z.lazy(() => UserIncludeSchema).optional(),
}).strict();

export const UserCountOutputTypeArgsSchema: z.ZodType<Prisma.UserCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => UserCountOutputTypeSelectSchema).nullish(),
}).strict();

export const UserCountOutputTypeSelectSchema: z.ZodType<Prisma.UserCountOutputTypeSelect> = z.object({
  sessions: z.boolean().optional(),
  accounts: z.boolean().optional(),
  apiKeys: z.boolean().optional(),
  passkeys: z.boolean().optional(),
  invitations: z.boolean().optional(),
  purchases: z.boolean().optional(),
  members: z.boolean().optional(),
  twofactors: z.boolean().optional(),
  aiChats: z.boolean().optional(),
  files: z.boolean().optional(),
  renderHistory: z.boolean().optional(),
  jobs: z.boolean().optional(),
  videoProjects: z.boolean().optional(),
}).strict();

export const UserSelectSchema: z.ZodType<Prisma.UserSelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  email: z.boolean().optional(),
  emailVerified: z.boolean().optional(),
  image: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  username: z.boolean().optional(),
  role: z.boolean().optional(),
  banned: z.boolean().optional(),
  banReason: z.boolean().optional(),
  banExpires: z.boolean().optional(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.boolean().optional(),
  locale: z.boolean().optional(),
  phoneNumber: z.boolean().optional(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.boolean().optional(),
  smsVerificationExpiresAt: z.boolean().optional(),
  smsVerificationAttempts: z.boolean().optional(),
  sessions: z.union([z.boolean(),z.lazy(() => SessionArgsSchema)]).optional(),
  accounts: z.union([z.boolean(),z.lazy(() => AccountArgsSchema)]).optional(),
  apiKeys: z.union([z.boolean(),z.lazy(() => ApiKeyArgsSchema)]).optional(),
  passkeys: z.union([z.boolean(),z.lazy(() => PasskeyArgsSchema)]).optional(),
  invitations: z.union([z.boolean(),z.lazy(() => InvitationArgsSchema)]).optional(),
  purchases: z.union([z.boolean(),z.lazy(() => PurchaseArgsSchema)]).optional(),
  members: z.union([z.boolean(),z.lazy(() => MemberArgsSchema)]).optional(),
  twofactors: z.union([z.boolean(),z.lazy(() => TwoFactorArgsSchema)]).optional(),
  aiChats: z.union([z.boolean(),z.lazy(() => AiChatArgsSchema)]).optional(),
  files: z.union([z.boolean(),z.lazy(() => AppFileArgsSchema)]).optional(),
  renderHistory: z.union([z.boolean(),z.lazy(() => RenderHistoryArgsSchema)]).optional(),
  jobs: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  videoProjects: z.union([z.boolean(),z.lazy(() => VideoProjectArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => UserCountOutputTypeArgsSchema)]).optional(),
}).strict()

// SESSION
//------------------------------------------------------

export const SessionIncludeSchema: z.ZodType<Prisma.SessionInclude> = z.object({
}).strict()

export const SessionArgsSchema: z.ZodType<Prisma.SessionDefaultArgs> = z.object({
  select: z.lazy(() => SessionSelectSchema).optional(),
  include: z.lazy(() => SessionIncludeSchema).optional(),
}).strict();

export const SessionSelectSchema: z.ZodType<Prisma.SessionSelect> = z.object({
  id: z.boolean().optional(),
  expiresAt: z.boolean().optional(),
  ipAddress: z.boolean().optional(),
  userAgent: z.boolean().optional(),
  userId: z.boolean().optional(),
  impersonatedBy: z.boolean().optional(),
  activeOrganizationId: z.boolean().optional(),
  token: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// ACCOUNT
//------------------------------------------------------

export const AccountIncludeSchema: z.ZodType<Prisma.AccountInclude> = z.object({
}).strict()

export const AccountArgsSchema: z.ZodType<Prisma.AccountDefaultArgs> = z.object({
  select: z.lazy(() => AccountSelectSchema).optional(),
  include: z.lazy(() => AccountIncludeSchema).optional(),
}).strict();

export const AccountSelectSchema: z.ZodType<Prisma.AccountSelect> = z.object({
  id: z.boolean().optional(),
  accountId: z.boolean().optional(),
  providerId: z.boolean().optional(),
  userId: z.boolean().optional(),
  accessToken: z.boolean().optional(),
  refreshToken: z.boolean().optional(),
  idToken: z.boolean().optional(),
  expiresAt: z.boolean().optional(),
  password: z.boolean().optional(),
  accessTokenExpiresAt: z.boolean().optional(),
  refreshTokenExpiresAt: z.boolean().optional(),
  scope: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// VERIFICATION
//------------------------------------------------------

export const VerificationArgsSchema: z.ZodType<Prisma.VerificationDefaultArgs> = z.object({
  select: z.lazy(() => VerificationSelectSchema).optional(),
}).strict();

export const VerificationSelectSchema: z.ZodType<Prisma.VerificationSelect> = z.object({
  id: z.boolean().optional(),
  identifier: z.boolean().optional(),
  value: z.boolean().optional(),
  expiresAt: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
}).strict()

// PASSKEY
//------------------------------------------------------

export const PasskeyIncludeSchema: z.ZodType<Prisma.PasskeyInclude> = z.object({
}).strict()

export const PasskeyArgsSchema: z.ZodType<Prisma.PasskeyDefaultArgs> = z.object({
  select: z.lazy(() => PasskeySelectSchema).optional(),
  include: z.lazy(() => PasskeyIncludeSchema).optional(),
}).strict();

export const PasskeySelectSchema: z.ZodType<Prisma.PasskeySelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  publicKey: z.boolean().optional(),
  userId: z.boolean().optional(),
  credentialID: z.boolean().optional(),
  counter: z.boolean().optional(),
  deviceType: z.boolean().optional(),
  backedUp: z.boolean().optional(),
  transports: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// TWO FACTOR
//------------------------------------------------------

export const TwoFactorIncludeSchema: z.ZodType<Prisma.TwoFactorInclude> = z.object({
}).strict()

export const TwoFactorArgsSchema: z.ZodType<Prisma.TwoFactorDefaultArgs> = z.object({
  select: z.lazy(() => TwoFactorSelectSchema).optional(),
  include: z.lazy(() => TwoFactorIncludeSchema).optional(),
}).strict();

export const TwoFactorSelectSchema: z.ZodType<Prisma.TwoFactorSelect> = z.object({
  id: z.boolean().optional(),
  secret: z.boolean().optional(),
  backupCodes: z.boolean().optional(),
  userId: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// ORGANIZATION
//------------------------------------------------------

export const OrganizationIncludeSchema: z.ZodType<Prisma.OrganizationInclude> = z.object({
}).strict()

export const OrganizationArgsSchema: z.ZodType<Prisma.OrganizationDefaultArgs> = z.object({
  select: z.lazy(() => OrganizationSelectSchema).optional(),
  include: z.lazy(() => OrganizationIncludeSchema).optional(),
}).strict();

export const OrganizationCountOutputTypeArgsSchema: z.ZodType<Prisma.OrganizationCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => OrganizationCountOutputTypeSelectSchema).nullish(),
}).strict();

export const OrganizationCountOutputTypeSelectSchema: z.ZodType<Prisma.OrganizationCountOutputTypeSelect> = z.object({
  members: z.boolean().optional(),
  apiKeys: z.boolean().optional(),
  invitations: z.boolean().optional(),
  purchases: z.boolean().optional(),
  aiChats: z.boolean().optional(),
  files: z.boolean().optional(),
  captionStyles: z.boolean().optional(),
  brandings: z.boolean().optional(),
  narratorProfiles: z.boolean().optional(),
  socialPosts: z.boolean().optional(),
  renderHistory: z.boolean().optional(),
  jobs: z.boolean().optional(),
  videoProjects: z.boolean().optional(),
}).strict();

export const OrganizationSelectSchema: z.ZodType<Prisma.OrganizationSelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  slug: z.boolean().optional(),
  logo: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  metadata: z.boolean().optional(),
  paymentsCustomerId: z.boolean().optional(),
  AutoArchiveDurationDays: z.boolean().optional(),
  members: z.union([z.boolean(),z.lazy(() => MemberArgsSchema)]).optional(),
  apiKeys: z.union([z.boolean(),z.lazy(() => ApiKeyArgsSchema)]).optional(),
  invitations: z.union([z.boolean(),z.lazy(() => InvitationArgsSchema)]).optional(),
  purchases: z.union([z.boolean(),z.lazy(() => PurchaseArgsSchema)]).optional(),
  aiChats: z.union([z.boolean(),z.lazy(() => AiChatArgsSchema)]).optional(),
  files: z.union([z.boolean(),z.lazy(() => AppFileArgsSchema)]).optional(),
  captionStyles: z.union([z.boolean(),z.lazy(() => CaptionStyleArgsSchema)]).optional(),
  brandings: z.union([z.boolean(),z.lazy(() => OrganizationBrandingArgsSchema)]).optional(),
  narratorProfiles: z.union([z.boolean(),z.lazy(() => OrganizationNarratorProfileArgsSchema)]).optional(),
  socialSettings: z.union([z.boolean(),z.lazy(() => OrganizationSocialSettingsArgsSchema)]).optional(),
  socialPosts: z.union([z.boolean(),z.lazy(() => OrganizationSocialPostArgsSchema)]).optional(),
  renderHistory: z.union([z.boolean(),z.lazy(() => RenderHistoryArgsSchema)]).optional(),
  jobs: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  videoProjects: z.union([z.boolean(),z.lazy(() => VideoProjectArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => OrganizationCountOutputTypeArgsSchema)]).optional(),
}).strict()

// MEMBER
//------------------------------------------------------

export const MemberIncludeSchema: z.ZodType<Prisma.MemberInclude> = z.object({
}).strict()

export const MemberArgsSchema: z.ZodType<Prisma.MemberDefaultArgs> = z.object({
  select: z.lazy(() => MemberSelectSchema).optional(),
  include: z.lazy(() => MemberIncludeSchema).optional(),
}).strict();

export const MemberSelectSchema: z.ZodType<Prisma.MemberSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  userId: z.boolean().optional(),
  role: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// INVITATION
//------------------------------------------------------

export const InvitationIncludeSchema: z.ZodType<Prisma.InvitationInclude> = z.object({
}).strict()

export const InvitationArgsSchema: z.ZodType<Prisma.InvitationDefaultArgs> = z.object({
  select: z.lazy(() => InvitationSelectSchema).optional(),
  include: z.lazy(() => InvitationIncludeSchema).optional(),
}).strict();

export const InvitationSelectSchema: z.ZodType<Prisma.InvitationSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  email: z.boolean().optional(),
  role: z.boolean().optional(),
  status: z.boolean().optional(),
  expiresAt: z.boolean().optional(),
  inviterId: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// PURCHASE
//------------------------------------------------------

export const PurchaseIncludeSchema: z.ZodType<Prisma.PurchaseInclude> = z.object({
}).strict()

export const PurchaseArgsSchema: z.ZodType<Prisma.PurchaseDefaultArgs> = z.object({
  select: z.lazy(() => PurchaseSelectSchema).optional(),
  include: z.lazy(() => PurchaseIncludeSchema).optional(),
}).strict();

export const PurchaseCountOutputTypeArgsSchema: z.ZodType<Prisma.PurchaseCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => PurchaseCountOutputTypeSelectSchema).nullish(),
}).strict();

export const PurchaseCountOutputTypeSelectSchema: z.ZodType<Prisma.PurchaseCountOutputTypeSelect> = z.object({
  jobs: z.boolean().optional(),
}).strict();

export const PurchaseSelectSchema: z.ZodType<Prisma.PurchaseSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  userId: z.boolean().optional(),
  type: z.boolean().optional(),
  customerId: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.boolean().optional(),
  credit: z.boolean().optional(),
  productId: z.boolean().optional(),
  status: z.boolean().optional(),
  startDate: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
  jobs: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => PurchaseCountOutputTypeArgsSchema)]).optional(),
}).strict()

// AI CHAT
//------------------------------------------------------

export const AiChatIncludeSchema: z.ZodType<Prisma.AiChatInclude> = z.object({
}).strict()

export const AiChatArgsSchema: z.ZodType<Prisma.AiChatDefaultArgs> = z.object({
  select: z.lazy(() => AiChatSelectSchema).optional(),
  include: z.lazy(() => AiChatIncludeSchema).optional(),
}).strict();

export const AiChatSelectSchema: z.ZodType<Prisma.AiChatSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  userId: z.boolean().optional(),
  title: z.boolean().optional(),
  messages: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
}).strict()

// API KEY
//------------------------------------------------------

export const ApiKeyIncludeSchema: z.ZodType<Prisma.ApiKeyInclude> = z.object({
}).strict()

export const ApiKeyArgsSchema: z.ZodType<Prisma.ApiKeyDefaultArgs> = z.object({
  select: z.lazy(() => ApiKeySelectSchema).optional(),
  include: z.lazy(() => ApiKeyIncludeSchema).optional(),
}).strict();

export const ApiKeyCountOutputTypeArgsSchema: z.ZodType<Prisma.ApiKeyCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => ApiKeyCountOutputTypeSelectSchema).nullish(),
}).strict();

export const ApiKeyCountOutputTypeSelectSchema: z.ZodType<Prisma.ApiKeyCountOutputTypeSelect> = z.object({
  jobs: z.boolean().optional(),
  renderHistories: z.boolean().optional(),
}).strict();

export const ApiKeySelectSchema: z.ZodType<Prisma.ApiKeySelect> = z.object({
  id: z.boolean().optional(),
  userId: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  name: z.boolean().optional(),
  key: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  jobs: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  renderHistories: z.union([z.boolean(),z.lazy(() => RenderHistoryArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => ApiKeyCountOutputTypeArgsSchema)]).optional(),
}).strict()

// JOB
//------------------------------------------------------

export const JobIncludeSchema: z.ZodType<Prisma.JobInclude> = z.object({
}).strict()

export const JobArgsSchema: z.ZodType<Prisma.JobDefaultArgs> = z.object({
  select: z.lazy(() => JobSelectSchema).optional(),
  include: z.lazy(() => JobIncludeSchema).optional(),
}).strict();

export const JobCountOutputTypeArgsSchema: z.ZodType<Prisma.JobCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => JobCountOutputTypeSelectSchema).nullish(),
}).strict();

export const JobCountOutputTypeSelectSchema: z.ZodType<Prisma.JobCountOutputTypeSelect> = z.object({
  files: z.boolean().optional(),
  renderHistories: z.boolean().optional(),
  socialPosts: z.boolean().optional(),
}).strict();

export const JobSelectSchema: z.ZodType<Prisma.JobSelect> = z.object({
  id: z.boolean().optional(),
  title: z.boolean().optional(),
  description: z.boolean().optional(),
  userId: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  keyId: z.boolean().optional(),
  purchaseId: z.boolean().optional(),
  cost: z.boolean().optional(),
  costBreakDown: z.union([z.boolean(),z.lazy(() => CostBreakdownArgsSchema)]).optional(),
  creditUsageStatus: z.boolean().optional(),
  status: z.boolean().optional(),
  webhook: z.union([z.boolean(),z.lazy(() => WebhookArgsSchema)]).optional(),
  errorMessage: z.boolean().optional(),
  functionName: z.boolean().optional(),
  source: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  startTime: z.boolean().optional(),
  endTime: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  key: z.union([z.boolean(),z.lazy(() => ApiKeyArgsSchema)]).optional(),
  purchase: z.union([z.boolean(),z.lazy(() => PurchaseArgsSchema)]).optional(),
  video: z.union([z.boolean(),z.lazy(() => VideoArgsSchema)]).optional(),
  videoProject: z.union([z.boolean(),z.lazy(() => VideoProjectArgsSchema)]).optional(),
  files: z.union([z.boolean(),z.lazy(() => AppFileArgsSchema)]).optional(),
  renderHistories: z.union([z.boolean(),z.lazy(() => RenderHistoryArgsSchema)]).optional(),
  socialPosts: z.union([z.boolean(),z.lazy(() => OrganizationSocialPostArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => JobCountOutputTypeArgsSchema)]).optional(),
}).strict()

// VIDEO
//------------------------------------------------------

export const VideoIncludeSchema: z.ZodType<Prisma.VideoInclude> = z.object({
}).strict()

export const VideoArgsSchema: z.ZodType<Prisma.VideoDefaultArgs> = z.object({
  select: z.lazy(() => VideoSelectSchema).optional(),
  include: z.lazy(() => VideoIncludeSchema).optional(),
}).strict();

export const VideoSelectSchema: z.ZodType<Prisma.VideoSelect> = z.object({
  id: z.boolean().optional(),
  jobId: z.boolean().optional(),
  sourceId: z.boolean().optional(),
  videoLength: z.boolean().optional(),
  title: z.boolean().optional(),
  files: z.union([z.boolean(),z.lazy(() => SourceFilesArgsSchema)]).optional(),
  options: z.union([z.boolean(),z.lazy(() => VideoShortsOptionsArgsSchema)]).optional(),
  choosedDefinition: z.boolean().optional(),
  transcription: z.union([z.boolean(),z.lazy(() => ITranscriptionArgsSchema)]).optional(),
  shorts: z.union([z.boolean(),z.lazy(() => ShortArgsSchema)]).optional(),
  status: z.boolean().optional(),
  statusHistory: z.union([z.boolean(),z.lazy(() => StatusHistoryArgsSchema)]).optional(),
  createdAt: z.boolean().optional(),
  job: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
}).strict()

// VIDEO PROJECT
//------------------------------------------------------

export const VideoProjectIncludeSchema: z.ZodType<Prisma.VideoProjectInclude> = z.object({
}).strict()

export const VideoProjectArgsSchema: z.ZodType<Prisma.VideoProjectDefaultArgs> = z.object({
  select: z.lazy(() => VideoProjectSelectSchema).optional(),
  include: z.lazy(() => VideoProjectIncludeSchema).optional(),
}).strict();

export const VideoProjectSelectSchema: z.ZodType<Prisma.VideoProjectSelect> = z.object({
  id: z.boolean().optional(),
  jobId: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  createdByUserId: z.boolean().optional(),
  title: z.boolean().optional(),
  status: z.boolean().optional(),
  inputBrief: z.boolean().optional(),
  document: z.boolean().optional(),
  validation: z.boolean().optional(),
  errorMessage: z.boolean().optional(),
  cost: z.boolean().optional(),
  publishedAt: z.boolean().optional(),
  publishTag: z.boolean().optional(),
  episodeSummary: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  isDeleted: z.boolean().optional(),
  job: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  createdBy: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
  design: z.union([z.boolean(),z.lazy(() => VideoDesignArgsSchema)]).optional(),
}).strict()

// VIDEO DESIGN
//------------------------------------------------------

export const VideoDesignIncludeSchema: z.ZodType<Prisma.VideoDesignInclude> = z.object({
}).strict()

export const VideoDesignArgsSchema: z.ZodType<Prisma.VideoDesignDefaultArgs> = z.object({
  select: z.lazy(() => VideoDesignSelectSchema).optional(),
  include: z.lazy(() => VideoDesignIncludeSchema).optional(),
}).strict();

export const VideoDesignSelectSchema: z.ZodType<Prisma.VideoDesignSelect> = z.object({
  id: z.boolean().optional(),
  videoProjectId: z.boolean().optional(),
  jobId: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  status: z.boolean().optional(),
  design: z.boolean().optional(),
  fps: z.boolean().optional(),
  durationMs: z.boolean().optional(),
  width: z.boolean().optional(),
  height: z.boolean().optional(),
  latestRenderHistoryId: z.boolean().optional(),
  latestMp4Url: z.boolean().optional(),
  compiledAt: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  videoProject: z.union([z.boolean(),z.lazy(() => VideoProjectArgsSchema)]).optional(),
}).strict()

// APP FILE
//------------------------------------------------------

export const AppFileIncludeSchema: z.ZodType<Prisma.AppFileInclude> = z.object({
}).strict()

export const AppFileArgsSchema: z.ZodType<Prisma.AppFileDefaultArgs> = z.object({
  select: z.lazy(() => AppFileSelectSchema).optional(),
  include: z.lazy(() => AppFileIncludeSchema).optional(),
}).strict();

export const AppFileCountOutputTypeArgsSchema: z.ZodType<Prisma.AppFileCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => AppFileCountOutputTypeSelectSchema).nullish(),
}).strict();

export const AppFileCountOutputTypeSelectSchema: z.ZodType<Prisma.AppFileCountOutputTypeSelect> = z.object({
  renderHistories: z.boolean().optional(),
}).strict();

export const AppFileSelectSchema: z.ZodType<Prisma.AppFileSelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  userId: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  jobId: z.boolean().optional(),
  type: z.boolean().optional(),
  status: z.boolean().optional(),
  description: z.boolean().optional(),
  tags: z.boolean().optional(),
  size: z.boolean().optional(),
  length: z.boolean().optional(),
  key: z.boolean().optional(),
  thumbnailUrl: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  job: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  renderHistories: z.union([z.boolean(),z.lazy(() => RenderHistoryArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => AppFileCountOutputTypeArgsSchema)]).optional(),
}).strict()

// CAPTION STYLE
//------------------------------------------------------

export const CaptionStyleIncludeSchema: z.ZodType<Prisma.CaptionStyleInclude> = z.object({
}).strict()

export const CaptionStyleArgsSchema: z.ZodType<Prisma.CaptionStyleDefaultArgs> = z.object({
  select: z.lazy(() => CaptionStyleSelectSchema).optional(),
  include: z.lazy(() => CaptionStyleIncludeSchema).optional(),
}).strict();

export const CaptionStyleSelectSchema: z.ZodType<Prisma.CaptionStyleSelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  description: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  style: z.boolean().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
}).strict()

// ORGANIZATION BRANDING
//------------------------------------------------------

export const OrganizationBrandingIncludeSchema: z.ZodType<Prisma.OrganizationBrandingInclude> = z.object({
}).strict()

export const OrganizationBrandingArgsSchema: z.ZodType<Prisma.OrganizationBrandingDefaultArgs> = z.object({
  select: z.lazy(() => OrganizationBrandingSelectSchema).optional(),
  include: z.lazy(() => OrganizationBrandingIncludeSchema).optional(),
}).strict();

export const OrganizationBrandingSelectSchema: z.ZodType<Prisma.OrganizationBrandingSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  name: z.boolean().optional(),
  description: z.boolean().optional(),
  preset: z.boolean().optional(),
  size: z.boolean().optional(),
  options: z.boolean().optional(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
}).strict()

// ORGANIZATION NARRATOR PROFILE
//------------------------------------------------------

export const OrganizationNarratorProfileIncludeSchema: z.ZodType<Prisma.OrganizationNarratorProfileInclude> = z.object({
}).strict()

export const OrganizationNarratorProfileArgsSchema: z.ZodType<Prisma.OrganizationNarratorProfileDefaultArgs> = z.object({
  select: z.lazy(() => OrganizationNarratorProfileSelectSchema).optional(),
  include: z.lazy(() => OrganizationNarratorProfileIncludeSchema).optional(),
}).strict();

export const OrganizationNarratorProfileSelectSchema: z.ZodType<Prisma.OrganizationNarratorProfileSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  name: z.boolean().optional(),
  description: z.boolean().optional(),
  narratorName: z.boolean().optional(),
  gender: z.boolean().optional(),
  provider: z.boolean().optional(),
  voiceId: z.boolean().optional(),
  personality: z.boolean().optional(),
  styleNotes: z.boolean().optional(),
  ttsStyle: z.boolean().optional(),
  ttsPace: z.boolean().optional(),
  ttsAccent: z.boolean().optional(),
  ttsNotes: z.boolean().optional(),
  channelName: z.boolean().optional(),
  channelSignOff: z.boolean().optional(),
  overlayText: z.boolean().optional(),
  overlayTextDelayMs: z.boolean().optional(),
  overlayTextDurationMs: z.boolean().optional(),
  visualDefaults: z.boolean().optional(),
  speechSpeed: z.boolean().optional(),
  avatarFileAppId: z.boolean().optional(),
  avatarPhotos: z.boolean().optional(),
  avatarMinDurationMs: z.boolean().optional(),
  avatarMaxDurationMs: z.boolean().optional(),
  avatarAppearEveryMs: z.boolean().optional(),
  avatarIntroHoldMs: z.boolean().optional(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
}).strict()

// ORGANIZATION SOCIAL SETTINGS
//------------------------------------------------------

export const OrganizationSocialSettingsIncludeSchema: z.ZodType<Prisma.OrganizationSocialSettingsInclude> = z.object({
}).strict()

export const OrganizationSocialSettingsArgsSchema: z.ZodType<Prisma.OrganizationSocialSettingsDefaultArgs> = z.object({
  select: z.lazy(() => OrganizationSocialSettingsSelectSchema).optional(),
  include: z.lazy(() => OrganizationSocialSettingsIncludeSchema).optional(),
}).strict();

export const OrganizationSocialSettingsSelectSchema: z.ZodType<Prisma.OrganizationSocialSettingsSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  provider: z.boolean().optional(),
  providerProfileId: z.boolean().optional(),
  isEnabled: z.boolean().optional(),
  approveApiPosts: z.boolean().optional(),
  defaultTimezone: z.boolean().optional(),
  defaultPlatforms: z.boolean().optional(),
  queueSlots: z.boolean().optional(),
  maxPostsPerSlot: z.boolean().optional(),
  maxShortsOfSameVideo: z.boolean().optional(),
  maxShortsPerWeek: z.boolean().optional(),
  minimumVideoDurationSeconds: z.boolean().optional(),
  processOnlyPlaylistId: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
}).strict()

// ORGANIZATION SOCIAL POST
//------------------------------------------------------

export const OrganizationSocialPostIncludeSchema: z.ZodType<Prisma.OrganizationSocialPostInclude> = z.object({
}).strict()

export const OrganizationSocialPostArgsSchema: z.ZodType<Prisma.OrganizationSocialPostDefaultArgs> = z.object({
  select: z.lazy(() => OrganizationSocialPostSelectSchema).optional(),
  include: z.lazy(() => OrganizationSocialPostIncludeSchema).optional(),
}).strict();

export const OrganizationSocialPostSelectSchema: z.ZodType<Prisma.OrganizationSocialPostSelect> = z.object({
  id: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  jobId: z.boolean().optional(),
  provider: z.boolean().optional(),
  status: z.boolean().optional(),
  providerJobId: z.boolean().optional(),
  platforms: z.boolean().optional(),
  title: z.boolean().optional(),
  caption: z.boolean().optional(),
  videoUrl: z.boolean().optional(),
  scheduledFor: z.boolean().optional(),
  publishedAt: z.boolean().optional(),
  lastError: z.boolean().optional(),
  metadata: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  job: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
}).strict()

// RENDER HISTORY
//------------------------------------------------------

export const RenderHistoryIncludeSchema: z.ZodType<Prisma.RenderHistoryInclude> = z.object({
}).strict()

export const RenderHistoryArgsSchema: z.ZodType<Prisma.RenderHistoryDefaultArgs> = z.object({
  select: z.lazy(() => RenderHistorySelectSchema).optional(),
  include: z.lazy(() => RenderHistoryIncludeSchema).optional(),
}).strict();

export const RenderHistorySelectSchema: z.ZodType<Prisma.RenderHistorySelect> = z.object({
  id: z.boolean().optional(),
  renderId: z.boolean().optional(),
  organizationId: z.boolean().optional(),
  userId: z.boolean().optional(),
  keyId: z.boolean().optional(),
  jobId: z.boolean().optional(),
  appFileId: z.boolean().optional(),
  target: z.boolean().optional(),
  data: z.boolean().optional(),
  fileUrl: z.boolean().optional(),
  fileSize: z.boolean().optional(),
  cost: z.boolean().optional(),
  status: z.boolean().optional(),
  errorMessage: z.boolean().optional(),
  priority: z.boolean().optional(),
  retryCount: z.boolean().optional(),
  maxRetries: z.boolean().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  organization: z.union([z.boolean(),z.lazy(() => OrganizationArgsSchema)]).optional(),
  user: z.union([z.boolean(),z.lazy(() => UserArgsSchema)]).optional(),
  key: z.union([z.boolean(),z.lazy(() => ApiKeyArgsSchema)]).optional(),
  job: z.union([z.boolean(),z.lazy(() => JobArgsSchema)]).optional(),
  appFile: z.union([z.boolean(),z.lazy(() => AppFileArgsSchema)]).optional(),
}).strict()

// EXTERNAL KEYS
//------------------------------------------------------

export const ExternalKeysArgsSchema: z.ZodType<Prisma.ExternalKeysDefaultArgs> = z.object({
  select: z.lazy(() => ExternalKeysSelectSchema).optional(),
}).strict();

export const ExternalKeysSelectSchema: z.ZodType<Prisma.ExternalKeysSelect> = z.object({
  id: z.boolean().optional(),
  key: z.boolean().optional(),
  name: z.boolean().optional(),
  status: z.boolean().optional(),
  type: z.boolean().optional(),
  errors: z.boolean().optional(),
  currency: z.boolean().optional(),
  usage: z.boolean().optional(),
  usageCount: z.boolean().optional(),
  lastResetDate: z.boolean().optional(),
  monthlyUsageCount: z.boolean().optional(),
  lastUsedDate: z.boolean().optional(),
  cooldownUntil: z.boolean().optional(),
  monthlyUsage: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
}).strict()

// PROXY
//------------------------------------------------------

export const ProxyIncludeSchema: z.ZodType<Prisma.ProxyInclude> = z.object({
}).strict()

export const ProxyArgsSchema: z.ZodType<Prisma.ProxyDefaultArgs> = z.object({
  select: z.lazy(() => ProxySelectSchema).optional(),
  include: z.lazy(() => ProxyIncludeSchema).optional(),
}).strict();

export const ProxyCountOutputTypeArgsSchema: z.ZodType<Prisma.ProxyCountOutputTypeDefaultArgs> = z.object({
  select: z.lazy(() => ProxyCountOutputTypeSelectSchema).nullish(),
}).strict();

export const ProxyCountOutputTypeSelectSchema: z.ZodType<Prisma.ProxyCountOutputTypeSelect> = z.object({
  cookieIdentities: z.boolean().optional(),
}).strict();

export const ProxySelectSchema: z.ZodType<Prisma.ProxySelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  proxyUrl: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  cookieIdentities: z.union([z.boolean(),z.lazy(() => CookieIdentityArgsSchema)]).optional(),
  _count: z.union([z.boolean(),z.lazy(() => ProxyCountOutputTypeArgsSchema)]).optional(),
}).strict()

// COOKIE IDENTITY
//------------------------------------------------------

export const CookieIdentityIncludeSchema: z.ZodType<Prisma.CookieIdentityInclude> = z.object({
}).strict()

export const CookieIdentityArgsSchema: z.ZodType<Prisma.CookieIdentityDefaultArgs> = z.object({
  select: z.lazy(() => CookieIdentitySelectSchema).optional(),
  include: z.lazy(() => CookieIdentityIncludeSchema).optional(),
}).strict();

export const CookieIdentitySelectSchema: z.ZodType<Prisma.CookieIdentitySelect> = z.object({
  id: z.boolean().optional(),
  name: z.boolean().optional(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.boolean().optional(),
  proxyId: z.boolean().optional(),
  cookieContent: z.boolean().optional(),
  useCount: z.boolean().optional(),
  errors: z.boolean().optional(),
  createdAt: z.boolean().optional(),
  updatedAt: z.boolean().optional(),
  proxy: z.union([z.boolean(),z.lazy(() => ProxyArgsSchema)]).optional(),
}).strict()

// COST BREAKDOWN
//------------------------------------------------------

export const CostBreakdownArgsSchema: z.ZodType<Prisma.CostBreakdownDefaultArgs> = z.object({
  select: z.lazy(() => CostBreakdownSelectSchema).optional(),
}).strict();

export const CostBreakdownSelectSchema: z.ZodType<Prisma.CostBreakdownSelect> = z.object({
  shortGen: z.boolean().optional(),
  youtubeProcessing: z.boolean().optional(),
  render: z.boolean().optional(),
  bRoll: z.boolean().optional(),
}).strict()

// WEBHOOK
//------------------------------------------------------

export const WebhookArgsSchema: z.ZodType<Prisma.WebhookDefaultArgs> = z.object({
  select: z.lazy(() => WebhookSelectSchema).optional(),
}).strict();

export const WebhookSelectSchema: z.ZodType<Prisma.WebhookSelect> = z.object({
  url: z.boolean().optional(),
  status: z.boolean().optional(),
  retryCount: z.boolean().optional(),
  error: z.boolean().optional(),
}).strict()

// SOURCE FILES
//------------------------------------------------------

export const SourceFilesArgsSchema: z.ZodType<Prisma.SourceFilesDefaultArgs> = z.object({
  select: z.lazy(() => SourceFilesSelectSchema).optional(),
}).strict();

export const SourceFilesSelectSchema: z.ZodType<Prisma.SourceFilesSelect> = z.object({
  isInS3: z.boolean().optional(),
  target: z.boolean().optional(),
  targetFPS: z.boolean().optional(),
  preview: z.boolean().optional(),
  previewFPS: z.boolean().optional(),
}).strict()

// VIDEO SHORTS OPTIONS
//------------------------------------------------------

export const VideoShortsOptionsIncludeSchema: z.ZodType<Prisma.VideoShortsOptionsInclude> = z.object({
}).strict()

export const VideoShortsOptionsArgsSchema: z.ZodType<Prisma.VideoShortsOptionsDefaultArgs> = z.object({
  select: z.lazy(() => VideoShortsOptionsSelectSchema).optional(),
  include: z.lazy(() => VideoShortsOptionsIncludeSchema).optional(),
}).strict();

export const VideoShortsOptionsSelectSchema: z.ZodType<Prisma.VideoShortsOptionsSelect> = z.object({
  predefinedShorts: z.union([z.boolean(),z.lazy(() => PredefinedShortsArgsSchema)]).optional(),
  isRTL: z.boolean().optional(),
}).strict()

// I TRANSCRIPTION
//------------------------------------------------------

export const ITranscriptionIncludeSchema: z.ZodType<Prisma.ITranscriptionInclude> = z.object({
}).strict()

export const ITranscriptionArgsSchema: z.ZodType<Prisma.ITranscriptionDefaultArgs> = z.object({
  select: z.lazy(() => ITranscriptionSelectSchema).optional(),
  include: z.lazy(() => ITranscriptionIncludeSchema).optional(),
}).strict();

export const ITranscriptionSelectSchema: z.ZodType<Prisma.ITranscriptionSelect> = z.object({
  prediction: z.union([z.boolean(),z.lazy(() => PredictionArgsSchema)]).optional(),
}).strict()

// SHORT
//------------------------------------------------------

export const ShortIncludeSchema: z.ZodType<Prisma.ShortInclude> = z.object({
}).strict()

export const ShortArgsSchema: z.ZodType<Prisma.ShortDefaultArgs> = z.object({
  select: z.lazy(() => ShortSelectSchema).optional(),
  include: z.lazy(() => ShortIncludeSchema).optional(),
}).strict();

export const ShortSelectSchema: z.ZodType<Prisma.ShortSelect> = z.object({
  id: z.boolean().optional(),
  title: z.boolean().optional(),
  reason: z.boolean().optional(),
  startId: z.boolean().optional(),
  endId: z.boolean().optional(),
  startTime: z.boolean().optional(),
  endTime: z.boolean().optional(),
  status: z.boolean().optional(),
  text: z.boolean().optional(),
  cropFileUrl: z.boolean().optional(),
  cropFilekey: z.boolean().optional(),
  description: z.boolean().optional(),
  score: z.union([z.boolean(),z.lazy(() => ShortScoreArgsSchema)]).optional(),
  appFileId: z.boolean().optional(),
  asd: z.union([z.boolean(),z.lazy(() => AsdRootArgsSchema)]).optional(),
  bRolls: z.union([z.boolean(),z.lazy(() => BRollItemArgsSchema)]).optional(),
}).strict()

// STATUS HISTORY
//------------------------------------------------------

export const StatusHistoryArgsSchema: z.ZodType<Prisma.StatusHistoryDefaultArgs> = z.object({
  select: z.lazy(() => StatusHistorySelectSchema).optional(),
}).strict();

export const StatusHistorySelectSchema: z.ZodType<Prisma.StatusHistorySelect> = z.object({
  status: z.boolean().optional(),
  startTime: z.boolean().optional(),
  endTime: z.boolean().optional(),
}).strict()

// PREDEFINED SHORTS
//------------------------------------------------------

export const PredefinedShortsArgsSchema: z.ZodType<Prisma.PredefinedShortsDefaultArgs> = z.object({
  select: z.lazy(() => PredefinedShortsSelectSchema).optional(),
}).strict();

export const PredefinedShortsSelectSchema: z.ZodType<Prisma.PredefinedShortsSelect> = z.object({
  title: z.boolean().optional(),
  start: z.boolean().optional(),
  end: z.boolean().optional(),
}).strict()

// PREDICTION
//------------------------------------------------------

export const PredictionIncludeSchema: z.ZodType<Prisma.PredictionInclude> = z.object({
}).strict()

export const PredictionArgsSchema: z.ZodType<Prisma.PredictionDefaultArgs> = z.object({
  select: z.lazy(() => PredictionSelectSchema).optional(),
  include: z.lazy(() => PredictionIncludeSchema).optional(),
}).strict();

export const PredictionSelectSchema: z.ZodType<Prisma.PredictionSelect> = z.object({
  words: z.union([z.boolean(),z.lazy(() => WordArgsSchema)]).optional(),
  language: z.boolean().optional(),
  transcription: z.boolean().optional(),
  confidence: z.boolean().optional(),
  time_begin: z.boolean().optional(),
  time_end: z.boolean().optional(),
  speaker: z.boolean().optional(),
  channel: z.boolean().optional(),
}).strict()

// SHORT SCORE
//------------------------------------------------------

export const ShortScoreArgsSchema: z.ZodType<Prisma.ShortScoreDefaultArgs> = z.object({
  select: z.lazy(() => ShortScoreSelectSchema).optional(),
}).strict();

export const ShortScoreSelectSchema: z.ZodType<Prisma.ShortScoreSelect> = z.object({
  final_creative_score: z.boolean().optional(),
  narrative_payoff_score: z.boolean().optional(),
  hook_quality_score: z.boolean().optional(),
  engagement_potential_score: z.boolean().optional(),
  brand_alignment_score: z.boolean().optional(),
}).strict()

// ASD ROOT
//------------------------------------------------------

export const AsdRootIncludeSchema: z.ZodType<Prisma.AsdRootInclude> = z.object({
}).strict()

export const AsdRootArgsSchema: z.ZodType<Prisma.AsdRootDefaultArgs> = z.object({
  select: z.lazy(() => AsdRootSelectSchema).optional(),
  include: z.lazy(() => AsdRootIncludeSchema).optional(),
}).strict();

export const AsdRootSelectSchema: z.ZodType<Prisma.AsdRootSelect> = z.object({
  status: z.boolean().optional(),
  success: z.boolean().optional(),
  segment_id: z.boolean().optional(),
  timestamp: z.boolean().optional(),
  result: z.union([z.boolean(),z.lazy(() => AsdResultArgsSchema)]).optional(),
  video_info: z.union([z.boolean(),z.lazy(() => AsdVideoInfoArgsSchema)]).optional(),
  detection_count: z.boolean().optional(),
  scenes_count: z.boolean().optional(),
  has_rendered_video: z.boolean().optional(),
  config: z.union([z.boolean(),z.lazy(() => AsdConfigArgsSchema)]).optional(),
}).strict()

// B ROLL ITEM
//------------------------------------------------------

export const BRollItemArgsSchema: z.ZodType<Prisma.BRollItemDefaultArgs> = z.object({
  select: z.lazy(() => BRollItemSelectSchema).optional(),
}).strict();

export const BRollItemSelectSchema: z.ZodType<Prisma.BRollItemSelect> = z.object({
  id: z.boolean().optional(),
  sentenceIds: z.boolean().optional(),
  fileUrl: z.boolean().optional(),
  fileType: z.boolean().optional(),
  pexelsId: z.boolean().optional(),
  startTime: z.boolean().optional(),
  endTime: z.boolean().optional(),
  position: z.boolean().optional(),
  metadata: z.boolean().optional(),
}).strict()

// WORD
//------------------------------------------------------

export const WordArgsSchema: z.ZodType<Prisma.WordDefaultArgs> = z.object({
  select: z.lazy(() => WordSelectSchema).optional(),
}).strict();

export const WordSelectSchema: z.ZodType<Prisma.WordSelect> = z.object({
  word: z.boolean().optional(),
  time_begin: z.boolean().optional(),
  time_end: z.boolean().optional(),
  confidence: z.boolean().optional(),
}).strict()

// ASD RESULT
//------------------------------------------------------

export const AsdResultIncludeSchema: z.ZodType<Prisma.AsdResultInclude> = z.object({
}).strict()

export const AsdResultArgsSchema: z.ZodType<Prisma.AsdResultDefaultArgs> = z.object({
  select: z.lazy(() => AsdResultSelectSchema).optional(),
  include: z.lazy(() => AsdResultIncludeSchema).optional(),
}).strict();

export const AsdResultSelectSchema: z.ZodType<Prisma.AsdResultSelect> = z.object({
  detection_results: z.union([z.boolean(),z.lazy(() => AsdDetectionResultArgsSchema)]).optional(),
  video_info: z.union([z.boolean(),z.lazy(() => AsdVideoInfoArgsSchema)]).optional(),
  scenes: z.union([z.boolean(),z.lazy(() => AsdSceneArgsSchema)]).optional(),
  rendered_video_path: z.boolean().optional(),
  rendered_video_url: z.boolean().optional(),
}).strict()

// ASD VIDEO INFO
//------------------------------------------------------

export const AsdVideoInfoIncludeSchema: z.ZodType<Prisma.AsdVideoInfoInclude> = z.object({
}).strict()

export const AsdVideoInfoArgsSchema: z.ZodType<Prisma.AsdVideoInfoDefaultArgs> = z.object({
  select: z.lazy(() => AsdVideoInfoSelectSchema).optional(),
  include: z.lazy(() => AsdVideoInfoIncludeSchema).optional(),
}).strict();

export const AsdVideoInfoSelectSchema: z.ZodType<Prisma.AsdVideoInfoSelect> = z.object({
  fps: z.boolean().optional(),
  processing_fps: z.boolean().optional(),
  total_frames: z.boolean().optional(),
  duration: z.boolean().optional(),
  start_time: z.boolean().optional(),
  end_time: z.boolean().optional(),
  frame_numbers_start_from: z.boolean().optional(),
  resolution: z.union([z.boolean(),z.lazy(() => AsdResolutionArgsSchema)]).optional(),
}).strict()

// ASD CONFIG
//------------------------------------------------------

export const AsdConfigIncludeSchema: z.ZodType<Prisma.AsdConfigInclude> = z.object({
}).strict()

export const AsdConfigArgsSchema: z.ZodType<Prisma.AsdConfigDefaultArgs> = z.object({
  select: z.lazy(() => AsdConfigSelectSchema).optional(),
  include: z.lazy(() => AsdConfigIncludeSchema).optional(),
}).strict();

export const AsdConfigSelectSchema: z.ZodType<Prisma.AsdConfigSelect> = z.object({
  camera: z.union([z.boolean(),z.lazy(() => AsdCameraConfigArgsSchema)]).optional(),
  layout: z.union([z.boolean(),z.lazy(() => AsdLayoutConfigArgsSchema)]).optional(),
  intelligence: z.union([z.boolean(),z.lazy(() => AsdIntelligenceConfigArgsSchema)]).optional(),
  overlaysAndTiming: z.union([z.boolean(),z.lazy(() => AsdOverlaysAndTimingConfigArgsSchema)]).optional(),
}).strict()

// ASD DETECTION RESULT
//------------------------------------------------------

export const AsdDetectionResultIncludeSchema: z.ZodType<Prisma.AsdDetectionResultInclude> = z.object({
}).strict()

export const AsdDetectionResultArgsSchema: z.ZodType<Prisma.AsdDetectionResultDefaultArgs> = z.object({
  select: z.lazy(() => AsdDetectionResultSelectSchema).optional(),
  include: z.lazy(() => AsdDetectionResultIncludeSchema).optional(),
}).strict();

export const AsdDetectionResultSelectSchema: z.ZodType<Prisma.AsdDetectionResultSelect> = z.object({
  frame_number: z.boolean().optional(),
  faces: z.union([z.boolean(),z.lazy(() => AsdFaceArgsSchema)]).optional(),
}).strict()

// ASD SCENE
//------------------------------------------------------

export const AsdSceneArgsSchema: z.ZodType<Prisma.AsdSceneDefaultArgs> = z.object({
  select: z.lazy(() => AsdSceneSelectSchema).optional(),
}).strict();

export const AsdSceneSelectSchema: z.ZodType<Prisma.AsdSceneSelect> = z.object({
  scene_number: z.boolean().optional(),
  start_seconds: z.boolean().optional(),
  end_seconds: z.boolean().optional(),
  start_timecode: z.boolean().optional(),
  end_timecode: z.boolean().optional(),
  start_frame: z.boolean().optional(),
  end_frame: z.boolean().optional(),
}).strict()

// ASD RESOLUTION
//------------------------------------------------------

export const AsdResolutionArgsSchema: z.ZodType<Prisma.AsdResolutionDefaultArgs> = z.object({
  select: z.lazy(() => AsdResolutionSelectSchema).optional(),
}).strict();

export const AsdResolutionSelectSchema: z.ZodType<Prisma.AsdResolutionSelect> = z.object({
  width: z.boolean().optional(),
  height: z.boolean().optional(),
}).strict()

// ASD CAMERA CONFIG
//------------------------------------------------------

export const AsdCameraConfigArgsSchema: z.ZodType<Prisma.AsdCameraConfigDefaultArgs> = z.object({
  select: z.lazy(() => AsdCameraConfigSelectSchema).optional(),
}).strict();

export const AsdCameraConfigSelectSchema: z.ZodType<Prisma.AsdCameraConfigSelect> = z.object({
  facePaddingFactor: z.boolean().optional(),
  maxZoomFactor: z.boolean().optional(),
  smoothingFactor: z.boolean().optional(),
  predictionFrames: z.boolean().optional(),
  predictionWeight: z.boolean().optional(),
  movementThresholdFactor: z.boolean().optional(),
}).strict()

// ASD LAYOUT CONFIG
//------------------------------------------------------

export const AsdLayoutConfigArgsSchema: z.ZodType<Prisma.AsdLayoutConfigDefaultArgs> = z.object({
  select: z.lazy(() => AsdLayoutConfigSelectSchema).optional(),
}).strict();

export const AsdLayoutConfigSelectSchema: z.ZodType<Prisma.AsdLayoutConfigSelect> = z.object({
  minIndicatorDurationFrames: z.boolean().optional(),
  speakerLingerFrames: z.boolean().optional(),
}).strict()

// ASD INTELLIGENCE CONFIG
//------------------------------------------------------

export const AsdIntelligenceConfigArgsSchema: z.ZodType<Prisma.AsdIntelligenceConfigDefaultArgs> = z.object({
  select: z.lazy(() => AsdIntelligenceConfigSelectSchema).optional(),
}).strict();

export const AsdIntelligenceConfigSelectSchema: z.ZodType<Prisma.AsdIntelligenceConfigSelect> = z.object({
  focusSpeakingScoreThreshold: z.boolean().optional(),
  maxSpeakersToFocus: z.boolean().optional(),
  disableFocusThreshold: z.boolean().optional(),
  speakerDominanceWindowFrames: z.boolean().optional(),
}).strict()

// ASD OVERLAYS AND TIMING CONFIG
//------------------------------------------------------

export const AsdOverlaysAndTimingConfigArgsSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigDefaultArgs> = z.object({
  select: z.lazy(() => AsdOverlaysAndTimingConfigSelectSchema).optional(),
}).strict();

export const AsdOverlaysAndTimingConfigSelectSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigSelect> = z.object({
  showFaceBoundingBoxes: z.boolean().optional(),
  asdFrameOffset: z.boolean().optional(),
}).strict()

// ASD FACE
//------------------------------------------------------

export const AsdFaceArgsSchema: z.ZodType<Prisma.AsdFaceDefaultArgs> = z.object({
  select: z.lazy(() => AsdFaceSelectSchema).optional(),
}).strict();

export const AsdFaceSelectSchema: z.ZodType<Prisma.AsdFaceSelect> = z.object({
  x1: z.boolean().optional(),
  y1: z.boolean().optional(),
  x2: z.boolean().optional(),
  y2: z.boolean().optional(),
  speaking_score: z.boolean().optional(),
  active: z.boolean().optional(),
  track_id: z.boolean().optional(),
}).strict()


/////////////////////////////////////////
// INPUT TYPES
/////////////////////////////////////////

export const UserWhereInputSchema: z.ZodType<Prisma.UserWhereInput> = z.object({
  AND: z.union([ z.lazy(() => UserWhereInputSchema),z.lazy(() => UserWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => UserWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => UserWhereInputSchema),z.lazy(() => UserWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  email: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  emailVerified: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  image: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  username: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  role: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  banned: z.union([ z.lazy(() => BoolNullableFilterSchema),z.boolean() ]).optional().nullable(),
  banReason: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  banExpires: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  onboardingComplete: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  paymentsCustomerId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  locale: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  phoneNumber: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  phoneVerified: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  smsVerificationCode: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  sessions: z.lazy(() => SessionListRelationFilterSchema).optional(),
  accounts: z.lazy(() => AccountListRelationFilterSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyListRelationFilterSchema).optional(),
  passkeys: z.lazy(() => PasskeyListRelationFilterSchema).optional(),
  invitations: z.lazy(() => InvitationListRelationFilterSchema).optional(),
  purchases: z.lazy(() => PurchaseListRelationFilterSchema).optional(),
  members: z.lazy(() => MemberListRelationFilterSchema).optional(),
  twofactors: z.lazy(() => TwoFactorListRelationFilterSchema).optional(),
  aiChats: z.lazy(() => AiChatListRelationFilterSchema).optional(),
  files: z.lazy(() => AppFileListRelationFilterSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryListRelationFilterSchema).optional(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectListRelationFilterSchema).optional()
}).strict();

export const UserOrderByWithRelationInputSchema: z.ZodType<Prisma.UserOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  emailVerified: z.lazy(() => SortOrderSchema).optional(),
  image: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  username: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  banned: z.lazy(() => SortOrderSchema).optional(),
  banReason: z.lazy(() => SortOrderSchema).optional(),
  banExpires: z.lazy(() => SortOrderSchema).optional(),
  onboardingComplete: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  locale: z.lazy(() => SortOrderSchema).optional(),
  phoneNumber: z.lazy(() => SortOrderSchema).optional(),
  phoneVerified: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationCode: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional(),
  sessions: z.lazy(() => SessionOrderByRelationAggregateInputSchema).optional(),
  accounts: z.lazy(() => AccountOrderByRelationAggregateInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyOrderByRelationAggregateInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyOrderByRelationAggregateInputSchema).optional(),
  invitations: z.lazy(() => InvitationOrderByRelationAggregateInputSchema).optional(),
  purchases: z.lazy(() => PurchaseOrderByRelationAggregateInputSchema).optional(),
  members: z.lazy(() => MemberOrderByRelationAggregateInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorOrderByRelationAggregateInputSchema).optional(),
  aiChats: z.lazy(() => AiChatOrderByRelationAggregateInputSchema).optional(),
  files: z.lazy(() => AppFileOrderByRelationAggregateInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryOrderByRelationAggregateInputSchema).optional(),
  jobs: z.lazy(() => JobOrderByRelationAggregateInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectOrderByRelationAggregateInputSchema).optional()
}).strict();

export const UserWhereUniqueInputSchema: z.ZodType<Prisma.UserWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    email: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    email: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  email: z.string().optional(),
  AND: z.union([ z.lazy(() => UserWhereInputSchema),z.lazy(() => UserWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => UserWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => UserWhereInputSchema),z.lazy(() => UserWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  emailVerified: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  image: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  username: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  role: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  banned: z.union([ z.lazy(() => BoolNullableFilterSchema),z.boolean() ]).optional().nullable(),
  banReason: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  banExpires: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  onboardingComplete: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  paymentsCustomerId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  locale: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  phoneNumber: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  phoneVerified: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  smsVerificationCode: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  sessions: z.lazy(() => SessionListRelationFilterSchema).optional(),
  accounts: z.lazy(() => AccountListRelationFilterSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyListRelationFilterSchema).optional(),
  passkeys: z.lazy(() => PasskeyListRelationFilterSchema).optional(),
  invitations: z.lazy(() => InvitationListRelationFilterSchema).optional(),
  purchases: z.lazy(() => PurchaseListRelationFilterSchema).optional(),
  members: z.lazy(() => MemberListRelationFilterSchema).optional(),
  twofactors: z.lazy(() => TwoFactorListRelationFilterSchema).optional(),
  aiChats: z.lazy(() => AiChatListRelationFilterSchema).optional(),
  files: z.lazy(() => AppFileListRelationFilterSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryListRelationFilterSchema).optional(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectListRelationFilterSchema).optional()
}).strict());

export const UserOrderByWithAggregationInputSchema: z.ZodType<Prisma.UserOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  emailVerified: z.lazy(() => SortOrderSchema).optional(),
  image: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  username: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  banned: z.lazy(() => SortOrderSchema).optional(),
  banReason: z.lazy(() => SortOrderSchema).optional(),
  banExpires: z.lazy(() => SortOrderSchema).optional(),
  onboardingComplete: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  locale: z.lazy(() => SortOrderSchema).optional(),
  phoneNumber: z.lazy(() => SortOrderSchema).optional(),
  phoneVerified: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationCode: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => UserCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => UserAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => UserMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => UserMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => UserSumOrderByAggregateInputSchema).optional()
}).strict();

export const UserScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.UserScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => UserScalarWhereWithAggregatesInputSchema),z.lazy(() => UserScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => UserScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => UserScalarWhereWithAggregatesInputSchema),z.lazy(() => UserScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  email: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  emailVerified: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  image: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  username: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  role: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  banned: z.union([ z.lazy(() => BoolNullableWithAggregatesFilterSchema),z.boolean() ]).optional().nullable(),
  banReason: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  banExpires: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  onboardingComplete: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  paymentsCustomerId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  locale: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  phoneNumber: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  phoneVerified: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  smsVerificationCode: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
}).strict();

export const SessionWhereInputSchema: z.ZodType<Prisma.SessionWhereInput> = z.object({
  AND: z.union([ z.lazy(() => SessionWhereInputSchema),z.lazy(() => SessionWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => SessionWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => SessionWhereInputSchema),z.lazy(() => SessionWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  ipAddress: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userAgent: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  impersonatedBy: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  activeOrganizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  token: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict();

export const SessionOrderByWithRelationInputSchema: z.ZodType<Prisma.SessionOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  ipAddress: z.lazy(() => SortOrderSchema).optional(),
  userAgent: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  impersonatedBy: z.lazy(() => SortOrderSchema).optional(),
  activeOrganizationId: z.lazy(() => SortOrderSchema).optional(),
  token: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const SessionWhereUniqueInputSchema: z.ZodType<Prisma.SessionWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    token: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    token: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  token: z.string().optional(),
  AND: z.union([ z.lazy(() => SessionWhereInputSchema),z.lazy(() => SessionWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => SessionWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => SessionWhereInputSchema),z.lazy(() => SessionWhereInputSchema).array() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  ipAddress: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userAgent: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  impersonatedBy: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  activeOrganizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict());

export const SessionOrderByWithAggregationInputSchema: z.ZodType<Prisma.SessionOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  ipAddress: z.lazy(() => SortOrderSchema).optional(),
  userAgent: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  impersonatedBy: z.lazy(() => SortOrderSchema).optional(),
  activeOrganizationId: z.lazy(() => SortOrderSchema).optional(),
  token: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => SessionCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => SessionMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => SessionMinOrderByAggregateInputSchema).optional()
}).strict();

export const SessionScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.SessionScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => SessionScalarWhereWithAggregatesInputSchema),z.lazy(() => SessionScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => SessionScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => SessionScalarWhereWithAggregatesInputSchema),z.lazy(() => SessionScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  ipAddress: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  userAgent: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  impersonatedBy: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  activeOrganizationId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  token: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const AccountWhereInputSchema: z.ZodType<Prisma.AccountWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AccountWhereInputSchema),z.lazy(() => AccountWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AccountWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AccountWhereInputSchema),z.lazy(() => AccountWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  accountId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  providerId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  accessToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  refreshToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  idToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  expiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  password: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  scope: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict();

export const AccountOrderByWithRelationInputSchema: z.ZodType<Prisma.AccountOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  accountId: z.lazy(() => SortOrderSchema).optional(),
  providerId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  accessToken: z.lazy(() => SortOrderSchema).optional(),
  refreshToken: z.lazy(() => SortOrderSchema).optional(),
  idToken: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  password: z.lazy(() => SortOrderSchema).optional(),
  accessTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  refreshTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  scope: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const AccountWhereUniqueInputSchema: z.ZodType<Prisma.AccountWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => AccountWhereInputSchema),z.lazy(() => AccountWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AccountWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AccountWhereInputSchema),z.lazy(() => AccountWhereInputSchema).array() ]).optional(),
  accountId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  providerId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  accessToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  refreshToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  idToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  expiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  password: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  scope: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict());

export const AccountOrderByWithAggregationInputSchema: z.ZodType<Prisma.AccountOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  accountId: z.lazy(() => SortOrderSchema).optional(),
  providerId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  accessToken: z.lazy(() => SortOrderSchema).optional(),
  refreshToken: z.lazy(() => SortOrderSchema).optional(),
  idToken: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  password: z.lazy(() => SortOrderSchema).optional(),
  accessTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  refreshTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  scope: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => AccountCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => AccountMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => AccountMinOrderByAggregateInputSchema).optional()
}).strict();

export const AccountScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.AccountScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => AccountScalarWhereWithAggregatesInputSchema),z.lazy(() => AccountScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => AccountScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AccountScalarWhereWithAggregatesInputSchema),z.lazy(() => AccountScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  accountId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  providerId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  accessToken: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  refreshToken: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  idToken: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  expiresAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  password: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  scope: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const VerificationWhereInputSchema: z.ZodType<Prisma.VerificationWhereInput> = z.object({
  AND: z.union([ z.lazy(() => VerificationWhereInputSchema),z.lazy(() => VerificationWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VerificationWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VerificationWhereInputSchema),z.lazy(() => VerificationWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  identifier: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  value: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  updatedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const VerificationOrderByWithRelationInputSchema: z.ZodType<Prisma.VerificationOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  identifier: z.lazy(() => SortOrderSchema).optional(),
  value: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VerificationWhereUniqueInputSchema: z.ZodType<Prisma.VerificationWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => VerificationWhereInputSchema),z.lazy(() => VerificationWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VerificationWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VerificationWhereInputSchema),z.lazy(() => VerificationWhereInputSchema).array() ]).optional(),
  identifier: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  value: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  updatedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict());

export const VerificationOrderByWithAggregationInputSchema: z.ZodType<Prisma.VerificationOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  identifier: z.lazy(() => SortOrderSchema).optional(),
  value: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => VerificationCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => VerificationMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => VerificationMinOrderByAggregateInputSchema).optional()
}).strict();

export const VerificationScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.VerificationScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => VerificationScalarWhereWithAggregatesInputSchema),z.lazy(() => VerificationScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => VerificationScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VerificationScalarWhereWithAggregatesInputSchema),z.lazy(() => VerificationScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  identifier: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  value: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  updatedAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const PasskeyWhereInputSchema: z.ZodType<Prisma.PasskeyWhereInput> = z.object({
  AND: z.union([ z.lazy(() => PasskeyWhereInputSchema),z.lazy(() => PasskeyWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PasskeyWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PasskeyWhereInputSchema),z.lazy(() => PasskeyWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  publicKey: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  credentialID: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  counter: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  deviceType: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  backedUp: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  transports: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict();

export const PasskeyOrderByWithRelationInputSchema: z.ZodType<Prisma.PasskeyOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  publicKey: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  credentialID: z.lazy(() => SortOrderSchema).optional(),
  counter: z.lazy(() => SortOrderSchema).optional(),
  deviceType: z.lazy(() => SortOrderSchema).optional(),
  backedUp: z.lazy(() => SortOrderSchema).optional(),
  transports: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const PasskeyWhereUniqueInputSchema: z.ZodType<Prisma.PasskeyWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => PasskeyWhereInputSchema),z.lazy(() => PasskeyWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PasskeyWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PasskeyWhereInputSchema),z.lazy(() => PasskeyWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  publicKey: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  credentialID: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  counter: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  deviceType: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  backedUp: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  transports: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict());

export const PasskeyOrderByWithAggregationInputSchema: z.ZodType<Prisma.PasskeyOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  publicKey: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  credentialID: z.lazy(() => SortOrderSchema).optional(),
  counter: z.lazy(() => SortOrderSchema).optional(),
  deviceType: z.lazy(() => SortOrderSchema).optional(),
  backedUp: z.lazy(() => SortOrderSchema).optional(),
  transports: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => PasskeyCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => PasskeyAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => PasskeyMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => PasskeyMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => PasskeySumOrderByAggregateInputSchema).optional()
}).strict();

export const PasskeyScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.PasskeyScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => PasskeyScalarWhereWithAggregatesInputSchema),z.lazy(() => PasskeyScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => PasskeyScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PasskeyScalarWhereWithAggregatesInputSchema),z.lazy(() => PasskeyScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  publicKey: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  credentialID: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  counter: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  deviceType: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  backedUp: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  transports: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const TwoFactorWhereInputSchema: z.ZodType<Prisma.TwoFactorWhereInput> = z.object({
  AND: z.union([ z.lazy(() => TwoFactorWhereInputSchema),z.lazy(() => TwoFactorWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => TwoFactorWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => TwoFactorWhereInputSchema),z.lazy(() => TwoFactorWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  secret: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  backupCodes: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict();

export const TwoFactorOrderByWithRelationInputSchema: z.ZodType<Prisma.TwoFactorOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  secret: z.lazy(() => SortOrderSchema).optional(),
  backupCodes: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const TwoFactorWhereUniqueInputSchema: z.ZodType<Prisma.TwoFactorWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => TwoFactorWhereInputSchema),z.lazy(() => TwoFactorWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => TwoFactorWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => TwoFactorWhereInputSchema),z.lazy(() => TwoFactorWhereInputSchema).array() ]).optional(),
  secret: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  backupCodes: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict());

export const TwoFactorOrderByWithAggregationInputSchema: z.ZodType<Prisma.TwoFactorOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  secret: z.lazy(() => SortOrderSchema).optional(),
  backupCodes: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => TwoFactorCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => TwoFactorMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => TwoFactorMinOrderByAggregateInputSchema).optional()
}).strict();

export const TwoFactorScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.TwoFactorScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => TwoFactorScalarWhereWithAggregatesInputSchema),z.lazy(() => TwoFactorScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => TwoFactorScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => TwoFactorScalarWhereWithAggregatesInputSchema),z.lazy(() => TwoFactorScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  secret: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  backupCodes: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
}).strict();

export const OrganizationWhereInputSchema: z.ZodType<Prisma.OrganizationWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationWhereInputSchema),z.lazy(() => OrganizationWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationWhereInputSchema),z.lazy(() => OrganizationWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  slug: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  logo: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  metadata: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  members: z.lazy(() => MemberListRelationFilterSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyListRelationFilterSchema).optional(),
  invitations: z.lazy(() => InvitationListRelationFilterSchema).optional(),
  purchases: z.lazy(() => PurchaseListRelationFilterSchema).optional(),
  aiChats: z.lazy(() => AiChatListRelationFilterSchema).optional(),
  files: z.lazy(() => AppFileListRelationFilterSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleListRelationFilterSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingListRelationFilterSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileListRelationFilterSchema).optional(),
  socialSettings: z.union([ z.lazy(() => OrganizationSocialSettingsNullableScalarRelationFilterSchema),z.lazy(() => OrganizationSocialSettingsWhereInputSchema) ]).optional().nullable(),
  socialPosts: z.lazy(() => OrganizationSocialPostListRelationFilterSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryListRelationFilterSchema).optional(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectListRelationFilterSchema).optional()
}).strict();

export const OrganizationOrderByWithRelationInputSchema: z.ZodType<Prisma.OrganizationOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  slug: z.lazy(() => SortOrderSchema).optional(),
  logo: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional(),
  members: z.lazy(() => MemberOrderByRelationAggregateInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyOrderByRelationAggregateInputSchema).optional(),
  invitations: z.lazy(() => InvitationOrderByRelationAggregateInputSchema).optional(),
  purchases: z.lazy(() => PurchaseOrderByRelationAggregateInputSchema).optional(),
  aiChats: z.lazy(() => AiChatOrderByRelationAggregateInputSchema).optional(),
  files: z.lazy(() => AppFileOrderByRelationAggregateInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleOrderByRelationAggregateInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingOrderByRelationAggregateInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileOrderByRelationAggregateInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsOrderByWithRelationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostOrderByRelationAggregateInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryOrderByRelationAggregateInputSchema).optional(),
  jobs: z.lazy(() => JobOrderByRelationAggregateInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectOrderByRelationAggregateInputSchema).optional()
}).strict();

export const OrganizationWhereUniqueInputSchema: z.ZodType<Prisma.OrganizationWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    slug: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    slug: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  slug: z.string().optional(),
  AND: z.union([ z.lazy(() => OrganizationWhereInputSchema),z.lazy(() => OrganizationWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationWhereInputSchema),z.lazy(() => OrganizationWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  logo: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  metadata: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  members: z.lazy(() => MemberListRelationFilterSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyListRelationFilterSchema).optional(),
  invitations: z.lazy(() => InvitationListRelationFilterSchema).optional(),
  purchases: z.lazy(() => PurchaseListRelationFilterSchema).optional(),
  aiChats: z.lazy(() => AiChatListRelationFilterSchema).optional(),
  files: z.lazy(() => AppFileListRelationFilterSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleListRelationFilterSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingListRelationFilterSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileListRelationFilterSchema).optional(),
  socialSettings: z.union([ z.lazy(() => OrganizationSocialSettingsNullableScalarRelationFilterSchema),z.lazy(() => OrganizationSocialSettingsWhereInputSchema) ]).optional().nullable(),
  socialPosts: z.lazy(() => OrganizationSocialPostListRelationFilterSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryListRelationFilterSchema).optional(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectListRelationFilterSchema).optional()
}).strict());

export const OrganizationOrderByWithAggregationInputSchema: z.ZodType<Prisma.OrganizationOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  slug: z.lazy(() => SortOrderSchema).optional(),
  logo: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => OrganizationCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => OrganizationAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => OrganizationMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => OrganizationMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => OrganizationSumOrderByAggregateInputSchema).optional()
}).strict();

export const OrganizationScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.OrganizationScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  slug: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  logo: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  metadata: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const MemberWhereInputSchema: z.ZodType<Prisma.MemberWhereInput> = z.object({
  AND: z.union([ z.lazy(() => MemberWhereInputSchema),z.lazy(() => MemberWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => MemberWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => MemberWhereInputSchema),z.lazy(() => MemberWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict();

export const MemberOrderByWithRelationInputSchema: z.ZodType<Prisma.MemberOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const MemberWhereUniqueInputSchema: z.ZodType<Prisma.MemberWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    organizationId_userId: z.lazy(() => MemberOrganizationIdUserIdCompoundUniqueInputSchema)
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    organizationId_userId: z.lazy(() => MemberOrganizationIdUserIdCompoundUniqueInputSchema),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  organizationId_userId: z.lazy(() => MemberOrganizationIdUserIdCompoundUniqueInputSchema).optional(),
  AND: z.union([ z.lazy(() => MemberWhereInputSchema),z.lazy(() => MemberWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => MemberWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => MemberWhereInputSchema),z.lazy(() => MemberWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict());

export const MemberOrderByWithAggregationInputSchema: z.ZodType<Prisma.MemberOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => MemberCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => MemberMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => MemberMinOrderByAggregateInputSchema).optional()
}).strict();

export const MemberScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.MemberScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => MemberScalarWhereWithAggregatesInputSchema),z.lazy(() => MemberScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => MemberScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => MemberScalarWhereWithAggregatesInputSchema),z.lazy(() => MemberScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const InvitationWhereInputSchema: z.ZodType<Prisma.InvitationWhereInput> = z.object({
  AND: z.union([ z.lazy(() => InvitationWhereInputSchema),z.lazy(() => InvitationWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => InvitationWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => InvitationWhereInputSchema),z.lazy(() => InvitationWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  email: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  status: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  inviterId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict();

export const InvitationOrderByWithRelationInputSchema: z.ZodType<Prisma.InvitationOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  inviterId: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const InvitationWhereUniqueInputSchema: z.ZodType<Prisma.InvitationWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => InvitationWhereInputSchema),z.lazy(() => InvitationWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => InvitationWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => InvitationWhereInputSchema),z.lazy(() => InvitationWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  email: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  status: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  inviterId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
}).strict());

export const InvitationOrderByWithAggregationInputSchema: z.ZodType<Prisma.InvitationOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  inviterId: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => InvitationCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => InvitationMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => InvitationMinOrderByAggregateInputSchema).optional()
}).strict();

export const InvitationScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.InvitationScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => InvitationScalarWhereWithAggregatesInputSchema),z.lazy(() => InvitationScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => InvitationScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => InvitationScalarWhereWithAggregatesInputSchema),z.lazy(() => InvitationScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  email: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  status: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  inviterId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
}).strict();

export const PurchaseWhereInputSchema: z.ZodType<Prisma.PurchaseWhereInput> = z.object({
  AND: z.union([ z.lazy(() => PurchaseWhereInputSchema),z.lazy(() => PurchaseWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PurchaseWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PurchaseWhereInputSchema),z.lazy(() => PurchaseWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumPurchaseTypeFilterSchema),z.lazy(() => PurchaseTypeSchema) ]).optional(),
  customerId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  subscriptionId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  credit: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  productId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  startDate: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserNullableScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional()
}).strict();

export const PurchaseOrderByWithRelationInputSchema: z.ZodType<Prisma.PurchaseOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  customerId: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  subscriptionId: z.lazy(() => SortOrderSchema).optional(),
  credit: z.lazy(() => SortOrderSchema).optional(),
  productId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  startDate: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional(),
  jobs: z.lazy(() => JobOrderByRelationAggregateInputSchema).optional()
}).strict();

export const PurchaseWhereUniqueInputSchema: z.ZodType<Prisma.PurchaseWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    subscriptionId: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    subscriptionId: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  subscriptionId: z.string().optional(),
  AND: z.union([ z.lazy(() => PurchaseWhereInputSchema),z.lazy(() => PurchaseWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PurchaseWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PurchaseWhereInputSchema),z.lazy(() => PurchaseWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumPurchaseTypeFilterSchema),z.lazy(() => PurchaseTypeSchema) ]).optional(),
  customerId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  credit: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  productId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  startDate: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserNullableScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional()
}).strict());

export const PurchaseOrderByWithAggregationInputSchema: z.ZodType<Prisma.PurchaseOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  customerId: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  subscriptionId: z.lazy(() => SortOrderSchema).optional(),
  credit: z.lazy(() => SortOrderSchema).optional(),
  productId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  startDate: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => PurchaseCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => PurchaseAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => PurchaseMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => PurchaseMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => PurchaseSumOrderByAggregateInputSchema).optional()
}).strict();

export const PurchaseScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.PurchaseScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => PurchaseScalarWhereWithAggregatesInputSchema),z.lazy(() => PurchaseScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => PurchaseScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PurchaseScalarWhereWithAggregatesInputSchema),z.lazy(() => PurchaseScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumPurchaseTypeWithAggregatesFilterSchema),z.lazy(() => PurchaseTypeSchema) ]).optional(),
  customerId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  subscriptionId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  credit: z.union([ z.lazy(() => FloatNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  productId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  startDate: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const AiChatWhereInputSchema: z.ZodType<Prisma.AiChatWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AiChatWhereInputSchema),z.lazy(() => AiChatWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AiChatWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AiChatWhereInputSchema),z.lazy(() => AiChatWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  title: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  messages: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserNullableScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional().nullable(),
}).strict();

export const AiChatOrderByWithRelationInputSchema: z.ZodType<Prisma.AiChatOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  messages: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional()
}).strict();

export const AiChatWhereUniqueInputSchema: z.ZodType<Prisma.AiChatWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => AiChatWhereInputSchema),z.lazy(() => AiChatWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AiChatWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AiChatWhereInputSchema),z.lazy(() => AiChatWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  title: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  messages: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserNullableScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional().nullable(),
}).strict());

export const AiChatOrderByWithAggregationInputSchema: z.ZodType<Prisma.AiChatOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  messages: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => AiChatCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => AiChatMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => AiChatMinOrderByAggregateInputSchema).optional()
}).strict();

export const AiChatScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.AiChatScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => AiChatScalarWhereWithAggregatesInputSchema),z.lazy(() => AiChatScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => AiChatScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AiChatScalarWhereWithAggregatesInputSchema),z.lazy(() => AiChatScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  title: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  messages: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const ApiKeyWhereInputSchema: z.ZodType<Prisma.ApiKeyWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ApiKeyWhereInputSchema),z.lazy(() => ApiKeyWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ApiKeyWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ApiKeyWhereInputSchema),z.lazy(() => ApiKeyWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryListRelationFilterSchema).optional()
}).strict();

export const ApiKeyOrderByWithRelationInputSchema: z.ZodType<Prisma.ApiKeyOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  jobs: z.lazy(() => JobOrderByRelationAggregateInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryOrderByRelationAggregateInputSchema).optional()
}).strict();

export const ApiKeyWhereUniqueInputSchema: z.ZodType<Prisma.ApiKeyWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => ApiKeyWhereInputSchema),z.lazy(() => ApiKeyWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ApiKeyWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ApiKeyWhereInputSchema),z.lazy(() => ApiKeyWhereInputSchema).array() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobListRelationFilterSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryListRelationFilterSchema).optional()
}).strict());

export const ApiKeyOrderByWithAggregationInputSchema: z.ZodType<Prisma.ApiKeyOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => ApiKeyCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => ApiKeyMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => ApiKeyMinOrderByAggregateInputSchema).optional()
}).strict();

export const ApiKeyScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.ApiKeyScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => ApiKeyScalarWhereWithAggregatesInputSchema),z.lazy(() => ApiKeyScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => ApiKeyScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ApiKeyScalarWhereWithAggregatesInputSchema),z.lazy(() => ApiKeyScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  key: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const JobWhereInputSchema: z.ZodType<Prisma.JobWhereInput> = z.object({
  AND: z.union([ z.lazy(() => JobWhereInputSchema),z.lazy(() => JobWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => JobWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => JobWhereInputSchema),z.lazy(() => JobWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  keyId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  purchaseId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCompositeFilterSchema),z.lazy(() => CostBreakdownObjectEqualityInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => EnumCreditUsageStatusFilterSchema),z.lazy(() => CreditUsageStatusSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumJobStatusNullableFilterSchema),z.lazy(() => JobStatusSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCompositeFilterSchema),z.lazy(() => WebhookObjectEqualityInputSchema) ]).optional().nullable(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  functionName: z.union([ z.lazy(() => EnumJobFunctionNameFilterSchema),z.lazy(() => JobFunctionNameSchema) ]).optional(),
  source: z.union([ z.lazy(() => EnumJobSourceFilterSchema),z.lazy(() => JobSourceSchema) ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  startTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  endTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  archivedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  key: z.union([ z.lazy(() => ApiKeyNullableScalarRelationFilterSchema),z.lazy(() => ApiKeyWhereInputSchema) ]).optional().nullable(),
  purchase: z.union([ z.lazy(() => PurchaseNullableScalarRelationFilterSchema),z.lazy(() => PurchaseWhereInputSchema) ]).optional().nullable(),
  video: z.union([ z.lazy(() => VideoNullableScalarRelationFilterSchema),z.lazy(() => VideoWhereInputSchema) ]).optional().nullable(),
  videoProject: z.union([ z.lazy(() => VideoProjectNullableScalarRelationFilterSchema),z.lazy(() => VideoProjectWhereInputSchema) ]).optional().nullable(),
  files: z.lazy(() => AppFileListRelationFilterSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryListRelationFilterSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostListRelationFilterSchema).optional()
}).strict();

export const JobOrderByWithRelationInputSchema: z.ZodType<Prisma.JobOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  purchaseId: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  costBreakDown: z.lazy(() => CostBreakdownOrderByInputSchema).optional(),
  creditUsageStatus: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  webhook: z.lazy(() => WebhookOrderByInputSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  functionName: z.lazy(() => SortOrderSchema).optional(),
  source: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  startTime: z.lazy(() => SortOrderSchema).optional(),
  endTime: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  archivedAt: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  key: z.lazy(() => ApiKeyOrderByWithRelationInputSchema).optional(),
  purchase: z.lazy(() => PurchaseOrderByWithRelationInputSchema).optional(),
  video: z.lazy(() => VideoOrderByWithRelationInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectOrderByWithRelationInputSchema).optional(),
  files: z.lazy(() => AppFileOrderByRelationAggregateInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryOrderByRelationAggregateInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostOrderByRelationAggregateInputSchema).optional()
}).strict();

export const JobWhereUniqueInputSchema: z.ZodType<Prisma.JobWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => JobWhereInputSchema),z.lazy(() => JobWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => JobWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => JobWhereInputSchema),z.lazy(() => JobWhereInputSchema).array() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  keyId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  purchaseId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCompositeFilterSchema),z.lazy(() => CostBreakdownObjectEqualityInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => EnumCreditUsageStatusFilterSchema),z.lazy(() => CreditUsageStatusSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumJobStatusNullableFilterSchema),z.lazy(() => JobStatusSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCompositeFilterSchema),z.lazy(() => WebhookObjectEqualityInputSchema) ]).optional().nullable(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  functionName: z.union([ z.lazy(() => EnumJobFunctionNameFilterSchema),z.lazy(() => JobFunctionNameSchema) ]).optional(),
  source: z.union([ z.lazy(() => EnumJobSourceFilterSchema),z.lazy(() => JobSourceSchema) ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  startTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  endTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  archivedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  key: z.union([ z.lazy(() => ApiKeyNullableScalarRelationFilterSchema),z.lazy(() => ApiKeyWhereInputSchema) ]).optional().nullable(),
  purchase: z.union([ z.lazy(() => PurchaseNullableScalarRelationFilterSchema),z.lazy(() => PurchaseWhereInputSchema) ]).optional().nullable(),
  video: z.union([ z.lazy(() => VideoNullableScalarRelationFilterSchema),z.lazy(() => VideoWhereInputSchema) ]).optional().nullable(),
  videoProject: z.union([ z.lazy(() => VideoProjectNullableScalarRelationFilterSchema),z.lazy(() => VideoProjectWhereInputSchema) ]).optional().nullable(),
  files: z.lazy(() => AppFileListRelationFilterSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryListRelationFilterSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostListRelationFilterSchema).optional()
}).strict());

export const JobOrderByWithAggregationInputSchema: z.ZodType<Prisma.JobOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  purchaseId: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  creditUsageStatus: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  functionName: z.lazy(() => SortOrderSchema).optional(),
  source: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  startTime: z.lazy(() => SortOrderSchema).optional(),
  endTime: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  archivedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => JobCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => JobAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => JobMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => JobMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => JobSumOrderByAggregateInputSchema).optional()
}).strict();

export const JobScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.JobScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => JobScalarWhereWithAggregatesInputSchema),z.lazy(() => JobScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => JobScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => JobScalarWhereWithAggregatesInputSchema),z.lazy(() => JobScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  keyId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  purchaseId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatWithAggregatesFilterSchema),z.number() ]).optional(),
  creditUsageStatus: z.union([ z.lazy(() => EnumCreditUsageStatusWithAggregatesFilterSchema),z.lazy(() => CreditUsageStatusSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumJobStatusNullableWithAggregatesFilterSchema),z.lazy(() => JobStatusSchema) ]).optional().nullable(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  functionName: z.union([ z.lazy(() => EnumJobFunctionNameWithAggregatesFilterSchema),z.lazy(() => JobFunctionNameSchema) ]).optional(),
  source: z.union([ z.lazy(() => EnumJobSourceWithAggregatesFilterSchema),z.lazy(() => JobSourceSchema) ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  startTime: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  endTime: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  isDeleted: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  archivedAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const VideoWhereInputSchema: z.ZodType<Prisma.VideoWhereInput> = z.object({
  AND: z.union([ z.lazy(() => VideoWhereInputSchema),z.lazy(() => VideoWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoWhereInputSchema),z.lazy(() => VideoWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  sourceId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  videoLength: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableCompositeFilterSchema),z.lazy(() => SourceFilesObjectEqualityInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCompositeFilterSchema),z.lazy(() => VideoShortsOptionsObjectEqualityInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCompositeFilterSchema),z.lazy(() => ITranscriptionObjectEqualityInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortCompositeListFilterSchema),z.lazy(() => ShortObjectEqualityInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoStatusFilterSchema),z.lazy(() => VideoStatusSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryCompositeListFilterSchema),z.lazy(() => StatusHistoryObjectEqualityInputSchema).array() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  job: z.union([ z.lazy(() => JobScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional(),
}).strict();

export const VideoOrderByWithRelationInputSchema: z.ZodType<Prisma.VideoOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  sourceId: z.lazy(() => SortOrderSchema).optional(),
  videoLength: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  files: z.lazy(() => SourceFilesOrderByInputSchema).optional(),
  options: z.lazy(() => VideoShortsOptionsOrderByInputSchema).optional(),
  choosedDefinition: z.lazy(() => SortOrderSchema).optional(),
  transcription: z.lazy(() => ITranscriptionOrderByInputSchema).optional(),
  shorts: z.lazy(() => ShortOrderByCompositeAggregateInputSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  statusHistory: z.lazy(() => StatusHistoryOrderByCompositeAggregateInputSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  job: z.lazy(() => JobOrderByWithRelationInputSchema).optional()
}).strict();

export const VideoWhereUniqueInputSchema: z.ZodType<Prisma.VideoWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    jobId: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    jobId: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  jobId: z.string().optional(),
  AND: z.union([ z.lazy(() => VideoWhereInputSchema),z.lazy(() => VideoWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoWhereInputSchema),z.lazy(() => VideoWhereInputSchema).array() ]).optional(),
  sourceId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  videoLength: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableCompositeFilterSchema),z.lazy(() => SourceFilesObjectEqualityInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCompositeFilterSchema),z.lazy(() => VideoShortsOptionsObjectEqualityInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCompositeFilterSchema),z.lazy(() => ITranscriptionObjectEqualityInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortCompositeListFilterSchema),z.lazy(() => ShortObjectEqualityInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoStatusFilterSchema),z.lazy(() => VideoStatusSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryCompositeListFilterSchema),z.lazy(() => StatusHistoryObjectEqualityInputSchema).array() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  job: z.union([ z.lazy(() => JobScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional(),
}).strict());

export const VideoOrderByWithAggregationInputSchema: z.ZodType<Prisma.VideoOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  sourceId: z.lazy(() => SortOrderSchema).optional(),
  videoLength: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  choosedDefinition: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => VideoCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => VideoAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => VideoMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => VideoMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => VideoSumOrderByAggregateInputSchema).optional()
}).strict();

export const VideoScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.VideoScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => VideoScalarWhereWithAggregatesInputSchema),z.lazy(() => VideoScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoScalarWhereWithAggregatesInputSchema),z.lazy(() => VideoScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  sourceId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  videoLength: z.union([ z.lazy(() => FloatWithAggregatesFilterSchema),z.number() ]).optional(),
  title: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  choosedDefinition: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoStatusWithAggregatesFilterSchema),z.lazy(() => VideoStatusSchema) ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const VideoProjectWhereInputSchema: z.ZodType<Prisma.VideoProjectWhereInput> = z.object({
  AND: z.union([ z.lazy(() => VideoProjectWhereInputSchema),z.lazy(() => VideoProjectWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoProjectWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoProjectWhereInputSchema),z.lazy(() => VideoProjectWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdByUserId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoProjectStatusFilterSchema),z.lazy(() => VideoProjectStatusSchema) ]).optional(),
  inputBrief: z.lazy(() => JsonFilterSchema).optional(),
  document: z.lazy(() => JsonNullableFilterSchema).optional(),
  validation: z.lazy(() => JsonNullableFilterSchema).optional(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  cost: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishTag: z.lazy(() => JsonNullableFilterSchema).optional(),
  episodeSummary: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  job: z.union([ z.lazy(() => JobScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  createdBy: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  design: z.union([ z.lazy(() => VideoDesignNullableScalarRelationFilterSchema),z.lazy(() => VideoDesignWhereInputSchema) ]).optional().nullable(),
}).strict();

export const VideoProjectOrderByWithRelationInputSchema: z.ZodType<Prisma.VideoProjectOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  createdByUserId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  inputBrief: z.lazy(() => SortOrderSchema).optional(),
  document: z.lazy(() => SortOrderSchema).optional(),
  validation: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  publishTag: z.lazy(() => SortOrderSchema).optional(),
  episodeSummary: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  job: z.lazy(() => JobOrderByWithRelationInputSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  createdBy: z.lazy(() => UserOrderByWithRelationInputSchema).optional(),
  design: z.lazy(() => VideoDesignOrderByWithRelationInputSchema).optional()
}).strict();

export const VideoProjectWhereUniqueInputSchema: z.ZodType<Prisma.VideoProjectWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    jobId: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    jobId: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  jobId: z.string().optional(),
  AND: z.union([ z.lazy(() => VideoProjectWhereInputSchema),z.lazy(() => VideoProjectWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoProjectWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoProjectWhereInputSchema),z.lazy(() => VideoProjectWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdByUserId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoProjectStatusFilterSchema),z.lazy(() => VideoProjectStatusSchema) ]).optional(),
  inputBrief: z.lazy(() => JsonFilterSchema).optional(),
  document: z.lazy(() => JsonNullableFilterSchema).optional(),
  validation: z.lazy(() => JsonNullableFilterSchema).optional(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  cost: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishTag: z.lazy(() => JsonNullableFilterSchema).optional(),
  episodeSummary: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  job: z.union([ z.lazy(() => JobScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  createdBy: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  design: z.union([ z.lazy(() => VideoDesignNullableScalarRelationFilterSchema),z.lazy(() => VideoDesignWhereInputSchema) ]).optional().nullable(),
}).strict());

export const VideoProjectOrderByWithAggregationInputSchema: z.ZodType<Prisma.VideoProjectOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  createdByUserId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  inputBrief: z.lazy(() => SortOrderSchema).optional(),
  document: z.lazy(() => SortOrderSchema).optional(),
  validation: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  publishTag: z.lazy(() => SortOrderSchema).optional(),
  episodeSummary: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => VideoProjectCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => VideoProjectAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => VideoProjectMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => VideoProjectMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => VideoProjectSumOrderByAggregateInputSchema).optional()
}).strict();

export const VideoProjectScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.VideoProjectScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => VideoProjectScalarWhereWithAggregatesInputSchema),z.lazy(() => VideoProjectScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoProjectScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoProjectScalarWhereWithAggregatesInputSchema),z.lazy(() => VideoProjectScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  createdByUserId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoProjectStatusWithAggregatesFilterSchema),z.lazy(() => VideoProjectStatusSchema) ]).optional(),
  inputBrief: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  document: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  validation: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  cost: z.union([ z.lazy(() => FloatNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishTag: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  episodeSummary: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
}).strict();

export const VideoDesignWhereInputSchema: z.ZodType<Prisma.VideoDesignWhereInput> = z.object({
  AND: z.union([ z.lazy(() => VideoDesignWhereInputSchema),z.lazy(() => VideoDesignWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoDesignWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoDesignWhereInputSchema),z.lazy(() => VideoDesignWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  videoProjectId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoDesignStatusFilterSchema),z.lazy(() => VideoDesignStatusSchema) ]).optional(),
  design: z.lazy(() => JsonFilterSchema).optional(),
  fps: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  durationMs: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  width: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  height: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  latestRenderHistoryId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  latestMp4Url: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  compiledAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  videoProject: z.union([ z.lazy(() => VideoProjectScalarRelationFilterSchema),z.lazy(() => VideoProjectWhereInputSchema) ]).optional(),
}).strict();

export const VideoDesignOrderByWithRelationInputSchema: z.ZodType<Prisma.VideoDesignOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  videoProjectId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  design: z.lazy(() => SortOrderSchema).optional(),
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional(),
  latestRenderHistoryId: z.lazy(() => SortOrderSchema).optional(),
  latestMp4Url: z.lazy(() => SortOrderSchema).optional(),
  compiledAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  videoProject: z.lazy(() => VideoProjectOrderByWithRelationInputSchema).optional()
}).strict();

export const VideoDesignWhereUniqueInputSchema: z.ZodType<Prisma.VideoDesignWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    videoProjectId: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    videoProjectId: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  videoProjectId: z.string().optional(),
  AND: z.union([ z.lazy(() => VideoDesignWhereInputSchema),z.lazy(() => VideoDesignWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoDesignWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoDesignWhereInputSchema),z.lazy(() => VideoDesignWhereInputSchema).array() ]).optional(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoDesignStatusFilterSchema),z.lazy(() => VideoDesignStatusSchema) ]).optional(),
  design: z.lazy(() => JsonFilterSchema).optional(),
  fps: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  durationMs: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  width: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  height: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  latestRenderHistoryId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  latestMp4Url: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  compiledAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  videoProject: z.union([ z.lazy(() => VideoProjectScalarRelationFilterSchema),z.lazy(() => VideoProjectWhereInputSchema) ]).optional(),
}).strict());

export const VideoDesignOrderByWithAggregationInputSchema: z.ZodType<Prisma.VideoDesignOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  videoProjectId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  design: z.lazy(() => SortOrderSchema).optional(),
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional(),
  latestRenderHistoryId: z.lazy(() => SortOrderSchema).optional(),
  latestMp4Url: z.lazy(() => SortOrderSchema).optional(),
  compiledAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => VideoDesignCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => VideoDesignAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => VideoDesignMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => VideoDesignMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => VideoDesignSumOrderByAggregateInputSchema).optional()
}).strict();

export const VideoDesignScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.VideoDesignScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => VideoDesignScalarWhereWithAggregatesInputSchema),z.lazy(() => VideoDesignScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoDesignScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoDesignScalarWhereWithAggregatesInputSchema),z.lazy(() => VideoDesignScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  videoProjectId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoDesignStatusWithAggregatesFilterSchema),z.lazy(() => VideoDesignStatusSchema) ]).optional(),
  design: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  fps: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  durationMs: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  width: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  height: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  latestRenderHistoryId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  latestMp4Url: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  compiledAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const AppFileWhereInputSchema: z.ZodType<Prisma.AppFileWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AppFileWhereInputSchema),z.lazy(() => AppFileWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AppFileWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AppFileWhereInputSchema),z.lazy(() => AppFileWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumAppFileTypeFilterSchema),z.lazy(() => AppFileTypeSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumAppFileStatusFilterSchema),z.lazy(() => AppFileStatusSchema) ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  tags: z.lazy(() => StringNullableListFilterSchema).optional(),
  size: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  length: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  thumbnailUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  job: z.union([ z.lazy(() => JobNullableScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional().nullable(),
  renderHistories: z.lazy(() => RenderHistoryListRelationFilterSchema).optional()
}).strict();

export const AppFileOrderByWithRelationInputSchema: z.ZodType<Prisma.AppFileOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  tags: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  thumbnailUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  job: z.lazy(() => JobOrderByWithRelationInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryOrderByRelationAggregateInputSchema).optional()
}).strict();

export const AppFileWhereUniqueInputSchema: z.ZodType<Prisma.AppFileWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => AppFileWhereInputSchema),z.lazy(() => AppFileWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AppFileWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AppFileWhereInputSchema),z.lazy(() => AppFileWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumAppFileTypeFilterSchema),z.lazy(() => AppFileTypeSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumAppFileStatusFilterSchema),z.lazy(() => AppFileStatusSchema) ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  tags: z.lazy(() => StringNullableListFilterSchema).optional(),
  size: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  length: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  thumbnailUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  user: z.union([ z.lazy(() => UserScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationNullableScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional().nullable(),
  job: z.union([ z.lazy(() => JobNullableScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional().nullable(),
  renderHistories: z.lazy(() => RenderHistoryListRelationFilterSchema).optional()
}).strict());

export const AppFileOrderByWithAggregationInputSchema: z.ZodType<Prisma.AppFileOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  tags: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  thumbnailUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => AppFileCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => AppFileAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => AppFileMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => AppFileMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => AppFileSumOrderByAggregateInputSchema).optional()
}).strict();

export const AppFileScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.AppFileScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => AppFileScalarWhereWithAggregatesInputSchema),z.lazy(() => AppFileScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => AppFileScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AppFileScalarWhereWithAggregatesInputSchema),z.lazy(() => AppFileScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumAppFileTypeWithAggregatesFilterSchema),z.lazy(() => AppFileTypeSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumAppFileStatusWithAggregatesFilterSchema),z.lazy(() => AppFileStatusSchema) ]).optional(),
  description: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  tags: z.lazy(() => StringNullableListFilterSchema).optional(),
  size: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  length: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  key: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  thumbnailUrl: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const CaptionStyleWhereInputSchema: z.ZodType<Prisma.CaptionStyleWhereInput> = z.object({
  AND: z.union([ z.lazy(() => CaptionStyleWhereInputSchema),z.lazy(() => CaptionStyleWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CaptionStyleWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CaptionStyleWhereInputSchema),z.lazy(() => CaptionStyleWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  style: z.lazy(() => JsonFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict();

export const CaptionStyleOrderByWithRelationInputSchema: z.ZodType<Prisma.CaptionStyleOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  style: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional()
}).strict();

export const CaptionStyleWhereUniqueInputSchema: z.ZodType<Prisma.CaptionStyleWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => CaptionStyleWhereInputSchema),z.lazy(() => CaptionStyleWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CaptionStyleWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CaptionStyleWhereInputSchema),z.lazy(() => CaptionStyleWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  style: z.lazy(() => JsonFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict());

export const CaptionStyleOrderByWithAggregationInputSchema: z.ZodType<Prisma.CaptionStyleOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  style: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => CaptionStyleCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => CaptionStyleMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => CaptionStyleMinOrderByAggregateInputSchema).optional()
}).strict();

export const CaptionStyleScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.CaptionStyleScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => CaptionStyleScalarWhereWithAggregatesInputSchema),z.lazy(() => CaptionStyleScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => CaptionStyleScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CaptionStyleScalarWhereWithAggregatesInputSchema),z.lazy(() => CaptionStyleScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  style: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationBrandingWhereInputSchema: z.ZodType<Prisma.OrganizationBrandingWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationBrandingWhereInputSchema),z.lazy(() => OrganizationBrandingWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationBrandingWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationBrandingWhereInputSchema),z.lazy(() => OrganizationBrandingWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  preset: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  size: z.lazy(() => JsonNullableFilterSchema).optional(),
  options: z.lazy(() => JsonNullableFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingOrderByWithRelationInputSchema: z.ZodType<Prisma.OrganizationBrandingOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  preset: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  options: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional()
}).strict();

export const OrganizationBrandingWhereUniqueInputSchema: z.ZodType<Prisma.OrganizationBrandingWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => OrganizationBrandingWhereInputSchema),z.lazy(() => OrganizationBrandingWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationBrandingWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationBrandingWhereInputSchema),z.lazy(() => OrganizationBrandingWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  preset: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  size: z.lazy(() => JsonNullableFilterSchema).optional(),
  options: z.lazy(() => JsonNullableFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict());

export const OrganizationBrandingOrderByWithAggregationInputSchema: z.ZodType<Prisma.OrganizationBrandingOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  preset: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  options: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => OrganizationBrandingCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => OrganizationBrandingMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => OrganizationBrandingMinOrderByAggregateInputSchema).optional()
}).strict();

export const OrganizationBrandingScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.OrganizationBrandingScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationBrandingScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationBrandingScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationBrandingScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationBrandingScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationBrandingScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  preset: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  size: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  options: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationNarratorProfileWhereInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationNarratorProfileWhereInputSchema),z.lazy(() => OrganizationNarratorProfileWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationNarratorProfileWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationNarratorProfileWhereInputSchema),z.lazy(() => OrganizationNarratorProfileWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  narratorName: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  gender: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  provider: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  voiceId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  personality: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  styleNotes: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  ttsStyle: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsPace: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsAccent: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsNotes: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  channelName: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  channelSignOff: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  overlayText: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  visualDefaults: z.lazy(() => JsonNullableFilterSchema).optional(),
  speechSpeed: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  avatarFileAppId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  avatarPhotos: z.lazy(() => JsonNullableFilterSchema).optional(),
  avatarMinDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileOrderByWithRelationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  narratorName: z.lazy(() => SortOrderSchema).optional(),
  gender: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  voiceId: z.lazy(() => SortOrderSchema).optional(),
  personality: z.lazy(() => SortOrderSchema).optional(),
  styleNotes: z.lazy(() => SortOrderSchema).optional(),
  ttsStyle: z.lazy(() => SortOrderSchema).optional(),
  ttsPace: z.lazy(() => SortOrderSchema).optional(),
  ttsAccent: z.lazy(() => SortOrderSchema).optional(),
  ttsNotes: z.lazy(() => SortOrderSchema).optional(),
  channelName: z.lazy(() => SortOrderSchema).optional(),
  channelSignOff: z.lazy(() => SortOrderSchema).optional(),
  overlayText: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  visualDefaults: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarFileAppId: z.lazy(() => SortOrderSchema).optional(),
  avatarPhotos: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional()
}).strict();

export const OrganizationNarratorProfileWhereUniqueInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => OrganizationNarratorProfileWhereInputSchema),z.lazy(() => OrganizationNarratorProfileWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationNarratorProfileWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationNarratorProfileWhereInputSchema),z.lazy(() => OrganizationNarratorProfileWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  narratorName: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  gender: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  provider: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  voiceId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  personality: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  styleNotes: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  ttsStyle: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsPace: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsAccent: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsNotes: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  channelName: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  channelSignOff: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  overlayText: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  visualDefaults: z.lazy(() => JsonNullableFilterSchema).optional(),
  speechSpeed: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  avatarFileAppId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  avatarPhotos: z.lazy(() => JsonNullableFilterSchema).optional(),
  avatarMinDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict());

export const OrganizationNarratorProfileOrderByWithAggregationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  narratorName: z.lazy(() => SortOrderSchema).optional(),
  gender: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  voiceId: z.lazy(() => SortOrderSchema).optional(),
  personality: z.lazy(() => SortOrderSchema).optional(),
  styleNotes: z.lazy(() => SortOrderSchema).optional(),
  ttsStyle: z.lazy(() => SortOrderSchema).optional(),
  ttsPace: z.lazy(() => SortOrderSchema).optional(),
  ttsAccent: z.lazy(() => SortOrderSchema).optional(),
  ttsNotes: z.lazy(() => SortOrderSchema).optional(),
  channelName: z.lazy(() => SortOrderSchema).optional(),
  channelSignOff: z.lazy(() => SortOrderSchema).optional(),
  overlayText: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  visualDefaults: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarFileAppId: z.lazy(() => SortOrderSchema).optional(),
  avatarPhotos: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => OrganizationNarratorProfileCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => OrganizationNarratorProfileAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => OrganizationNarratorProfileMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => OrganizationNarratorProfileMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => OrganizationNarratorProfileSumOrderByAggregateInputSchema).optional()
}).strict();

export const OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  narratorName: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  gender: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  provider: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  voiceId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  personality: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  styleNotes: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  ttsStyle: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  ttsPace: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  ttsAccent: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  ttsNotes: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  channelName: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  channelSignOff: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  overlayText: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  visualDefaults: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  speechSpeed: z.union([ z.lazy(() => FloatWithAggregatesFilterSchema),z.number() ]).optional(),
  avatarFileAppId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  avatarPhotos: z.lazy(() => JsonNullableWithAggregatesFilterSchema).optional(),
  avatarMinDurationMs: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  isActive: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationSocialSettingsWhereInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationSocialSettingsWhereInputSchema),z.lazy(() => OrganizationSocialSettingsWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialSettingsWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialSettingsWhereInputSchema),z.lazy(() => OrganizationSocialSettingsWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  providerProfileId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  isEnabled: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  approveApiPosts: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  defaultTimezone: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  defaultPlatforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  queueSlots: z.lazy(() => JsonFilterSchema).optional(),
  maxPostsPerSlot: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  maxShortsOfSameVideo: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  maxShortsPerWeek: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  processOnlyPlaylistId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsOrderByWithRelationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  providerProfileId: z.lazy(() => SortOrderSchema).optional(),
  isEnabled: z.lazy(() => SortOrderSchema).optional(),
  approveApiPosts: z.lazy(() => SortOrderSchema).optional(),
  defaultTimezone: z.lazy(() => SortOrderSchema).optional(),
  defaultPlatforms: z.lazy(() => SortOrderSchema).optional(),
  queueSlots: z.lazy(() => SortOrderSchema).optional(),
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional(),
  processOnlyPlaylistId: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional()
}).strict();

export const OrganizationSocialSettingsWhereUniqueInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsWhereUniqueInput> = z.union([
  z.object({
    id: z.string().cuid(),
    organizationId: z.string()
  }),
  z.object({
    id: z.string().cuid(),
  }),
  z.object({
    organizationId: z.string(),
  }),
])
.and(z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional(),
  AND: z.union([ z.lazy(() => OrganizationSocialSettingsWhereInputSchema),z.lazy(() => OrganizationSocialSettingsWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialSettingsWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialSettingsWhereInputSchema),z.lazy(() => OrganizationSocialSettingsWhereInputSchema).array() ]).optional(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  providerProfileId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  isEnabled: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  approveApiPosts: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  defaultTimezone: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  defaultPlatforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  queueSlots: z.lazy(() => JsonFilterSchema).optional(),
  maxPostsPerSlot: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  maxShortsOfSameVideo: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  maxShortsPerWeek: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  processOnlyPlaylistId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
}).strict());

export const OrganizationSocialSettingsOrderByWithAggregationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  providerProfileId: z.lazy(() => SortOrderSchema).optional(),
  isEnabled: z.lazy(() => SortOrderSchema).optional(),
  approveApiPosts: z.lazy(() => SortOrderSchema).optional(),
  defaultTimezone: z.lazy(() => SortOrderSchema).optional(),
  defaultPlatforms: z.lazy(() => SortOrderSchema).optional(),
  queueSlots: z.lazy(() => SortOrderSchema).optional(),
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional(),
  processOnlyPlaylistId: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => OrganizationSocialSettingsCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => OrganizationSocialSettingsAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => OrganizationSocialSettingsMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => OrganizationSocialSettingsMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => OrganizationSocialSettingsSumOrderByAggregateInputSchema).optional()
}).strict();

export const OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderWithAggregatesFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  providerProfileId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  isEnabled: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  approveApiPosts: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  defaultTimezone: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  defaultPlatforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  queueSlots: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  maxPostsPerSlot: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  maxShortsOfSameVideo: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  maxShortsPerWeek: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  processOnlyPlaylistId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationSocialPostWhereInputSchema: z.ZodType<Prisma.OrganizationSocialPostWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationSocialPostWhereInputSchema),z.lazy(() => OrganizationSocialPostWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialPostWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialPostWhereInputSchema),z.lazy(() => OrganizationSocialPostWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumOrganizationSocialPostStatusFilterSchema),z.lazy(() => OrganizationSocialPostStatusSchema) ]).optional(),
  providerJobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  platforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  title: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  caption: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  videoUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  scheduledFor: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  lastError: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  metadata: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  job: z.union([ z.lazy(() => JobNullableScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional().nullable(),
}).strict();

export const OrganizationSocialPostOrderByWithRelationInputSchema: z.ZodType<Prisma.OrganizationSocialPostOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  providerJobId: z.lazy(() => SortOrderSchema).optional(),
  platforms: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  caption: z.lazy(() => SortOrderSchema).optional(),
  videoUrl: z.lazy(() => SortOrderSchema).optional(),
  scheduledFor: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  lastError: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  job: z.lazy(() => JobOrderByWithRelationInputSchema).optional()
}).strict();

export const OrganizationSocialPostWhereUniqueInputSchema: z.ZodType<Prisma.OrganizationSocialPostWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => OrganizationSocialPostWhereInputSchema),z.lazy(() => OrganizationSocialPostWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialPostWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialPostWhereInputSchema),z.lazy(() => OrganizationSocialPostWhereInputSchema).array() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumOrganizationSocialPostStatusFilterSchema),z.lazy(() => OrganizationSocialPostStatusSchema) ]).optional(),
  providerJobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  platforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  title: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  caption: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  videoUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  scheduledFor: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  lastError: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  metadata: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  job: z.union([ z.lazy(() => JobNullableScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional().nullable(),
}).strict());

export const OrganizationSocialPostOrderByWithAggregationInputSchema: z.ZodType<Prisma.OrganizationSocialPostOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  providerJobId: z.lazy(() => SortOrderSchema).optional(),
  platforms: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  caption: z.lazy(() => SortOrderSchema).optional(),
  videoUrl: z.lazy(() => SortOrderSchema).optional(),
  scheduledFor: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  lastError: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => OrganizationSocialPostCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => OrganizationSocialPostMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => OrganizationSocialPostMinOrderByAggregateInputSchema).optional()
}).strict();

export const OrganizationSocialPostScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.OrganizationSocialPostScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialPostScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereWithAggregatesInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderWithAggregatesFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumOrganizationSocialPostStatusWithAggregatesFilterSchema),z.lazy(() => OrganizationSocialPostStatusSchema) ]).optional(),
  providerJobId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  platforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  title: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  caption: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  videoUrl: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  scheduledFor: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  lastError: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  metadata: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const RenderHistoryWhereInputSchema: z.ZodType<Prisma.RenderHistoryWhereInput> = z.object({
  AND: z.union([ z.lazy(() => RenderHistoryWhereInputSchema),z.lazy(() => RenderHistoryWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => RenderHistoryWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => RenderHistoryWhereInputSchema),z.lazy(() => RenderHistoryWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  renderId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  keyId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  appFileId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  target: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  data: z.lazy(() => JsonFilterSchema).optional(),
  fileUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  fileSize: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  status: z.union([ z.lazy(() => EnumRenderHistoryStatusFilterSchema),z.lazy(() => RenderHistoryStatusSchema) ]).optional(),
  errorMessage: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  priority: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  retryCount: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  maxRetries: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  webhookReceived: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  webhookReceivedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  user: z.union([ z.lazy(() => UserNullableScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional().nullable(),
  key: z.union([ z.lazy(() => ApiKeyNullableScalarRelationFilterSchema),z.lazy(() => ApiKeyWhereInputSchema) ]).optional().nullable(),
  job: z.union([ z.lazy(() => JobScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional(),
  appFile: z.union([ z.lazy(() => AppFileNullableScalarRelationFilterSchema),z.lazy(() => AppFileWhereInputSchema) ]).optional().nullable(),
}).strict();

export const RenderHistoryOrderByWithRelationInputSchema: z.ZodType<Prisma.RenderHistoryOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  renderId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  appFileId: z.lazy(() => SortOrderSchema).optional(),
  target: z.lazy(() => SortOrderSchema).optional(),
  data: z.lazy(() => SortOrderSchema).optional(),
  fileUrl: z.lazy(() => SortOrderSchema).optional(),
  fileSize: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional(),
  webhookReceived: z.lazy(() => SortOrderSchema).optional(),
  webhookReceivedAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  organization: z.lazy(() => OrganizationOrderByWithRelationInputSchema).optional(),
  user: z.lazy(() => UserOrderByWithRelationInputSchema).optional(),
  key: z.lazy(() => ApiKeyOrderByWithRelationInputSchema).optional(),
  job: z.lazy(() => JobOrderByWithRelationInputSchema).optional(),
  appFile: z.lazy(() => AppFileOrderByWithRelationInputSchema).optional()
}).strict();

export const RenderHistoryWhereUniqueInputSchema: z.ZodType<Prisma.RenderHistoryWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => RenderHistoryWhereInputSchema),z.lazy(() => RenderHistoryWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => RenderHistoryWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => RenderHistoryWhereInputSchema),z.lazy(() => RenderHistoryWhereInputSchema).array() ]).optional(),
  renderId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  keyId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  appFileId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  target: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  data: z.lazy(() => JsonFilterSchema).optional(),
  fileUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  fileSize: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  status: z.union([ z.lazy(() => EnumRenderHistoryStatusFilterSchema),z.lazy(() => RenderHistoryStatusSchema) ]).optional(),
  errorMessage: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  priority: z.union([ z.lazy(() => IntNullableFilterSchema),z.number().int() ]).optional().nullable(),
  retryCount: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  maxRetries: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  webhookReceived: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  webhookReceivedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  organization: z.union([ z.lazy(() => OrganizationScalarRelationFilterSchema),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  user: z.union([ z.lazy(() => UserNullableScalarRelationFilterSchema),z.lazy(() => UserWhereInputSchema) ]).optional().nullable(),
  key: z.union([ z.lazy(() => ApiKeyNullableScalarRelationFilterSchema),z.lazy(() => ApiKeyWhereInputSchema) ]).optional().nullable(),
  job: z.union([ z.lazy(() => JobScalarRelationFilterSchema),z.lazy(() => JobWhereInputSchema) ]).optional(),
  appFile: z.union([ z.lazy(() => AppFileNullableScalarRelationFilterSchema),z.lazy(() => AppFileWhereInputSchema) ]).optional().nullable(),
}).strict());

export const RenderHistoryOrderByWithAggregationInputSchema: z.ZodType<Prisma.RenderHistoryOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  renderId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  appFileId: z.lazy(() => SortOrderSchema).optional(),
  target: z.lazy(() => SortOrderSchema).optional(),
  data: z.lazy(() => SortOrderSchema).optional(),
  fileUrl: z.lazy(() => SortOrderSchema).optional(),
  fileSize: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional(),
  webhookReceived: z.lazy(() => SortOrderSchema).optional(),
  webhookReceivedAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => RenderHistoryCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => RenderHistoryAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => RenderHistoryMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => RenderHistoryMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => RenderHistorySumOrderByAggregateInputSchema).optional()
}).strict();

export const RenderHistoryScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.RenderHistoryScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => RenderHistoryScalarWhereWithAggregatesInputSchema),z.lazy(() => RenderHistoryScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => RenderHistoryScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => RenderHistoryScalarWhereWithAggregatesInputSchema),z.lazy(() => RenderHistoryScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  renderId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  keyId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  appFileId: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  target: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  data: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  fileUrl: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  fileSize: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  status: z.union([ z.lazy(() => EnumRenderHistoryStatusWithAggregatesFilterSchema),z.lazy(() => RenderHistoryStatusSchema) ]).optional(),
  errorMessage: z.union([ z.lazy(() => StringNullableWithAggregatesFilterSchema),z.string() ]).optional().nullable(),
  priority: z.union([ z.lazy(() => IntNullableWithAggregatesFilterSchema),z.number() ]).optional().nullable(),
  retryCount: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  maxRetries: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  webhookReceived: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  webhookReceivedAt: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const ExternalKeysWhereInputSchema: z.ZodType<Prisma.ExternalKeysWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ExternalKeysWhereInputSchema),z.lazy(() => ExternalKeysWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ExternalKeysWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ExternalKeysWhereInputSchema),z.lazy(() => ExternalKeysWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumExternalKeysStatusFilterSchema),z.lazy(() => ExternalKeysStatusSchema) ]).optional(),
  type: z.union([ z.lazy(() => EnumExternalKeysTypeFilterSchema),z.lazy(() => ExternalKeysTypeSchema) ]).optional(),
  errors: z.lazy(() => StringNullableListFilterSchema).optional(),
  currency: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  usage: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  usageCount: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  lastResetDate: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  lastUsedDate: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  cooldownUntil: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  monthlyUsage: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const ExternalKeysOrderByWithRelationInputSchema: z.ZodType<Prisma.ExternalKeysOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  errors: z.lazy(() => SortOrderSchema).optional(),
  currency: z.lazy(() => SortOrderSchema).optional(),
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  lastResetDate: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  lastUsedDate: z.lazy(() => SortOrderSchema).optional(),
  cooldownUntil: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ExternalKeysWhereUniqueInputSchema: z.ZodType<Prisma.ExternalKeysWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => ExternalKeysWhereInputSchema),z.lazy(() => ExternalKeysWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ExternalKeysWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ExternalKeysWhereInputSchema),z.lazy(() => ExternalKeysWhereInputSchema).array() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumExternalKeysStatusFilterSchema),z.lazy(() => ExternalKeysStatusSchema) ]).optional(),
  type: z.union([ z.lazy(() => EnumExternalKeysTypeFilterSchema),z.lazy(() => ExternalKeysTypeSchema) ]).optional(),
  errors: z.lazy(() => StringNullableListFilterSchema).optional(),
  currency: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  usage: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  usageCount: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  lastResetDate: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  lastUsedDate: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  cooldownUntil: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  monthlyUsage: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict());

export const ExternalKeysOrderByWithAggregationInputSchema: z.ZodType<Prisma.ExternalKeysOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  errors: z.lazy(() => SortOrderSchema).optional(),
  currency: z.lazy(() => SortOrderSchema).optional(),
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  lastResetDate: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  lastUsedDate: z.lazy(() => SortOrderSchema).optional(),
  cooldownUntil: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => ExternalKeysCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => ExternalKeysAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => ExternalKeysMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => ExternalKeysMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => ExternalKeysSumOrderByAggregateInputSchema).optional()
}).strict();

export const ExternalKeysScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.ExternalKeysScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => ExternalKeysScalarWhereWithAggregatesInputSchema),z.lazy(() => ExternalKeysScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => ExternalKeysScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ExternalKeysScalarWhereWithAggregatesInputSchema),z.lazy(() => ExternalKeysScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  key: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumExternalKeysStatusWithAggregatesFilterSchema),z.lazy(() => ExternalKeysStatusSchema) ]).optional(),
  type: z.union([ z.lazy(() => EnumExternalKeysTypeWithAggregatesFilterSchema),z.lazy(() => ExternalKeysTypeSchema) ]).optional(),
  errors: z.lazy(() => StringNullableListFilterSchema).optional(),
  currency: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  usage: z.union([ z.lazy(() => FloatWithAggregatesFilterSchema),z.number() ]).optional(),
  usageCount: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  lastResetDate: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  lastUsedDate: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  cooldownUntil: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  monthlyUsage: z.union([ z.lazy(() => FloatWithAggregatesFilterSchema),z.number() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const ProxyWhereInputSchema: z.ZodType<Prisma.ProxyWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ProxyWhereInputSchema),z.lazy(() => ProxyWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ProxyWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ProxyWhereInputSchema),z.lazy(() => ProxyWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  proxyUrl: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  cookieIdentities: z.lazy(() => CookieIdentityListRelationFilterSchema).optional()
}).strict();

export const ProxyOrderByWithRelationInputSchema: z.ZodType<Prisma.ProxyOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  proxyUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  cookieIdentities: z.lazy(() => CookieIdentityOrderByRelationAggregateInputSchema).optional()
}).strict();

export const ProxyWhereUniqueInputSchema: z.ZodType<Prisma.ProxyWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => ProxyWhereInputSchema),z.lazy(() => ProxyWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ProxyWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ProxyWhereInputSchema),z.lazy(() => ProxyWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  proxyUrl: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  cookieIdentities: z.lazy(() => CookieIdentityListRelationFilterSchema).optional()
}).strict());

export const ProxyOrderByWithAggregationInputSchema: z.ZodType<Prisma.ProxyOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  proxyUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => ProxyCountOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => ProxyMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => ProxyMinOrderByAggregateInputSchema).optional()
}).strict();

export const ProxyScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.ProxyScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => ProxyScalarWhereWithAggregatesInputSchema),z.lazy(() => ProxyScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => ProxyScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ProxyScalarWhereWithAggregatesInputSchema),z.lazy(() => ProxyScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  proxyUrl: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const CookieIdentityWhereInputSchema: z.ZodType<Prisma.CookieIdentityWhereInput> = z.object({
  AND: z.union([ z.lazy(() => CookieIdentityWhereInputSchema),z.lazy(() => CookieIdentityWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CookieIdentityWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CookieIdentityWhereInputSchema),z.lazy(() => CookieIdentityWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  lastRequestTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  proxyId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  cookieContent: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  useCount: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  errors: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  proxy: z.union([ z.lazy(() => ProxyScalarRelationFilterSchema),z.lazy(() => ProxyWhereInputSchema) ]).optional(),
}).strict();

export const CookieIdentityOrderByWithRelationInputSchema: z.ZodType<Prisma.CookieIdentityOrderByWithRelationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  lastRequestTime: z.lazy(() => SortOrderSchema).optional(),
  proxyId: z.lazy(() => SortOrderSchema).optional(),
  cookieContent: z.lazy(() => SortOrderSchema).optional(),
  useCount: z.lazy(() => SortOrderSchema).optional(),
  errors: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  proxy: z.lazy(() => ProxyOrderByWithRelationInputSchema).optional()
}).strict();

export const CookieIdentityWhereUniqueInputSchema: z.ZodType<Prisma.CookieIdentityWhereUniqueInput> = z.object({
  id: z.string().cuid()
})
.and(z.object({
  id: z.string().cuid().optional(),
  AND: z.union([ z.lazy(() => CookieIdentityWhereInputSchema),z.lazy(() => CookieIdentityWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CookieIdentityWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CookieIdentityWhereInputSchema),z.lazy(() => CookieIdentityWhereInputSchema).array() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  lastRequestTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  proxyId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  cookieContent: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  useCount: z.union([ z.lazy(() => IntFilterSchema),z.number().int() ]).optional(),
  errors: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  proxy: z.union([ z.lazy(() => ProxyScalarRelationFilterSchema),z.lazy(() => ProxyWhereInputSchema) ]).optional(),
}).strict());

export const CookieIdentityOrderByWithAggregationInputSchema: z.ZodType<Prisma.CookieIdentityOrderByWithAggregationInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  lastRequestTime: z.lazy(() => SortOrderSchema).optional(),
  proxyId: z.lazy(() => SortOrderSchema).optional(),
  cookieContent: z.lazy(() => SortOrderSchema).optional(),
  useCount: z.lazy(() => SortOrderSchema).optional(),
  errors: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  _count: z.lazy(() => CookieIdentityCountOrderByAggregateInputSchema).optional(),
  _avg: z.lazy(() => CookieIdentityAvgOrderByAggregateInputSchema).optional(),
  _max: z.lazy(() => CookieIdentityMaxOrderByAggregateInputSchema).optional(),
  _min: z.lazy(() => CookieIdentityMinOrderByAggregateInputSchema).optional(),
  _sum: z.lazy(() => CookieIdentitySumOrderByAggregateInputSchema).optional()
}).strict();

export const CookieIdentityScalarWhereWithAggregatesInputSchema: z.ZodType<Prisma.CookieIdentityScalarWhereWithAggregatesInput> = z.object({
  AND: z.union([ z.lazy(() => CookieIdentityScalarWhereWithAggregatesInputSchema),z.lazy(() => CookieIdentityScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  OR: z.lazy(() => CookieIdentityScalarWhereWithAggregatesInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CookieIdentityScalarWhereWithAggregatesInputSchema),z.lazy(() => CookieIdentityScalarWhereWithAggregatesInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  isActive: z.union([ z.lazy(() => BoolWithAggregatesFilterSchema),z.boolean() ]).optional(),
  lastRequestTime: z.union([ z.lazy(() => DateTimeNullableWithAggregatesFilterSchema),z.coerce.date() ]).optional().nullable(),
  proxyId: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  cookieContent: z.union([ z.lazy(() => StringWithAggregatesFilterSchema),z.string() ]).optional(),
  useCount: z.union([ z.lazy(() => IntWithAggregatesFilterSchema),z.number() ]).optional(),
  errors: z.lazy(() => JsonWithAggregatesFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeWithAggregatesFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const UserCreateInputSchema: z.ZodType<Prisma.UserCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateInputSchema: z.ZodType<Prisma.UserUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUpdateInputSchema: z.ZodType<Prisma.UserUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateInputSchema: z.ZodType<Prisma.UserUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserCreateManyInputSchema: z.ZodType<Prisma.UserCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional()
}).strict();

export const UserUpdateManyMutationInputSchema: z.ZodType<Prisma.UserUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const UserUncheckedUpdateManyInputSchema: z.ZodType<Prisma.UserUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const SessionCreateInputSchema: z.ZodType<Prisma.SessionCreateInput> = z.object({
  id: z.string().cuid().optional(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  impersonatedBy: z.string().optional().nullable(),
  activeOrganizationId: z.string().optional().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  user: z.lazy(() => UserCreateNestedOneWithoutSessionsInputSchema)
}).strict();

export const SessionUncheckedCreateInputSchema: z.ZodType<Prisma.SessionUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  userId: z.string(),
  impersonatedBy: z.string().optional().nullable(),
  activeOrganizationId: z.string().optional().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const SessionUpdateInputSchema: z.ZodType<Prisma.SessionUpdateInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutSessionsNestedInputSchema).optional()
}).strict();

export const SessionUncheckedUpdateInputSchema: z.ZodType<Prisma.SessionUncheckedUpdateInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const SessionCreateManyInputSchema: z.ZodType<Prisma.SessionCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  userId: z.string(),
  impersonatedBy: z.string().optional().nullable(),
  activeOrganizationId: z.string().optional().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const SessionUpdateManyMutationInputSchema: z.ZodType<Prisma.SessionUpdateManyMutationInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const SessionUncheckedUpdateManyInputSchema: z.ZodType<Prisma.SessionUncheckedUpdateManyInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AccountCreateInputSchema: z.ZodType<Prisma.AccountCreateInput> = z.object({
  id: z.string().cuid().optional(),
  accountId: z.string(),
  providerId: z.string(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  password: z.string().optional().nullable(),
  accessTokenExpiresAt: z.coerce.date().optional().nullable(),
  refreshTokenExpiresAt: z.coerce.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  user: z.lazy(() => UserCreateNestedOneWithoutAccountsInputSchema)
}).strict();

export const AccountUncheckedCreateInputSchema: z.ZodType<Prisma.AccountUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  accountId: z.string(),
  providerId: z.string(),
  userId: z.string(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  password: z.string().optional().nullable(),
  accessTokenExpiresAt: z.coerce.date().optional().nullable(),
  refreshTokenExpiresAt: z.coerce.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const AccountUpdateInputSchema: z.ZodType<Prisma.AccountUpdateInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutAccountsNestedInputSchema).optional()
}).strict();

export const AccountUncheckedUpdateInputSchema: z.ZodType<Prisma.AccountUncheckedUpdateInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AccountCreateManyInputSchema: z.ZodType<Prisma.AccountCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  accountId: z.string(),
  providerId: z.string(),
  userId: z.string(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  password: z.string().optional().nullable(),
  accessTokenExpiresAt: z.coerce.date().optional().nullable(),
  refreshTokenExpiresAt: z.coerce.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const AccountUpdateManyMutationInputSchema: z.ZodType<Prisma.AccountUpdateManyMutationInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AccountUncheckedUpdateManyInputSchema: z.ZodType<Prisma.AccountUncheckedUpdateManyInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VerificationCreateInputSchema: z.ZodType<Prisma.VerificationCreateInput> = z.object({
  id: z.string().cuid().optional(),
  identifier: z.string(),
  value: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable()
}).strict();

export const VerificationUncheckedCreateInputSchema: z.ZodType<Prisma.VerificationUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  identifier: z.string(),
  value: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable()
}).strict();

export const VerificationUpdateInputSchema: z.ZodType<Prisma.VerificationUpdateInput> = z.object({
  identifier: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  value: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VerificationUncheckedUpdateInputSchema: z.ZodType<Prisma.VerificationUncheckedUpdateInput> = z.object({
  identifier: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  value: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VerificationCreateManyInputSchema: z.ZodType<Prisma.VerificationCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  identifier: z.string(),
  value: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable()
}).strict();

export const VerificationUpdateManyMutationInputSchema: z.ZodType<Prisma.VerificationUpdateManyMutationInput> = z.object({
  identifier: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  value: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VerificationUncheckedUpdateManyInputSchema: z.ZodType<Prisma.VerificationUncheckedUpdateManyInput> = z.object({
  identifier: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  value: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const PasskeyCreateInputSchema: z.ZodType<Prisma.PasskeyCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string().optional().nullable(),
  publicKey: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutPasskeysInputSchema)
}).strict();

export const PasskeyUncheckedCreateInputSchema: z.ZodType<Prisma.PasskeyUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string().optional().nullable(),
  publicKey: z.string(),
  userId: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const PasskeyUpdateInputSchema: z.ZodType<Prisma.PasskeyUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutPasskeysNestedInputSchema).optional()
}).strict();

export const PasskeyUncheckedUpdateInputSchema: z.ZodType<Prisma.PasskeyUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const PasskeyCreateManyInputSchema: z.ZodType<Prisma.PasskeyCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string().optional().nullable(),
  publicKey: z.string(),
  userId: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const PasskeyUpdateManyMutationInputSchema: z.ZodType<Prisma.PasskeyUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const PasskeyUncheckedUpdateManyInputSchema: z.ZodType<Prisma.PasskeyUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const TwoFactorCreateInputSchema: z.ZodType<Prisma.TwoFactorCreateInput> = z.object({
  id: z.string().cuid().optional(),
  secret: z.string(),
  backupCodes: z.string(),
  user: z.lazy(() => UserCreateNestedOneWithoutTwofactorsInputSchema)
}).strict();

export const TwoFactorUncheckedCreateInputSchema: z.ZodType<Prisma.TwoFactorUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  secret: z.string(),
  backupCodes: z.string(),
  userId: z.string()
}).strict();

export const TwoFactorUpdateInputSchema: z.ZodType<Prisma.TwoFactorUpdateInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutTwofactorsNestedInputSchema).optional()
}).strict();

export const TwoFactorUncheckedUpdateInputSchema: z.ZodType<Prisma.TwoFactorUncheckedUpdateInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const TwoFactorCreateManyInputSchema: z.ZodType<Prisma.TwoFactorCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  secret: z.string(),
  backupCodes: z.string(),
  userId: z.string()
}).strict();

export const TwoFactorUpdateManyMutationInputSchema: z.ZodType<Prisma.TwoFactorUpdateManyMutationInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const TwoFactorUncheckedUpdateManyInputSchema: z.ZodType<Prisma.TwoFactorUncheckedUpdateManyInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationCreateInputSchema: z.ZodType<Prisma.OrganizationCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUpdateInputSchema: z.ZodType<Prisma.OrganizationUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationCreateManyInputSchema: z.ZodType<Prisma.OrganizationCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable()
}).strict();

export const OrganizationUpdateManyMutationInputSchema: z.ZodType<Prisma.OrganizationUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const OrganizationUncheckedUpdateManyInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const MemberCreateInputSchema: z.ZodType<Prisma.MemberCreateInput> = z.object({
  id: z.string().cuid().optional(),
  role: z.string(),
  createdAt: z.coerce.date(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutMembersInputSchema),
  user: z.lazy(() => UserCreateNestedOneWithoutMembersInputSchema)
}).strict();

export const MemberUncheckedCreateInputSchema: z.ZodType<Prisma.MemberUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date()
}).strict();

export const MemberUpdateInputSchema: z.ZodType<Prisma.MemberUpdateInput> = z.object({
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutMembersNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutMembersNestedInputSchema).optional()
}).strict();

export const MemberUncheckedUpdateInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const MemberCreateManyInputSchema: z.ZodType<Prisma.MemberCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date()
}).strict();

export const MemberUpdateManyMutationInputSchema: z.ZodType<Prisma.MemberUpdateManyMutationInput> = z.object({
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const MemberUncheckedUpdateManyInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const InvitationCreateInputSchema: z.ZodType<Prisma.InvitationCreateInput> = z.object({
  id: z.string().cuid().optional(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutInvitationsInputSchema),
  user: z.lazy(() => UserCreateNestedOneWithoutInvitationsInputSchema)
}).strict();

export const InvitationUncheckedCreateInputSchema: z.ZodType<Prisma.InvitationUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  inviterId: z.string()
}).strict();

export const InvitationUpdateInputSchema: z.ZodType<Prisma.InvitationUpdateInput> = z.object({
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutInvitationsNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutInvitationsNestedInputSchema).optional()
}).strict();

export const InvitationUncheckedUpdateInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  inviterId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const InvitationCreateManyInputSchema: z.ZodType<Prisma.InvitationCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  inviterId: z.string()
}).strict();

export const InvitationUpdateManyMutationInputSchema: z.ZodType<Prisma.InvitationUpdateManyMutationInput> = z.object({
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const InvitationUncheckedUpdateManyInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  inviterId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PurchaseCreateInputSchema: z.ZodType<Prisma.PurchaseCreateInput> = z.object({
  id: z.string().cuid().optional(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutPurchasesInputSchema).optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutPurchasesInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutPurchaseInputSchema).optional()
}).strict();

export const PurchaseUncheckedCreateInputSchema: z.ZodType<Prisma.PurchaseUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutPurchaseInputSchema).optional()
}).strict();

export const PurchaseUpdateInputSchema: z.ZodType<Prisma.PurchaseUpdateInput> = z.object({
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutPurchasesNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutPurchasesNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutPurchaseNestedInputSchema).optional()
}).strict();

export const PurchaseUncheckedUpdateInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutPurchaseNestedInputSchema).optional()
}).strict();

export const PurchaseCreateManyInputSchema: z.ZodType<Prisma.PurchaseCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const PurchaseUpdateManyMutationInputSchema: z.ZodType<Prisma.PurchaseUpdateManyMutationInput> = z.object({
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PurchaseUncheckedUpdateManyInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatCreateInputSchema: z.ZodType<Prisma.AiChatCreateInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutAiChatsInputSchema).optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutAiChatsInputSchema).optional()
}).strict();

export const AiChatUncheckedCreateInputSchema: z.ZodType<Prisma.AiChatUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AiChatUpdateInputSchema: z.ZodType<Prisma.AiChatUpdateInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutAiChatsNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutAiChatsNestedInputSchema).optional()
}).strict();

export const AiChatUncheckedUpdateInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatCreateManyInputSchema: z.ZodType<Prisma.AiChatCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AiChatUpdateManyMutationInputSchema: z.ZodType<Prisma.AiChatUpdateManyMutationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatUncheckedUpdateManyInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ApiKeyCreateInputSchema: z.ZodType<Prisma.ApiKeyCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutApiKeysInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutApiKeysInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutKeyInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyUncheckedCreateInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutKeyInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyUpdateInputSchema: z.ZodType<Prisma.ApiKeyUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutApiKeysNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutApiKeysNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutKeyNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutKeyNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyCreateManyInputSchema: z.ZodType<Prisma.ApiKeyCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const ApiKeyUpdateManyMutationInputSchema: z.ZodType<Prisma.ApiKeyUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const ApiKeyUncheckedUpdateManyInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateManyInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const JobCreateInputSchema: z.ZodType<Prisma.JobCreateInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateInputSchema: z.ZodType<Prisma.JobUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUpdateInputSchema: z.ZodType<Prisma.JobUpdateInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateInputSchema: z.ZodType<Prisma.JobUncheckedUpdateInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobCreateManyInputSchema: z.ZodType<Prisma.JobCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable()
}).strict();

export const JobUpdateManyMutationInputSchema: z.ZodType<Prisma.JobUpdateManyMutationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const JobUncheckedUpdateManyInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VideoCreateInputSchema: z.ZodType<Prisma.VideoCreateInput> = z.object({
  id: z.string().cuid().optional(),
  sourceId: z.string(),
  videoLength: z.number(),
  title: z.string(),
  files: z.union([ z.lazy(() => SourceFilesNullableCreateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCreateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.string(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCreateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListCreateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.lazy(() => VideoStatusSchema),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListCreateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.coerce.date().optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutVideoInputSchema)
}).strict();

export const VideoUncheckedCreateInputSchema: z.ZodType<Prisma.VideoUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  sourceId: z.string(),
  videoLength: z.number(),
  title: z.string(),
  files: z.union([ z.lazy(() => SourceFilesNullableCreateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCreateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.string(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCreateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListCreateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.lazy(() => VideoStatusSchema),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListCreateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.coerce.date().optional()
}).strict();

export const VideoUpdateInputSchema: z.ZodType<Prisma.VideoUpdateInput> = z.object({
  sourceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  videoLength: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableUpdateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableUpdateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableUpdateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListUpdateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => EnumVideoStatusFieldUpdateOperationsInputSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListUpdateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutVideoNestedInputSchema).optional()
}).strict();

export const VideoUncheckedUpdateInputSchema: z.ZodType<Prisma.VideoUncheckedUpdateInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  sourceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  videoLength: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableUpdateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableUpdateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableUpdateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListUpdateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => EnumVideoStatusFieldUpdateOperationsInputSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListUpdateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoCreateManyInputSchema: z.ZodType<Prisma.VideoCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  sourceId: z.string(),
  videoLength: z.number(),
  title: z.string(),
  files: z.union([ z.lazy(() => SourceFilesNullableCreateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCreateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.string(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCreateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListCreateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.lazy(() => VideoStatusSchema),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListCreateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.coerce.date().optional()
}).strict();

export const VideoUpdateManyMutationInputSchema: z.ZodType<Prisma.VideoUpdateManyMutationInput> = z.object({
  sourceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  videoLength: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableUpdateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableUpdateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableUpdateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListUpdateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => EnumVideoStatusFieldUpdateOperationsInputSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListUpdateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoUncheckedUpdateManyInputSchema: z.ZodType<Prisma.VideoUncheckedUpdateManyInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  sourceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  videoLength: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableUpdateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableUpdateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableUpdateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListUpdateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => EnumVideoStatusFieldUpdateOperationsInputSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListUpdateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoProjectCreateInputSchema: z.ZodType<Prisma.VideoProjectCreateInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutVideoProjectInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutVideoProjectsInputSchema),
  createdBy: z.lazy(() => UserCreateNestedOneWithoutVideoProjectsInputSchema),
  design: z.lazy(() => VideoDesignCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectUncheckedCreateInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  design: z.lazy(() => VideoDesignUncheckedCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectUpdateInputSchema: z.ZodType<Prisma.VideoProjectUpdateInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutVideoProjectNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  createdBy: z.lazy(() => UserUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  design: z.lazy(() => VideoDesignUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdByUserId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.lazy(() => VideoDesignUncheckedUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectCreateManyInputSchema: z.ZodType<Prisma.VideoProjectCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional()
}).strict();

export const VideoProjectUpdateManyMutationInputSchema: z.ZodType<Prisma.VideoProjectUpdateManyMutationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoProjectUncheckedUpdateManyInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateManyInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdByUserId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoDesignCreateInputSchema: z.ZodType<Prisma.VideoDesignCreateInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  status: z.lazy(() => VideoDesignStatusSchema).optional(),
  design: z.record(z.string(), z.any()).optional(),
  fps: z.number().int().optional(),
  durationMs: z.number().int().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  latestRenderHistoryId: z.string().optional().nullable(),
  latestMp4Url: z.string().optional().nullable(),
  compiledAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutDesignInputSchema)
}).strict();

export const VideoDesignUncheckedCreateInputSchema: z.ZodType<Prisma.VideoDesignUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  videoProjectId: z.string(),
  jobId: z.string(),
  organizationId: z.string(),
  status: z.lazy(() => VideoDesignStatusSchema).optional(),
  design: z.record(z.string(), z.any()).optional(),
  fps: z.number().int().optional(),
  durationMs: z.number().int().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  latestRenderHistoryId: z.string().optional().nullable(),
  latestMp4Url: z.string().optional().nullable(),
  compiledAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const VideoDesignUpdateInputSchema: z.ZodType<Prisma.VideoDesignUpdateInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => EnumVideoDesignStatusFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fps: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  durationMs: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  width: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  latestRenderHistoryId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  latestMp4Url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  compiledAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneRequiredWithoutDesignNestedInputSchema).optional()
}).strict();

export const VideoDesignUncheckedUpdateInputSchema: z.ZodType<Prisma.VideoDesignUncheckedUpdateInput> = z.object({
  videoProjectId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => EnumVideoDesignStatusFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fps: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  durationMs: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  width: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  latestRenderHistoryId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  latestMp4Url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  compiledAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoDesignCreateManyInputSchema: z.ZodType<Prisma.VideoDesignCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  videoProjectId: z.string(),
  jobId: z.string(),
  organizationId: z.string(),
  status: z.lazy(() => VideoDesignStatusSchema).optional(),
  design: z.record(z.string(), z.any()).optional(),
  fps: z.number().int().optional(),
  durationMs: z.number().int().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  latestRenderHistoryId: z.string().optional().nullable(),
  latestMp4Url: z.string().optional().nullable(),
  compiledAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const VideoDesignUpdateManyMutationInputSchema: z.ZodType<Prisma.VideoDesignUpdateManyMutationInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => EnumVideoDesignStatusFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fps: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  durationMs: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  width: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  latestRenderHistoryId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  latestMp4Url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  compiledAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoDesignUncheckedUpdateManyInputSchema: z.ZodType<Prisma.VideoDesignUncheckedUpdateManyInput> = z.object({
  videoProjectId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => EnumVideoDesignStatusFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fps: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  durationMs: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  width: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  latestRenderHistoryId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  latestMp4Url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  compiledAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AppFileCreateInputSchema: z.ZodType<Prisma.AppFileCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutFilesInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutFilesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutFilesInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileUncheckedCreateInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileUpdateInputSchema: z.ZodType<Prisma.AppFileUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutFilesNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutFilesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneWithoutFilesNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileCreateManyInputSchema: z.ZodType<Prisma.AppFileCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional()
}).strict();

export const AppFileUpdateManyMutationInputSchema: z.ZodType<Prisma.AppFileUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AppFileUncheckedUpdateManyInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CaptionStyleCreateInputSchema: z.ZodType<Prisma.CaptionStyleCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  style: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutCaptionStylesInputSchema)
}).strict();

export const CaptionStyleUncheckedCreateInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  organizationId: z.string(),
  style: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CaptionStyleUpdateInputSchema: z.ZodType<Prisma.CaptionStyleUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutCaptionStylesNestedInputSchema).optional()
}).strict();

export const CaptionStyleUncheckedUpdateInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CaptionStyleCreateManyInputSchema: z.ZodType<Prisma.CaptionStyleCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  organizationId: z.string(),
  style: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CaptionStyleUpdateManyMutationInputSchema: z.ZodType<Prisma.CaptionStyleUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CaptionStyleUncheckedUpdateManyInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingCreateInputSchema: z.ZodType<Prisma.OrganizationBrandingCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  preset: z.string().optional().nullable(),
  size: InputJsonValueSchema.optional().nullable(),
  options: InputJsonValueSchema.optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutBrandingsInputSchema)
}).strict();

export const OrganizationBrandingUncheckedCreateInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  preset: z.string().optional().nullable(),
  size: InputJsonValueSchema.optional().nullable(),
  options: InputJsonValueSchema.optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationBrandingUpdateInputSchema: z.ZodType<Prisma.OrganizationBrandingUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutBrandingsNestedInputSchema).optional()
}).strict();

export const OrganizationBrandingUncheckedUpdateInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingCreateManyInputSchema: z.ZodType<Prisma.OrganizationBrandingCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  preset: z.string().optional().nullable(),
  size: InputJsonValueSchema.optional().nullable(),
  options: InputJsonValueSchema.optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationBrandingUpdateManyMutationInputSchema: z.ZodType<Prisma.OrganizationBrandingUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingUncheckedUpdateManyInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileCreateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  personality: z.string(),
  styleNotes: z.string().optional().nullable(),
  ttsStyle: z.string().optional(),
  ttsPace: z.string().optional(),
  ttsAccent: z.string().optional(),
  ttsNotes: z.string().optional().nullable(),
  channelName: z.string().optional().nullable(),
  channelSignOff: z.string().optional().nullable(),
  overlayText: z.string().optional().nullable(),
  overlayTextDelayMs: z.number().int().optional().nullable(),
  overlayTextDurationMs: z.number().int().optional().nullable(),
  visualDefaults: InputJsonValueSchema.optional().nullable(),
  speechSpeed: z.number().optional(),
  avatarFileAppId: z.string().optional().nullable(),
  avatarPhotos: InputJsonValueSchema.optional().nullable(),
  avatarMinDurationMs: z.number().int().optional().nullable(),
  avatarMaxDurationMs: z.number().int().optional().nullable(),
  avatarAppearEveryMs: z.number().int().optional().nullable(),
  avatarIntroHoldMs: z.number().int().optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutNarratorProfilesInputSchema)
}).strict();

export const OrganizationNarratorProfileUncheckedCreateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  personality: z.string(),
  styleNotes: z.string().optional().nullable(),
  ttsStyle: z.string().optional(),
  ttsPace: z.string().optional(),
  ttsAccent: z.string().optional(),
  ttsNotes: z.string().optional().nullable(),
  channelName: z.string().optional().nullable(),
  channelSignOff: z.string().optional().nullable(),
  overlayText: z.string().optional().nullable(),
  overlayTextDelayMs: z.number().int().optional().nullable(),
  overlayTextDurationMs: z.number().int().optional().nullable(),
  visualDefaults: InputJsonValueSchema.optional().nullable(),
  speechSpeed: z.number().optional(),
  avatarFileAppId: z.string().optional().nullable(),
  avatarPhotos: InputJsonValueSchema.optional().nullable(),
  avatarMinDurationMs: z.number().int().optional().nullable(),
  avatarMaxDurationMs: z.number().int().optional().nullable(),
  avatarAppearEveryMs: z.number().int().optional().nullable(),
  avatarIntroHoldMs: z.number().int().optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationNarratorProfileUpdateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutNarratorProfilesNestedInputSchema).optional()
}).strict();

export const OrganizationNarratorProfileUncheckedUpdateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileCreateManyInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  personality: z.string(),
  styleNotes: z.string().optional().nullable(),
  ttsStyle: z.string().optional(),
  ttsPace: z.string().optional(),
  ttsAccent: z.string().optional(),
  ttsNotes: z.string().optional().nullable(),
  channelName: z.string().optional().nullable(),
  channelSignOff: z.string().optional().nullable(),
  overlayText: z.string().optional().nullable(),
  overlayTextDelayMs: z.number().int().optional().nullable(),
  overlayTextDurationMs: z.number().int().optional().nullable(),
  visualDefaults: InputJsonValueSchema.optional().nullable(),
  speechSpeed: z.number().optional(),
  avatarFileAppId: z.string().optional().nullable(),
  avatarPhotos: InputJsonValueSchema.optional().nullable(),
  avatarMinDurationMs: z.number().int().optional().nullable(),
  avatarMaxDurationMs: z.number().int().optional().nullable(),
  avatarAppearEveryMs: z.number().int().optional().nullable(),
  avatarIntroHoldMs: z.number().int().optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationNarratorProfileUpdateManyMutationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileUncheckedUpdateManyInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsCreateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreateInput> = z.object({
  id: z.string().cuid().optional(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  providerProfileId: z.string().optional().nullable(),
  isEnabled: z.boolean().optional(),
  approveApiPosts: z.boolean().optional(),
  defaultTimezone: z.string().optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsCreatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: InputJsonValueSchema.optional(),
  maxPostsPerSlot: z.number().int().optional(),
  maxShortsOfSameVideo: z.number().int().optional(),
  maxShortsPerWeek: z.number().int().optional(),
  minimumVideoDurationSeconds: z.number().int().optional(),
  processOnlyPlaylistId: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutSocialSettingsInputSchema)
}).strict();

export const OrganizationSocialSettingsUncheckedCreateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  providerProfileId: z.string().optional().nullable(),
  isEnabled: z.boolean().optional(),
  approveApiPosts: z.boolean().optional(),
  defaultTimezone: z.string().optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsCreatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: InputJsonValueSchema.optional(),
  maxPostsPerSlot: z.number().int().optional(),
  maxShortsOfSameVideo: z.number().int().optional(),
  maxShortsPerWeek: z.number().int().optional(),
  minimumVideoDurationSeconds: z.number().int().optional(),
  processOnlyPlaylistId: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialSettingsUpdateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdateInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  providerProfileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isEnabled: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  approveApiPosts: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  defaultTimezone: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  maxPostsPerSlot: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsOfSameVideo: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsPerWeek: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  processOnlyPlaylistId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutSocialSettingsNestedInputSchema).optional()
}).strict();

export const OrganizationSocialSettingsUncheckedUpdateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  providerProfileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isEnabled: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  approveApiPosts: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  defaultTimezone: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  maxPostsPerSlot: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsOfSameVideo: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsPerWeek: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  processOnlyPlaylistId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsCreateManyInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  providerProfileId: z.string().optional().nullable(),
  isEnabled: z.boolean().optional(),
  approveApiPosts: z.boolean().optional(),
  defaultTimezone: z.string().optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsCreatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: InputJsonValueSchema.optional(),
  maxPostsPerSlot: z.number().int().optional(),
  maxShortsOfSameVideo: z.number().int().optional(),
  maxShortsPerWeek: z.number().int().optional(),
  minimumVideoDurationSeconds: z.number().int().optional(),
  processOnlyPlaylistId: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialSettingsUpdateManyMutationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdateManyMutationInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  providerProfileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isEnabled: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  approveApiPosts: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  defaultTimezone: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  maxPostsPerSlot: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsOfSameVideo: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsPerWeek: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  processOnlyPlaylistId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsUncheckedUpdateManyInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  providerProfileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isEnabled: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  approveApiPosts: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  defaultTimezone: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  maxPostsPerSlot: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsOfSameVideo: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsPerWeek: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  processOnlyPlaylistId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostCreateInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateInput> = z.object({
  id: z.string().cuid().optional(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutSocialPostsInputSchema),
  job: z.lazy(() => JobCreateNestedOneWithoutSocialPostsInputSchema).optional()
}).strict();

export const OrganizationSocialPostUncheckedCreateInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  jobId: z.string().optional().nullable(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialPostUpdateInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutSocialPostsNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneWithoutSocialPostsNestedInputSchema).optional()
}).strict();

export const OrganizationSocialPostUncheckedUpdateInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostCreateManyInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  jobId: z.string().optional().nullable(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialPostUpdateManyMutationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateManyMutationInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUncheckedUpdateManyInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateManyInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryCreateInputSchema: z.ZodType<Prisma.RenderHistoryCreateInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutRenderHistoryInputSchema),
  user: z.lazy(() => UserCreateNestedOneWithoutRenderHistoryInputSchema).optional(),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutRenderHistoriesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutRenderHistoriesInputSchema),
  appFile: z.lazy(() => AppFileCreateNestedOneWithoutRenderHistoriesInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedCreateInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryUpdateInputSchema: z.ZodType<Prisma.RenderHistoryUpdateInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutRenderHistoryNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutRenderHistoriesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutRenderHistoriesNestedInputSchema).optional(),
  appFile: z.lazy(() => AppFileUpdateOneWithoutRenderHistoriesNestedInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedUpdateInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryCreateManyInputSchema: z.ZodType<Prisma.RenderHistoryCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryUpdateManyMutationInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyMutationInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ExternalKeysCreateInputSchema: z.ZodType<Prisma.ExternalKeysCreateInput> = z.object({
  id: z.string().cuid().optional(),
  key: z.string(),
  name: z.string(),
  status: z.lazy(() => ExternalKeysStatusSchema).optional(),
  type: z.lazy(() => ExternalKeysTypeSchema),
  errors: z.union([ z.lazy(() => ExternalKeysCreateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.string().optional(),
  usage: z.number().optional(),
  usageCount: z.number().int().optional(),
  lastResetDate: z.coerce.date().optional().nullable(),
  monthlyUsageCount: z.number().int().optional(),
  lastUsedDate: z.coerce.date().optional().nullable(),
  cooldownUntil: z.coerce.date().optional().nullable(),
  monthlyUsage: z.number().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const ExternalKeysUncheckedCreateInputSchema: z.ZodType<Prisma.ExternalKeysUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  key: z.string(),
  name: z.string(),
  status: z.lazy(() => ExternalKeysStatusSchema).optional(),
  type: z.lazy(() => ExternalKeysTypeSchema),
  errors: z.union([ z.lazy(() => ExternalKeysCreateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.string().optional(),
  usage: z.number().optional(),
  usageCount: z.number().int().optional(),
  lastResetDate: z.coerce.date().optional().nullable(),
  monthlyUsageCount: z.number().int().optional(),
  lastUsedDate: z.coerce.date().optional().nullable(),
  cooldownUntil: z.coerce.date().optional().nullable(),
  monthlyUsage: z.number().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const ExternalKeysUpdateInputSchema: z.ZodType<Prisma.ExternalKeysUpdateInput> = z.object({
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => EnumExternalKeysStatusFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => EnumExternalKeysTypeFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.lazy(() => ExternalKeysUpdateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  usage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  usageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastResetDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastUsedDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cooldownUntil: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ExternalKeysUncheckedUpdateInputSchema: z.ZodType<Prisma.ExternalKeysUncheckedUpdateInput> = z.object({
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => EnumExternalKeysStatusFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => EnumExternalKeysTypeFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.lazy(() => ExternalKeysUpdateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  usage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  usageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastResetDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastUsedDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cooldownUntil: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ExternalKeysCreateManyInputSchema: z.ZodType<Prisma.ExternalKeysCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  key: z.string(),
  name: z.string(),
  status: z.lazy(() => ExternalKeysStatusSchema).optional(),
  type: z.lazy(() => ExternalKeysTypeSchema),
  errors: z.union([ z.lazy(() => ExternalKeysCreateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.string().optional(),
  usage: z.number().optional(),
  usageCount: z.number().int().optional(),
  lastResetDate: z.coerce.date().optional().nullable(),
  monthlyUsageCount: z.number().int().optional(),
  lastUsedDate: z.coerce.date().optional().nullable(),
  cooldownUntil: z.coerce.date().optional().nullable(),
  monthlyUsage: z.number().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const ExternalKeysUpdateManyMutationInputSchema: z.ZodType<Prisma.ExternalKeysUpdateManyMutationInput> = z.object({
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => EnumExternalKeysStatusFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => EnumExternalKeysTypeFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.lazy(() => ExternalKeysUpdateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  usage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  usageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastResetDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastUsedDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cooldownUntil: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ExternalKeysUncheckedUpdateManyInputSchema: z.ZodType<Prisma.ExternalKeysUncheckedUpdateManyInput> = z.object({
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => EnumExternalKeysStatusFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => EnumExternalKeysTypeFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.lazy(() => ExternalKeysUpdateerrorsInputSchema),z.string().array() ]).optional(),
  currency: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  usage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  usageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastResetDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsageCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  lastUsedDate: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cooldownUntil: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  monthlyUsage: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ProxyCreateInputSchema: z.ZodType<Prisma.ProxyCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  proxyUrl: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  cookieIdentities: z.lazy(() => CookieIdentityCreateNestedManyWithoutProxyInputSchema).optional()
}).strict();

export const ProxyUncheckedCreateInputSchema: z.ZodType<Prisma.ProxyUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  proxyUrl: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  cookieIdentities: z.lazy(() => CookieIdentityUncheckedCreateNestedManyWithoutProxyInputSchema).optional()
}).strict();

export const ProxyUpdateInputSchema: z.ZodType<Prisma.ProxyUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  proxyUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  cookieIdentities: z.lazy(() => CookieIdentityUpdateManyWithoutProxyNestedInputSchema).optional()
}).strict();

export const ProxyUncheckedUpdateInputSchema: z.ZodType<Prisma.ProxyUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  proxyUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  cookieIdentities: z.lazy(() => CookieIdentityUncheckedUpdateManyWithoutProxyNestedInputSchema).optional()
}).strict();

export const ProxyCreateManyInputSchema: z.ZodType<Prisma.ProxyCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  proxyUrl: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const ProxyUpdateManyMutationInputSchema: z.ZodType<Prisma.ProxyUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  proxyUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ProxyUncheckedUpdateManyInputSchema: z.ZodType<Prisma.ProxyUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  proxyUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityCreateInputSchema: z.ZodType<Prisma.CookieIdentityCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.coerce.date().optional().nullable(),
  cookieContent: z.string(),
  useCount: z.number().int().optional(),
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  proxy: z.lazy(() => ProxyCreateNestedOneWithoutCookieIdentitiesInputSchema)
}).strict();

export const CookieIdentityUncheckedCreateInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedCreateInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.coerce.date().optional().nullable(),
  proxyId: z.string(),
  cookieContent: z.string(),
  useCount: z.number().int().optional(),
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CookieIdentityUpdateInputSchema: z.ZodType<Prisma.CookieIdentityUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  proxy: z.lazy(() => ProxyUpdateOneRequiredWithoutCookieIdentitiesNestedInputSchema).optional()
}).strict();

export const CookieIdentityUncheckedUpdateInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedUpdateInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  proxyId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityCreateManyInputSchema: z.ZodType<Prisma.CookieIdentityCreateManyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.coerce.date().optional().nullable(),
  proxyId: z.string(),
  cookieContent: z.string(),
  useCount: z.number().int().optional(),
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CookieIdentityUpdateManyMutationInputSchema: z.ZodType<Prisma.CookieIdentityUpdateManyMutationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityUncheckedUpdateManyInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedUpdateManyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  proxyId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const StringFilterSchema: z.ZodType<Prisma.StringFilter> = z.object({
  equals: z.string().optional(),
  in: z.string().array().optional(),
  notIn: z.string().array().optional(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  mode: z.lazy(() => QueryModeSchema).optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringFilterSchema) ]).optional(),
}).strict();

export const BoolFilterSchema: z.ZodType<Prisma.BoolFilter> = z.object({
  equals: z.boolean().optional(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolFilterSchema) ]).optional(),
}).strict();

export const StringNullableFilterSchema: z.ZodType<Prisma.StringNullableFilter> = z.object({
  equals: z.string().optional().nullable(),
  in: z.string().array().optional().nullable(),
  notIn: z.string().array().optional().nullable(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  mode: z.lazy(() => QueryModeSchema).optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const DateTimeFilterSchema: z.ZodType<Prisma.DateTimeFilter> = z.object({
  equals: z.coerce.date().optional(),
  in: z.coerce.date().array().optional(),
  notIn: z.coerce.date().array().optional(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeFilterSchema) ]).optional(),
}).strict();

export const BoolNullableFilterSchema: z.ZodType<Prisma.BoolNullableFilter> = z.object({
  equals: z.boolean().optional().nullable(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const DateTimeNullableFilterSchema: z.ZodType<Prisma.DateTimeNullableFilter> = z.object({
  equals: z.coerce.date().optional().nullable(),
  in: z.coerce.date().array().optional().nullable(),
  notIn: z.coerce.date().array().optional().nullable(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const IntFilterSchema: z.ZodType<Prisma.IntFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntFilterSchema) ]).optional(),
}).strict();

export const SessionListRelationFilterSchema: z.ZodType<Prisma.SessionListRelationFilter> = z.object({
  every: z.lazy(() => SessionWhereInputSchema).optional(),
  some: z.lazy(() => SessionWhereInputSchema).optional(),
  none: z.lazy(() => SessionWhereInputSchema).optional()
}).strict();

export const AccountListRelationFilterSchema: z.ZodType<Prisma.AccountListRelationFilter> = z.object({
  every: z.lazy(() => AccountWhereInputSchema).optional(),
  some: z.lazy(() => AccountWhereInputSchema).optional(),
  none: z.lazy(() => AccountWhereInputSchema).optional()
}).strict();

export const ApiKeyListRelationFilterSchema: z.ZodType<Prisma.ApiKeyListRelationFilter> = z.object({
  every: z.lazy(() => ApiKeyWhereInputSchema).optional(),
  some: z.lazy(() => ApiKeyWhereInputSchema).optional(),
  none: z.lazy(() => ApiKeyWhereInputSchema).optional()
}).strict();

export const PasskeyListRelationFilterSchema: z.ZodType<Prisma.PasskeyListRelationFilter> = z.object({
  every: z.lazy(() => PasskeyWhereInputSchema).optional(),
  some: z.lazy(() => PasskeyWhereInputSchema).optional(),
  none: z.lazy(() => PasskeyWhereInputSchema).optional()
}).strict();

export const InvitationListRelationFilterSchema: z.ZodType<Prisma.InvitationListRelationFilter> = z.object({
  every: z.lazy(() => InvitationWhereInputSchema).optional(),
  some: z.lazy(() => InvitationWhereInputSchema).optional(),
  none: z.lazy(() => InvitationWhereInputSchema).optional()
}).strict();

export const PurchaseListRelationFilterSchema: z.ZodType<Prisma.PurchaseListRelationFilter> = z.object({
  every: z.lazy(() => PurchaseWhereInputSchema).optional(),
  some: z.lazy(() => PurchaseWhereInputSchema).optional(),
  none: z.lazy(() => PurchaseWhereInputSchema).optional()
}).strict();

export const MemberListRelationFilterSchema: z.ZodType<Prisma.MemberListRelationFilter> = z.object({
  every: z.lazy(() => MemberWhereInputSchema).optional(),
  some: z.lazy(() => MemberWhereInputSchema).optional(),
  none: z.lazy(() => MemberWhereInputSchema).optional()
}).strict();

export const TwoFactorListRelationFilterSchema: z.ZodType<Prisma.TwoFactorListRelationFilter> = z.object({
  every: z.lazy(() => TwoFactorWhereInputSchema).optional(),
  some: z.lazy(() => TwoFactorWhereInputSchema).optional(),
  none: z.lazy(() => TwoFactorWhereInputSchema).optional()
}).strict();

export const AiChatListRelationFilterSchema: z.ZodType<Prisma.AiChatListRelationFilter> = z.object({
  every: z.lazy(() => AiChatWhereInputSchema).optional(),
  some: z.lazy(() => AiChatWhereInputSchema).optional(),
  none: z.lazy(() => AiChatWhereInputSchema).optional()
}).strict();

export const AppFileListRelationFilterSchema: z.ZodType<Prisma.AppFileListRelationFilter> = z.object({
  every: z.lazy(() => AppFileWhereInputSchema).optional(),
  some: z.lazy(() => AppFileWhereInputSchema).optional(),
  none: z.lazy(() => AppFileWhereInputSchema).optional()
}).strict();

export const RenderHistoryListRelationFilterSchema: z.ZodType<Prisma.RenderHistoryListRelationFilter> = z.object({
  every: z.lazy(() => RenderHistoryWhereInputSchema).optional(),
  some: z.lazy(() => RenderHistoryWhereInputSchema).optional(),
  none: z.lazy(() => RenderHistoryWhereInputSchema).optional()
}).strict();

export const JobListRelationFilterSchema: z.ZodType<Prisma.JobListRelationFilter> = z.object({
  every: z.lazy(() => JobWhereInputSchema).optional(),
  some: z.lazy(() => JobWhereInputSchema).optional(),
  none: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const VideoProjectListRelationFilterSchema: z.ZodType<Prisma.VideoProjectListRelationFilter> = z.object({
  every: z.lazy(() => VideoProjectWhereInputSchema).optional(),
  some: z.lazy(() => VideoProjectWhereInputSchema).optional(),
  none: z.lazy(() => VideoProjectWhereInputSchema).optional()
}).strict();

export const SessionOrderByRelationAggregateInputSchema: z.ZodType<Prisma.SessionOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AccountOrderByRelationAggregateInputSchema: z.ZodType<Prisma.AccountOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ApiKeyOrderByRelationAggregateInputSchema: z.ZodType<Prisma.ApiKeyOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PasskeyOrderByRelationAggregateInputSchema: z.ZodType<Prisma.PasskeyOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const InvitationOrderByRelationAggregateInputSchema: z.ZodType<Prisma.InvitationOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PurchaseOrderByRelationAggregateInputSchema: z.ZodType<Prisma.PurchaseOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const MemberOrderByRelationAggregateInputSchema: z.ZodType<Prisma.MemberOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const TwoFactorOrderByRelationAggregateInputSchema: z.ZodType<Prisma.TwoFactorOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AiChatOrderByRelationAggregateInputSchema: z.ZodType<Prisma.AiChatOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AppFileOrderByRelationAggregateInputSchema: z.ZodType<Prisma.AppFileOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const RenderHistoryOrderByRelationAggregateInputSchema: z.ZodType<Prisma.RenderHistoryOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JobOrderByRelationAggregateInputSchema: z.ZodType<Prisma.JobOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoProjectOrderByRelationAggregateInputSchema: z.ZodType<Prisma.VideoProjectOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const UserCountOrderByAggregateInputSchema: z.ZodType<Prisma.UserCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  emailVerified: z.lazy(() => SortOrderSchema).optional(),
  image: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  username: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  banned: z.lazy(() => SortOrderSchema).optional(),
  banReason: z.lazy(() => SortOrderSchema).optional(),
  banExpires: z.lazy(() => SortOrderSchema).optional(),
  onboardingComplete: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  locale: z.lazy(() => SortOrderSchema).optional(),
  phoneNumber: z.lazy(() => SortOrderSchema).optional(),
  phoneVerified: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationCode: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const UserAvgOrderByAggregateInputSchema: z.ZodType<Prisma.UserAvgOrderByAggregateInput> = z.object({
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const UserMaxOrderByAggregateInputSchema: z.ZodType<Prisma.UserMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  emailVerified: z.lazy(() => SortOrderSchema).optional(),
  image: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  username: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  banned: z.lazy(() => SortOrderSchema).optional(),
  banReason: z.lazy(() => SortOrderSchema).optional(),
  banExpires: z.lazy(() => SortOrderSchema).optional(),
  onboardingComplete: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  locale: z.lazy(() => SortOrderSchema).optional(),
  phoneNumber: z.lazy(() => SortOrderSchema).optional(),
  phoneVerified: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationCode: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const UserMinOrderByAggregateInputSchema: z.ZodType<Prisma.UserMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  emailVerified: z.lazy(() => SortOrderSchema).optional(),
  image: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  username: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  banned: z.lazy(() => SortOrderSchema).optional(),
  banReason: z.lazy(() => SortOrderSchema).optional(),
  banExpires: z.lazy(() => SortOrderSchema).optional(),
  onboardingComplete: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  locale: z.lazy(() => SortOrderSchema).optional(),
  phoneNumber: z.lazy(() => SortOrderSchema).optional(),
  phoneVerified: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationCode: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const UserSumOrderByAggregateInputSchema: z.ZodType<Prisma.UserSumOrderByAggregateInput> = z.object({
  smsVerificationAttempts: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const StringWithAggregatesFilterSchema: z.ZodType<Prisma.StringWithAggregatesFilter> = z.object({
  equals: z.string().optional(),
  in: z.string().array().optional(),
  notIn: z.string().array().optional(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  mode: z.lazy(() => QueryModeSchema).optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedStringFilterSchema).optional(),
  _max: z.lazy(() => NestedStringFilterSchema).optional()
}).strict();

export const BoolWithAggregatesFilterSchema: z.ZodType<Prisma.BoolWithAggregatesFilter> = z.object({
  equals: z.boolean().optional(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedBoolFilterSchema).optional(),
  _max: z.lazy(() => NestedBoolFilterSchema).optional()
}).strict();

export const StringNullableWithAggregatesFilterSchema: z.ZodType<Prisma.StringNullableWithAggregatesFilter> = z.object({
  equals: z.string().optional().nullable(),
  in: z.string().array().optional().nullable(),
  notIn: z.string().array().optional().nullable(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  mode: z.lazy(() => QueryModeSchema).optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedStringNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedStringNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const DateTimeWithAggregatesFilterSchema: z.ZodType<Prisma.DateTimeWithAggregatesFilter> = z.object({
  equals: z.coerce.date().optional(),
  in: z.coerce.date().array().optional(),
  notIn: z.coerce.date().array().optional(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedDateTimeFilterSchema).optional(),
  _max: z.lazy(() => NestedDateTimeFilterSchema).optional()
}).strict();

export const BoolNullableWithAggregatesFilterSchema: z.ZodType<Prisma.BoolNullableWithAggregatesFilter> = z.object({
  equals: z.boolean().optional().nullable(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedBoolNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedBoolNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const DateTimeNullableWithAggregatesFilterSchema: z.ZodType<Prisma.DateTimeNullableWithAggregatesFilter> = z.object({
  equals: z.coerce.date().optional().nullable(),
  in: z.coerce.date().array().optional().nullable(),
  notIn: z.coerce.date().array().optional().nullable(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedDateTimeNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedDateTimeNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const IntWithAggregatesFilterSchema: z.ZodType<Prisma.IntWithAggregatesFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatFilterSchema).optional(),
  _sum: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedIntFilterSchema).optional(),
  _max: z.lazy(() => NestedIntFilterSchema).optional()
}).strict();

export const UserScalarRelationFilterSchema: z.ZodType<Prisma.UserScalarRelationFilter> = z.object({
  is: z.lazy(() => UserWhereInputSchema).optional(),
  isNot: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const SessionCountOrderByAggregateInputSchema: z.ZodType<Prisma.SessionCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  ipAddress: z.lazy(() => SortOrderSchema).optional(),
  userAgent: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  impersonatedBy: z.lazy(() => SortOrderSchema).optional(),
  activeOrganizationId: z.lazy(() => SortOrderSchema).optional(),
  token: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const SessionMaxOrderByAggregateInputSchema: z.ZodType<Prisma.SessionMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  ipAddress: z.lazy(() => SortOrderSchema).optional(),
  userAgent: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  impersonatedBy: z.lazy(() => SortOrderSchema).optional(),
  activeOrganizationId: z.lazy(() => SortOrderSchema).optional(),
  token: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const SessionMinOrderByAggregateInputSchema: z.ZodType<Prisma.SessionMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  ipAddress: z.lazy(() => SortOrderSchema).optional(),
  userAgent: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  impersonatedBy: z.lazy(() => SortOrderSchema).optional(),
  activeOrganizationId: z.lazy(() => SortOrderSchema).optional(),
  token: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AccountCountOrderByAggregateInputSchema: z.ZodType<Prisma.AccountCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  accountId: z.lazy(() => SortOrderSchema).optional(),
  providerId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  accessToken: z.lazy(() => SortOrderSchema).optional(),
  refreshToken: z.lazy(() => SortOrderSchema).optional(),
  idToken: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  password: z.lazy(() => SortOrderSchema).optional(),
  accessTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  refreshTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  scope: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AccountMaxOrderByAggregateInputSchema: z.ZodType<Prisma.AccountMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  accountId: z.lazy(() => SortOrderSchema).optional(),
  providerId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  accessToken: z.lazy(() => SortOrderSchema).optional(),
  refreshToken: z.lazy(() => SortOrderSchema).optional(),
  idToken: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  password: z.lazy(() => SortOrderSchema).optional(),
  accessTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  refreshTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  scope: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AccountMinOrderByAggregateInputSchema: z.ZodType<Prisma.AccountMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  accountId: z.lazy(() => SortOrderSchema).optional(),
  providerId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  accessToken: z.lazy(() => SortOrderSchema).optional(),
  refreshToken: z.lazy(() => SortOrderSchema).optional(),
  idToken: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  password: z.lazy(() => SortOrderSchema).optional(),
  accessTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  refreshTokenExpiresAt: z.lazy(() => SortOrderSchema).optional(),
  scope: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VerificationCountOrderByAggregateInputSchema: z.ZodType<Prisma.VerificationCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  identifier: z.lazy(() => SortOrderSchema).optional(),
  value: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VerificationMaxOrderByAggregateInputSchema: z.ZodType<Prisma.VerificationMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  identifier: z.lazy(() => SortOrderSchema).optional(),
  value: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VerificationMinOrderByAggregateInputSchema: z.ZodType<Prisma.VerificationMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  identifier: z.lazy(() => SortOrderSchema).optional(),
  value: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PasskeyCountOrderByAggregateInputSchema: z.ZodType<Prisma.PasskeyCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  publicKey: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  credentialID: z.lazy(() => SortOrderSchema).optional(),
  counter: z.lazy(() => SortOrderSchema).optional(),
  deviceType: z.lazy(() => SortOrderSchema).optional(),
  backedUp: z.lazy(() => SortOrderSchema).optional(),
  transports: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PasskeyAvgOrderByAggregateInputSchema: z.ZodType<Prisma.PasskeyAvgOrderByAggregateInput> = z.object({
  counter: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PasskeyMaxOrderByAggregateInputSchema: z.ZodType<Prisma.PasskeyMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  publicKey: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  credentialID: z.lazy(() => SortOrderSchema).optional(),
  counter: z.lazy(() => SortOrderSchema).optional(),
  deviceType: z.lazy(() => SortOrderSchema).optional(),
  backedUp: z.lazy(() => SortOrderSchema).optional(),
  transports: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PasskeyMinOrderByAggregateInputSchema: z.ZodType<Prisma.PasskeyMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  publicKey: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  credentialID: z.lazy(() => SortOrderSchema).optional(),
  counter: z.lazy(() => SortOrderSchema).optional(),
  deviceType: z.lazy(() => SortOrderSchema).optional(),
  backedUp: z.lazy(() => SortOrderSchema).optional(),
  transports: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PasskeySumOrderByAggregateInputSchema: z.ZodType<Prisma.PasskeySumOrderByAggregateInput> = z.object({
  counter: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const TwoFactorCountOrderByAggregateInputSchema: z.ZodType<Prisma.TwoFactorCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  secret: z.lazy(() => SortOrderSchema).optional(),
  backupCodes: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const TwoFactorMaxOrderByAggregateInputSchema: z.ZodType<Prisma.TwoFactorMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  secret: z.lazy(() => SortOrderSchema).optional(),
  backupCodes: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const TwoFactorMinOrderByAggregateInputSchema: z.ZodType<Prisma.TwoFactorMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  secret: z.lazy(() => SortOrderSchema).optional(),
  backupCodes: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const IntNullableFilterSchema: z.ZodType<Prisma.IntNullableFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const CaptionStyleListRelationFilterSchema: z.ZodType<Prisma.CaptionStyleListRelationFilter> = z.object({
  every: z.lazy(() => CaptionStyleWhereInputSchema).optional(),
  some: z.lazy(() => CaptionStyleWhereInputSchema).optional(),
  none: z.lazy(() => CaptionStyleWhereInputSchema).optional()
}).strict();

export const OrganizationBrandingListRelationFilterSchema: z.ZodType<Prisma.OrganizationBrandingListRelationFilter> = z.object({
  every: z.lazy(() => OrganizationBrandingWhereInputSchema).optional(),
  some: z.lazy(() => OrganizationBrandingWhereInputSchema).optional(),
  none: z.lazy(() => OrganizationBrandingWhereInputSchema).optional()
}).strict();

export const OrganizationNarratorProfileListRelationFilterSchema: z.ZodType<Prisma.OrganizationNarratorProfileListRelationFilter> = z.object({
  every: z.lazy(() => OrganizationNarratorProfileWhereInputSchema).optional(),
  some: z.lazy(() => OrganizationNarratorProfileWhereInputSchema).optional(),
  none: z.lazy(() => OrganizationNarratorProfileWhereInputSchema).optional()
}).strict();

export const OrganizationSocialSettingsNullableScalarRelationFilterSchema: z.ZodType<Prisma.OrganizationSocialSettingsNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => OrganizationSocialSettingsWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => OrganizationSocialSettingsWhereInputSchema).optional().nullable()
}).strict();

export const OrganizationSocialPostListRelationFilterSchema: z.ZodType<Prisma.OrganizationSocialPostListRelationFilter> = z.object({
  every: z.lazy(() => OrganizationSocialPostWhereInputSchema).optional(),
  some: z.lazy(() => OrganizationSocialPostWhereInputSchema).optional(),
  none: z.lazy(() => OrganizationSocialPostWhereInputSchema).optional()
}).strict();

export const CaptionStyleOrderByRelationAggregateInputSchema: z.ZodType<Prisma.CaptionStyleOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationBrandingOrderByRelationAggregateInputSchema: z.ZodType<Prisma.OrganizationBrandingOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationNarratorProfileOrderByRelationAggregateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialPostOrderByRelationAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialPostOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationCountOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  slug: z.lazy(() => SortOrderSchema).optional(),
  logo: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationAvgOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationAvgOrderByAggregateInput> = z.object({
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationMaxOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  slug: z.lazy(() => SortOrderSchema).optional(),
  logo: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationMinOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  slug: z.lazy(() => SortOrderSchema).optional(),
  logo: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  paymentsCustomerId: z.lazy(() => SortOrderSchema).optional(),
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSumOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSumOrderByAggregateInput> = z.object({
  AutoArchiveDurationDays: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const IntNullableWithAggregatesFilterSchema: z.ZodType<Prisma.IntNullableWithAggregatesFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _sum: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const OrganizationScalarRelationFilterSchema: z.ZodType<Prisma.OrganizationScalarRelationFilter> = z.object({
  is: z.lazy(() => OrganizationWhereInputSchema).optional(),
  isNot: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const MemberOrganizationIdUserIdCompoundUniqueInputSchema: z.ZodType<Prisma.MemberOrganizationIdUserIdCompoundUniqueInput> = z.object({
  organizationId: z.string(),
  userId: z.string()
}).strict();

export const MemberCountOrderByAggregateInputSchema: z.ZodType<Prisma.MemberCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const MemberMaxOrderByAggregateInputSchema: z.ZodType<Prisma.MemberMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const MemberMinOrderByAggregateInputSchema: z.ZodType<Prisma.MemberMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const InvitationCountOrderByAggregateInputSchema: z.ZodType<Prisma.InvitationCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  inviterId: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const InvitationMaxOrderByAggregateInputSchema: z.ZodType<Prisma.InvitationMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  inviterId: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const InvitationMinOrderByAggregateInputSchema: z.ZodType<Prisma.InvitationMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  email: z.lazy(() => SortOrderSchema).optional(),
  role: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  expiresAt: z.lazy(() => SortOrderSchema).optional(),
  inviterId: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumPurchaseTypeFilterSchema: z.ZodType<Prisma.EnumPurchaseTypeFilter> = z.object({
  equals: z.lazy(() => PurchaseTypeSchema).optional(),
  in: z.lazy(() => PurchaseTypeSchema).array().optional(),
  notIn: z.lazy(() => PurchaseTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => NestedEnumPurchaseTypeFilterSchema) ]).optional(),
}).strict();

export const FloatNullableFilterSchema: z.ZodType<Prisma.FloatNullableFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const OrganizationNullableScalarRelationFilterSchema: z.ZodType<Prisma.OrganizationNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => OrganizationWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => OrganizationWhereInputSchema).optional().nullable()
}).strict();

export const UserNullableScalarRelationFilterSchema: z.ZodType<Prisma.UserNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => UserWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => UserWhereInputSchema).optional().nullable()
}).strict();

export const PurchaseCountOrderByAggregateInputSchema: z.ZodType<Prisma.PurchaseCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  customerId: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  subscriptionId: z.lazy(() => SortOrderSchema).optional(),
  credit: z.lazy(() => SortOrderSchema).optional(),
  productId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  startDate: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PurchaseAvgOrderByAggregateInputSchema: z.ZodType<Prisma.PurchaseAvgOrderByAggregateInput> = z.object({
  credit: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PurchaseMaxOrderByAggregateInputSchema: z.ZodType<Prisma.PurchaseMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  customerId: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  subscriptionId: z.lazy(() => SortOrderSchema).optional(),
  credit: z.lazy(() => SortOrderSchema).optional(),
  productId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  startDate: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PurchaseMinOrderByAggregateInputSchema: z.ZodType<Prisma.PurchaseMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  customerId: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  subscriptionId: z.lazy(() => SortOrderSchema).optional(),
  credit: z.lazy(() => SortOrderSchema).optional(),
  productId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  startDate: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PurchaseSumOrderByAggregateInputSchema: z.ZodType<Prisma.PurchaseSumOrderByAggregateInput> = z.object({
  credit: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumPurchaseTypeWithAggregatesFilterSchema: z.ZodType<Prisma.EnumPurchaseTypeWithAggregatesFilter> = z.object({
  equals: z.lazy(() => PurchaseTypeSchema).optional(),
  in: z.lazy(() => PurchaseTypeSchema).array().optional(),
  notIn: z.lazy(() => PurchaseTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => NestedEnumPurchaseTypeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumPurchaseTypeFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumPurchaseTypeFilterSchema).optional()
}).strict();

export const FloatNullableWithAggregatesFilterSchema: z.ZodType<Prisma.FloatNullableWithAggregatesFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _sum: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const JsonFilterSchema: z.ZodType<Prisma.JsonFilter> = z.object({
  equals: InputJsonValueSchema.optional(),
  not: InputJsonValueSchema.optional()
}).strict();

export const AiChatCountOrderByAggregateInputSchema: z.ZodType<Prisma.AiChatCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  messages: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AiChatMaxOrderByAggregateInputSchema: z.ZodType<Prisma.AiChatMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AiChatMinOrderByAggregateInputSchema: z.ZodType<Prisma.AiChatMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JsonWithAggregatesFilterSchema: z.ZodType<Prisma.JsonWithAggregatesFilter> = z.object({
  equals: InputJsonValueSchema.optional(),
  not: InputJsonValueSchema.optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedJsonFilterSchema).optional(),
  _max: z.lazy(() => NestedJsonFilterSchema).optional()
}).strict();

export const ApiKeyCountOrderByAggregateInputSchema: z.ZodType<Prisma.ApiKeyCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ApiKeyMaxOrderByAggregateInputSchema: z.ZodType<Prisma.ApiKeyMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ApiKeyMinOrderByAggregateInputSchema: z.ZodType<Prisma.ApiKeyMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const FloatFilterSchema: z.ZodType<Prisma.FloatFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatFilterSchema) ]).optional(),
}).strict();

export const CostBreakdownNullableCompositeFilterSchema: z.ZodType<Prisma.CostBreakdownNullableCompositeFilter> = z.object({
  equals: z.lazy(() => CostBreakdownObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => CostBreakdownWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => CostBreakdownWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const CostBreakdownObjectEqualityInputSchema: z.ZodType<Prisma.CostBreakdownObjectEqualityInput> = z.object({
  shortGen: z.number().optional().nullable(),
  youtubeProcessing: z.number().optional().nullable(),
  render: z.number().optional().nullable(),
  bRoll: z.number().optional().nullable()
}).strict();

export const EnumCreditUsageStatusFilterSchema: z.ZodType<Prisma.EnumCreditUsageStatusFilter> = z.object({
  equals: z.lazy(() => CreditUsageStatusSchema).optional(),
  in: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  notIn: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => NestedEnumCreditUsageStatusFilterSchema) ]).optional(),
}).strict();

export const EnumJobStatusNullableFilterSchema: z.ZodType<Prisma.EnumJobStatusNullableFilter> = z.object({
  equals: z.lazy(() => JobStatusSchema).optional().nullable(),
  in: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  notIn: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  not: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NestedEnumJobStatusNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const WebhookNullableCompositeFilterSchema: z.ZodType<Prisma.WebhookNullableCompositeFilter> = z.object({
  equals: z.lazy(() => WebhookObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => WebhookWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => WebhookWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const WebhookObjectEqualityInputSchema: z.ZodType<Prisma.WebhookObjectEqualityInput> = z.object({
  url: z.string(),
  status: z.number().optional().nullable(),
  retryCount: z.number().optional().nullable(),
  error: z.string().optional().nullable()
}).strict();

export const StringNullableListFilterSchema: z.ZodType<Prisma.StringNullableListFilter> = z.object({
  equals: z.string().array().optional().nullable(),
  has: z.string().optional().nullable(),
  hasEvery: z.string().array().optional(),
  hasSome: z.string().array().optional(),
  isEmpty: z.boolean().optional()
}).strict();

export const EnumJobFunctionNameFilterSchema: z.ZodType<Prisma.EnumJobFunctionNameFilter> = z.object({
  equals: z.lazy(() => JobFunctionNameSchema).optional(),
  in: z.lazy(() => JobFunctionNameSchema).array().optional(),
  notIn: z.lazy(() => JobFunctionNameSchema).array().optional(),
  not: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => NestedEnumJobFunctionNameFilterSchema) ]).optional(),
}).strict();

export const EnumJobSourceFilterSchema: z.ZodType<Prisma.EnumJobSourceFilter> = z.object({
  equals: z.lazy(() => JobSourceSchema).optional(),
  in: z.lazy(() => JobSourceSchema).array().optional(),
  notIn: z.lazy(() => JobSourceSchema).array().optional(),
  not: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => NestedEnumJobSourceFilterSchema) ]).optional(),
}).strict();

export const ApiKeyNullableScalarRelationFilterSchema: z.ZodType<Prisma.ApiKeyNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => ApiKeyWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => ApiKeyWhereInputSchema).optional().nullable()
}).strict();

export const PurchaseNullableScalarRelationFilterSchema: z.ZodType<Prisma.PurchaseNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => PurchaseWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => PurchaseWhereInputSchema).optional().nullable()
}).strict();

export const VideoNullableScalarRelationFilterSchema: z.ZodType<Prisma.VideoNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => VideoWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => VideoWhereInputSchema).optional().nullable()
}).strict();

export const VideoProjectNullableScalarRelationFilterSchema: z.ZodType<Prisma.VideoProjectNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => VideoProjectWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => VideoProjectWhereInputSchema).optional().nullable()
}).strict();

export const CostBreakdownOrderByInputSchema: z.ZodType<Prisma.CostBreakdownOrderByInput> = z.object({
  shortGen: z.lazy(() => SortOrderSchema).optional(),
  youtubeProcessing: z.lazy(() => SortOrderSchema).optional(),
  render: z.lazy(() => SortOrderSchema).optional(),
  bRoll: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const WebhookOrderByInputSchema: z.ZodType<Prisma.WebhookOrderByInput> = z.object({
  url: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  error: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JobCountOrderByAggregateInputSchema: z.ZodType<Prisma.JobCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  purchaseId: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  creditUsageStatus: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  functionName: z.lazy(() => SortOrderSchema).optional(),
  source: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  startTime: z.lazy(() => SortOrderSchema).optional(),
  endTime: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  archivedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JobAvgOrderByAggregateInputSchema: z.ZodType<Prisma.JobAvgOrderByAggregateInput> = z.object({
  cost: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JobMaxOrderByAggregateInputSchema: z.ZodType<Prisma.JobMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  purchaseId: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  creditUsageStatus: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  functionName: z.lazy(() => SortOrderSchema).optional(),
  source: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  startTime: z.lazy(() => SortOrderSchema).optional(),
  endTime: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  archivedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JobMinOrderByAggregateInputSchema: z.ZodType<Prisma.JobMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  purchaseId: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  creditUsageStatus: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  functionName: z.lazy(() => SortOrderSchema).optional(),
  source: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  startTime: z.lazy(() => SortOrderSchema).optional(),
  endTime: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional(),
  archivedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const JobSumOrderByAggregateInputSchema: z.ZodType<Prisma.JobSumOrderByAggregateInput> = z.object({
  cost: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const FloatWithAggregatesFilterSchema: z.ZodType<Prisma.FloatWithAggregatesFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatFilterSchema).optional(),
  _sum: z.lazy(() => NestedFloatFilterSchema).optional(),
  _min: z.lazy(() => NestedFloatFilterSchema).optional(),
  _max: z.lazy(() => NestedFloatFilterSchema).optional()
}).strict();

export const EnumCreditUsageStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumCreditUsageStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => CreditUsageStatusSchema).optional(),
  in: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  notIn: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => NestedEnumCreditUsageStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumCreditUsageStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumCreditUsageStatusFilterSchema).optional()
}).strict();

export const EnumJobStatusNullableWithAggregatesFilterSchema: z.ZodType<Prisma.EnumJobStatusNullableWithAggregatesFilter> = z.object({
  equals: z.lazy(() => JobStatusSchema).optional().nullable(),
  in: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  notIn: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  not: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NestedEnumJobStatusNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumJobStatusNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumJobStatusNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const EnumJobFunctionNameWithAggregatesFilterSchema: z.ZodType<Prisma.EnumJobFunctionNameWithAggregatesFilter> = z.object({
  equals: z.lazy(() => JobFunctionNameSchema).optional(),
  in: z.lazy(() => JobFunctionNameSchema).array().optional(),
  notIn: z.lazy(() => JobFunctionNameSchema).array().optional(),
  not: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => NestedEnumJobFunctionNameWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumJobFunctionNameFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumJobFunctionNameFilterSchema).optional()
}).strict();

export const EnumJobSourceWithAggregatesFilterSchema: z.ZodType<Prisma.EnumJobSourceWithAggregatesFilter> = z.object({
  equals: z.lazy(() => JobSourceSchema).optional(),
  in: z.lazy(() => JobSourceSchema).array().optional(),
  notIn: z.lazy(() => JobSourceSchema).array().optional(),
  not: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => NestedEnumJobSourceWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumJobSourceFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumJobSourceFilterSchema).optional()
}).strict();

export const SourceFilesNullableCompositeFilterSchema: z.ZodType<Prisma.SourceFilesNullableCompositeFilter> = z.object({
  equals: z.lazy(() => SourceFilesObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => SourceFilesWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => SourceFilesWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const SourceFilesObjectEqualityInputSchema: z.ZodType<Prisma.SourceFilesObjectEqualityInput> = z.object({
  isInS3: z.boolean().optional().nullable(),
  target: z.string().optional().nullable(),
  targetFPS: z.number().optional().nullable(),
  preview: z.string().optional().nullable(),
  previewFPS: z.number().optional().nullable()
}).strict();

export const VideoShortsOptionsNullableCompositeFilterSchema: z.ZodType<Prisma.VideoShortsOptionsNullableCompositeFilter> = z.object({
  equals: z.lazy(() => VideoShortsOptionsObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => VideoShortsOptionsWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => VideoShortsOptionsWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const VideoShortsOptionsObjectEqualityInputSchema: z.ZodType<Prisma.VideoShortsOptionsObjectEqualityInput> = z.object({
  predefinedShorts: z.lazy(() => PredefinedShortsObjectEqualityInputSchema).array().optional(),
  isRTL: z.boolean().optional().nullable()
}).strict();

export const ITranscriptionNullableCompositeFilterSchema: z.ZodType<Prisma.ITranscriptionNullableCompositeFilter> = z.object({
  equals: z.lazy(() => ITranscriptionObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => ITranscriptionWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => ITranscriptionWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const ITranscriptionObjectEqualityInputSchema: z.ZodType<Prisma.ITranscriptionObjectEqualityInput> = z.object({
  prediction: z.lazy(() => PredictionObjectEqualityInputSchema).array().optional()
}).strict();

export const ShortCompositeListFilterSchema: z.ZodType<Prisma.ShortCompositeListFilter> = z.object({
  equals: z.lazy(() => ShortObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => ShortWhereInputSchema).optional(),
  some: z.lazy(() => ShortWhereInputSchema).optional(),
  none: z.lazy(() => ShortWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const ShortObjectEqualityInputSchema: z.ZodType<Prisma.ShortObjectEqualityInput> = z.object({
  id: z.number(),
  title: z.string(),
  reason: z.string(),
  startId: z.number(),
  endId: z.number(),
  startTime: z.number(),
  endTime: z.number(),
  status: z.lazy(() => ShortStatusSchema),
  text: z.string(),
  cropFileUrl: z.string().optional().nullable(),
  cropFilekey: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  score: z.lazy(() => ShortScoreObjectEqualityInputSchema).optional().nullable(),
  appFileId: z.string().optional().nullable(),
  asd: z.lazy(() => AsdRootObjectEqualityInputSchema).optional().nullable(),
  bRolls: z.lazy(() => BRollItemObjectEqualityInputSchema).array().optional()
}).strict();

export const EnumVideoStatusFilterSchema: z.ZodType<Prisma.EnumVideoStatusFilter> = z.object({
  equals: z.lazy(() => VideoStatusSchema).optional(),
  in: z.lazy(() => VideoStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => NestedEnumVideoStatusFilterSchema) ]).optional(),
}).strict();

export const StatusHistoryCompositeListFilterSchema: z.ZodType<Prisma.StatusHistoryCompositeListFilter> = z.object({
  equals: z.lazy(() => StatusHistoryObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => StatusHistoryWhereInputSchema).optional(),
  some: z.lazy(() => StatusHistoryWhereInputSchema).optional(),
  none: z.lazy(() => StatusHistoryWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const StatusHistoryObjectEqualityInputSchema: z.ZodType<Prisma.StatusHistoryObjectEqualityInput> = z.object({
  status: z.string(),
  startTime: z.string(),
  endTime: z.string().optional().nullable()
}).strict();

export const JobScalarRelationFilterSchema: z.ZodType<Prisma.JobScalarRelationFilter> = z.object({
  is: z.lazy(() => JobWhereInputSchema).optional(),
  isNot: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const SourceFilesOrderByInputSchema: z.ZodType<Prisma.SourceFilesOrderByInput> = z.object({
  isInS3: z.lazy(() => SortOrderSchema).optional(),
  target: z.lazy(() => SortOrderSchema).optional(),
  targetFPS: z.lazy(() => SortOrderSchema).optional(),
  preview: z.lazy(() => SortOrderSchema).optional(),
  previewFPS: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoShortsOptionsOrderByInputSchema: z.ZodType<Prisma.VideoShortsOptionsOrderByInput> = z.object({
  predefinedShorts: z.lazy(() => PredefinedShortsOrderByCompositeAggregateInputSchema).optional(),
  isRTL: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ITranscriptionOrderByInputSchema: z.ZodType<Prisma.ITranscriptionOrderByInput> = z.object({
  prediction: z.lazy(() => PredictionOrderByCompositeAggregateInputSchema).optional()
}).strict();

export const ShortOrderByCompositeAggregateInputSchema: z.ZodType<Prisma.ShortOrderByCompositeAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const StatusHistoryOrderByCompositeAggregateInputSchema: z.ZodType<Prisma.StatusHistoryOrderByCompositeAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoCountOrderByAggregateInputSchema: z.ZodType<Prisma.VideoCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  sourceId: z.lazy(() => SortOrderSchema).optional(),
  videoLength: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  choosedDefinition: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoAvgOrderByAggregateInputSchema: z.ZodType<Prisma.VideoAvgOrderByAggregateInput> = z.object({
  videoLength: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoMaxOrderByAggregateInputSchema: z.ZodType<Prisma.VideoMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  sourceId: z.lazy(() => SortOrderSchema).optional(),
  videoLength: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  choosedDefinition: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoMinOrderByAggregateInputSchema: z.ZodType<Prisma.VideoMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  sourceId: z.lazy(() => SortOrderSchema).optional(),
  videoLength: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  choosedDefinition: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoSumOrderByAggregateInputSchema: z.ZodType<Prisma.VideoSumOrderByAggregateInput> = z.object({
  videoLength: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumVideoStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumVideoStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => VideoStatusSchema).optional(),
  in: z.lazy(() => VideoStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => NestedEnumVideoStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumVideoStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumVideoStatusFilterSchema).optional()
}).strict();

export const EnumVideoProjectStatusFilterSchema: z.ZodType<Prisma.EnumVideoProjectStatusFilter> = z.object({
  equals: z.lazy(() => VideoProjectStatusSchema).optional(),
  in: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => NestedEnumVideoProjectStatusFilterSchema) ]).optional(),
}).strict();

export const JsonNullableFilterSchema: z.ZodType<Prisma.JsonNullableFilter> = z.object({
  equals: InputJsonValueSchema.optional().nullable(),
  not: InputJsonValueSchema.optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const VideoDesignNullableScalarRelationFilterSchema: z.ZodType<Prisma.VideoDesignNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => VideoDesignWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => VideoDesignWhereInputSchema).optional().nullable()
}).strict();

export const VideoProjectCountOrderByAggregateInputSchema: z.ZodType<Prisma.VideoProjectCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  createdByUserId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  inputBrief: z.lazy(() => SortOrderSchema).optional(),
  document: z.lazy(() => SortOrderSchema).optional(),
  validation: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  publishTag: z.lazy(() => SortOrderSchema).optional(),
  episodeSummary: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoProjectAvgOrderByAggregateInputSchema: z.ZodType<Prisma.VideoProjectAvgOrderByAggregateInput> = z.object({
  cost: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoProjectMaxOrderByAggregateInputSchema: z.ZodType<Prisma.VideoProjectMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  createdByUserId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  episodeSummary: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoProjectMinOrderByAggregateInputSchema: z.ZodType<Prisma.VideoProjectMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  createdByUserId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  episodeSummary: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional(),
  isDeleted: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoProjectSumOrderByAggregateInputSchema: z.ZodType<Prisma.VideoProjectSumOrderByAggregateInput> = z.object({
  cost: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumVideoProjectStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumVideoProjectStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => VideoProjectStatusSchema).optional(),
  in: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => NestedEnumVideoProjectStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumVideoProjectStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumVideoProjectStatusFilterSchema).optional()
}).strict();

export const JsonNullableWithAggregatesFilterSchema: z.ZodType<Prisma.JsonNullableWithAggregatesFilter> = z.object({
  equals: InputJsonValueSchema.optional().nullable(),
  not: InputJsonValueSchema.optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedJsonNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedJsonNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const EnumVideoDesignStatusFilterSchema: z.ZodType<Prisma.EnumVideoDesignStatusFilter> = z.object({
  equals: z.lazy(() => VideoDesignStatusSchema).optional(),
  in: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => NestedEnumVideoDesignStatusFilterSchema) ]).optional(),
}).strict();

export const VideoProjectScalarRelationFilterSchema: z.ZodType<Prisma.VideoProjectScalarRelationFilter> = z.object({
  is: z.lazy(() => VideoProjectWhereInputSchema).optional(),
  isNot: z.lazy(() => VideoProjectWhereInputSchema).optional()
}).strict();

export const VideoDesignCountOrderByAggregateInputSchema: z.ZodType<Prisma.VideoDesignCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  videoProjectId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  design: z.lazy(() => SortOrderSchema).optional(),
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional(),
  latestRenderHistoryId: z.lazy(() => SortOrderSchema).optional(),
  latestMp4Url: z.lazy(() => SortOrderSchema).optional(),
  compiledAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoDesignAvgOrderByAggregateInputSchema: z.ZodType<Prisma.VideoDesignAvgOrderByAggregateInput> = z.object({
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoDesignMaxOrderByAggregateInputSchema: z.ZodType<Prisma.VideoDesignMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  videoProjectId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional(),
  latestRenderHistoryId: z.lazy(() => SortOrderSchema).optional(),
  latestMp4Url: z.lazy(() => SortOrderSchema).optional(),
  compiledAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoDesignMinOrderByAggregateInputSchema: z.ZodType<Prisma.VideoDesignMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  videoProjectId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional(),
  latestRenderHistoryId: z.lazy(() => SortOrderSchema).optional(),
  latestMp4Url: z.lazy(() => SortOrderSchema).optional(),
  compiledAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const VideoDesignSumOrderByAggregateInputSchema: z.ZodType<Prisma.VideoDesignSumOrderByAggregateInput> = z.object({
  fps: z.lazy(() => SortOrderSchema).optional(),
  durationMs: z.lazy(() => SortOrderSchema).optional(),
  width: z.lazy(() => SortOrderSchema).optional(),
  height: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumVideoDesignStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumVideoDesignStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => VideoDesignStatusSchema).optional(),
  in: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => NestedEnumVideoDesignStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumVideoDesignStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumVideoDesignStatusFilterSchema).optional()
}).strict();

export const EnumAppFileTypeFilterSchema: z.ZodType<Prisma.EnumAppFileTypeFilter> = z.object({
  equals: z.lazy(() => AppFileTypeSchema).optional(),
  in: z.lazy(() => AppFileTypeSchema).array().optional(),
  notIn: z.lazy(() => AppFileTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => NestedEnumAppFileTypeFilterSchema) ]).optional(),
}).strict();

export const EnumAppFileStatusFilterSchema: z.ZodType<Prisma.EnumAppFileStatusFilter> = z.object({
  equals: z.lazy(() => AppFileStatusSchema).optional(),
  in: z.lazy(() => AppFileStatusSchema).array().optional(),
  notIn: z.lazy(() => AppFileStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => NestedEnumAppFileStatusFilterSchema) ]).optional(),
}).strict();

export const JobNullableScalarRelationFilterSchema: z.ZodType<Prisma.JobNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => JobWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => JobWhereInputSchema).optional().nullable()
}).strict();

export const AppFileCountOrderByAggregateInputSchema: z.ZodType<Prisma.AppFileCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  tags: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  thumbnailUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AppFileAvgOrderByAggregateInputSchema: z.ZodType<Prisma.AppFileAvgOrderByAggregateInput> = z.object({
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AppFileMaxOrderByAggregateInputSchema: z.ZodType<Prisma.AppFileMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  thumbnailUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AppFileMinOrderByAggregateInputSchema: z.ZodType<Prisma.AppFileMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  thumbnailUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const AppFileSumOrderByAggregateInputSchema: z.ZodType<Prisma.AppFileSumOrderByAggregateInput> = z.object({
  size: z.lazy(() => SortOrderSchema).optional(),
  length: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumAppFileTypeWithAggregatesFilterSchema: z.ZodType<Prisma.EnumAppFileTypeWithAggregatesFilter> = z.object({
  equals: z.lazy(() => AppFileTypeSchema).optional(),
  in: z.lazy(() => AppFileTypeSchema).array().optional(),
  notIn: z.lazy(() => AppFileTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => NestedEnumAppFileTypeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumAppFileTypeFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumAppFileTypeFilterSchema).optional()
}).strict();

export const EnumAppFileStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumAppFileStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => AppFileStatusSchema).optional(),
  in: z.lazy(() => AppFileStatusSchema).array().optional(),
  notIn: z.lazy(() => AppFileStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => NestedEnumAppFileStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumAppFileStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumAppFileStatusFilterSchema).optional()
}).strict();

export const CaptionStyleCountOrderByAggregateInputSchema: z.ZodType<Prisma.CaptionStyleCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  style: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const CaptionStyleMaxOrderByAggregateInputSchema: z.ZodType<Prisma.CaptionStyleMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const CaptionStyleMinOrderByAggregateInputSchema: z.ZodType<Prisma.CaptionStyleMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationBrandingCountOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationBrandingCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  preset: z.lazy(() => SortOrderSchema).optional(),
  size: z.lazy(() => SortOrderSchema).optional(),
  options: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationBrandingMaxOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationBrandingMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  preset: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationBrandingMinOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationBrandingMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  preset: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationNarratorProfileCountOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  narratorName: z.lazy(() => SortOrderSchema).optional(),
  gender: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  voiceId: z.lazy(() => SortOrderSchema).optional(),
  personality: z.lazy(() => SortOrderSchema).optional(),
  styleNotes: z.lazy(() => SortOrderSchema).optional(),
  ttsStyle: z.lazy(() => SortOrderSchema).optional(),
  ttsPace: z.lazy(() => SortOrderSchema).optional(),
  ttsAccent: z.lazy(() => SortOrderSchema).optional(),
  ttsNotes: z.lazy(() => SortOrderSchema).optional(),
  channelName: z.lazy(() => SortOrderSchema).optional(),
  channelSignOff: z.lazy(() => SortOrderSchema).optional(),
  overlayText: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  visualDefaults: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarFileAppId: z.lazy(() => SortOrderSchema).optional(),
  avatarPhotos: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationNarratorProfileAvgOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileAvgOrderByAggregateInput> = z.object({
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationNarratorProfileMaxOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  narratorName: z.lazy(() => SortOrderSchema).optional(),
  gender: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  voiceId: z.lazy(() => SortOrderSchema).optional(),
  personality: z.lazy(() => SortOrderSchema).optional(),
  styleNotes: z.lazy(() => SortOrderSchema).optional(),
  ttsStyle: z.lazy(() => SortOrderSchema).optional(),
  ttsPace: z.lazy(() => SortOrderSchema).optional(),
  ttsAccent: z.lazy(() => SortOrderSchema).optional(),
  ttsNotes: z.lazy(() => SortOrderSchema).optional(),
  channelName: z.lazy(() => SortOrderSchema).optional(),
  channelSignOff: z.lazy(() => SortOrderSchema).optional(),
  overlayText: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarFileAppId: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationNarratorProfileMinOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  description: z.lazy(() => SortOrderSchema).optional(),
  narratorName: z.lazy(() => SortOrderSchema).optional(),
  gender: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  voiceId: z.lazy(() => SortOrderSchema).optional(),
  personality: z.lazy(() => SortOrderSchema).optional(),
  styleNotes: z.lazy(() => SortOrderSchema).optional(),
  ttsStyle: z.lazy(() => SortOrderSchema).optional(),
  ttsPace: z.lazy(() => SortOrderSchema).optional(),
  ttsAccent: z.lazy(() => SortOrderSchema).optional(),
  ttsNotes: z.lazy(() => SortOrderSchema).optional(),
  channelName: z.lazy(() => SortOrderSchema).optional(),
  channelSignOff: z.lazy(() => SortOrderSchema).optional(),
  overlayText: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarFileAppId: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  isDefault: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationNarratorProfileSumOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileSumOrderByAggregateInput> = z.object({
  overlayTextDelayMs: z.lazy(() => SortOrderSchema).optional(),
  overlayTextDurationMs: z.lazy(() => SortOrderSchema).optional(),
  speechSpeed: z.lazy(() => SortOrderSchema).optional(),
  avatarMinDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarMaxDurationMs: z.lazy(() => SortOrderSchema).optional(),
  avatarAppearEveryMs: z.lazy(() => SortOrderSchema).optional(),
  avatarIntroHoldMs: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumOrganizationSocialProviderFilterSchema: z.ZodType<Prisma.EnumOrganizationSocialProviderFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  in: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => NestedEnumOrganizationSocialProviderFilterSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsCountOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  providerProfileId: z.lazy(() => SortOrderSchema).optional(),
  isEnabled: z.lazy(() => SortOrderSchema).optional(),
  approveApiPosts: z.lazy(() => SortOrderSchema).optional(),
  defaultTimezone: z.lazy(() => SortOrderSchema).optional(),
  defaultPlatforms: z.lazy(() => SortOrderSchema).optional(),
  queueSlots: z.lazy(() => SortOrderSchema).optional(),
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional(),
  processOnlyPlaylistId: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialSettingsAvgOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsAvgOrderByAggregateInput> = z.object({
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialSettingsMaxOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  providerProfileId: z.lazy(() => SortOrderSchema).optional(),
  isEnabled: z.lazy(() => SortOrderSchema).optional(),
  approveApiPosts: z.lazy(() => SortOrderSchema).optional(),
  defaultTimezone: z.lazy(() => SortOrderSchema).optional(),
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional(),
  processOnlyPlaylistId: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialSettingsMinOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  providerProfileId: z.lazy(() => SortOrderSchema).optional(),
  isEnabled: z.lazy(() => SortOrderSchema).optional(),
  approveApiPosts: z.lazy(() => SortOrderSchema).optional(),
  defaultTimezone: z.lazy(() => SortOrderSchema).optional(),
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional(),
  processOnlyPlaylistId: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialSettingsSumOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsSumOrderByAggregateInput> = z.object({
  maxPostsPerSlot: z.lazy(() => SortOrderSchema).optional(),
  maxShortsOfSameVideo: z.lazy(() => SortOrderSchema).optional(),
  maxShortsPerWeek: z.lazy(() => SortOrderSchema).optional(),
  minimumVideoDurationSeconds: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumOrganizationSocialProviderWithAggregatesFilterSchema: z.ZodType<Prisma.EnumOrganizationSocialProviderWithAggregatesFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  in: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => NestedEnumOrganizationSocialProviderWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumOrganizationSocialProviderFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumOrganizationSocialProviderFilterSchema).optional()
}).strict();

export const EnumOrganizationSocialPostStatusFilterSchema: z.ZodType<Prisma.EnumOrganizationSocialPostStatusFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  in: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => NestedEnumOrganizationSocialPostStatusFilterSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostCountOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialPostCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  providerJobId: z.lazy(() => SortOrderSchema).optional(),
  platforms: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  caption: z.lazy(() => SortOrderSchema).optional(),
  videoUrl: z.lazy(() => SortOrderSchema).optional(),
  scheduledFor: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  lastError: z.lazy(() => SortOrderSchema).optional(),
  metadata: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialPostMaxOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialPostMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  providerJobId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  caption: z.lazy(() => SortOrderSchema).optional(),
  videoUrl: z.lazy(() => SortOrderSchema).optional(),
  scheduledFor: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  lastError: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const OrganizationSocialPostMinOrderByAggregateInputSchema: z.ZodType<Prisma.OrganizationSocialPostMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  provider: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  providerJobId: z.lazy(() => SortOrderSchema).optional(),
  title: z.lazy(() => SortOrderSchema).optional(),
  caption: z.lazy(() => SortOrderSchema).optional(),
  videoUrl: z.lazy(() => SortOrderSchema).optional(),
  scheduledFor: z.lazy(() => SortOrderSchema).optional(),
  publishedAt: z.lazy(() => SortOrderSchema).optional(),
  lastError: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumOrganizationSocialPostStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumOrganizationSocialPostStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  in: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => NestedEnumOrganizationSocialPostStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumOrganizationSocialPostStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumOrganizationSocialPostStatusFilterSchema).optional()
}).strict();

export const EnumRenderHistoryStatusFilterSchema: z.ZodType<Prisma.EnumRenderHistoryStatusFilter> = z.object({
  equals: z.lazy(() => RenderHistoryStatusSchema).optional(),
  in: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  notIn: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => NestedEnumRenderHistoryStatusFilterSchema) ]).optional(),
}).strict();

export const AppFileNullableScalarRelationFilterSchema: z.ZodType<Prisma.AppFileNullableScalarRelationFilter> = z.object({
  is: z.lazy(() => AppFileWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AppFileWhereInputSchema).optional().nullable()
}).strict();

export const RenderHistoryCountOrderByAggregateInputSchema: z.ZodType<Prisma.RenderHistoryCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  renderId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  appFileId: z.lazy(() => SortOrderSchema).optional(),
  target: z.lazy(() => SortOrderSchema).optional(),
  data: z.lazy(() => SortOrderSchema).optional(),
  fileUrl: z.lazy(() => SortOrderSchema).optional(),
  fileSize: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional(),
  webhookReceived: z.lazy(() => SortOrderSchema).optional(),
  webhookReceivedAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const RenderHistoryAvgOrderByAggregateInputSchema: z.ZodType<Prisma.RenderHistoryAvgOrderByAggregateInput> = z.object({
  target: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const RenderHistoryMaxOrderByAggregateInputSchema: z.ZodType<Prisma.RenderHistoryMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  renderId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  appFileId: z.lazy(() => SortOrderSchema).optional(),
  target: z.lazy(() => SortOrderSchema).optional(),
  fileUrl: z.lazy(() => SortOrderSchema).optional(),
  fileSize: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional(),
  webhookReceived: z.lazy(() => SortOrderSchema).optional(),
  webhookReceivedAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const RenderHistoryMinOrderByAggregateInputSchema: z.ZodType<Prisma.RenderHistoryMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  renderId: z.lazy(() => SortOrderSchema).optional(),
  organizationId: z.lazy(() => SortOrderSchema).optional(),
  userId: z.lazy(() => SortOrderSchema).optional(),
  keyId: z.lazy(() => SortOrderSchema).optional(),
  jobId: z.lazy(() => SortOrderSchema).optional(),
  appFileId: z.lazy(() => SortOrderSchema).optional(),
  target: z.lazy(() => SortOrderSchema).optional(),
  fileUrl: z.lazy(() => SortOrderSchema).optional(),
  fileSize: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  errorMessage: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional(),
  webhookReceived: z.lazy(() => SortOrderSchema).optional(),
  webhookReceivedAt: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const RenderHistorySumOrderByAggregateInputSchema: z.ZodType<Prisma.RenderHistorySumOrderByAggregateInput> = z.object({
  target: z.lazy(() => SortOrderSchema).optional(),
  cost: z.lazy(() => SortOrderSchema).optional(),
  priority: z.lazy(() => SortOrderSchema).optional(),
  retryCount: z.lazy(() => SortOrderSchema).optional(),
  maxRetries: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumRenderHistoryStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumRenderHistoryStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => RenderHistoryStatusSchema).optional(),
  in: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  notIn: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => NestedEnumRenderHistoryStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumRenderHistoryStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumRenderHistoryStatusFilterSchema).optional()
}).strict();

export const EnumExternalKeysStatusFilterSchema: z.ZodType<Prisma.EnumExternalKeysStatusFilter> = z.object({
  equals: z.lazy(() => ExternalKeysStatusSchema).optional(),
  in: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => NestedEnumExternalKeysStatusFilterSchema) ]).optional(),
}).strict();

export const EnumExternalKeysTypeFilterSchema: z.ZodType<Prisma.EnumExternalKeysTypeFilter> = z.object({
  equals: z.lazy(() => ExternalKeysTypeSchema).optional(),
  in: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => NestedEnumExternalKeysTypeFilterSchema) ]).optional(),
}).strict();

export const ExternalKeysCountOrderByAggregateInputSchema: z.ZodType<Prisma.ExternalKeysCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  errors: z.lazy(() => SortOrderSchema).optional(),
  currency: z.lazy(() => SortOrderSchema).optional(),
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  lastResetDate: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  lastUsedDate: z.lazy(() => SortOrderSchema).optional(),
  cooldownUntil: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ExternalKeysAvgOrderByAggregateInputSchema: z.ZodType<Prisma.ExternalKeysAvgOrderByAggregateInput> = z.object({
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ExternalKeysMaxOrderByAggregateInputSchema: z.ZodType<Prisma.ExternalKeysMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  currency: z.lazy(() => SortOrderSchema).optional(),
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  lastResetDate: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  lastUsedDate: z.lazy(() => SortOrderSchema).optional(),
  cooldownUntil: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ExternalKeysMinOrderByAggregateInputSchema: z.ZodType<Prisma.ExternalKeysMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  key: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  status: z.lazy(() => SortOrderSchema).optional(),
  type: z.lazy(() => SortOrderSchema).optional(),
  currency: z.lazy(() => SortOrderSchema).optional(),
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  lastResetDate: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  lastUsedDate: z.lazy(() => SortOrderSchema).optional(),
  cooldownUntil: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ExternalKeysSumOrderByAggregateInputSchema: z.ZodType<Prisma.ExternalKeysSumOrderByAggregateInput> = z.object({
  usage: z.lazy(() => SortOrderSchema).optional(),
  usageCount: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsageCount: z.lazy(() => SortOrderSchema).optional(),
  monthlyUsage: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const EnumExternalKeysStatusWithAggregatesFilterSchema: z.ZodType<Prisma.EnumExternalKeysStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => ExternalKeysStatusSchema).optional(),
  in: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => NestedEnumExternalKeysStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumExternalKeysStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumExternalKeysStatusFilterSchema).optional()
}).strict();

export const EnumExternalKeysTypeWithAggregatesFilterSchema: z.ZodType<Prisma.EnumExternalKeysTypeWithAggregatesFilter> = z.object({
  equals: z.lazy(() => ExternalKeysTypeSchema).optional(),
  in: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => NestedEnumExternalKeysTypeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumExternalKeysTypeFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumExternalKeysTypeFilterSchema).optional()
}).strict();

export const CookieIdentityListRelationFilterSchema: z.ZodType<Prisma.CookieIdentityListRelationFilter> = z.object({
  every: z.lazy(() => CookieIdentityWhereInputSchema).optional(),
  some: z.lazy(() => CookieIdentityWhereInputSchema).optional(),
  none: z.lazy(() => CookieIdentityWhereInputSchema).optional()
}).strict();

export const CookieIdentityOrderByRelationAggregateInputSchema: z.ZodType<Prisma.CookieIdentityOrderByRelationAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ProxyCountOrderByAggregateInputSchema: z.ZodType<Prisma.ProxyCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  proxyUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ProxyMaxOrderByAggregateInputSchema: z.ZodType<Prisma.ProxyMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  proxyUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ProxyMinOrderByAggregateInputSchema: z.ZodType<Prisma.ProxyMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  proxyUrl: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const ProxyScalarRelationFilterSchema: z.ZodType<Prisma.ProxyScalarRelationFilter> = z.object({
  is: z.lazy(() => ProxyWhereInputSchema).optional(),
  isNot: z.lazy(() => ProxyWhereInputSchema).optional()
}).strict();

export const CookieIdentityCountOrderByAggregateInputSchema: z.ZodType<Prisma.CookieIdentityCountOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  lastRequestTime: z.lazy(() => SortOrderSchema).optional(),
  proxyId: z.lazy(() => SortOrderSchema).optional(),
  cookieContent: z.lazy(() => SortOrderSchema).optional(),
  useCount: z.lazy(() => SortOrderSchema).optional(),
  errors: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const CookieIdentityAvgOrderByAggregateInputSchema: z.ZodType<Prisma.CookieIdentityAvgOrderByAggregateInput> = z.object({
  useCount: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const CookieIdentityMaxOrderByAggregateInputSchema: z.ZodType<Prisma.CookieIdentityMaxOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  lastRequestTime: z.lazy(() => SortOrderSchema).optional(),
  proxyId: z.lazy(() => SortOrderSchema).optional(),
  cookieContent: z.lazy(() => SortOrderSchema).optional(),
  useCount: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const CookieIdentityMinOrderByAggregateInputSchema: z.ZodType<Prisma.CookieIdentityMinOrderByAggregateInput> = z.object({
  id: z.lazy(() => SortOrderSchema).optional(),
  name: z.lazy(() => SortOrderSchema).optional(),
  isActive: z.lazy(() => SortOrderSchema).optional(),
  lastRequestTime: z.lazy(() => SortOrderSchema).optional(),
  proxyId: z.lazy(() => SortOrderSchema).optional(),
  cookieContent: z.lazy(() => SortOrderSchema).optional(),
  useCount: z.lazy(() => SortOrderSchema).optional(),
  createdAt: z.lazy(() => SortOrderSchema).optional(),
  updatedAt: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const CookieIdentitySumOrderByAggregateInputSchema: z.ZodType<Prisma.CookieIdentitySumOrderByAggregateInput> = z.object({
  useCount: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const SessionCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.SessionCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => SessionCreateWithoutUserInputSchema),z.lazy(() => SessionCreateWithoutUserInputSchema).array(),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema),z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => SessionCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AccountCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.AccountCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => AccountCreateWithoutUserInputSchema),z.lazy(() => AccountCreateWithoutUserInputSchema).array(),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema),z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AccountCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutUserInputSchema),z.lazy(() => ApiKeyCreateWithoutUserInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const PasskeyCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.PasskeyCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => PasskeyCreateWithoutUserInputSchema),z.lazy(() => PasskeyCreateWithoutUserInputSchema).array(),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PasskeyCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const InvitationCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.InvitationCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutUserInputSchema),z.lazy(() => InvitationCreateWithoutUserInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const PurchaseCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.PurchaseCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutUserInputSchema),z.lazy(() => PurchaseCreateWithoutUserInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const MemberCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.MemberCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutUserInputSchema),z.lazy(() => MemberCreateWithoutUserInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema),z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const TwoFactorCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => TwoFactorCreateWithoutUserInputSchema),z.lazy(() => TwoFactorCreateWithoutUserInputSchema).array(),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema),z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => TwoFactorCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AiChatCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.AiChatCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutUserInputSchema),z.lazy(() => AiChatCreateWithoutUserInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AppFileCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.AppFileCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutUserInputSchema),z.lazy(() => AppFileCreateWithoutUserInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateWithoutUserInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const JobCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.JobCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutUserInputSchema),z.lazy(() => JobCreateWithoutUserInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutUserInputSchema),z.lazy(() => JobCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectCreateNestedManyWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectCreateNestedManyWithoutCreatedByInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyCreatedByInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const SessionUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.SessionUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => SessionCreateWithoutUserInputSchema),z.lazy(() => SessionCreateWithoutUserInputSchema).array(),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema),z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => SessionCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AccountUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.AccountUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => AccountCreateWithoutUserInputSchema),z.lazy(() => AccountCreateWithoutUserInputSchema).array(),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema),z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AccountCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutUserInputSchema),z.lazy(() => ApiKeyCreateWithoutUserInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const PasskeyUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => PasskeyCreateWithoutUserInputSchema),z.lazy(() => PasskeyCreateWithoutUserInputSchema).array(),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PasskeyCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const InvitationUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.InvitationUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutUserInputSchema),z.lazy(() => InvitationCreateWithoutUserInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const PurchaseUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutUserInputSchema),z.lazy(() => PurchaseCreateWithoutUserInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const MemberUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.MemberUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutUserInputSchema),z.lazy(() => MemberCreateWithoutUserInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema),z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => TwoFactorCreateWithoutUserInputSchema),z.lazy(() => TwoFactorCreateWithoutUserInputSchema).array(),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema),z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => TwoFactorCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AiChatUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.AiChatUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutUserInputSchema),z.lazy(() => AiChatCreateWithoutUserInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AppFileUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutUserInputSchema),z.lazy(() => AppFileCreateWithoutUserInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateWithoutUserInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedCreateNestedManyWithoutUserInputSchema: z.ZodType<Prisma.JobUncheckedCreateNestedManyWithoutUserInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutUserInputSchema),z.lazy(() => JobCreateWithoutUserInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutUserInputSchema),z.lazy(() => JobCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyUserInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateNestedManyWithoutCreatedByInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyCreatedByInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const StringFieldUpdateOperationsInputSchema: z.ZodType<Prisma.StringFieldUpdateOperationsInput> = z.object({
  set: z.string().optional()
}).strict();

export const BoolFieldUpdateOperationsInputSchema: z.ZodType<Prisma.BoolFieldUpdateOperationsInput> = z.object({
  set: z.boolean().optional()
}).strict();

export const NullableStringFieldUpdateOperationsInputSchema: z.ZodType<Prisma.NullableStringFieldUpdateOperationsInput> = z.object({
  set: z.string().optional().nullable(),
  unset: z.boolean().optional()
}).strict();

export const DateTimeFieldUpdateOperationsInputSchema: z.ZodType<Prisma.DateTimeFieldUpdateOperationsInput> = z.object({
  set: z.coerce.date().optional()
}).strict();

export const NullableBoolFieldUpdateOperationsInputSchema: z.ZodType<Prisma.NullableBoolFieldUpdateOperationsInput> = z.object({
  set: z.boolean().optional().nullable(),
  unset: z.boolean().optional()
}).strict();

export const NullableDateTimeFieldUpdateOperationsInputSchema: z.ZodType<Prisma.NullableDateTimeFieldUpdateOperationsInput> = z.object({
  set: z.coerce.date().optional().nullable(),
  unset: z.boolean().optional()
}).strict();

export const IntFieldUpdateOperationsInputSchema: z.ZodType<Prisma.IntFieldUpdateOperationsInput> = z.object({
  set: z.number().optional(),
  increment: z.number().optional(),
  decrement: z.number().optional(),
  multiply: z.number().optional(),
  divide: z.number().optional()
}).strict();

export const SessionUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.SessionUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => SessionCreateWithoutUserInputSchema),z.lazy(() => SessionCreateWithoutUserInputSchema).array(),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema),z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => SessionUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => SessionUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => SessionCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => SessionUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => SessionUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => SessionUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => SessionUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => SessionScalarWhereInputSchema),z.lazy(() => SessionScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AccountUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.AccountUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => AccountCreateWithoutUserInputSchema),z.lazy(() => AccountCreateWithoutUserInputSchema).array(),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema),z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AccountUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AccountUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AccountCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AccountUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AccountUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AccountUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => AccountUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AccountScalarWhereInputSchema),z.lazy(() => AccountScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.ApiKeyUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutUserInputSchema),z.lazy(() => ApiKeyCreateWithoutUserInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => ApiKeyUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => ApiKeyUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => ApiKeyScalarWhereInputSchema),z.lazy(() => ApiKeyScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const PasskeyUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.PasskeyUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => PasskeyCreateWithoutUserInputSchema),z.lazy(() => PasskeyCreateWithoutUserInputSchema).array(),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => PasskeyUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PasskeyUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PasskeyCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => PasskeyUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PasskeyUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => PasskeyUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => PasskeyUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => PasskeyScalarWhereInputSchema),z.lazy(() => PasskeyScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const InvitationUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.InvitationUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutUserInputSchema),z.lazy(() => InvitationCreateWithoutUserInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => InvitationUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => InvitationUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => InvitationUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => InvitationUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => InvitationUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => InvitationUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => InvitationScalarWhereInputSchema),z.lazy(() => InvitationScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const PurchaseUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.PurchaseUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutUserInputSchema),z.lazy(() => PurchaseCreateWithoutUserInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => PurchaseUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => PurchaseUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => PurchaseScalarWhereInputSchema),z.lazy(() => PurchaseScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const MemberUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.MemberUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutUserInputSchema),z.lazy(() => MemberCreateWithoutUserInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema),z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => MemberUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => MemberUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => MemberUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => MemberUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => MemberUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => MemberUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => MemberScalarWhereInputSchema),z.lazy(() => MemberScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const TwoFactorUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.TwoFactorUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => TwoFactorCreateWithoutUserInputSchema),z.lazy(() => TwoFactorCreateWithoutUserInputSchema).array(),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema),z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => TwoFactorUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => TwoFactorUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => TwoFactorCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => TwoFactorUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => TwoFactorUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => TwoFactorUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => TwoFactorUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => TwoFactorScalarWhereInputSchema),z.lazy(() => TwoFactorScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AiChatUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.AiChatUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutUserInputSchema),z.lazy(() => AiChatCreateWithoutUserInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AiChatUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AiChatUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AiChatUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AiChatUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AiChatUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => AiChatUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AiChatScalarWhereInputSchema),z.lazy(() => AiChatScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AppFileUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.AppFileUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutUserInputSchema),z.lazy(() => AppFileCreateWithoutUserInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AppFileUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AppFileUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AppFileUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AppFileUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => AppFileUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateWithoutUserInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const JobUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.JobUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutUserInputSchema),z.lazy(() => JobCreateWithoutUserInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutUserInputSchema),z.lazy(() => JobCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectUpdateManyWithoutCreatedByNestedInputSchema: z.ZodType<Prisma.VideoProjectUpdateManyWithoutCreatedByNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutCreatedByInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyCreatedByInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutCreatedByInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => VideoProjectUpdateManyWithWhereWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUpdateManyWithWhereWithoutCreatedByInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => VideoProjectScalarWhereInputSchema),z.lazy(() => VideoProjectScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const SessionUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.SessionUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => SessionCreateWithoutUserInputSchema),z.lazy(() => SessionCreateWithoutUserInputSchema).array(),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema),z.lazy(() => SessionCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => SessionUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => SessionUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => SessionCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => SessionWhereUniqueInputSchema),z.lazy(() => SessionWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => SessionUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => SessionUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => SessionUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => SessionUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => SessionScalarWhereInputSchema),z.lazy(() => SessionScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AccountUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.AccountUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => AccountCreateWithoutUserInputSchema),z.lazy(() => AccountCreateWithoutUserInputSchema).array(),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema),z.lazy(() => AccountCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AccountUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AccountUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AccountCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AccountWhereUniqueInputSchema),z.lazy(() => AccountWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AccountUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AccountUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AccountUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => AccountUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AccountScalarWhereInputSchema),z.lazy(() => AccountScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutUserInputSchema),z.lazy(() => ApiKeyCreateWithoutUserInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => ApiKeyUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => ApiKeyUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => ApiKeyScalarWhereInputSchema),z.lazy(() => ApiKeyScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.PasskeyUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => PasskeyCreateWithoutUserInputSchema),z.lazy(() => PasskeyCreateWithoutUserInputSchema).array(),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema),z.lazy(() => PasskeyCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => PasskeyUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PasskeyUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PasskeyCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => PasskeyWhereUniqueInputSchema),z.lazy(() => PasskeyWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => PasskeyUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PasskeyUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => PasskeyUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => PasskeyUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => PasskeyScalarWhereInputSchema),z.lazy(() => PasskeyScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const InvitationUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutUserInputSchema),z.lazy(() => InvitationCreateWithoutUserInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => InvitationUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => InvitationUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => InvitationUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => InvitationUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => InvitationUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => InvitationUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => InvitationScalarWhereInputSchema),z.lazy(() => InvitationScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutUserInputSchema),z.lazy(() => PurchaseCreateWithoutUserInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => PurchaseUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => PurchaseUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => PurchaseScalarWhereInputSchema),z.lazy(() => PurchaseScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const MemberUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutUserInputSchema),z.lazy(() => MemberCreateWithoutUserInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema),z.lazy(() => MemberCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => MemberUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => MemberUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => MemberUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => MemberUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => MemberUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => MemberUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => MemberScalarWhereInputSchema),z.lazy(() => MemberScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.TwoFactorUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => TwoFactorCreateWithoutUserInputSchema),z.lazy(() => TwoFactorCreateWithoutUserInputSchema).array(),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema),z.lazy(() => TwoFactorCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => TwoFactorUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => TwoFactorUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => TwoFactorCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => TwoFactorWhereUniqueInputSchema),z.lazy(() => TwoFactorWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => TwoFactorUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => TwoFactorUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => TwoFactorUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => TwoFactorUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => TwoFactorScalarWhereInputSchema),z.lazy(() => TwoFactorScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AiChatUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutUserInputSchema),z.lazy(() => AiChatCreateWithoutUserInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AiChatUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AiChatUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AiChatUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AiChatUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AiChatUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => AiChatUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AiChatScalarWhereInputSchema),z.lazy(() => AiChatScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AppFileUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutUserInputSchema),z.lazy(() => AppFileCreateWithoutUserInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AppFileUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AppFileUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => AppFileUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AppFileUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => AppFileUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateWithoutUserInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedUpdateManyWithoutUserNestedInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutUserNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutUserInputSchema),z.lazy(() => JobCreateWithoutUserInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutUserInputSchema),z.lazy(() => JobCreateOrConnectWithoutUserInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutUserInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyUserInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutUserInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutUserInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutUserInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutUserInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutCreatedByInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutCreatedByInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyCreatedByInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutCreatedByInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => VideoProjectUpdateManyWithWhereWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUpdateManyWithWhereWithoutCreatedByInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => VideoProjectScalarWhereInputSchema),z.lazy(() => VideoProjectScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const UserCreateNestedOneWithoutSessionsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutSessionsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedCreateWithoutSessionsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutSessionsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const UserUpdateOneRequiredWithoutSessionsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutSessionsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedCreateWithoutSessionsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutSessionsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutSessionsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutSessionsInputSchema),z.lazy(() => UserUpdateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutSessionsInputSchema) ]).optional(),
}).strict();

export const UserCreateNestedOneWithoutAccountsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutAccountsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAccountsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutAccountsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const UserUpdateOneRequiredWithoutAccountsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutAccountsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAccountsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutAccountsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutAccountsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutAccountsInputSchema),z.lazy(() => UserUpdateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutAccountsInputSchema) ]).optional(),
}).strict();

export const UserCreateNestedOneWithoutPasskeysInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutPasskeysInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutPasskeysInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutPasskeysInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const UserUpdateOneRequiredWithoutPasskeysNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutPasskeysNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutPasskeysInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutPasskeysInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutPasskeysInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutPasskeysInputSchema),z.lazy(() => UserUpdateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedUpdateWithoutPasskeysInputSchema) ]).optional(),
}).strict();

export const UserCreateNestedOneWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutTwofactorsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedCreateWithoutTwofactorsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutTwofactorsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const UserUpdateOneRequiredWithoutTwofactorsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutTwofactorsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedCreateWithoutTwofactorsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutTwofactorsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutTwofactorsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutTwofactorsInputSchema),z.lazy(() => UserUpdateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutTwofactorsInputSchema) ]).optional(),
}).strict();

export const MemberCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutOrganizationInputSchema),z.lazy(() => MemberCreateWithoutOrganizationInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const InvitationCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateWithoutOrganizationInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const PurchaseCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AiChatCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AppFileCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const CaptionStyleCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema).array(),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CaptionStyleCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationBrandingCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationNarratorProfileCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialSettingsCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationSocialSettingsCreateOrConnectWithoutOrganizationInputSchema).optional(),
  connect: z.lazy(() => OrganizationSocialSettingsWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const JobCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.JobCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutOrganizationInputSchema),z.lazy(() => JobCreateWithoutOrganizationInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutOrganizationInputSchema),z.lazy(() => MemberCreateWithoutOrganizationInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateWithoutOrganizationInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema).array(),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CaptionStyleCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationBrandingCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationNarratorProfileCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialSettingsCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationSocialSettingsCreateOrConnectWithoutOrganizationInputSchema).optional(),
  connect: z.lazy(() => OrganizationSocialSettingsWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutOrganizationInputSchema),z.lazy(() => JobCreateWithoutOrganizationInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateNestedManyWithoutOrganizationInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyOrganizationInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const NullableIntFieldUpdateOperationsInputSchema: z.ZodType<Prisma.NullableIntFieldUpdateOperationsInput> = z.object({
  set: z.number().optional().nullable(),
  increment: z.number().optional(),
  decrement: z.number().optional(),
  multiply: z.number().optional(),
  divide: z.number().optional(),
  unset: z.boolean().optional()
}).strict();

export const MemberUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.MemberUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutOrganizationInputSchema),z.lazy(() => MemberCreateWithoutOrganizationInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => MemberUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => MemberUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => MemberUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => MemberUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => MemberUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => MemberUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => MemberScalarWhereInputSchema),z.lazy(() => MemberScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.ApiKeyUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => ApiKeyUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => ApiKeyScalarWhereInputSchema),z.lazy(() => ApiKeyScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const InvitationUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.InvitationUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateWithoutOrganizationInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => InvitationUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => InvitationUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => InvitationUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => InvitationUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => InvitationUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => InvitationUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => InvitationScalarWhereInputSchema),z.lazy(() => InvitationScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const PurchaseUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.PurchaseUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => PurchaseUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => PurchaseUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => PurchaseScalarWhereInputSchema),z.lazy(() => PurchaseScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AiChatUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.AiChatUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AiChatUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AiChatUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AiChatUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AiChatUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AiChatUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => AiChatUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AiChatScalarWhereInputSchema),z.lazy(() => AiChatScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AppFileUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.AppFileUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AppFileUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AppFileUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AppFileUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AppFileUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => AppFileUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.CaptionStyleUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema).array(),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => CaptionStyleUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CaptionStyleCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => CaptionStyleUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => CaptionStyleUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => CaptionStyleScalarWhereInputSchema),z.lazy(() => CaptionStyleScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationBrandingUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationBrandingUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationBrandingCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationBrandingUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationBrandingUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationBrandingScalarWhereInputSchema),z.lazy(() => OrganizationBrandingScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationNarratorProfileUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationNarratorProfileCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema),z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialSettingsCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationSocialSettingsCreateOrConnectWithoutOrganizationInputSchema).optional(),
  upsert: z.lazy(() => OrganizationSocialSettingsUpsertWithoutOrganizationInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => OrganizationSocialSettingsWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => OrganizationSocialSettingsWhereInputSchema) ]).optional(),
  connect: z.lazy(() => OrganizationSocialSettingsWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationSocialSettingsUpdateToOneWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedUpdateWithoutOrganizationInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const JobUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.JobUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutOrganizationInputSchema),z.lazy(() => JobCreateWithoutOrganizationInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.VideoProjectUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => VideoProjectUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => VideoProjectScalarWhereInputSchema),z.lazy(() => VideoProjectScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => MemberCreateWithoutOrganizationInputSchema),z.lazy(() => MemberCreateWithoutOrganizationInputSchema).array(),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => MemberCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => MemberUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => MemberUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => MemberCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => MemberWhereUniqueInputSchema),z.lazy(() => MemberWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => MemberUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => MemberUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => MemberUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => MemberUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => MemberScalarWhereInputSchema),z.lazy(() => MemberScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema).array(),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => ApiKeyCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => ApiKeyCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => ApiKeyWhereUniqueInputSchema),z.lazy(() => ApiKeyWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => ApiKeyUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => ApiKeyScalarWhereInputSchema),z.lazy(() => ApiKeyScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => InvitationCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateWithoutOrganizationInputSchema).array(),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => InvitationCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => InvitationUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => InvitationUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => InvitationCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => InvitationWhereUniqueInputSchema),z.lazy(() => InvitationWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => InvitationUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => InvitationUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => InvitationUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => InvitationUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => InvitationScalarWhereInputSchema),z.lazy(() => InvitationScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema).array(),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => PurchaseCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => PurchaseUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => PurchaseCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => PurchaseWhereUniqueInputSchema),z.lazy(() => PurchaseWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => PurchaseUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => PurchaseUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => PurchaseUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => PurchaseScalarWhereInputSchema),z.lazy(() => PurchaseScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => AiChatCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AiChatCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AiChatUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AiChatUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AiChatCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AiChatWhereUniqueInputSchema),z.lazy(() => AiChatWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AiChatUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AiChatUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AiChatUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => AiChatUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AiChatScalarWhereInputSchema),z.lazy(() => AiChatScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AppFileUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AppFileUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => AppFileUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AppFileUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => AppFileUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema).array(),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => CaptionStyleUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CaptionStyleCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => CaptionStyleWhereUniqueInputSchema),z.lazy(() => CaptionStyleWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => CaptionStyleUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => CaptionStyleUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => CaptionStyleScalarWhereInputSchema),z.lazy(() => CaptionStyleScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationBrandingUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationBrandingCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),z.lazy(() => OrganizationBrandingWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationBrandingUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationBrandingUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationBrandingScalarWhereInputSchema),z.lazy(() => OrganizationBrandingScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationNarratorProfileUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationNarratorProfileCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema),z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialSettingsCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationSocialSettingsCreateOrConnectWithoutOrganizationInputSchema).optional(),
  upsert: z.lazy(() => OrganizationSocialSettingsUpsertWithoutOrganizationInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => OrganizationSocialSettingsWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => OrganizationSocialSettingsWhereInputSchema) ]).optional(),
  connect: z.lazy(() => OrganizationSocialSettingsWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationSocialSettingsUpdateToOneWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedUpdateWithoutOrganizationInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutOrganizationInputSchema),z.lazy(() => JobCreateWithoutOrganizationInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => JobCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema).array(),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema),z.lazy(() => VideoProjectCreateOrConnectWithoutOrganizationInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUpsertWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  createMany: z.lazy(() => VideoProjectCreateManyOrganizationInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => VideoProjectWhereUniqueInputSchema),z.lazy(() => VideoProjectWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUpdateWithWhereUniqueWithoutOrganizationInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => VideoProjectUpdateManyWithWhereWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUpdateManyWithWhereWithoutOrganizationInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => VideoProjectScalarWhereInputSchema),z.lazy(() => VideoProjectScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutMembersInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutMembersInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutMembersInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const UserCreateNestedOneWithoutMembersInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutMembersInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutMembersInputSchema),z.lazy(() => UserUncheckedCreateWithoutMembersInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutMembersInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationUpdateOneRequiredWithoutMembersNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutMembersNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutMembersInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutMembersInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutMembersInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutMembersInputSchema),z.lazy(() => OrganizationUpdateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutMembersInputSchema) ]).optional(),
}).strict();

export const UserUpdateOneRequiredWithoutMembersNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutMembersNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutMembersInputSchema),z.lazy(() => UserUncheckedCreateWithoutMembersInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutMembersInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutMembersInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutMembersInputSchema),z.lazy(() => UserUpdateWithoutMembersInputSchema),z.lazy(() => UserUncheckedUpdateWithoutMembersInputSchema) ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutInvitationsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutInvitationsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutInvitationsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const UserCreateNestedOneWithoutInvitationsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutInvitationsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedCreateWithoutInvitationsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutInvitationsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationUpdateOneRequiredWithoutInvitationsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutInvitationsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutInvitationsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutInvitationsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutInvitationsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutInvitationsInputSchema),z.lazy(() => OrganizationUpdateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutInvitationsInputSchema) ]).optional(),
}).strict();

export const UserUpdateOneRequiredWithoutInvitationsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutInvitationsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedCreateWithoutInvitationsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutInvitationsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutInvitationsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutInvitationsInputSchema),z.lazy(() => UserUpdateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutInvitationsInputSchema) ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutPurchasesInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutPurchasesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutPurchasesInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const UserCreateNestedOneWithoutPurchasesInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutPurchasesInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedCreateWithoutPurchasesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutPurchasesInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const JobCreateNestedManyWithoutPurchaseInputSchema: z.ZodType<Prisma.JobCreateNestedManyWithoutPurchaseInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutPurchaseInputSchema),z.lazy(() => JobCreateWithoutPurchaseInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema),z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyPurchaseInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedCreateNestedManyWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUncheckedCreateNestedManyWithoutPurchaseInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutPurchaseInputSchema),z.lazy(() => JobCreateWithoutPurchaseInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema),z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyPurchaseInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const EnumPurchaseTypeFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumPurchaseTypeFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => PurchaseTypeSchema).optional()
}).strict();

export const NullableFloatFieldUpdateOperationsInputSchema: z.ZodType<Prisma.NullableFloatFieldUpdateOperationsInput> = z.object({
  set: z.number().optional().nullable(),
  increment: z.number().optional(),
  decrement: z.number().optional(),
  multiply: z.number().optional(),
  divide: z.number().optional(),
  unset: z.boolean().optional()
}).strict();

export const OrganizationUpdateOneWithoutPurchasesNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneWithoutPurchasesNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutPurchasesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutPurchasesInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutPurchasesInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutPurchasesInputSchema),z.lazy(() => OrganizationUpdateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutPurchasesInputSchema) ]).optional(),
}).strict();

export const UserUpdateOneWithoutPurchasesNestedInputSchema: z.ZodType<Prisma.UserUpdateOneWithoutPurchasesNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedCreateWithoutPurchasesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutPurchasesInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutPurchasesInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => UserWhereInputSchema) ]).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutPurchasesInputSchema),z.lazy(() => UserUpdateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedUpdateWithoutPurchasesInputSchema) ]).optional(),
}).strict();

export const JobUpdateManyWithoutPurchaseNestedInputSchema: z.ZodType<Prisma.JobUpdateManyWithoutPurchaseNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutPurchaseInputSchema),z.lazy(() => JobCreateWithoutPurchaseInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema),z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutPurchaseInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutPurchaseInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyPurchaseInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutPurchaseInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutPurchaseInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutPurchaseInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutPurchaseInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedUpdateManyWithoutPurchaseNestedInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutPurchaseNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutPurchaseInputSchema),z.lazy(() => JobCreateWithoutPurchaseInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema),z.lazy(() => JobCreateOrConnectWithoutPurchaseInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutPurchaseInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutPurchaseInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyPurchaseInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutPurchaseInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutPurchaseInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutPurchaseInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutPurchaseInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutAiChatsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutAiChatsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutAiChatsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const UserCreateNestedOneWithoutAiChatsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutAiChatsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAiChatsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutAiChatsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationUpdateOneWithoutAiChatsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneWithoutAiChatsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutAiChatsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutAiChatsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutAiChatsInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutAiChatsInputSchema),z.lazy(() => OrganizationUpdateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutAiChatsInputSchema) ]).optional(),
}).strict();

export const UserUpdateOneWithoutAiChatsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneWithoutAiChatsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAiChatsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutAiChatsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutAiChatsInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => UserWhereInputSchema) ]).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutAiChatsInputSchema),z.lazy(() => UserUpdateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutAiChatsInputSchema) ]).optional(),
}).strict();

export const UserCreateNestedOneWithoutApiKeysInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutApiKeysInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutApiKeysInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutApiKeysInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationCreateNestedOneWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutApiKeysInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutApiKeysInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutApiKeysInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const JobCreateNestedManyWithoutKeyInputSchema: z.ZodType<Prisma.JobCreateNestedManyWithoutKeyInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutKeyInputSchema),z.lazy(() => JobCreateWithoutKeyInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema),z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyKeyInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryCreateNestedManyWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryCreateNestedManyWithoutKeyInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyKeyInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedCreateNestedManyWithoutKeyInputSchema: z.ZodType<Prisma.JobUncheckedCreateNestedManyWithoutKeyInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutKeyInputSchema),z.lazy(() => JobCreateWithoutKeyInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema),z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyKeyInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedCreateNestedManyWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateNestedManyWithoutKeyInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyKeyInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const UserUpdateOneRequiredWithoutApiKeysNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutApiKeysNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutApiKeysInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutApiKeysInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutApiKeysInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutApiKeysInputSchema),z.lazy(() => UserUpdateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedUpdateWithoutApiKeysInputSchema) ]).optional(),
}).strict();

export const OrganizationUpdateOneWithoutApiKeysNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneWithoutApiKeysNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutApiKeysInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutApiKeysInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutApiKeysInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutApiKeysInputSchema),z.lazy(() => OrganizationUpdateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutApiKeysInputSchema) ]).optional(),
}).strict();

export const JobUpdateManyWithoutKeyNestedInputSchema: z.ZodType<Prisma.JobUpdateManyWithoutKeyNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutKeyInputSchema),z.lazy(() => JobCreateWithoutKeyInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema),z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyKeyInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutKeyInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutKeyInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUpdateManyWithoutKeyNestedInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithoutKeyNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyKeyInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutKeyInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutKeyInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const JobUncheckedUpdateManyWithoutKeyNestedInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutKeyNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutKeyInputSchema),z.lazy(() => JobCreateWithoutKeyInputSchema).array(),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema),z.lazy(() => JobCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => JobUpsertWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => JobUpsertWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => JobCreateManyKeyInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => JobWhereUniqueInputSchema),z.lazy(() => JobWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => JobUpdateWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => JobUpdateWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => JobUpdateManyWithWhereWithoutKeyInputSchema),z.lazy(() => JobUpdateManyWithWhereWithoutKeyInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutKeyNestedInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutKeyNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutKeyInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyKeyInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutKeyInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutKeyInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutKeyInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutKeyInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const CostBreakdownNullableCreateEnvelopeInputSchema: z.ZodType<Prisma.CostBreakdownNullableCreateEnvelopeInput> = z.object({
  set: z.lazy(() => CostBreakdownCreateInputSchema).optional().nullable()
}).strict();

export const CostBreakdownCreateInputSchema: z.ZodType<Prisma.CostBreakdownCreateInput> = z.object({
  shortGen: z.number().optional().nullable(),
  youtubeProcessing: z.number().optional().nullable(),
  render: z.number().optional().nullable(),
  bRoll: z.number().optional().nullable()
}).strict();

export const WebhookNullableCreateEnvelopeInputSchema: z.ZodType<Prisma.WebhookNullableCreateEnvelopeInput> = z.object({
  set: z.lazy(() => WebhookCreateInputSchema).optional().nullable()
}).strict();

export const WebhookCreateInputSchema: z.ZodType<Prisma.WebhookCreateInput> = z.object({
  url: z.string(),
  status: z.number().optional().nullable(),
  retryCount: z.number().optional().nullable(),
  error: z.string().optional().nullable()
}).strict();

export const JobCreateerrorMessageInputSchema: z.ZodType<Prisma.JobCreateerrorMessageInput> = z.object({
  set: z.string().array()
}).strict();

export const UserCreateNestedOneWithoutJobsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutJobsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutJobsInputSchema),z.lazy(() => UserUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutJobsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationCreateNestedOneWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutJobsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutJobsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const ApiKeyCreateNestedOneWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyCreateNestedOneWithoutJobsInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => ApiKeyCreateOrConnectWithoutJobsInputSchema).optional(),
  connect: z.lazy(() => ApiKeyWhereUniqueInputSchema).optional()
}).strict();

export const PurchaseCreateNestedOneWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseCreateNestedOneWithoutJobsInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => PurchaseCreateOrConnectWithoutJobsInputSchema).optional(),
  connect: z.lazy(() => PurchaseWhereUniqueInputSchema).optional()
}).strict();

export const VideoCreateNestedOneWithoutJobInputSchema: z.ZodType<Prisma.VideoCreateNestedOneWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => VideoCreateWithoutJobInputSchema),z.lazy(() => VideoUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoCreateOrConnectWithoutJobInputSchema).optional(),
  connect: z.lazy(() => VideoWhereUniqueInputSchema).optional()
}).strict();

export const VideoProjectCreateNestedOneWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectCreateNestedOneWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoProjectCreateOrConnectWithoutJobInputSchema).optional(),
  connect: z.lazy(() => VideoProjectWhereUniqueInputSchema).optional()
}).strict();

export const AppFileCreateNestedManyWithoutJobInputSchema: z.ZodType<Prisma.AppFileCreateNestedManyWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutJobInputSchema),z.lazy(() => AppFileCreateWithoutJobInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyJobInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryCreateNestedManyWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryCreateNestedManyWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateWithoutJobInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyJobInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialPostCreateNestedManyWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateNestedManyWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyJobInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const VideoUncheckedCreateNestedOneWithoutJobInputSchema: z.ZodType<Prisma.VideoUncheckedCreateNestedOneWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => VideoCreateWithoutJobInputSchema),z.lazy(() => VideoUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoCreateOrConnectWithoutJobInputSchema).optional(),
  connect: z.lazy(() => VideoWhereUniqueInputSchema).optional()
}).strict();

export const VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateNestedOneWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoProjectCreateOrConnectWithoutJobInputSchema).optional(),
  connect: z.lazy(() => VideoProjectWhereUniqueInputSchema).optional()
}).strict();

export const AppFileUncheckedCreateNestedManyWithoutJobInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateNestedManyWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutJobInputSchema),z.lazy(() => AppFileCreateWithoutJobInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyJobInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateNestedManyWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateWithoutJobInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyJobInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyJobInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const FloatFieldUpdateOperationsInputSchema: z.ZodType<Prisma.FloatFieldUpdateOperationsInput> = z.object({
  set: z.number().optional(),
  increment: z.number().optional(),
  decrement: z.number().optional(),
  multiply: z.number().optional(),
  divide: z.number().optional()
}).strict();

export const CostBreakdownNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.CostBreakdownNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => CostBreakdownCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => CostBreakdownUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const EnumCreditUsageStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumCreditUsageStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => CreditUsageStatusSchema).optional()
}).strict();

export const NullableEnumJobStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.NullableEnumJobStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => JobStatusSchema).optional().nullable(),
  unset: z.boolean().optional()
}).strict();

export const WebhookNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.WebhookNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => WebhookCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => WebhookUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const JobUpdateerrorMessageInputSchema: z.ZodType<Prisma.JobUpdateerrorMessageInput> = z.object({
  set: z.string().array().optional(),
  push: z.union([ z.string(),z.string().array() ]).optional(),
}).strict();

export const EnumJobFunctionNameFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumJobFunctionNameFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => JobFunctionNameSchema).optional()
}).strict();

export const EnumJobSourceFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumJobSourceFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => JobSourceSchema).optional()
}).strict();

export const UserUpdateOneRequiredWithoutJobsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutJobsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutJobsInputSchema),z.lazy(() => UserUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutJobsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutJobsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutJobsInputSchema),z.lazy(() => UserUpdateWithoutJobsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutJobsInputSchema) ]).optional(),
}).strict();

export const OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutJobsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutJobsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutJobsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutJobsInputSchema),z.lazy(() => OrganizationUpdateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutJobsInputSchema) ]).optional(),
}).strict();

export const ApiKeyUpdateOneWithoutJobsNestedInputSchema: z.ZodType<Prisma.ApiKeyUpdateOneWithoutJobsNestedInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => ApiKeyCreateOrConnectWithoutJobsInputSchema).optional(),
  upsert: z.lazy(() => ApiKeyUpsertWithoutJobsInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => ApiKeyWhereInputSchema) ]).optional(),
  connect: z.lazy(() => ApiKeyWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => ApiKeyUpdateToOneWithWhereWithoutJobsInputSchema),z.lazy(() => ApiKeyUpdateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutJobsInputSchema) ]).optional(),
}).strict();

export const PurchaseUpdateOneWithoutJobsNestedInputSchema: z.ZodType<Prisma.PurchaseUpdateOneWithoutJobsNestedInput> = z.object({
  create: z.union([ z.lazy(() => PurchaseCreateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutJobsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => PurchaseCreateOrConnectWithoutJobsInputSchema).optional(),
  upsert: z.lazy(() => PurchaseUpsertWithoutJobsInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => PurchaseWhereInputSchema) ]).optional(),
  connect: z.lazy(() => PurchaseWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => PurchaseUpdateToOneWithWhereWithoutJobsInputSchema),z.lazy(() => PurchaseUpdateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutJobsInputSchema) ]).optional(),
}).strict();

export const VideoUpdateOneWithoutJobNestedInputSchema: z.ZodType<Prisma.VideoUpdateOneWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoCreateWithoutJobInputSchema),z.lazy(() => VideoUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoCreateOrConnectWithoutJobInputSchema).optional(),
  upsert: z.lazy(() => VideoUpsertWithoutJobInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => VideoWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => VideoWhereInputSchema) ]).optional(),
  connect: z.lazy(() => VideoWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoUpdateToOneWithWhereWithoutJobInputSchema),z.lazy(() => VideoUpdateWithoutJobInputSchema),z.lazy(() => VideoUncheckedUpdateWithoutJobInputSchema) ]).optional(),
}).strict();

export const VideoProjectUpdateOneWithoutJobNestedInputSchema: z.ZodType<Prisma.VideoProjectUpdateOneWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoProjectCreateOrConnectWithoutJobInputSchema).optional(),
  upsert: z.lazy(() => VideoProjectUpsertWithoutJobInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => VideoProjectWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => VideoProjectWhereInputSchema) ]).optional(),
  connect: z.lazy(() => VideoProjectWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateToOneWithWhereWithoutJobInputSchema),z.lazy(() => VideoProjectUpdateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutJobInputSchema) ]).optional(),
}).strict();

export const AppFileUpdateManyWithoutJobNestedInputSchema: z.ZodType<Prisma.AppFileUpdateManyWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutJobInputSchema),z.lazy(() => AppFileCreateWithoutJobInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AppFileUpsertWithWhereUniqueWithoutJobInputSchema),z.lazy(() => AppFileUpsertWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyJobInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateWithWhereUniqueWithoutJobInputSchema),z.lazy(() => AppFileUpdateWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AppFileUpdateManyWithWhereWithoutJobInputSchema),z.lazy(() => AppFileUpdateManyWithWhereWithoutJobInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUpdateManyWithoutJobNestedInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateWithoutJobInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutJobInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyJobInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutJobInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutJobInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutJobInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateManyWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyJobInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutJobInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const VideoUncheckedUpdateOneWithoutJobNestedInputSchema: z.ZodType<Prisma.VideoUncheckedUpdateOneWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoCreateWithoutJobInputSchema),z.lazy(() => VideoUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoCreateOrConnectWithoutJobInputSchema).optional(),
  upsert: z.lazy(() => VideoUpsertWithoutJobInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => VideoWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => VideoWhereInputSchema) ]).optional(),
  connect: z.lazy(() => VideoWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoUpdateToOneWithWhereWithoutJobInputSchema),z.lazy(() => VideoUpdateWithoutJobInputSchema),z.lazy(() => VideoUncheckedUpdateWithoutJobInputSchema) ]).optional(),
}).strict();

export const VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateOneWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutJobInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoProjectCreateOrConnectWithoutJobInputSchema).optional(),
  upsert: z.lazy(() => VideoProjectUpsertWithoutJobInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => VideoProjectWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => VideoProjectWhereInputSchema) ]).optional(),
  connect: z.lazy(() => VideoProjectWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateToOneWithWhereWithoutJobInputSchema),z.lazy(() => VideoProjectUpdateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutJobInputSchema) ]).optional(),
}).strict();

export const AppFileUncheckedUpdateManyWithoutJobNestedInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutJobInputSchema),z.lazy(() => AppFileCreateWithoutJobInputSchema).array(),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema),z.lazy(() => AppFileCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => AppFileUpsertWithWhereUniqueWithoutJobInputSchema),z.lazy(() => AppFileUpsertWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => AppFileCreateManyJobInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => AppFileWhereUniqueInputSchema),z.lazy(() => AppFileWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateWithWhereUniqueWithoutJobInputSchema),z.lazy(() => AppFileUpdateWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => AppFileUpdateManyWithWhereWithoutJobInputSchema),z.lazy(() => AppFileUpdateManyWithWhereWithoutJobInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateWithoutJobInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutJobInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyJobInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutJobInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutJobInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutJobInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema).array(),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostCreateOrConnectWithoutJobInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUpsertWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  createMany: z.lazy(() => OrganizationSocialPostCreateManyJobInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUpdateWithWhereUniqueWithoutJobInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUpdateManyWithWhereWithoutJobInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const SourceFilesNullableCreateEnvelopeInputSchema: z.ZodType<Prisma.SourceFilesNullableCreateEnvelopeInput> = z.object({
  set: z.lazy(() => SourceFilesCreateInputSchema).optional().nullable()
}).strict();

export const SourceFilesCreateInputSchema: z.ZodType<Prisma.SourceFilesCreateInput> = z.object({
  isInS3: z.boolean().optional().nullable(),
  target: z.string().optional().nullable(),
  targetFPS: z.number().optional().nullable(),
  preview: z.string().optional().nullable(),
  previewFPS: z.number().optional().nullable()
}).strict();

export const VideoShortsOptionsNullableCreateEnvelopeInputSchema: z.ZodType<Prisma.VideoShortsOptionsNullableCreateEnvelopeInput> = z.object({
  set: z.lazy(() => VideoShortsOptionsCreateInputSchema).optional().nullable()
}).strict();

export const VideoShortsOptionsCreateInputSchema: z.ZodType<Prisma.VideoShortsOptionsCreateInput> = z.object({
  predefinedShorts: z.union([ z.lazy(() => PredefinedShortsCreateInputSchema),z.lazy(() => PredefinedShortsCreateInputSchema).array() ]).optional(),
  isRTL: z.boolean().optional().nullable()
}).strict();

export const ITranscriptionNullableCreateEnvelopeInputSchema: z.ZodType<Prisma.ITranscriptionNullableCreateEnvelopeInput> = z.object({
  set: z.lazy(() => ITranscriptionCreateInputSchema).optional().nullable()
}).strict();

export const ITranscriptionCreateInputSchema: z.ZodType<Prisma.ITranscriptionCreateInput> = z.object({
  prediction: z.union([ z.lazy(() => PredictionCreateInputSchema),z.lazy(() => PredictionCreateInputSchema).array() ]).optional(),
}).strict();

export const ShortListCreateEnvelopeInputSchema: z.ZodType<Prisma.ShortListCreateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
}).strict();

export const ShortCreateInputSchema: z.ZodType<Prisma.ShortCreateInput> = z.object({
  id: z.number(),
  title: z.string(),
  reason: z.string(),
  startId: z.number(),
  endId: z.number(),
  startTime: z.number(),
  endTime: z.number(),
  status: z.lazy(() => ShortStatusSchema).optional(),
  text: z.string(),
  cropFileUrl: z.string().optional().nullable(),
  cropFilekey: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  score: z.lazy(() => ShortScoreCreateInputSchema).optional().nullable(),
  appFileId: z.string().optional().nullable(),
  asd: z.lazy(() => AsdRootCreateInputSchema).optional().nullable(),
  bRolls: z.union([ z.lazy(() => BRollItemCreateInputSchema),z.lazy(() => BRollItemCreateInputSchema).array() ]).optional(),
}).strict();

export const StatusHistoryListCreateEnvelopeInputSchema: z.ZodType<Prisma.StatusHistoryListCreateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
}).strict();

export const StatusHistoryCreateInputSchema: z.ZodType<Prisma.StatusHistoryCreateInput> = z.object({
  status: z.string(),
  startTime: z.string(),
  endTime: z.string().optional().nullable()
}).strict();

export const JobCreateNestedOneWithoutVideoInputSchema: z.ZodType<Prisma.JobCreateNestedOneWithoutVideoInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutVideoInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutVideoInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional()
}).strict();

export const SourceFilesNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.SourceFilesNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => SourceFilesCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => SourceFilesUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const VideoShortsOptionsNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.VideoShortsOptionsNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => VideoShortsOptionsCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => VideoShortsOptionsUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const ITranscriptionNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.ITranscriptionNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => ITranscriptionCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => ITranscriptionUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const ShortListUpdateEnvelopeInputSchema: z.ZodType<Prisma.ShortListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => ShortUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => ShortDeleteManyInputSchema).optional()
}).strict();

export const EnumVideoStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumVideoStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => VideoStatusSchema).optional()
}).strict();

export const StatusHistoryListUpdateEnvelopeInputSchema: z.ZodType<Prisma.StatusHistoryListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => StatusHistoryUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => StatusHistoryDeleteManyInputSchema).optional()
}).strict();

export const JobUpdateOneRequiredWithoutVideoNestedInputSchema: z.ZodType<Prisma.JobUpdateOneRequiredWithoutVideoNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutVideoInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutVideoInputSchema).optional(),
  upsert: z.lazy(() => JobUpsertWithoutVideoInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => JobUpdateToOneWithWhereWithoutVideoInputSchema),z.lazy(() => JobUpdateWithoutVideoInputSchema),z.lazy(() => JobUncheckedUpdateWithoutVideoInputSchema) ]).optional(),
}).strict();

export const VideoProjectCreateerrorMessageInputSchema: z.ZodType<Prisma.VideoProjectCreateerrorMessageInput> = z.object({
  set: z.string().array()
}).strict();

export const JobCreateNestedOneWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobCreateNestedOneWithoutVideoProjectInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoProjectInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutVideoProjectInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationCreateNestedOneWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutVideoProjectsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutVideoProjectsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutVideoProjectsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const UserCreateNestedOneWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutVideoProjectsInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedCreateWithoutVideoProjectsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutVideoProjectsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const VideoDesignCreateNestedOneWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignCreateNestedOneWithoutVideoProjectInput> = z.object({
  create: z.union([ z.lazy(() => VideoDesignCreateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedCreateWithoutVideoProjectInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoDesignCreateOrConnectWithoutVideoProjectInputSchema).optional(),
  connect: z.lazy(() => VideoDesignWhereUniqueInputSchema).optional()
}).strict();

export const VideoDesignUncheckedCreateNestedOneWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignUncheckedCreateNestedOneWithoutVideoProjectInput> = z.object({
  create: z.union([ z.lazy(() => VideoDesignCreateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedCreateWithoutVideoProjectInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoDesignCreateOrConnectWithoutVideoProjectInputSchema).optional(),
  connect: z.lazy(() => VideoDesignWhereUniqueInputSchema).optional()
}).strict();

export const EnumVideoProjectStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumVideoProjectStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => VideoProjectStatusSchema).optional()
}).strict();

export const VideoProjectUpdateerrorMessageInputSchema: z.ZodType<Prisma.VideoProjectUpdateerrorMessageInput> = z.object({
  set: z.string().array().optional(),
  push: z.union([ z.string(),z.string().array() ]).optional(),
}).strict();

export const JobUpdateOneRequiredWithoutVideoProjectNestedInputSchema: z.ZodType<Prisma.JobUpdateOneRequiredWithoutVideoProjectNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoProjectInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutVideoProjectInputSchema).optional(),
  upsert: z.lazy(() => JobUpsertWithoutVideoProjectInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => JobUpdateToOneWithWhereWithoutVideoProjectInputSchema),z.lazy(() => JobUpdateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedUpdateWithoutVideoProjectInputSchema) ]).optional(),
}).strict();

export const OrganizationUpdateOneRequiredWithoutVideoProjectsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutVideoProjectsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutVideoProjectsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutVideoProjectsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutVideoProjectsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUpdateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutVideoProjectsInputSchema) ]).optional(),
}).strict();

export const UserUpdateOneRequiredWithoutVideoProjectsNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutVideoProjectsNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedCreateWithoutVideoProjectsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutVideoProjectsInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutVideoProjectsInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutVideoProjectsInputSchema),z.lazy(() => UserUpdateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutVideoProjectsInputSchema) ]).optional(),
}).strict();

export const VideoDesignUpdateOneWithoutVideoProjectNestedInputSchema: z.ZodType<Prisma.VideoDesignUpdateOneWithoutVideoProjectNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoDesignCreateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedCreateWithoutVideoProjectInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoDesignCreateOrConnectWithoutVideoProjectInputSchema).optional(),
  upsert: z.lazy(() => VideoDesignUpsertWithoutVideoProjectInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => VideoDesignWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => VideoDesignWhereInputSchema) ]).optional(),
  connect: z.lazy(() => VideoDesignWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoDesignUpdateToOneWithWhereWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUpdateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedUpdateWithoutVideoProjectInputSchema) ]).optional(),
}).strict();

export const VideoDesignUncheckedUpdateOneWithoutVideoProjectNestedInputSchema: z.ZodType<Prisma.VideoDesignUncheckedUpdateOneWithoutVideoProjectNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoDesignCreateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedCreateWithoutVideoProjectInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoDesignCreateOrConnectWithoutVideoProjectInputSchema).optional(),
  upsert: z.lazy(() => VideoDesignUpsertWithoutVideoProjectInputSchema).optional(),
  disconnect: z.union([ z.boolean(),z.lazy(() => VideoDesignWhereInputSchema) ]).optional(),
  delete: z.union([ z.boolean(),z.lazy(() => VideoDesignWhereInputSchema) ]).optional(),
  connect: z.lazy(() => VideoDesignWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoDesignUpdateToOneWithWhereWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUpdateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedUpdateWithoutVideoProjectInputSchema) ]).optional(),
}).strict();

export const VideoProjectCreateNestedOneWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectCreateNestedOneWithoutDesignInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutDesignInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoProjectCreateOrConnectWithoutDesignInputSchema).optional(),
  connect: z.lazy(() => VideoProjectWhereUniqueInputSchema).optional()
}).strict();

export const EnumVideoDesignStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumVideoDesignStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => VideoDesignStatusSchema).optional()
}).strict();

export const VideoProjectUpdateOneRequiredWithoutDesignNestedInputSchema: z.ZodType<Prisma.VideoProjectUpdateOneRequiredWithoutDesignNestedInput> = z.object({
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutDesignInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => VideoProjectCreateOrConnectWithoutDesignInputSchema).optional(),
  upsert: z.lazy(() => VideoProjectUpsertWithoutDesignInputSchema).optional(),
  connect: z.lazy(() => VideoProjectWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => VideoProjectUpdateToOneWithWhereWithoutDesignInputSchema),z.lazy(() => VideoProjectUpdateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutDesignInputSchema) ]).optional(),
}).strict();

export const AppFileCreatetagsInputSchema: z.ZodType<Prisma.AppFileCreatetagsInput> = z.object({
  set: z.string().array()
}).strict();

export const UserCreateNestedOneWithoutFilesInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutFilesInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutFilesInputSchema),z.lazy(() => UserUncheckedCreateWithoutFilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutFilesInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationCreateNestedOneWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutFilesInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutFilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutFilesInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const JobCreateNestedOneWithoutFilesInputSchema: z.ZodType<Prisma.JobCreateNestedOneWithoutFilesInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutFilesInputSchema),z.lazy(() => JobUncheckedCreateWithoutFilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutFilesInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional()
}).strict();

export const RenderHistoryCreateNestedManyWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryCreateNestedManyWithoutAppFileInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyAppFileInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedCreateNestedManyWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateNestedManyWithoutAppFileInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyAppFileInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const EnumAppFileTypeFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumAppFileTypeFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => AppFileTypeSchema).optional()
}).strict();

export const EnumAppFileStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumAppFileStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => AppFileStatusSchema).optional()
}).strict();

export const AppFileUpdatetagsInputSchema: z.ZodType<Prisma.AppFileUpdatetagsInput> = z.object({
  set: z.string().array().optional(),
  push: z.union([ z.string(),z.string().array() ]).optional(),
}).strict();

export const UserUpdateOneRequiredWithoutFilesNestedInputSchema: z.ZodType<Prisma.UserUpdateOneRequiredWithoutFilesNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutFilesInputSchema),z.lazy(() => UserUncheckedCreateWithoutFilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutFilesInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutFilesInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutFilesInputSchema),z.lazy(() => UserUpdateWithoutFilesInputSchema),z.lazy(() => UserUncheckedUpdateWithoutFilesInputSchema) ]).optional(),
}).strict();

export const OrganizationUpdateOneWithoutFilesNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneWithoutFilesNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutFilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutFilesInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutFilesInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => OrganizationWhereInputSchema) ]).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutFilesInputSchema),z.lazy(() => OrganizationUpdateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutFilesInputSchema) ]).optional(),
}).strict();

export const JobUpdateOneWithoutFilesNestedInputSchema: z.ZodType<Prisma.JobUpdateOneWithoutFilesNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutFilesInputSchema),z.lazy(() => JobUncheckedCreateWithoutFilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutFilesInputSchema).optional(),
  upsert: z.lazy(() => JobUpsertWithoutFilesInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => JobWhereInputSchema) ]).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => JobUpdateToOneWithWhereWithoutFilesInputSchema),z.lazy(() => JobUpdateWithoutFilesInputSchema),z.lazy(() => JobUncheckedUpdateWithoutFilesInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUpdateManyWithoutAppFileNestedInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithoutAppFileNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutAppFileInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyAppFileInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutAppFileInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutAppFileInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutAppFileNestedInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutAppFileNestedInput> = z.object({
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema).array(),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema),z.lazy(() => RenderHistoryCreateOrConnectWithoutAppFileInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUpsertWithWhereUniqueWithoutAppFileInputSchema).array() ]).optional(),
  createMany: z.lazy(() => RenderHistoryCreateManyAppFileInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => RenderHistoryWhereUniqueInputSchema),z.lazy(() => RenderHistoryWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUpdateWithWhereUniqueWithoutAppFileInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUpdateManyWithWhereWithoutAppFileInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutCaptionStylesInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutCaptionStylesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutCaptionStylesInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationUpdateOneRequiredWithoutCaptionStylesNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutCaptionStylesNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutCaptionStylesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutCaptionStylesInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutCaptionStylesInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUpdateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutCaptionStylesInputSchema) ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutBrandingsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutBrandingsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutBrandingsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationUpdateOneRequiredWithoutBrandingsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutBrandingsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutBrandingsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutBrandingsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutBrandingsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutBrandingsInputSchema),z.lazy(() => OrganizationUpdateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutBrandingsInputSchema) ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutNarratorProfilesInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutNarratorProfilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutNarratorProfilesInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const OrganizationUpdateOneRequiredWithoutNarratorProfilesNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutNarratorProfilesNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutNarratorProfilesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutNarratorProfilesInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutNarratorProfilesInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUpdateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutNarratorProfilesInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsCreatedefaultPlatformsInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreatedefaultPlatformsInput> = z.object({
  set: z.string().array()
}).strict();

export const OrganizationCreateNestedOneWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutSocialSettingsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialSettingsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutSocialSettingsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumOrganizationSocialProviderFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => OrganizationSocialProviderSchema).optional()
}).strict();

export const OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdatedefaultPlatformsInput> = z.object({
  set: z.string().array().optional(),
  push: z.union([ z.string(),z.string().array() ]).optional(),
}).strict();

export const OrganizationUpdateOneRequiredWithoutSocialSettingsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutSocialSettingsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialSettingsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutSocialSettingsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutSocialSettingsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUpdateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutSocialSettingsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostCreateplatformsInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateplatformsInput> = z.object({
  set: z.string().array()
}).strict();

export const OrganizationCreateNestedOneWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutSocialPostsInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialPostsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutSocialPostsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const JobCreateNestedOneWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobCreateNestedOneWithoutSocialPostsInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedCreateWithoutSocialPostsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutSocialPostsInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional()
}).strict();

export const EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumOrganizationSocialPostStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => OrganizationSocialPostStatusSchema).optional()
}).strict();

export const OrganizationSocialPostUpdateplatformsInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateplatformsInput> = z.object({
  set: z.string().array().optional(),
  push: z.union([ z.string(),z.string().array() ]).optional(),
}).strict();

export const OrganizationUpdateOneRequiredWithoutSocialPostsNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutSocialPostsNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialPostsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutSocialPostsInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutSocialPostsInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUpdateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutSocialPostsInputSchema) ]).optional(),
}).strict();

export const JobUpdateOneWithoutSocialPostsNestedInputSchema: z.ZodType<Prisma.JobUpdateOneWithoutSocialPostsNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedCreateWithoutSocialPostsInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutSocialPostsInputSchema).optional(),
  upsert: z.lazy(() => JobUpsertWithoutSocialPostsInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => JobWhereInputSchema) ]).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => JobUpdateToOneWithWhereWithoutSocialPostsInputSchema),z.lazy(() => JobUpdateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedUpdateWithoutSocialPostsInputSchema) ]).optional(),
}).strict();

export const OrganizationCreateNestedOneWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationCreateNestedOneWithoutRenderHistoryInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutRenderHistoryInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutRenderHistoryInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional()
}).strict();

export const UserCreateNestedOneWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserCreateNestedOneWithoutRenderHistoryInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedCreateWithoutRenderHistoryInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutRenderHistoryInputSchema).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional()
}).strict();

export const ApiKeyCreateNestedOneWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyCreateNestedOneWithoutRenderHistoriesInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutRenderHistoriesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => ApiKeyCreateOrConnectWithoutRenderHistoriesInputSchema).optional(),
  connect: z.lazy(() => ApiKeyWhereUniqueInputSchema).optional()
}).strict();

export const JobCreateNestedOneWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobCreateNestedOneWithoutRenderHistoriesInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedCreateWithoutRenderHistoriesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutRenderHistoriesInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional()
}).strict();

export const AppFileCreateNestedOneWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileCreateNestedOneWithoutRenderHistoriesInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutRenderHistoriesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => AppFileCreateOrConnectWithoutRenderHistoriesInputSchema).optional(),
  connect: z.lazy(() => AppFileWhereUniqueInputSchema).optional()
}).strict();

export const EnumRenderHistoryStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumRenderHistoryStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => RenderHistoryStatusSchema).optional()
}).strict();

export const OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInputSchema: z.ZodType<Prisma.OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInput> = z.object({
  create: z.union([ z.lazy(() => OrganizationCreateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutRenderHistoryInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => OrganizationCreateOrConnectWithoutRenderHistoryInputSchema).optional(),
  upsert: z.lazy(() => OrganizationUpsertWithoutRenderHistoryInputSchema).optional(),
  connect: z.lazy(() => OrganizationWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => OrganizationUpdateToOneWithWhereWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUpdateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutRenderHistoryInputSchema) ]).optional(),
}).strict();

export const UserUpdateOneWithoutRenderHistoryNestedInputSchema: z.ZodType<Prisma.UserUpdateOneWithoutRenderHistoryNestedInput> = z.object({
  create: z.union([ z.lazy(() => UserCreateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedCreateWithoutRenderHistoryInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => UserCreateOrConnectWithoutRenderHistoryInputSchema).optional(),
  upsert: z.lazy(() => UserUpsertWithoutRenderHistoryInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => UserWhereInputSchema) ]).optional(),
  connect: z.lazy(() => UserWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => UserUpdateToOneWithWhereWithoutRenderHistoryInputSchema),z.lazy(() => UserUpdateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedUpdateWithoutRenderHistoryInputSchema) ]).optional(),
}).strict();

export const ApiKeyUpdateOneWithoutRenderHistoriesNestedInputSchema: z.ZodType<Prisma.ApiKeyUpdateOneWithoutRenderHistoriesNestedInput> = z.object({
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutRenderHistoriesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => ApiKeyCreateOrConnectWithoutRenderHistoriesInputSchema).optional(),
  upsert: z.lazy(() => ApiKeyUpsertWithoutRenderHistoriesInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => ApiKeyWhereInputSchema) ]).optional(),
  connect: z.lazy(() => ApiKeyWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => ApiKeyUpdateToOneWithWhereWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutRenderHistoriesInputSchema) ]).optional(),
}).strict();

export const JobUpdateOneRequiredWithoutRenderHistoriesNestedInputSchema: z.ZodType<Prisma.JobUpdateOneRequiredWithoutRenderHistoriesNestedInput> = z.object({
  create: z.union([ z.lazy(() => JobCreateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedCreateWithoutRenderHistoriesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => JobCreateOrConnectWithoutRenderHistoriesInputSchema).optional(),
  upsert: z.lazy(() => JobUpsertWithoutRenderHistoriesInputSchema).optional(),
  connect: z.lazy(() => JobWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => JobUpdateToOneWithWhereWithoutRenderHistoriesInputSchema),z.lazy(() => JobUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedUpdateWithoutRenderHistoriesInputSchema) ]).optional(),
}).strict();

export const AppFileUpdateOneWithoutRenderHistoriesNestedInputSchema: z.ZodType<Prisma.AppFileUpdateOneWithoutRenderHistoriesNestedInput> = z.object({
  create: z.union([ z.lazy(() => AppFileCreateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutRenderHistoriesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => AppFileCreateOrConnectWithoutRenderHistoriesInputSchema).optional(),
  upsert: z.lazy(() => AppFileUpsertWithoutRenderHistoriesInputSchema).optional(),
  disconnect: z.boolean().optional(),
  delete: z.union([ z.boolean(),z.lazy(() => AppFileWhereInputSchema) ]).optional(),
  connect: z.lazy(() => AppFileWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => AppFileUpdateToOneWithWhereWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutRenderHistoriesInputSchema) ]).optional(),
}).strict();

export const ExternalKeysCreateerrorsInputSchema: z.ZodType<Prisma.ExternalKeysCreateerrorsInput> = z.object({
  set: z.string().array()
}).strict();

export const EnumExternalKeysStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumExternalKeysStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => ExternalKeysStatusSchema).optional()
}).strict();

export const EnumExternalKeysTypeFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumExternalKeysTypeFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => ExternalKeysTypeSchema).optional()
}).strict();

export const ExternalKeysUpdateerrorsInputSchema: z.ZodType<Prisma.ExternalKeysUpdateerrorsInput> = z.object({
  set: z.string().array().optional(),
  push: z.union([ z.string(),z.string().array() ]).optional(),
}).strict();

export const CookieIdentityCreateNestedManyWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityCreateNestedManyWithoutProxyInput> = z.object({
  create: z.union([ z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema).array(),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CookieIdentityCreateManyProxyInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const CookieIdentityUncheckedCreateNestedManyWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedCreateNestedManyWithoutProxyInput> = z.object({
  create: z.union([ z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema).array(),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CookieIdentityCreateManyProxyInputEnvelopeSchema).optional(),
  connect: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
}).strict();

export const CookieIdentityUpdateManyWithoutProxyNestedInputSchema: z.ZodType<Prisma.CookieIdentityUpdateManyWithoutProxyNestedInput> = z.object({
  create: z.union([ z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema).array(),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => CookieIdentityUpsertWithWhereUniqueWithoutProxyInputSchema),z.lazy(() => CookieIdentityUpsertWithWhereUniqueWithoutProxyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CookieIdentityCreateManyProxyInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => CookieIdentityUpdateWithWhereUniqueWithoutProxyInputSchema),z.lazy(() => CookieIdentityUpdateWithWhereUniqueWithoutProxyInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => CookieIdentityUpdateManyWithWhereWithoutProxyInputSchema),z.lazy(() => CookieIdentityUpdateManyWithWhereWithoutProxyInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => CookieIdentityScalarWhereInputSchema),z.lazy(() => CookieIdentityScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const CookieIdentityUncheckedUpdateManyWithoutProxyNestedInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedUpdateManyWithoutProxyNestedInput> = z.object({
  create: z.union([ z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema).array(),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema).array() ]).optional(),
  connectOrCreate: z.union([ z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema),z.lazy(() => CookieIdentityCreateOrConnectWithoutProxyInputSchema).array() ]).optional(),
  upsert: z.union([ z.lazy(() => CookieIdentityUpsertWithWhereUniqueWithoutProxyInputSchema),z.lazy(() => CookieIdentityUpsertWithWhereUniqueWithoutProxyInputSchema).array() ]).optional(),
  createMany: z.lazy(() => CookieIdentityCreateManyProxyInputEnvelopeSchema).optional(),
  set: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  disconnect: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  delete: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  connect: z.union([ z.lazy(() => CookieIdentityWhereUniqueInputSchema),z.lazy(() => CookieIdentityWhereUniqueInputSchema).array() ]).optional(),
  update: z.union([ z.lazy(() => CookieIdentityUpdateWithWhereUniqueWithoutProxyInputSchema),z.lazy(() => CookieIdentityUpdateWithWhereUniqueWithoutProxyInputSchema).array() ]).optional(),
  updateMany: z.union([ z.lazy(() => CookieIdentityUpdateManyWithWhereWithoutProxyInputSchema),z.lazy(() => CookieIdentityUpdateManyWithWhereWithoutProxyInputSchema).array() ]).optional(),
  deleteMany: z.union([ z.lazy(() => CookieIdentityScalarWhereInputSchema),z.lazy(() => CookieIdentityScalarWhereInputSchema).array() ]).optional(),
}).strict();

export const ProxyCreateNestedOneWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyCreateNestedOneWithoutCookieIdentitiesInput> = z.object({
  create: z.union([ z.lazy(() => ProxyCreateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedCreateWithoutCookieIdentitiesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => ProxyCreateOrConnectWithoutCookieIdentitiesInputSchema).optional(),
  connect: z.lazy(() => ProxyWhereUniqueInputSchema).optional()
}).strict();

export const ProxyUpdateOneRequiredWithoutCookieIdentitiesNestedInputSchema: z.ZodType<Prisma.ProxyUpdateOneRequiredWithoutCookieIdentitiesNestedInput> = z.object({
  create: z.union([ z.lazy(() => ProxyCreateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedCreateWithoutCookieIdentitiesInputSchema) ]).optional(),
  connectOrCreate: z.lazy(() => ProxyCreateOrConnectWithoutCookieIdentitiesInputSchema).optional(),
  upsert: z.lazy(() => ProxyUpsertWithoutCookieIdentitiesInputSchema).optional(),
  connect: z.lazy(() => ProxyWhereUniqueInputSchema).optional(),
  update: z.union([ z.lazy(() => ProxyUpdateToOneWithWhereWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUpdateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedUpdateWithoutCookieIdentitiesInputSchema) ]).optional(),
}).strict();

export const NestedStringFilterSchema: z.ZodType<Prisma.NestedStringFilter> = z.object({
  equals: z.string().optional(),
  in: z.string().array().optional(),
  notIn: z.string().array().optional(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringFilterSchema) ]).optional(),
}).strict();

export const NestedBoolFilterSchema: z.ZodType<Prisma.NestedBoolFilter> = z.object({
  equals: z.boolean().optional(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolFilterSchema) ]).optional(),
}).strict();

export const NestedStringNullableFilterSchema: z.ZodType<Prisma.NestedStringNullableFilter> = z.object({
  equals: z.string().optional().nullable(),
  in: z.string().array().optional().nullable(),
  notIn: z.string().array().optional().nullable(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const NestedDateTimeFilterSchema: z.ZodType<Prisma.NestedDateTimeFilter> = z.object({
  equals: z.coerce.date().optional(),
  in: z.coerce.date().array().optional(),
  notIn: z.coerce.date().array().optional(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeFilterSchema) ]).optional(),
}).strict();

export const NestedBoolNullableFilterSchema: z.ZodType<Prisma.NestedBoolNullableFilter> = z.object({
  equals: z.boolean().optional().nullable(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const NestedDateTimeNullableFilterSchema: z.ZodType<Prisma.NestedDateTimeNullableFilter> = z.object({
  equals: z.coerce.date().optional().nullable(),
  in: z.coerce.date().array().optional().nullable(),
  notIn: z.coerce.date().array().optional().nullable(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const NestedIntFilterSchema: z.ZodType<Prisma.NestedIntFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntFilterSchema) ]).optional(),
}).strict();

export const NestedStringWithAggregatesFilterSchema: z.ZodType<Prisma.NestedStringWithAggregatesFilter> = z.object({
  equals: z.string().optional(),
  in: z.string().array().optional(),
  notIn: z.string().array().optional(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedStringFilterSchema).optional(),
  _max: z.lazy(() => NestedStringFilterSchema).optional()
}).strict();

export const NestedBoolWithAggregatesFilterSchema: z.ZodType<Prisma.NestedBoolWithAggregatesFilter> = z.object({
  equals: z.boolean().optional(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedBoolFilterSchema).optional(),
  _max: z.lazy(() => NestedBoolFilterSchema).optional()
}).strict();

export const NestedStringNullableWithAggregatesFilterSchema: z.ZodType<Prisma.NestedStringNullableWithAggregatesFilter> = z.object({
  equals: z.string().optional().nullable(),
  in: z.string().array().optional().nullable(),
  notIn: z.string().array().optional().nullable(),
  lt: z.string().optional(),
  lte: z.string().optional(),
  gt: z.string().optional(),
  gte: z.string().optional(),
  contains: z.string().optional(),
  startsWith: z.string().optional(),
  endsWith: z.string().optional(),
  not: z.union([ z.string(),z.lazy(() => NestedStringNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedStringNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedStringNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const NestedIntNullableFilterSchema: z.ZodType<Prisma.NestedIntNullableFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const NestedDateTimeWithAggregatesFilterSchema: z.ZodType<Prisma.NestedDateTimeWithAggregatesFilter> = z.object({
  equals: z.coerce.date().optional(),
  in: z.coerce.date().array().optional(),
  notIn: z.coerce.date().array().optional(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedDateTimeFilterSchema).optional(),
  _max: z.lazy(() => NestedDateTimeFilterSchema).optional()
}).strict();

export const NestedBoolNullableWithAggregatesFilterSchema: z.ZodType<Prisma.NestedBoolNullableWithAggregatesFilter> = z.object({
  equals: z.boolean().optional().nullable(),
  not: z.union([ z.boolean(),z.lazy(() => NestedBoolNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedBoolNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedBoolNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const NestedDateTimeNullableWithAggregatesFilterSchema: z.ZodType<Prisma.NestedDateTimeNullableWithAggregatesFilter> = z.object({
  equals: z.coerce.date().optional().nullable(),
  in: z.coerce.date().array().optional().nullable(),
  notIn: z.coerce.date().array().optional().nullable(),
  lt: z.coerce.date().optional(),
  lte: z.coerce.date().optional(),
  gt: z.coerce.date().optional(),
  gte: z.coerce.date().optional(),
  not: z.union([ z.coerce.date(),z.lazy(() => NestedDateTimeNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedDateTimeNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedDateTimeNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const NestedIntWithAggregatesFilterSchema: z.ZodType<Prisma.NestedIntWithAggregatesFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatFilterSchema).optional(),
  _sum: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedIntFilterSchema).optional(),
  _max: z.lazy(() => NestedIntFilterSchema).optional()
}).strict();

export const NestedFloatFilterSchema: z.ZodType<Prisma.NestedFloatFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatFilterSchema) ]).optional(),
}).strict();

export const NestedIntNullableWithAggregatesFilterSchema: z.ZodType<Prisma.NestedIntNullableWithAggregatesFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedIntNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _sum: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const NestedFloatNullableFilterSchema: z.ZodType<Prisma.NestedFloatNullableFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const NestedEnumPurchaseTypeFilterSchema: z.ZodType<Prisma.NestedEnumPurchaseTypeFilter> = z.object({
  equals: z.lazy(() => PurchaseTypeSchema).optional(),
  in: z.lazy(() => PurchaseTypeSchema).array().optional(),
  notIn: z.lazy(() => PurchaseTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => NestedEnumPurchaseTypeFilterSchema) ]).optional(),
}).strict();

export const NestedEnumPurchaseTypeWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumPurchaseTypeWithAggregatesFilter> = z.object({
  equals: z.lazy(() => PurchaseTypeSchema).optional(),
  in: z.lazy(() => PurchaseTypeSchema).array().optional(),
  notIn: z.lazy(() => PurchaseTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => NestedEnumPurchaseTypeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumPurchaseTypeFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumPurchaseTypeFilterSchema).optional()
}).strict();

export const NestedFloatNullableWithAggregatesFilterSchema: z.ZodType<Prisma.NestedFloatNullableWithAggregatesFilter> = z.object({
  equals: z.number().optional().nullable(),
  in: z.number().array().optional().nullable(),
  notIn: z.number().array().optional().nullable(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _sum: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedFloatNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const NestedJsonFilterSchema: z.ZodType<Prisma.NestedJsonFilter> = z.object({
  equals: InputJsonValueSchema.optional(),
  not: InputJsonValueSchema.optional()
}).strict();

export const CostBreakdownWhereInputSchema: z.ZodType<Prisma.CostBreakdownWhereInput> = z.object({
  AND: z.union([ z.lazy(() => CostBreakdownWhereInputSchema),z.lazy(() => CostBreakdownWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CostBreakdownWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CostBreakdownWhereInputSchema),z.lazy(() => CostBreakdownWhereInputSchema).array() ]).optional(),
  shortGen: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  youtubeProcessing: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  render: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  bRoll: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const NestedEnumCreditUsageStatusFilterSchema: z.ZodType<Prisma.NestedEnumCreditUsageStatusFilter> = z.object({
  equals: z.lazy(() => CreditUsageStatusSchema).optional(),
  in: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  notIn: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => NestedEnumCreditUsageStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumJobStatusNullableFilterSchema: z.ZodType<Prisma.NestedEnumJobStatusNullableFilter> = z.object({
  equals: z.lazy(() => JobStatusSchema).optional().nullable(),
  in: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  notIn: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  not: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NestedEnumJobStatusNullableFilterSchema) ]).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const WebhookWhereInputSchema: z.ZodType<Prisma.WebhookWhereInput> = z.object({
  AND: z.union([ z.lazy(() => WebhookWhereInputSchema),z.lazy(() => WebhookWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => WebhookWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => WebhookWhereInputSchema),z.lazy(() => WebhookWhereInputSchema).array() ]).optional(),
  url: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  retryCount: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  error: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
}).strict();

export const NestedEnumJobFunctionNameFilterSchema: z.ZodType<Prisma.NestedEnumJobFunctionNameFilter> = z.object({
  equals: z.lazy(() => JobFunctionNameSchema).optional(),
  in: z.lazy(() => JobFunctionNameSchema).array().optional(),
  notIn: z.lazy(() => JobFunctionNameSchema).array().optional(),
  not: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => NestedEnumJobFunctionNameFilterSchema) ]).optional(),
}).strict();

export const NestedEnumJobSourceFilterSchema: z.ZodType<Prisma.NestedEnumJobSourceFilter> = z.object({
  equals: z.lazy(() => JobSourceSchema).optional(),
  in: z.lazy(() => JobSourceSchema).array().optional(),
  notIn: z.lazy(() => JobSourceSchema).array().optional(),
  not: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => NestedEnumJobSourceFilterSchema) ]).optional(),
}).strict();

export const NestedFloatWithAggregatesFilterSchema: z.ZodType<Prisma.NestedFloatWithAggregatesFilter> = z.object({
  equals: z.number().optional(),
  in: z.number().array().optional(),
  notIn: z.number().array().optional(),
  lt: z.number().optional(),
  lte: z.number().optional(),
  gt: z.number().optional(),
  gte: z.number().optional(),
  not: z.union([ z.number(),z.lazy(() => NestedFloatWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _avg: z.lazy(() => NestedFloatFilterSchema).optional(),
  _sum: z.lazy(() => NestedFloatFilterSchema).optional(),
  _min: z.lazy(() => NestedFloatFilterSchema).optional(),
  _max: z.lazy(() => NestedFloatFilterSchema).optional()
}).strict();

export const NestedEnumCreditUsageStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumCreditUsageStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => CreditUsageStatusSchema).optional(),
  in: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  notIn: z.lazy(() => CreditUsageStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => NestedEnumCreditUsageStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumCreditUsageStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumCreditUsageStatusFilterSchema).optional()
}).strict();

export const NestedEnumJobStatusNullableWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumJobStatusNullableWithAggregatesFilter> = z.object({
  equals: z.lazy(() => JobStatusSchema).optional().nullable(),
  in: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  notIn: z.lazy(() => JobStatusSchema).array().optional().nullable(),
  not: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NestedEnumJobStatusNullableWithAggregatesFilterSchema) ]).optional().nullable(),
  _count: z.lazy(() => NestedIntNullableFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumJobStatusNullableFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumJobStatusNullableFilterSchema).optional(),
  isSet: z.boolean().optional()
}).strict();

export const NestedEnumJobFunctionNameWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumJobFunctionNameWithAggregatesFilter> = z.object({
  equals: z.lazy(() => JobFunctionNameSchema).optional(),
  in: z.lazy(() => JobFunctionNameSchema).array().optional(),
  notIn: z.lazy(() => JobFunctionNameSchema).array().optional(),
  not: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => NestedEnumJobFunctionNameWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumJobFunctionNameFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumJobFunctionNameFilterSchema).optional()
}).strict();

export const NestedEnumJobSourceWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumJobSourceWithAggregatesFilter> = z.object({
  equals: z.lazy(() => JobSourceSchema).optional(),
  in: z.lazy(() => JobSourceSchema).array().optional(),
  notIn: z.lazy(() => JobSourceSchema).array().optional(),
  not: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => NestedEnumJobSourceWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumJobSourceFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumJobSourceFilterSchema).optional()
}).strict();

export const SourceFilesWhereInputSchema: z.ZodType<Prisma.SourceFilesWhereInput> = z.object({
  AND: z.union([ z.lazy(() => SourceFilesWhereInputSchema),z.lazy(() => SourceFilesWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => SourceFilesWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => SourceFilesWhereInputSchema),z.lazy(() => SourceFilesWhereInputSchema).array() ]).optional(),
  isInS3: z.union([ z.lazy(() => BoolNullableFilterSchema),z.boolean() ]).optional().nullable(),
  target: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  targetFPS: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  preview: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  previewFPS: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const VideoShortsOptionsWhereInputSchema: z.ZodType<Prisma.VideoShortsOptionsWhereInput> = z.object({
  AND: z.union([ z.lazy(() => VideoShortsOptionsWhereInputSchema),z.lazy(() => VideoShortsOptionsWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoShortsOptionsWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoShortsOptionsWhereInputSchema),z.lazy(() => VideoShortsOptionsWhereInputSchema).array() ]).optional(),
  predefinedShorts: z.union([ z.lazy(() => PredefinedShortsCompositeListFilterSchema),z.lazy(() => PredefinedShortsObjectEqualityInputSchema).array() ]).optional(),
  isRTL: z.union([ z.lazy(() => BoolNullableFilterSchema),z.boolean() ]).optional().nullable(),
}).strict();

export const PredefinedShortsObjectEqualityInputSchema: z.ZodType<Prisma.PredefinedShortsObjectEqualityInput> = z.object({
  title: z.string(),
  start: z.number(),
  end: z.number()
}).strict();

export const ITranscriptionWhereInputSchema: z.ZodType<Prisma.ITranscriptionWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ITranscriptionWhereInputSchema),z.lazy(() => ITranscriptionWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ITranscriptionWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ITranscriptionWhereInputSchema),z.lazy(() => ITranscriptionWhereInputSchema).array() ]).optional(),
  prediction: z.union([ z.lazy(() => PredictionCompositeListFilterSchema),z.lazy(() => PredictionObjectEqualityInputSchema).array() ]).optional(),
}).strict();

export const PredictionObjectEqualityInputSchema: z.ZodType<Prisma.PredictionObjectEqualityInput> = z.object({
  words: z.lazy(() => WordObjectEqualityInputSchema).array().optional(),
  language: z.string().optional().nullable(),
  transcription: z.string(),
  confidence: z.number(),
  time_begin: z.number(),
  time_end: z.number(),
  speaker: z.string(),
  channel: z.string()
}).strict();

export const ShortWhereInputSchema: z.ZodType<Prisma.ShortWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ShortWhereInputSchema),z.lazy(() => ShortWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ShortWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ShortWhereInputSchema),z.lazy(() => ShortWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  reason: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  startId: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  endId: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  startTime: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  endTime: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  status: z.union([ z.lazy(() => EnumShortStatusFilterSchema),z.lazy(() => ShortStatusSchema) ]).optional(),
  text: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  cropFileUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cropFilekey: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  score: z.union([ z.lazy(() => ShortScoreNullableCompositeFilterSchema),z.lazy(() => ShortScoreObjectEqualityInputSchema) ]).optional().nullable(),
  appFileId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  asd: z.union([ z.lazy(() => AsdRootNullableCompositeFilterSchema),z.lazy(() => AsdRootObjectEqualityInputSchema) ]).optional().nullable(),
  bRolls: z.union([ z.lazy(() => BRollItemCompositeListFilterSchema),z.lazy(() => BRollItemObjectEqualityInputSchema).array() ]).optional(),
}).strict();

export const ShortScoreObjectEqualityInputSchema: z.ZodType<Prisma.ShortScoreObjectEqualityInput> = z.object({
  final_creative_score: z.number(),
  narrative_payoff_score: z.number(),
  hook_quality_score: z.number(),
  engagement_potential_score: z.number(),
  brand_alignment_score: z.number()
}).strict();

export const AsdRootObjectEqualityInputSchema: z.ZodType<Prisma.AsdRootObjectEqualityInput> = z.object({
  status: z.string(),
  success: z.boolean(),
  segment_id: z.number(),
  timestamp: z.string(),
  result: z.lazy(() => AsdResultObjectEqualityInputSchema),
  video_info: z.lazy(() => AsdVideoInfoObjectEqualityInputSchema),
  detection_count: z.number(),
  scenes_count: z.number(),
  has_rendered_video: z.boolean(),
  config: z.lazy(() => AsdConfigObjectEqualityInputSchema).optional().nullable()
}).strict();

export const BRollItemObjectEqualityInputSchema: z.ZodType<Prisma.BRollItemObjectEqualityInput> = z.object({
  id: z.string(),
  sentenceIds: z.number().array().optional(),
  fileUrl: z.string(),
  fileType: z.lazy(() => BRollFileTypeSchema),
  pexelsId: z.string().optional().nullable(),
  startTime: z.number(),
  endTime: z.number(),
  position: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional().nullable()
}).strict();

export const NestedEnumVideoStatusFilterSchema: z.ZodType<Prisma.NestedEnumVideoStatusFilter> = z.object({
  equals: z.lazy(() => VideoStatusSchema).optional(),
  in: z.lazy(() => VideoStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => NestedEnumVideoStatusFilterSchema) ]).optional(),
}).strict();

export const StatusHistoryWhereInputSchema: z.ZodType<Prisma.StatusHistoryWhereInput> = z.object({
  AND: z.union([ z.lazy(() => StatusHistoryWhereInputSchema),z.lazy(() => StatusHistoryWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => StatusHistoryWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => StatusHistoryWhereInputSchema),z.lazy(() => StatusHistoryWhereInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  startTime: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  endTime: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
}).strict();

export const PredefinedShortsOrderByCompositeAggregateInputSchema: z.ZodType<Prisma.PredefinedShortsOrderByCompositeAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const PredictionOrderByCompositeAggregateInputSchema: z.ZodType<Prisma.PredictionOrderByCompositeAggregateInput> = z.object({
  _count: z.lazy(() => SortOrderSchema).optional()
}).strict();

export const NestedEnumVideoStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumVideoStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => VideoStatusSchema).optional(),
  in: z.lazy(() => VideoStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => NestedEnumVideoStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumVideoStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumVideoStatusFilterSchema).optional()
}).strict();

export const NestedEnumVideoProjectStatusFilterSchema: z.ZodType<Prisma.NestedEnumVideoProjectStatusFilter> = z.object({
  equals: z.lazy(() => VideoProjectStatusSchema).optional(),
  in: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => NestedEnumVideoProjectStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumVideoProjectStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumVideoProjectStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => VideoProjectStatusSchema).optional(),
  in: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoProjectStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => NestedEnumVideoProjectStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumVideoProjectStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumVideoProjectStatusFilterSchema).optional()
}).strict();

export const NestedJsonNullableFilterSchema: z.ZodType<Prisma.NestedJsonNullableFilter> = z.object({
  equals: InputJsonValueSchema.optional().nullable(),
  not: InputJsonValueSchema.optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const NestedEnumVideoDesignStatusFilterSchema: z.ZodType<Prisma.NestedEnumVideoDesignStatusFilter> = z.object({
  equals: z.lazy(() => VideoDesignStatusSchema).optional(),
  in: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => NestedEnumVideoDesignStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumVideoDesignStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumVideoDesignStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => VideoDesignStatusSchema).optional(),
  in: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  notIn: z.lazy(() => VideoDesignStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => NestedEnumVideoDesignStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumVideoDesignStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumVideoDesignStatusFilterSchema).optional()
}).strict();

export const NestedEnumAppFileTypeFilterSchema: z.ZodType<Prisma.NestedEnumAppFileTypeFilter> = z.object({
  equals: z.lazy(() => AppFileTypeSchema).optional(),
  in: z.lazy(() => AppFileTypeSchema).array().optional(),
  notIn: z.lazy(() => AppFileTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => NestedEnumAppFileTypeFilterSchema) ]).optional(),
}).strict();

export const NestedEnumAppFileStatusFilterSchema: z.ZodType<Prisma.NestedEnumAppFileStatusFilter> = z.object({
  equals: z.lazy(() => AppFileStatusSchema).optional(),
  in: z.lazy(() => AppFileStatusSchema).array().optional(),
  notIn: z.lazy(() => AppFileStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => NestedEnumAppFileStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumAppFileTypeWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumAppFileTypeWithAggregatesFilter> = z.object({
  equals: z.lazy(() => AppFileTypeSchema).optional(),
  in: z.lazy(() => AppFileTypeSchema).array().optional(),
  notIn: z.lazy(() => AppFileTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => NestedEnumAppFileTypeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumAppFileTypeFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumAppFileTypeFilterSchema).optional()
}).strict();

export const NestedEnumAppFileStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumAppFileStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => AppFileStatusSchema).optional(),
  in: z.lazy(() => AppFileStatusSchema).array().optional(),
  notIn: z.lazy(() => AppFileStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => NestedEnumAppFileStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumAppFileStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumAppFileStatusFilterSchema).optional()
}).strict();

export const NestedEnumOrganizationSocialProviderFilterSchema: z.ZodType<Prisma.NestedEnumOrganizationSocialProviderFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  in: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => NestedEnumOrganizationSocialProviderFilterSchema) ]).optional(),
}).strict();

export const NestedEnumOrganizationSocialProviderWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumOrganizationSocialProviderWithAggregatesFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  in: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialProviderSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => NestedEnumOrganizationSocialProviderWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumOrganizationSocialProviderFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumOrganizationSocialProviderFilterSchema).optional()
}).strict();

export const NestedEnumOrganizationSocialPostStatusFilterSchema: z.ZodType<Prisma.NestedEnumOrganizationSocialPostStatusFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  in: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => NestedEnumOrganizationSocialPostStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumOrganizationSocialPostStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumOrganizationSocialPostStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  in: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  notIn: z.lazy(() => OrganizationSocialPostStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => NestedEnumOrganizationSocialPostStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumOrganizationSocialPostStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumOrganizationSocialPostStatusFilterSchema).optional()
}).strict();

export const NestedEnumRenderHistoryStatusFilterSchema: z.ZodType<Prisma.NestedEnumRenderHistoryStatusFilter> = z.object({
  equals: z.lazy(() => RenderHistoryStatusSchema).optional(),
  in: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  notIn: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => NestedEnumRenderHistoryStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumRenderHistoryStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumRenderHistoryStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => RenderHistoryStatusSchema).optional(),
  in: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  notIn: z.lazy(() => RenderHistoryStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => NestedEnumRenderHistoryStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumRenderHistoryStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumRenderHistoryStatusFilterSchema).optional()
}).strict();

export const NestedEnumExternalKeysStatusFilterSchema: z.ZodType<Prisma.NestedEnumExternalKeysStatusFilter> = z.object({
  equals: z.lazy(() => ExternalKeysStatusSchema).optional(),
  in: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => NestedEnumExternalKeysStatusFilterSchema) ]).optional(),
}).strict();

export const NestedEnumExternalKeysTypeFilterSchema: z.ZodType<Prisma.NestedEnumExternalKeysTypeFilter> = z.object({
  equals: z.lazy(() => ExternalKeysTypeSchema).optional(),
  in: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => NestedEnumExternalKeysTypeFilterSchema) ]).optional(),
}).strict();

export const NestedEnumExternalKeysStatusWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumExternalKeysStatusWithAggregatesFilter> = z.object({
  equals: z.lazy(() => ExternalKeysStatusSchema).optional(),
  in: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysStatusSchema),z.lazy(() => NestedEnumExternalKeysStatusWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumExternalKeysStatusFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumExternalKeysStatusFilterSchema).optional()
}).strict();

export const NestedEnumExternalKeysTypeWithAggregatesFilterSchema: z.ZodType<Prisma.NestedEnumExternalKeysTypeWithAggregatesFilter> = z.object({
  equals: z.lazy(() => ExternalKeysTypeSchema).optional(),
  in: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  notIn: z.lazy(() => ExternalKeysTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => ExternalKeysTypeSchema),z.lazy(() => NestedEnumExternalKeysTypeWithAggregatesFilterSchema) ]).optional(),
  _count: z.lazy(() => NestedIntFilterSchema).optional(),
  _min: z.lazy(() => NestedEnumExternalKeysTypeFilterSchema).optional(),
  _max: z.lazy(() => NestedEnumExternalKeysTypeFilterSchema).optional()
}).strict();

export const SessionCreateWithoutUserInputSchema: z.ZodType<Prisma.SessionCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  impersonatedBy: z.string().optional().nullable(),
  activeOrganizationId: z.string().optional().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const SessionUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.SessionUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  impersonatedBy: z.string().optional().nullable(),
  activeOrganizationId: z.string().optional().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const SessionCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.SessionCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => SessionWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => SessionCreateWithoutUserInputSchema),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const SessionCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.SessionCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => SessionCreateManyUserInputSchema),z.lazy(() => SessionCreateManyUserInputSchema).array() ]),
}).strict();

export const AccountCreateWithoutUserInputSchema: z.ZodType<Prisma.AccountCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  accountId: z.string(),
  providerId: z.string(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  password: z.string().optional().nullable(),
  accessTokenExpiresAt: z.coerce.date().optional().nullable(),
  refreshTokenExpiresAt: z.coerce.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const AccountUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.AccountUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  accountId: z.string(),
  providerId: z.string(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  password: z.string().optional().nullable(),
  accessTokenExpiresAt: z.coerce.date().optional().nullable(),
  refreshTokenExpiresAt: z.coerce.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const AccountCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.AccountCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => AccountWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AccountCreateWithoutUserInputSchema),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const AccountCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.AccountCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => AccountCreateManyUserInputSchema),z.lazy(() => AccountCreateManyUserInputSchema).array() ]),
}).strict();

export const ApiKeyCreateWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutApiKeysInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutKeyInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutKeyInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const ApiKeyCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.ApiKeyCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => ApiKeyCreateManyUserInputSchema),z.lazy(() => ApiKeyCreateManyUserInputSchema).array() ]),
}).strict();

export const PasskeyCreateWithoutUserInputSchema: z.ZodType<Prisma.PasskeyCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string().optional().nullable(),
  publicKey: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const PasskeyUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string().optional().nullable(),
  publicKey: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const PasskeyCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.PasskeyCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => PasskeyWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => PasskeyCreateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const PasskeyCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.PasskeyCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => PasskeyCreateManyUserInputSchema),z.lazy(() => PasskeyCreateManyUserInputSchema).array() ]),
}).strict();

export const InvitationCreateWithoutUserInputSchema: z.ZodType<Prisma.InvitationCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutInvitationsInputSchema)
}).strict();

export const InvitationUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.InvitationUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date()
}).strict();

export const InvitationCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.InvitationCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => InvitationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => InvitationCreateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const InvitationCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.InvitationCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => InvitationCreateManyUserInputSchema),z.lazy(() => InvitationCreateManyUserInputSchema).array() ]),
}).strict();

export const PurchaseCreateWithoutUserInputSchema: z.ZodType<Prisma.PurchaseCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutPurchasesInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutPurchaseInputSchema).optional()
}).strict();

export const PurchaseUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutPurchaseInputSchema).optional()
}).strict();

export const PurchaseCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.PurchaseCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => PurchaseCreateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const PurchaseCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.PurchaseCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => PurchaseCreateManyUserInputSchema),z.lazy(() => PurchaseCreateManyUserInputSchema).array() ]),
}).strict();

export const MemberCreateWithoutUserInputSchema: z.ZodType<Prisma.MemberCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  role: z.string(),
  createdAt: z.coerce.date(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutMembersInputSchema)
}).strict();

export const MemberUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.MemberUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date()
}).strict();

export const MemberCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.MemberCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => MemberWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => MemberCreateWithoutUserInputSchema),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const MemberCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.MemberCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => MemberCreateManyUserInputSchema),z.lazy(() => MemberCreateManyUserInputSchema).array() ]),
}).strict();

export const TwoFactorCreateWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  secret: z.string(),
  backupCodes: z.string()
}).strict();

export const TwoFactorUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  secret: z.string(),
  backupCodes: z.string()
}).strict();

export const TwoFactorCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => TwoFactorWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => TwoFactorCreateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const TwoFactorCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.TwoFactorCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => TwoFactorCreateManyUserInputSchema),z.lazy(() => TwoFactorCreateManyUserInputSchema).array() ]),
}).strict();

export const AiChatCreateWithoutUserInputSchema: z.ZodType<Prisma.AiChatCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutAiChatsInputSchema).optional()
}).strict();

export const AiChatUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.AiChatUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AiChatCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.AiChatCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => AiChatWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AiChatCreateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const AiChatCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.AiChatCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => AiChatCreateManyUserInputSchema),z.lazy(() => AiChatCreateManyUserInputSchema).array() ]),
}).strict();

export const AppFileCreateWithoutUserInputSchema: z.ZodType<Prisma.AppFileCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutFilesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutFilesInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  organizationId: z.string().optional().nullable(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.AppFileCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AppFileCreateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const AppFileCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.AppFileCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => AppFileCreateManyUserInputSchema),z.lazy(() => AppFileCreateManyUserInputSchema).array() ]),
}).strict();

export const RenderHistoryCreateWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutRenderHistoryInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutRenderHistoriesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutRenderHistoriesInputSchema),
  appFile: z.lazy(() => AppFileCreateNestedOneWithoutRenderHistoriesInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const RenderHistoryCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.RenderHistoryCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => RenderHistoryCreateManyUserInputSchema),z.lazy(() => RenderHistoryCreateManyUserInputSchema).array() ]),
}).strict();

export const JobCreateWithoutUserInputSchema: z.ZodType<Prisma.JobCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutUserInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutUserInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutUserInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutUserInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutUserInputSchema),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const JobCreateManyUserInputEnvelopeSchema: z.ZodType<Prisma.JobCreateManyUserInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => JobCreateManyUserInputSchema),z.lazy(() => JobCreateManyUserInputSchema).array() ]),
}).strict();

export const VideoProjectCreateWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectCreateWithoutCreatedByInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutVideoProjectInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutVideoProjectsInputSchema),
  design: z.lazy(() => VideoDesignCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectUncheckedCreateWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateWithoutCreatedByInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  design: z.lazy(() => VideoDesignUncheckedCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectCreateOrConnectWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectCreateOrConnectWithoutCreatedByInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema) ]),
}).strict();

export const VideoProjectCreateManyCreatedByInputEnvelopeSchema: z.ZodType<Prisma.VideoProjectCreateManyCreatedByInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => VideoProjectCreateManyCreatedByInputSchema),z.lazy(() => VideoProjectCreateManyCreatedByInputSchema).array() ]),
}).strict();

export const SessionUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.SessionUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => SessionWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => SessionUpdateWithoutUserInputSchema),z.lazy(() => SessionUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => SessionCreateWithoutUserInputSchema),z.lazy(() => SessionUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const SessionUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.SessionUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => SessionWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => SessionUpdateWithoutUserInputSchema),z.lazy(() => SessionUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const SessionUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.SessionUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => SessionScalarWhereInputSchema),
  data: z.union([ z.lazy(() => SessionUpdateManyMutationInputSchema),z.lazy(() => SessionUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const SessionScalarWhereInputSchema: z.ZodType<Prisma.SessionScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => SessionScalarWhereInputSchema),z.lazy(() => SessionScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => SessionScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => SessionScalarWhereInputSchema),z.lazy(() => SessionScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  ipAddress: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userAgent: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  impersonatedBy: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  activeOrganizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  token: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const AccountUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.AccountUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => AccountWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => AccountUpdateWithoutUserInputSchema),z.lazy(() => AccountUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => AccountCreateWithoutUserInputSchema),z.lazy(() => AccountUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const AccountUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.AccountUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => AccountWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => AccountUpdateWithoutUserInputSchema),z.lazy(() => AccountUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const AccountUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.AccountUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => AccountScalarWhereInputSchema),
  data: z.union([ z.lazy(() => AccountUpdateManyMutationInputSchema),z.lazy(() => AccountUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const AccountScalarWhereInputSchema: z.ZodType<Prisma.AccountScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AccountScalarWhereInputSchema),z.lazy(() => AccountScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AccountScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AccountScalarWhereInputSchema),z.lazy(() => AccountScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  accountId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  providerId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  accessToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  refreshToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  idToken: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  expiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  password: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  scope: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const ApiKeyUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => ApiKeyUpdateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const ApiKeyUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => ApiKeyUpdateWithoutUserInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const ApiKeyUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => ApiKeyScalarWhereInputSchema),
  data: z.union([ z.lazy(() => ApiKeyUpdateManyMutationInputSchema),z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const ApiKeyScalarWhereInputSchema: z.ZodType<Prisma.ApiKeyScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ApiKeyScalarWhereInputSchema),z.lazy(() => ApiKeyScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ApiKeyScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ApiKeyScalarWhereInputSchema),z.lazy(() => ApiKeyScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const PasskeyUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => PasskeyWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => PasskeyUpdateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => PasskeyCreateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const PasskeyUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => PasskeyWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => PasskeyUpdateWithoutUserInputSchema),z.lazy(() => PasskeyUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const PasskeyUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => PasskeyScalarWhereInputSchema),
  data: z.union([ z.lazy(() => PasskeyUpdateManyMutationInputSchema),z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const PasskeyScalarWhereInputSchema: z.ZodType<Prisma.PasskeyScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => PasskeyScalarWhereInputSchema),z.lazy(() => PasskeyScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PasskeyScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PasskeyScalarWhereInputSchema),z.lazy(() => PasskeyScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  publicKey: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  credentialID: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  counter: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  deviceType: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  backedUp: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  transports: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const InvitationUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.InvitationUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => InvitationWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => InvitationUpdateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => InvitationCreateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const InvitationUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.InvitationUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => InvitationWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => InvitationUpdateWithoutUserInputSchema),z.lazy(() => InvitationUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const InvitationUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.InvitationUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => InvitationScalarWhereInputSchema),
  data: z.union([ z.lazy(() => InvitationUpdateManyMutationInputSchema),z.lazy(() => InvitationUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const InvitationScalarWhereInputSchema: z.ZodType<Prisma.InvitationScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => InvitationScalarWhereInputSchema),z.lazy(() => InvitationScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => InvitationScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => InvitationScalarWhereInputSchema),z.lazy(() => InvitationScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  email: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  status: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  expiresAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  inviterId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
}).strict();

export const PurchaseUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => PurchaseUpdateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => PurchaseCreateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const PurchaseUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => PurchaseUpdateWithoutUserInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const PurchaseUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => PurchaseScalarWhereInputSchema),
  data: z.union([ z.lazy(() => PurchaseUpdateManyMutationInputSchema),z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const PurchaseScalarWhereInputSchema: z.ZodType<Prisma.PurchaseScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => PurchaseScalarWhereInputSchema),z.lazy(() => PurchaseScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PurchaseScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PurchaseScalarWhereInputSchema),z.lazy(() => PurchaseScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumPurchaseTypeFilterSchema),z.lazy(() => PurchaseTypeSchema) ]).optional(),
  customerId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  subscriptionId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  credit: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  productId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  startDate: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const MemberUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.MemberUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => MemberWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => MemberUpdateWithoutUserInputSchema),z.lazy(() => MemberUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => MemberCreateWithoutUserInputSchema),z.lazy(() => MemberUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const MemberUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.MemberUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => MemberWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => MemberUpdateWithoutUserInputSchema),z.lazy(() => MemberUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const MemberUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.MemberUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => MemberScalarWhereInputSchema),
  data: z.union([ z.lazy(() => MemberUpdateManyMutationInputSchema),z.lazy(() => MemberUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const MemberScalarWhereInputSchema: z.ZodType<Prisma.MemberScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => MemberScalarWhereInputSchema),z.lazy(() => MemberScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => MemberScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => MemberScalarWhereInputSchema),z.lazy(() => MemberScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  role: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const TwoFactorUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => TwoFactorWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => TwoFactorUpdateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => TwoFactorCreateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const TwoFactorUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => TwoFactorWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => TwoFactorUpdateWithoutUserInputSchema),z.lazy(() => TwoFactorUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const TwoFactorUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => TwoFactorScalarWhereInputSchema),
  data: z.union([ z.lazy(() => TwoFactorUpdateManyMutationInputSchema),z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const TwoFactorScalarWhereInputSchema: z.ZodType<Prisma.TwoFactorScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => TwoFactorScalarWhereInputSchema),z.lazy(() => TwoFactorScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => TwoFactorScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => TwoFactorScalarWhereInputSchema),z.lazy(() => TwoFactorScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  secret: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  backupCodes: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
}).strict();

export const AiChatUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.AiChatUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => AiChatWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => AiChatUpdateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => AiChatCreateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const AiChatUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.AiChatUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => AiChatWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => AiChatUpdateWithoutUserInputSchema),z.lazy(() => AiChatUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const AiChatUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.AiChatUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => AiChatScalarWhereInputSchema),
  data: z.union([ z.lazy(() => AiChatUpdateManyMutationInputSchema),z.lazy(() => AiChatUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const AiChatScalarWhereInputSchema: z.ZodType<Prisma.AiChatScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AiChatScalarWhereInputSchema),z.lazy(() => AiChatScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AiChatScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AiChatScalarWhereInputSchema),z.lazy(() => AiChatScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  title: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  messages: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const AppFileUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.AppFileUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => AppFileUpdateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => AppFileCreateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const AppFileUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.AppFileUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => AppFileUpdateWithoutUserInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const AppFileUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.AppFileUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => AppFileScalarWhereInputSchema),
  data: z.union([ z.lazy(() => AppFileUpdateManyMutationInputSchema),z.lazy(() => AppFileUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const AppFileScalarWhereInputSchema: z.ZodType<Prisma.AppFileScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AppFileScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AppFileScalarWhereInputSchema),z.lazy(() => AppFileScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  type: z.union([ z.lazy(() => EnumAppFileTypeFilterSchema),z.lazy(() => AppFileTypeSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumAppFileStatusFilterSchema),z.lazy(() => AppFileStatusSchema) ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  tags: z.lazy(() => StringNullableListFilterSchema).optional(),
  size: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  length: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  key: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  thumbnailUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const RenderHistoryUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const RenderHistoryUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateWithoutUserInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const RenderHistoryUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => RenderHistoryScalarWhereInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateManyMutationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const RenderHistoryScalarWhereInputSchema: z.ZodType<Prisma.RenderHistoryScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => RenderHistoryScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => RenderHistoryScalarWhereInputSchema),z.lazy(() => RenderHistoryScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  renderId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  userId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  keyId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  appFileId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  target: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  data: z.lazy(() => JsonFilterSchema).optional(),
  fileUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  fileSize: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  status: z.union([ z.lazy(() => EnumRenderHistoryStatusFilterSchema),z.lazy(() => RenderHistoryStatusSchema) ]).optional(),
  errorMessage: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  priority: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  retryCount: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  maxRetries: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  webhookReceived: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  webhookReceivedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const JobUpsertWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.JobUpsertWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => JobUpdateWithoutUserInputSchema),z.lazy(() => JobUncheckedUpdateWithoutUserInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutUserInputSchema),z.lazy(() => JobUncheckedCreateWithoutUserInputSchema) ]),
}).strict();

export const JobUpdateWithWhereUniqueWithoutUserInputSchema: z.ZodType<Prisma.JobUpdateWithWhereUniqueWithoutUserInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => JobUpdateWithoutUserInputSchema),z.lazy(() => JobUncheckedUpdateWithoutUserInputSchema) ]),
}).strict();

export const JobUpdateManyWithWhereWithoutUserInputSchema: z.ZodType<Prisma.JobUpdateManyWithWhereWithoutUserInput> = z.object({
  where: z.lazy(() => JobScalarWhereInputSchema),
  data: z.union([ z.lazy(() => JobUpdateManyMutationInputSchema),z.lazy(() => JobUncheckedUpdateManyWithoutUserInputSchema) ]),
}).strict();

export const JobScalarWhereInputSchema: z.ZodType<Prisma.JobScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => JobScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => JobScalarWhereInputSchema),z.lazy(() => JobScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  userId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  keyId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  purchaseId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  cost: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  creditUsageStatus: z.union([ z.lazy(() => EnumCreditUsageStatusFilterSchema),z.lazy(() => CreditUsageStatusSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumJobStatusNullableFilterSchema),z.lazy(() => JobStatusSchema) ]).optional().nullable(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  functionName: z.union([ z.lazy(() => EnumJobFunctionNameFilterSchema),z.lazy(() => JobFunctionNameSchema) ]).optional(),
  source: z.union([ z.lazy(() => EnumJobSourceFilterSchema),z.lazy(() => JobSourceSchema) ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  startTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  endTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  archivedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
}).strict();

export const VideoProjectUpsertWithWhereUniqueWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUpsertWithWhereUniqueWithoutCreatedByInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => VideoProjectUpdateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutCreatedByInputSchema) ]),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutCreatedByInputSchema) ]),
}).strict();

export const VideoProjectUpdateWithWhereUniqueWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUpdateWithWhereUniqueWithoutCreatedByInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => VideoProjectUpdateWithoutCreatedByInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutCreatedByInputSchema) ]),
}).strict();

export const VideoProjectUpdateManyWithWhereWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUpdateManyWithWhereWithoutCreatedByInput> = z.object({
  where: z.lazy(() => VideoProjectScalarWhereInputSchema),
  data: z.union([ z.lazy(() => VideoProjectUpdateManyMutationInputSchema),z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByInputSchema) ]),
}).strict();

export const VideoProjectScalarWhereInputSchema: z.ZodType<Prisma.VideoProjectScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => VideoProjectScalarWhereInputSchema),z.lazy(() => VideoProjectScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => VideoProjectScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => VideoProjectScalarWhereInputSchema),z.lazy(() => VideoProjectScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  createdByUserId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  status: z.union([ z.lazy(() => EnumVideoProjectStatusFilterSchema),z.lazy(() => VideoProjectStatusSchema) ]).optional(),
  inputBrief: z.lazy(() => JsonFilterSchema).optional(),
  document: z.lazy(() => JsonNullableFilterSchema).optional(),
  validation: z.lazy(() => JsonNullableFilterSchema).optional(),
  errorMessage: z.lazy(() => StringNullableListFilterSchema).optional(),
  cost: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishTag: z.lazy(() => JsonNullableFilterSchema).optional(),
  episodeSummary: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  isDeleted: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
}).strict();

export const UserCreateWithoutSessionsInputSchema: z.ZodType<Prisma.UserCreateWithoutSessionsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutSessionsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutSessionsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutSessionsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutSessionsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedCreateWithoutSessionsInputSchema) ]),
}).strict();

export const UserUpsertWithoutSessionsInputSchema: z.ZodType<Prisma.UserUpsertWithoutSessionsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutSessionsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedCreateWithoutSessionsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutSessionsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutSessionsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutSessionsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutSessionsInputSchema) ]),
}).strict();

export const UserUpdateWithoutSessionsInputSchema: z.ZodType<Prisma.UserUpdateWithoutSessionsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutSessionsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutSessionsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserCreateWithoutAccountsInputSchema: z.ZodType<Prisma.UserCreateWithoutAccountsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutAccountsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutAccountsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutAccountsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutAccountsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAccountsInputSchema) ]),
}).strict();

export const UserUpsertWithoutAccountsInputSchema: z.ZodType<Prisma.UserUpsertWithoutAccountsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutAccountsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAccountsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutAccountsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutAccountsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutAccountsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutAccountsInputSchema) ]),
}).strict();

export const UserUpdateWithoutAccountsInputSchema: z.ZodType<Prisma.UserUpdateWithoutAccountsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutAccountsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutAccountsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserCreateWithoutPasskeysInputSchema: z.ZodType<Prisma.UserCreateWithoutPasskeysInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutPasskeysInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutPasskeysInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutPasskeysInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutPasskeysInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutPasskeysInputSchema) ]),
}).strict();

export const UserUpsertWithoutPasskeysInputSchema: z.ZodType<Prisma.UserUpsertWithoutPasskeysInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedUpdateWithoutPasskeysInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutPasskeysInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutPasskeysInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutPasskeysInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutPasskeysInputSchema),z.lazy(() => UserUncheckedUpdateWithoutPasskeysInputSchema) ]),
}).strict();

export const UserUpdateWithoutPasskeysInputSchema: z.ZodType<Prisma.UserUpdateWithoutPasskeysInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutPasskeysInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutPasskeysInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserCreateWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserCreateWithoutTwofactorsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutTwofactorsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutTwofactorsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedCreateWithoutTwofactorsInputSchema) ]),
}).strict();

export const UserUpsertWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserUpsertWithoutTwofactorsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutTwofactorsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedCreateWithoutTwofactorsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutTwofactorsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutTwofactorsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutTwofactorsInputSchema) ]),
}).strict();

export const UserUpdateWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserUpdateWithoutTwofactorsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutTwofactorsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutTwofactorsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const MemberCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  role: z.string(),
  createdAt: z.coerce.date(),
  user: z.lazy(() => UserCreateNestedOneWithoutMembersInputSchema)
}).strict();

export const MemberUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date()
}).strict();

export const MemberCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => MemberWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => MemberCreateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const MemberCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.MemberCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => MemberCreateManyOrganizationInputSchema),z.lazy(() => MemberCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const ApiKeyCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutApiKeysInputSchema),
  jobs: z.lazy(() => JobCreateNestedManyWithoutKeyInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutKeyInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const ApiKeyCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.ApiKeyCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => ApiKeyCreateManyOrganizationInputSchema),z.lazy(() => ApiKeyCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const InvitationCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  user: z.lazy(() => UserCreateNestedOneWithoutInvitationsInputSchema)
}).strict();

export const InvitationUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  inviterId: z.string()
}).strict();

export const InvitationCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => InvitationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => InvitationCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const InvitationCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.InvitationCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => InvitationCreateManyOrganizationInputSchema),z.lazy(() => InvitationCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const PurchaseCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutPurchasesInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutPurchaseInputSchema).optional()
}).strict();

export const PurchaseUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutPurchaseInputSchema).optional()
}).strict();

export const PurchaseCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const PurchaseCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.PurchaseCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => PurchaseCreateManyOrganizationInputSchema),z.lazy(() => PurchaseCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const AiChatCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutAiChatsInputSchema).optional()
}).strict();

export const AiChatUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AiChatCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AiChatWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AiChatCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const AiChatCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.AiChatCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => AiChatCreateManyOrganizationInputSchema),z.lazy(() => AiChatCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const AppFileCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutFilesInputSchema),
  job: z.lazy(() => JobCreateNestedOneWithoutFilesInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AppFileCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const AppFileCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.AppFileCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => AppFileCreateManyOrganizationInputSchema),z.lazy(() => AppFileCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const CaptionStyleCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  style: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CaptionStyleUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  style: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CaptionStyleCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => CaptionStyleWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const CaptionStyleCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.CaptionStyleCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => CaptionStyleCreateManyOrganizationInputSchema),z.lazy(() => CaptionStyleCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const OrganizationBrandingCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  preset: z.string().optional().nullable(),
  size: InputJsonValueSchema.optional().nullable(),
  options: InputJsonValueSchema.optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  preset: z.string().optional().nullable(),
  size: InputJsonValueSchema.optional().nullable(),
  options: InputJsonValueSchema.optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationBrandingCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationBrandingCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.OrganizationBrandingCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => OrganizationBrandingCreateManyOrganizationInputSchema),z.lazy(() => OrganizationBrandingCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const OrganizationNarratorProfileCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  personality: z.string(),
  styleNotes: z.string().optional().nullable(),
  ttsStyle: z.string().optional(),
  ttsPace: z.string().optional(),
  ttsAccent: z.string().optional(),
  ttsNotes: z.string().optional().nullable(),
  channelName: z.string().optional().nullable(),
  channelSignOff: z.string().optional().nullable(),
  overlayText: z.string().optional().nullable(),
  overlayTextDelayMs: z.number().int().optional().nullable(),
  overlayTextDurationMs: z.number().int().optional().nullable(),
  visualDefaults: InputJsonValueSchema.optional().nullable(),
  speechSpeed: z.number().optional(),
  avatarFileAppId: z.string().optional().nullable(),
  avatarPhotos: InputJsonValueSchema.optional().nullable(),
  avatarMinDurationMs: z.number().int().optional().nullable(),
  avatarMaxDurationMs: z.number().int().optional().nullable(),
  avatarAppearEveryMs: z.number().int().optional().nullable(),
  avatarIntroHoldMs: z.number().int().optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  personality: z.string(),
  styleNotes: z.string().optional().nullable(),
  ttsStyle: z.string().optional(),
  ttsPace: z.string().optional(),
  ttsAccent: z.string().optional(),
  ttsNotes: z.string().optional().nullable(),
  channelName: z.string().optional().nullable(),
  channelSignOff: z.string().optional().nullable(),
  overlayText: z.string().optional().nullable(),
  overlayTextDelayMs: z.number().int().optional().nullable(),
  overlayTextDurationMs: z.number().int().optional().nullable(),
  visualDefaults: InputJsonValueSchema.optional().nullable(),
  speechSpeed: z.number().optional(),
  avatarFileAppId: z.string().optional().nullable(),
  avatarPhotos: InputJsonValueSchema.optional().nullable(),
  avatarMinDurationMs: z.number().int().optional().nullable(),
  avatarMaxDurationMs: z.number().int().optional().nullable(),
  avatarAppearEveryMs: z.number().int().optional().nullable(),
  avatarIntroHoldMs: z.number().int().optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationNarratorProfileCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => OrganizationNarratorProfileCreateManyOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const OrganizationSocialSettingsCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  providerProfileId: z.string().optional().nullable(),
  isEnabled: z.boolean().optional(),
  approveApiPosts: z.boolean().optional(),
  defaultTimezone: z.string().optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsCreatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: InputJsonValueSchema.optional(),
  maxPostsPerSlot: z.number().int().optional(),
  maxShortsOfSameVideo: z.number().int().optional(),
  maxShortsPerWeek: z.number().int().optional(),
  minimumVideoDurationSeconds: z.number().int().optional(),
  processOnlyPlaylistId: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  providerProfileId: z.string().optional().nullable(),
  isEnabled: z.boolean().optional(),
  approveApiPosts: z.boolean().optional(),
  defaultTimezone: z.string().optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsCreatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: InputJsonValueSchema.optional(),
  maxPostsPerSlot: z.number().int().optional(),
  maxShortsOfSameVideo: z.number().int().optional(),
  maxShortsPerWeek: z.number().int().optional(),
  minimumVideoDurationSeconds: z.number().int().optional(),
  processOnlyPlaylistId: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialSettingsCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationSocialSettingsWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationSocialSettingsCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationSocialPostCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutSocialPostsInputSchema).optional()
}).strict();

export const OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string().optional().nullable(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialPostCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationSocialPostCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.OrganizationSocialPostCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => OrganizationSocialPostCreateManyOrganizationInputSchema),z.lazy(() => OrganizationSocialPostCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const RenderHistoryCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutRenderHistoryInputSchema).optional(),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutRenderHistoriesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutRenderHistoriesInputSchema),
  appFile: z.lazy(() => AppFileCreateNestedOneWithoutRenderHistoriesInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const RenderHistoryCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.RenderHistoryCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => RenderHistoryCreateManyOrganizationInputSchema),z.lazy(() => RenderHistoryCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const JobCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.JobCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const JobCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.JobCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => JobCreateManyOrganizationInputSchema),z.lazy(() => JobCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const VideoProjectCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutVideoProjectInputSchema),
  createdBy: z.lazy(() => UserCreateNestedOneWithoutVideoProjectsInputSchema),
  design: z.lazy(() => VideoDesignCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectUncheckedCreateWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateWithoutOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  design: z.lazy(() => VideoDesignUncheckedCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectCreateOrConnectWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectCreateOrConnectWithoutOrganizationInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const VideoProjectCreateManyOrganizationInputEnvelopeSchema: z.ZodType<Prisma.VideoProjectCreateManyOrganizationInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => VideoProjectCreateManyOrganizationInputSchema),z.lazy(() => VideoProjectCreateManyOrganizationInputSchema).array() ]),
}).strict();

export const MemberUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => MemberWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => MemberUpdateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => MemberCreateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const MemberUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => MemberWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => MemberUpdateWithoutOrganizationInputSchema),z.lazy(() => MemberUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const MemberUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => MemberScalarWhereInputSchema),
  data: z.union([ z.lazy(() => MemberUpdateManyMutationInputSchema),z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const ApiKeyUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => ApiKeyUpdateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const ApiKeyUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => ApiKeyUpdateWithoutOrganizationInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const ApiKeyUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => ApiKeyScalarWhereInputSchema),
  data: z.union([ z.lazy(() => ApiKeyUpdateManyMutationInputSchema),z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const InvitationUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => InvitationWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => InvitationUpdateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => InvitationCreateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const InvitationUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => InvitationWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => InvitationUpdateWithoutOrganizationInputSchema),z.lazy(() => InvitationUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const InvitationUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => InvitationScalarWhereInputSchema),
  data: z.union([ z.lazy(() => InvitationUpdateManyMutationInputSchema),z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const PurchaseUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => PurchaseUpdateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => PurchaseCreateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const PurchaseUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => PurchaseUpdateWithoutOrganizationInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const PurchaseUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => PurchaseScalarWhereInputSchema),
  data: z.union([ z.lazy(() => PurchaseUpdateManyMutationInputSchema),z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const AiChatUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AiChatWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => AiChatUpdateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => AiChatCreateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const AiChatUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AiChatWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => AiChatUpdateWithoutOrganizationInputSchema),z.lazy(() => AiChatUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const AiChatUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AiChatScalarWhereInputSchema),
  data: z.union([ z.lazy(() => AiChatUpdateManyMutationInputSchema),z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const AppFileUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => AppFileUpdateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => AppFileCreateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const AppFileUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => AppFileUpdateWithoutOrganizationInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const AppFileUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => AppFileScalarWhereInputSchema),
  data: z.union([ z.lazy(() => AppFileUpdateManyMutationInputSchema),z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const CaptionStyleUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => CaptionStyleWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => CaptionStyleUpdateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => CaptionStyleCreateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const CaptionStyleUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => CaptionStyleWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => CaptionStyleUpdateWithoutOrganizationInputSchema),z.lazy(() => CaptionStyleUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const CaptionStyleUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => CaptionStyleScalarWhereInputSchema),
  data: z.union([ z.lazy(() => CaptionStyleUpdateManyMutationInputSchema),z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const CaptionStyleScalarWhereInputSchema: z.ZodType<Prisma.CaptionStyleScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => CaptionStyleScalarWhereInputSchema),z.lazy(() => CaptionStyleScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CaptionStyleScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CaptionStyleScalarWhereInputSchema),z.lazy(() => CaptionStyleScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  style: z.lazy(() => JsonFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationBrandingUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => OrganizationBrandingUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationBrandingCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationBrandingUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationBrandingWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => OrganizationBrandingUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationBrandingUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationBrandingUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationBrandingScalarWhereInputSchema),
  data: z.union([ z.lazy(() => OrganizationBrandingUpdateManyMutationInputSchema),z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationBrandingScalarWhereInputSchema: z.ZodType<Prisma.OrganizationBrandingScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationBrandingScalarWhereInputSchema),z.lazy(() => OrganizationBrandingScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationBrandingScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationBrandingScalarWhereInputSchema),z.lazy(() => OrganizationBrandingScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  preset: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  size: z.lazy(() => JsonNullableFilterSchema).optional(),
  options: z.lazy(() => JsonNullableFilterSchema).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationNarratorProfileUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationNarratorProfileCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationNarratorProfileUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationNarratorProfileWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationNarratorProfileUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema),
  data: z.union([ z.lazy(() => OrganizationNarratorProfileUpdateManyMutationInputSchema),z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationNarratorProfileScalarWhereInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema),z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema),z.lazy(() => OrganizationNarratorProfileScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  description: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  narratorName: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  gender: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  provider: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  voiceId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  personality: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  styleNotes: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  ttsStyle: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsPace: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsAccent: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  ttsNotes: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  channelName: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  channelSignOff: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  overlayText: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  visualDefaults: z.lazy(() => JsonNullableFilterSchema).optional(),
  speechSpeed: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  avatarFileAppId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  avatarPhotos: z.lazy(() => JsonNullableFilterSchema).optional(),
  avatarMinDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  isDefault: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const OrganizationSocialSettingsUpsertWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpsertWithoutOrganizationInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationSocialSettingsUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationSocialSettingsCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedCreateWithoutOrganizationInputSchema) ]),
  where: z.lazy(() => OrganizationSocialSettingsWhereInputSchema).optional()
}).strict();

export const OrganizationSocialSettingsUpdateToOneWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdateToOneWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationSocialSettingsWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationSocialSettingsUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialSettingsUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationSocialSettingsUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdateWithoutOrganizationInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  providerProfileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isEnabled: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  approveApiPosts: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  defaultTimezone: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  maxPostsPerSlot: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsOfSameVideo: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsPerWeek: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  processOnlyPlaylistId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialSettingsUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialSettingsUncheckedUpdateWithoutOrganizationInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  providerProfileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isEnabled: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  approveApiPosts: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  defaultTimezone: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  defaultPlatforms: z.union([ z.lazy(() => OrganizationSocialSettingsUpdatedefaultPlatformsInputSchema),z.string().array() ]).optional(),
  queueSlots: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  maxPostsPerSlot: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsOfSameVideo: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxShortsPerWeek: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  minimumVideoDurationSeconds: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  processOnlyPlaylistId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationSocialPostUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithoutOrganizationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationSocialPostUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),
  data: z.union([ z.lazy(() => OrganizationSocialPostUpdateManyMutationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationSocialPostScalarWhereInputSchema: z.ZodType<Prisma.OrganizationSocialPostScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),z.lazy(() => OrganizationSocialPostScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  organizationId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  jobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  provider: z.union([ z.lazy(() => EnumOrganizationSocialProviderFilterSchema),z.lazy(() => OrganizationSocialProviderSchema) ]).optional(),
  status: z.union([ z.lazy(() => EnumOrganizationSocialPostStatusFilterSchema),z.lazy(() => OrganizationSocialPostStatusSchema) ]).optional(),
  providerJobId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  platforms: z.lazy(() => StringNullableListFilterSchema).optional(),
  title: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  caption: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  videoUrl: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  scheduledFor: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  publishedAt: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  lastError: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  metadata: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const RenderHistoryUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const RenderHistoryUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateWithoutOrganizationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const RenderHistoryUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => RenderHistoryScalarWhereInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateManyMutationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const JobUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => JobUpdateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const JobUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => JobUpdateWithoutOrganizationInputSchema),z.lazy(() => JobUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const JobUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => JobScalarWhereInputSchema),
  data: z.union([ z.lazy(() => JobUpdateManyMutationInputSchema),z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const VideoProjectUpsertWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUpsertWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => VideoProjectUpdateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutOrganizationInputSchema) ]),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutOrganizationInputSchema) ]),
}).strict();

export const VideoProjectUpdateWithWhereUniqueWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUpdateWithWhereUniqueWithoutOrganizationInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => VideoProjectUpdateWithoutOrganizationInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutOrganizationInputSchema) ]),
}).strict();

export const VideoProjectUpdateManyWithWhereWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUpdateManyWithWhereWithoutOrganizationInput> = z.object({
  where: z.lazy(() => VideoProjectScalarWhereInputSchema),
  data: z.union([ z.lazy(() => VideoProjectUpdateManyMutationInputSchema),z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutMembersInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutMembersInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutMembersInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutMembersInputSchema) ]),
}).strict();

export const UserCreateWithoutMembersInputSchema: z.ZodType<Prisma.UserCreateWithoutMembersInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutMembersInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutMembersInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutMembersInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutMembersInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutMembersInputSchema),z.lazy(() => UserUncheckedCreateWithoutMembersInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutMembersInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutMembersInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutMembersInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutMembersInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutMembersInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutMembersInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutMembersInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutMembersInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutMembersInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const UserUpsertWithoutMembersInputSchema: z.ZodType<Prisma.UserUpsertWithoutMembersInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutMembersInputSchema),z.lazy(() => UserUncheckedUpdateWithoutMembersInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutMembersInputSchema),z.lazy(() => UserUncheckedCreateWithoutMembersInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutMembersInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutMembersInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutMembersInputSchema),z.lazy(() => UserUncheckedUpdateWithoutMembersInputSchema) ]),
}).strict();

export const UserUpdateWithoutMembersInputSchema: z.ZodType<Prisma.UserUpdateWithoutMembersInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutMembersInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutMembersInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutInvitationsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutInvitationsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutInvitationsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutInvitationsInputSchema) ]),
}).strict();

export const UserCreateWithoutInvitationsInputSchema: z.ZodType<Prisma.UserCreateWithoutInvitationsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutInvitationsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutInvitationsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutInvitationsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutInvitationsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedCreateWithoutInvitationsInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutInvitationsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutInvitationsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutInvitationsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutInvitationsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutInvitationsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutInvitationsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutInvitationsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutInvitationsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutInvitationsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const UserUpsertWithoutInvitationsInputSchema: z.ZodType<Prisma.UserUpsertWithoutInvitationsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutInvitationsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedCreateWithoutInvitationsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutInvitationsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutInvitationsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutInvitationsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutInvitationsInputSchema) ]),
}).strict();

export const UserUpdateWithoutInvitationsInputSchema: z.ZodType<Prisma.UserUpdateWithoutInvitationsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutInvitationsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutInvitationsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutPurchasesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutPurchasesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutPurchasesInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutPurchasesInputSchema) ]),
}).strict();

export const UserCreateWithoutPurchasesInputSchema: z.ZodType<Prisma.UserCreateWithoutPurchasesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutPurchasesInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutPurchasesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutPurchasesInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutPurchasesInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedCreateWithoutPurchasesInputSchema) ]),
}).strict();

export const JobCreateWithoutPurchaseInputSchema: z.ZodType<Prisma.JobCreateWithoutPurchaseInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutPurchaseInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutPurchaseInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutPurchaseInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema) ]),
}).strict();

export const JobCreateManyPurchaseInputEnvelopeSchema: z.ZodType<Prisma.JobCreateManyPurchaseInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => JobCreateManyPurchaseInputSchema),z.lazy(() => JobCreateManyPurchaseInputSchema).array() ]),
}).strict();

export const OrganizationUpsertWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutPurchasesInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutPurchasesInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutPurchasesInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutPurchasesInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutPurchasesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutPurchasesInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutPurchasesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutPurchasesInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutPurchasesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const UserUpsertWithoutPurchasesInputSchema: z.ZodType<Prisma.UserUpsertWithoutPurchasesInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedUpdateWithoutPurchasesInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedCreateWithoutPurchasesInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutPurchasesInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutPurchasesInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutPurchasesInputSchema),z.lazy(() => UserUncheckedUpdateWithoutPurchasesInputSchema) ]),
}).strict();

export const UserUpdateWithoutPurchasesInputSchema: z.ZodType<Prisma.UserUpdateWithoutPurchasesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutPurchasesInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutPurchasesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const JobUpsertWithWhereUniqueWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUpsertWithWhereUniqueWithoutPurchaseInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => JobUpdateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedUpdateWithoutPurchaseInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedCreateWithoutPurchaseInputSchema) ]),
}).strict();

export const JobUpdateWithWhereUniqueWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUpdateWithWhereUniqueWithoutPurchaseInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => JobUpdateWithoutPurchaseInputSchema),z.lazy(() => JobUncheckedUpdateWithoutPurchaseInputSchema) ]),
}).strict();

export const JobUpdateManyWithWhereWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUpdateManyWithWhereWithoutPurchaseInput> = z.object({
  where: z.lazy(() => JobScalarWhereInputSchema),
  data: z.union([ z.lazy(() => JobUpdateManyMutationInputSchema),z.lazy(() => JobUncheckedUpdateManyWithoutPurchaseInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutAiChatsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutAiChatsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutAiChatsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutAiChatsInputSchema) ]),
}).strict();

export const UserCreateWithoutAiChatsInputSchema: z.ZodType<Prisma.UserCreateWithoutAiChatsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutAiChatsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutAiChatsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutAiChatsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutAiChatsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAiChatsInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutAiChatsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutAiChatsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutAiChatsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutAiChatsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutAiChatsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutAiChatsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutAiChatsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutAiChatsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutAiChatsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const UserUpsertWithoutAiChatsInputSchema: z.ZodType<Prisma.UserUpsertWithoutAiChatsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutAiChatsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedCreateWithoutAiChatsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutAiChatsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutAiChatsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutAiChatsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutAiChatsInputSchema) ]),
}).strict();

export const UserUpdateWithoutAiChatsInputSchema: z.ZodType<Prisma.UserUpdateWithoutAiChatsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutAiChatsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutAiChatsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserCreateWithoutApiKeysInputSchema: z.ZodType<Prisma.UserCreateWithoutApiKeysInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutApiKeysInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutApiKeysInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutApiKeysInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutApiKeysInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutApiKeysInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutApiKeysInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutApiKeysInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutApiKeysInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutApiKeysInputSchema) ]),
}).strict();

export const JobCreateWithoutKeyInputSchema: z.ZodType<Prisma.JobCreateWithoutKeyInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutKeyInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutKeyInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutKeyInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutKeyInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutKeyInputSchema),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema) ]),
}).strict();

export const JobCreateManyKeyInputEnvelopeSchema: z.ZodType<Prisma.JobCreateManyKeyInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => JobCreateManyKeyInputSchema),z.lazy(() => JobCreateManyKeyInputSchema).array() ]),
}).strict();

export const RenderHistoryCreateWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryCreateWithoutKeyInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutRenderHistoryInputSchema),
  user: z.lazy(() => UserCreateNestedOneWithoutRenderHistoryInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutRenderHistoriesInputSchema),
  appFile: z.lazy(() => AppFileCreateNestedOneWithoutRenderHistoriesInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedCreateWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateWithoutKeyInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateOrConnectWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryCreateOrConnectWithoutKeyInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema) ]),
}).strict();

export const RenderHistoryCreateManyKeyInputEnvelopeSchema: z.ZodType<Prisma.RenderHistoryCreateManyKeyInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => RenderHistoryCreateManyKeyInputSchema),z.lazy(() => RenderHistoryCreateManyKeyInputSchema).array() ]),
}).strict();

export const UserUpsertWithoutApiKeysInputSchema: z.ZodType<Prisma.UserUpsertWithoutApiKeysInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedUpdateWithoutApiKeysInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedCreateWithoutApiKeysInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutApiKeysInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutApiKeysInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutApiKeysInputSchema),z.lazy(() => UserUncheckedUpdateWithoutApiKeysInputSchema) ]),
}).strict();

export const UserUpdateWithoutApiKeysInputSchema: z.ZodType<Prisma.UserUpdateWithoutApiKeysInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutApiKeysInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutApiKeysInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const OrganizationUpsertWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutApiKeysInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutApiKeysInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutApiKeysInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutApiKeysInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutApiKeysInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutApiKeysInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutApiKeysInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutApiKeysInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutApiKeysInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const JobUpsertWithWhereUniqueWithoutKeyInputSchema: z.ZodType<Prisma.JobUpsertWithWhereUniqueWithoutKeyInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => JobUpdateWithoutKeyInputSchema),z.lazy(() => JobUncheckedUpdateWithoutKeyInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutKeyInputSchema),z.lazy(() => JobUncheckedCreateWithoutKeyInputSchema) ]),
}).strict();

export const JobUpdateWithWhereUniqueWithoutKeyInputSchema: z.ZodType<Prisma.JobUpdateWithWhereUniqueWithoutKeyInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => JobUpdateWithoutKeyInputSchema),z.lazy(() => JobUncheckedUpdateWithoutKeyInputSchema) ]),
}).strict();

export const JobUpdateManyWithWhereWithoutKeyInputSchema: z.ZodType<Prisma.JobUpdateManyWithWhereWithoutKeyInput> = z.object({
  where: z.lazy(() => JobScalarWhereInputSchema),
  data: z.union([ z.lazy(() => JobUpdateManyMutationInputSchema),z.lazy(() => JobUncheckedUpdateManyWithoutKeyInputSchema) ]),
}).strict();

export const RenderHistoryUpsertWithWhereUniqueWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUpsertWithWhereUniqueWithoutKeyInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutKeyInputSchema) ]),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutKeyInputSchema) ]),
}).strict();

export const RenderHistoryUpdateWithWhereUniqueWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithWhereUniqueWithoutKeyInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateWithoutKeyInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutKeyInputSchema) ]),
}).strict();

export const RenderHistoryUpdateManyWithWhereWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithWhereWithoutKeyInput> = z.object({
  where: z.lazy(() => RenderHistoryScalarWhereInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateManyMutationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutKeyInputSchema) ]),
}).strict();

export const UserCreateWithoutJobsInputSchema: z.ZodType<Prisma.UserCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutJobsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutJobsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutJobsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutJobsInputSchema),z.lazy(() => UserUncheckedCreateWithoutJobsInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutJobsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutJobsInputSchema) ]),
}).strict();

export const ApiKeyCreateWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutApiKeysInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutApiKeysInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyUncheckedCreateWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyCreateOrConnectWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyCreateOrConnectWithoutJobsInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutJobsInputSchema) ]),
}).strict();

export const PurchaseCreateWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutPurchasesInputSchema).optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutPurchasesInputSchema).optional()
}).strict();

export const PurchaseUncheckedCreateWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseUncheckedCreateWithoutJobsInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const PurchaseCreateOrConnectWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseCreateOrConnectWithoutJobsInput> = z.object({
  where: z.lazy(() => PurchaseWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => PurchaseCreateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutJobsInputSchema) ]),
}).strict();

export const VideoCreateWithoutJobInputSchema: z.ZodType<Prisma.VideoCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  sourceId: z.string(),
  videoLength: z.number(),
  title: z.string(),
  files: z.union([ z.lazy(() => SourceFilesNullableCreateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCreateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.string(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCreateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListCreateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.lazy(() => VideoStatusSchema),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListCreateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.coerce.date().optional()
}).strict();

export const VideoUncheckedCreateWithoutJobInputSchema: z.ZodType<Prisma.VideoUncheckedCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  sourceId: z.string(),
  videoLength: z.number(),
  title: z.string(),
  files: z.union([ z.lazy(() => SourceFilesNullableCreateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableCreateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.string(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableCreateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListCreateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.lazy(() => VideoStatusSchema),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListCreateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.coerce.date().optional()
}).strict();

export const VideoCreateOrConnectWithoutJobInputSchema: z.ZodType<Prisma.VideoCreateOrConnectWithoutJobInput> = z.object({
  where: z.lazy(() => VideoWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => VideoCreateWithoutJobInputSchema),z.lazy(() => VideoUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const VideoProjectCreateWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutVideoProjectsInputSchema),
  createdBy: z.lazy(() => UserCreateNestedOneWithoutVideoProjectsInputSchema),
  design: z.lazy(() => VideoDesignCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectUncheckedCreateWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  design: z.lazy(() => VideoDesignUncheckedCreateNestedOneWithoutVideoProjectInputSchema).optional()
}).strict();

export const VideoProjectCreateOrConnectWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectCreateOrConnectWithoutJobInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const AppFileCreateWithoutJobInputSchema: z.ZodType<Prisma.AppFileCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutFilesInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutFilesInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileUncheckedCreateWithoutJobInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutAppFileInputSchema).optional()
}).strict();

export const AppFileCreateOrConnectWithoutJobInputSchema: z.ZodType<Prisma.AppFileCreateOrConnectWithoutJobInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AppFileCreateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const AppFileCreateManyJobInputEnvelopeSchema: z.ZodType<Prisma.AppFileCreateManyJobInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => AppFileCreateManyJobInputSchema),z.lazy(() => AppFileCreateManyJobInputSchema).array() ]),
}).strict();

export const RenderHistoryCreateWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutRenderHistoryInputSchema),
  user: z.lazy(() => UserCreateNestedOneWithoutRenderHistoryInputSchema).optional(),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutRenderHistoriesInputSchema).optional(),
  appFile: z.lazy(() => AppFileCreateNestedOneWithoutRenderHistoriesInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedCreateWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateOrConnectWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryCreateOrConnectWithoutJobInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const RenderHistoryCreateManyJobInputEnvelopeSchema: z.ZodType<Prisma.RenderHistoryCreateManyJobInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => RenderHistoryCreateManyJobInputSchema),z.lazy(() => RenderHistoryCreateManyJobInputSchema).array() ]),
}).strict();

export const OrganizationSocialPostCreateWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutSocialPostsInputSchema)
}).strict();

export const OrganizationSocialPostUncheckedCreateWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedCreateWithoutJobInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialPostCreateOrConnectWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateOrConnectWithoutJobInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const OrganizationSocialPostCreateManyJobInputEnvelopeSchema: z.ZodType<Prisma.OrganizationSocialPostCreateManyJobInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => OrganizationSocialPostCreateManyJobInputSchema),z.lazy(() => OrganizationSocialPostCreateManyJobInputSchema).array() ]),
}).strict();

export const CostBreakdownUpsertInputSchema: z.ZodType<Prisma.CostBreakdownUpsertInput> = z.object({
  set: z.lazy(() => CostBreakdownCreateInputSchema).nullable(),
  update: z.lazy(() => CostBreakdownUpdateInputSchema)
}).strict();

export const WebhookUpsertInputSchema: z.ZodType<Prisma.WebhookUpsertInput> = z.object({
  set: z.lazy(() => WebhookCreateInputSchema).nullable(),
  update: z.lazy(() => WebhookUpdateInputSchema)
}).strict();

export const UserUpsertWithoutJobsInputSchema: z.ZodType<Prisma.UserUpsertWithoutJobsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutJobsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutJobsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutJobsInputSchema),z.lazy(() => UserUncheckedCreateWithoutJobsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutJobsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutJobsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutJobsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutJobsInputSchema) ]),
}).strict();

export const UserUpdateWithoutJobsInputSchema: z.ZodType<Prisma.UserUpdateWithoutJobsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutJobsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutJobsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const OrganizationUpsertWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutJobsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutJobsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutJobsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutJobsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutJobsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutJobsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutJobsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutJobsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutJobsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const ApiKeyUpsertWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyUpsertWithoutJobsInput> = z.object({
  update: z.union([ z.lazy(() => ApiKeyUpdateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutJobsInputSchema) ]),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutJobsInputSchema) ]),
  where: z.lazy(() => ApiKeyWhereInputSchema).optional()
}).strict();

export const ApiKeyUpdateToOneWithWhereWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyUpdateToOneWithWhereWithoutJobsInput> = z.object({
  where: z.lazy(() => ApiKeyWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => ApiKeyUpdateWithoutJobsInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutJobsInputSchema) ]),
}).strict();

export const ApiKeyUpdateWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyUpdateWithoutJobsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutApiKeysNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutApiKeysNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateWithoutJobsInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateWithoutJobsInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const PurchaseUpsertWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseUpsertWithoutJobsInput> = z.object({
  update: z.union([ z.lazy(() => PurchaseUpdateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutJobsInputSchema) ]),
  create: z.union([ z.lazy(() => PurchaseCreateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedCreateWithoutJobsInputSchema) ]),
  where: z.lazy(() => PurchaseWhereInputSchema).optional()
}).strict();

export const PurchaseUpdateToOneWithWhereWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseUpdateToOneWithWhereWithoutJobsInput> = z.object({
  where: z.lazy(() => PurchaseWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => PurchaseUpdateWithoutJobsInputSchema),z.lazy(() => PurchaseUncheckedUpdateWithoutJobsInputSchema) ]),
}).strict();

export const PurchaseUpdateWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseUpdateWithoutJobsInput> = z.object({
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutPurchasesNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutPurchasesNestedInputSchema).optional()
}).strict();

export const PurchaseUncheckedUpdateWithoutJobsInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateWithoutJobsInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoUpsertWithoutJobInputSchema: z.ZodType<Prisma.VideoUpsertWithoutJobInput> = z.object({
  update: z.union([ z.lazy(() => VideoUpdateWithoutJobInputSchema),z.lazy(() => VideoUncheckedUpdateWithoutJobInputSchema) ]),
  create: z.union([ z.lazy(() => VideoCreateWithoutJobInputSchema),z.lazy(() => VideoUncheckedCreateWithoutJobInputSchema) ]),
  where: z.lazy(() => VideoWhereInputSchema).optional()
}).strict();

export const VideoUpdateToOneWithWhereWithoutJobInputSchema: z.ZodType<Prisma.VideoUpdateToOneWithWhereWithoutJobInput> = z.object({
  where: z.lazy(() => VideoWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => VideoUpdateWithoutJobInputSchema),z.lazy(() => VideoUncheckedUpdateWithoutJobInputSchema) ]),
}).strict();

export const VideoUpdateWithoutJobInputSchema: z.ZodType<Prisma.VideoUpdateWithoutJobInput> = z.object({
  sourceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  videoLength: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableUpdateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableUpdateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableUpdateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListUpdateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => EnumVideoStatusFieldUpdateOperationsInputSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListUpdateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoUncheckedUpdateWithoutJobInputSchema: z.ZodType<Prisma.VideoUncheckedUpdateWithoutJobInput> = z.object({
  sourceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  videoLength: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  files: z.union([ z.lazy(() => SourceFilesNullableUpdateEnvelopeInputSchema),z.lazy(() => SourceFilesCreateInputSchema) ]).optional().nullable(),
  options: z.union([ z.lazy(() => VideoShortsOptionsNullableUpdateEnvelopeInputSchema),z.lazy(() => VideoShortsOptionsCreateInputSchema) ]).optional().nullable(),
  choosedDefinition: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  transcription: z.union([ z.lazy(() => ITranscriptionNullableUpdateEnvelopeInputSchema),z.lazy(() => ITranscriptionCreateInputSchema) ]).optional().nullable(),
  shorts: z.union([ z.lazy(() => ShortListUpdateEnvelopeInputSchema),z.lazy(() => ShortCreateInputSchema),z.lazy(() => ShortCreateInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => VideoStatusSchema),z.lazy(() => EnumVideoStatusFieldUpdateOperationsInputSchema) ]).optional(),
  statusHistory: z.union([ z.lazy(() => StatusHistoryListUpdateEnvelopeInputSchema),z.lazy(() => StatusHistoryCreateInputSchema),z.lazy(() => StatusHistoryCreateInputSchema).array() ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoProjectUpsertWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectUpsertWithoutJobInput> = z.object({
  update: z.union([ z.lazy(() => VideoProjectUpdateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutJobInputSchema) ]),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutJobInputSchema) ]),
  where: z.lazy(() => VideoProjectWhereInputSchema).optional()
}).strict();

export const VideoProjectUpdateToOneWithWhereWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectUpdateToOneWithWhereWithoutJobInput> = z.object({
  where: z.lazy(() => VideoProjectWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => VideoProjectUpdateWithoutJobInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutJobInputSchema) ]),
}).strict();

export const VideoProjectUpdateWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectUpdateWithoutJobInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  createdBy: z.lazy(() => UserUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  design: z.lazy(() => VideoDesignUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateWithoutJobInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateWithoutJobInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdByUserId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.lazy(() => VideoDesignUncheckedUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const AppFileUpsertWithWhereUniqueWithoutJobInputSchema: z.ZodType<Prisma.AppFileUpsertWithWhereUniqueWithoutJobInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => AppFileUpdateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutJobInputSchema) ]),
  create: z.union([ z.lazy(() => AppFileCreateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const AppFileUpdateWithWhereUniqueWithoutJobInputSchema: z.ZodType<Prisma.AppFileUpdateWithWhereUniqueWithoutJobInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => AppFileUpdateWithoutJobInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutJobInputSchema) ]),
}).strict();

export const AppFileUpdateManyWithWhereWithoutJobInputSchema: z.ZodType<Prisma.AppFileUpdateManyWithWhereWithoutJobInput> = z.object({
  where: z.lazy(() => AppFileScalarWhereInputSchema),
  data: z.union([ z.lazy(() => AppFileUpdateManyMutationInputSchema),z.lazy(() => AppFileUncheckedUpdateManyWithoutJobInputSchema) ]),
}).strict();

export const RenderHistoryUpsertWithWhereUniqueWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUpsertWithWhereUniqueWithoutJobInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutJobInputSchema) ]),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const RenderHistoryUpdateWithWhereUniqueWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithWhereUniqueWithoutJobInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateWithoutJobInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutJobInputSchema) ]),
}).strict();

export const RenderHistoryUpdateManyWithWhereWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithWhereWithoutJobInput> = z.object({
  where: z.lazy(() => RenderHistoryScalarWhereInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateManyMutationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobInputSchema) ]),
}).strict();

export const OrganizationSocialPostUpsertWithWhereUniqueWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpsertWithWhereUniqueWithoutJobInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedUpdateWithoutJobInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationSocialPostCreateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedCreateWithoutJobInputSchema) ]),
}).strict();

export const OrganizationSocialPostUpdateWithWhereUniqueWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateWithWhereUniqueWithoutJobInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => OrganizationSocialPostUpdateWithoutJobInputSchema),z.lazy(() => OrganizationSocialPostUncheckedUpdateWithoutJobInputSchema) ]),
}).strict();

export const OrganizationSocialPostUpdateManyWithWhereWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateManyWithWhereWithoutJobInput> = z.object({
  where: z.lazy(() => OrganizationSocialPostScalarWhereInputSchema),
  data: z.union([ z.lazy(() => OrganizationSocialPostUpdateManyMutationInputSchema),z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobInputSchema) ]),
}).strict();

export const PredefinedShortsCreateInputSchema: z.ZodType<Prisma.PredefinedShortsCreateInput> = z.object({
  title: z.string(),
  start: z.number(),
  end: z.number()
}).strict();

export const PredictionCreateInputSchema: z.ZodType<Prisma.PredictionCreateInput> = z.object({
  words: z.union([ z.lazy(() => WordCreateInputSchema),z.lazy(() => WordCreateInputSchema).array() ]).optional(),
  language: z.string().optional().nullable(),
  transcription: z.string(),
  confidence: z.number(),
  time_begin: z.number(),
  time_end: z.number(),
  speaker: z.string(),
  channel: z.string()
}).strict();

export const ShortScoreCreateInputSchema: z.ZodType<Prisma.ShortScoreCreateInput> = z.object({
  final_creative_score: z.number(),
  narrative_payoff_score: z.number(),
  hook_quality_score: z.number(),
  engagement_potential_score: z.number(),
  brand_alignment_score: z.number()
}).strict();

export const AsdRootCreateInputSchema: z.ZodType<Prisma.AsdRootCreateInput> = z.object({
  status: z.string(),
  success: z.boolean(),
  segment_id: z.number(),
  timestamp: z.string(),
  result: z.lazy(() => AsdResultCreateInputSchema),
  video_info: z.lazy(() => AsdVideoInfoCreateInputSchema),
  detection_count: z.number(),
  scenes_count: z.number(),
  has_rendered_video: z.boolean(),
  config: z.lazy(() => AsdConfigCreateInputSchema).optional().nullable()
}).strict();

export const BRollItemCreateInputSchema: z.ZodType<Prisma.BRollItemCreateInput> = z.object({
  id: z.string(),
  sentenceIds: z.union([ z.lazy(() => BRollItemCreatesentenceIdsInputSchema),z.number().array() ]).optional(),
  fileUrl: z.string(),
  fileType: z.lazy(() => BRollFileTypeSchema),
  pexelsId: z.string().optional().nullable(),
  startTime: z.number(),
  endTime: z.number(),
  position: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional().nullable()
}).strict();

export const JobCreateWithoutVideoInputSchema: z.ZodType<Prisma.JobCreateWithoutVideoInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutVideoInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutVideoInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutVideoInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutVideoInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutVideoInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoInputSchema) ]),
}).strict();

export const SourceFilesUpsertInputSchema: z.ZodType<Prisma.SourceFilesUpsertInput> = z.object({
  set: z.lazy(() => SourceFilesCreateInputSchema).nullable(),
  update: z.lazy(() => SourceFilesUpdateInputSchema)
}).strict();

export const VideoShortsOptionsUpsertInputSchema: z.ZodType<Prisma.VideoShortsOptionsUpsertInput> = z.object({
  set: z.lazy(() => VideoShortsOptionsCreateInputSchema).nullable(),
  update: z.lazy(() => VideoShortsOptionsUpdateInputSchema)
}).strict();

export const ITranscriptionUpsertInputSchema: z.ZodType<Prisma.ITranscriptionUpsertInput> = z.object({
  set: z.lazy(() => ITranscriptionCreateInputSchema).nullable(),
  update: z.lazy(() => ITranscriptionUpdateInputSchema)
}).strict();

export const ShortUpdateManyInputSchema: z.ZodType<Prisma.ShortUpdateManyInput> = z.object({
  where: z.lazy(() => ShortWhereInputSchema),
  data: z.lazy(() => ShortUpdateInputSchema)
}).strict();

export const ShortDeleteManyInputSchema: z.ZodType<Prisma.ShortDeleteManyInput> = z.object({
  where: z.lazy(() => ShortWhereInputSchema)
}).strict();

export const StatusHistoryUpdateManyInputSchema: z.ZodType<Prisma.StatusHistoryUpdateManyInput> = z.object({
  where: z.lazy(() => StatusHistoryWhereInputSchema),
  data: z.lazy(() => StatusHistoryUpdateInputSchema)
}).strict();

export const StatusHistoryDeleteManyInputSchema: z.ZodType<Prisma.StatusHistoryDeleteManyInput> = z.object({
  where: z.lazy(() => StatusHistoryWhereInputSchema)
}).strict();

export const JobUpsertWithoutVideoInputSchema: z.ZodType<Prisma.JobUpsertWithoutVideoInput> = z.object({
  update: z.union([ z.lazy(() => JobUpdateWithoutVideoInputSchema),z.lazy(() => JobUncheckedUpdateWithoutVideoInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutVideoInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoInputSchema) ]),
  where: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const JobUpdateToOneWithWhereWithoutVideoInputSchema: z.ZodType<Prisma.JobUpdateToOneWithWhereWithoutVideoInput> = z.object({
  where: z.lazy(() => JobWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => JobUpdateWithoutVideoInputSchema),z.lazy(() => JobUncheckedUpdateWithoutVideoInputSchema) ]),
}).strict();

export const JobUpdateWithoutVideoInputSchema: z.ZodType<Prisma.JobUpdateWithoutVideoInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutVideoInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutVideoInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobCreateWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobCreateWithoutVideoProjectInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutVideoProjectInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutVideoProjectInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoProjectInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutVideoProjectsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutVideoProjectsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutVideoProjectsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutVideoProjectsInputSchema) ]),
}).strict();

export const UserCreateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserCreateWithoutVideoProjectsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutVideoProjectsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutVideoProjectsInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedCreateWithoutVideoProjectsInputSchema) ]),
}).strict();

export const VideoDesignCreateWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignCreateWithoutVideoProjectInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  status: z.lazy(() => VideoDesignStatusSchema).optional(),
  design: z.record(z.string(), z.any()).optional(),
  fps: z.number().int().optional(),
  durationMs: z.number().int().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  latestRenderHistoryId: z.string().optional().nullable(),
  latestMp4Url: z.string().optional().nullable(),
  compiledAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const VideoDesignUncheckedCreateWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignUncheckedCreateWithoutVideoProjectInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  status: z.lazy(() => VideoDesignStatusSchema).optional(),
  design: z.record(z.string(), z.any()).optional(),
  fps: z.number().int().optional(),
  durationMs: z.number().int().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  latestRenderHistoryId: z.string().optional().nullable(),
  latestMp4Url: z.string().optional().nullable(),
  compiledAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const VideoDesignCreateOrConnectWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignCreateOrConnectWithoutVideoProjectInput> = z.object({
  where: z.lazy(() => VideoDesignWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => VideoDesignCreateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedCreateWithoutVideoProjectInputSchema) ]),
}).strict();

export const JobUpsertWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobUpsertWithoutVideoProjectInput> = z.object({
  update: z.union([ z.lazy(() => JobUpdateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedUpdateWithoutVideoProjectInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedCreateWithoutVideoProjectInputSchema) ]),
  where: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const JobUpdateToOneWithWhereWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobUpdateToOneWithWhereWithoutVideoProjectInput> = z.object({
  where: z.lazy(() => JobWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => JobUpdateWithoutVideoProjectInputSchema),z.lazy(() => JobUncheckedUpdateWithoutVideoProjectInputSchema) ]),
}).strict();

export const JobUpdateWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobUpdateWithoutVideoProjectInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutVideoProjectInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutVideoProjectInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const OrganizationUpsertWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutVideoProjectsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutVideoProjectsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutVideoProjectsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutVideoProjectsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutVideoProjectsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutVideoProjectsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutVideoProjectsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutVideoProjectsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const UserUpsertWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserUpsertWithoutVideoProjectsInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutVideoProjectsInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedCreateWithoutVideoProjectsInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutVideoProjectsInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutVideoProjectsInputSchema),z.lazy(() => UserUncheckedUpdateWithoutVideoProjectsInputSchema) ]),
}).strict();

export const UserUpdateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserUpdateWithoutVideoProjectsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutVideoProjectsInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutVideoProjectsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional()
}).strict();

export const VideoDesignUpsertWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignUpsertWithoutVideoProjectInput> = z.object({
  update: z.union([ z.lazy(() => VideoDesignUpdateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedUpdateWithoutVideoProjectInputSchema) ]),
  create: z.union([ z.lazy(() => VideoDesignCreateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedCreateWithoutVideoProjectInputSchema) ]),
  where: z.lazy(() => VideoDesignWhereInputSchema).optional()
}).strict();

export const VideoDesignUpdateToOneWithWhereWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignUpdateToOneWithWhereWithoutVideoProjectInput> = z.object({
  where: z.lazy(() => VideoDesignWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => VideoDesignUpdateWithoutVideoProjectInputSchema),z.lazy(() => VideoDesignUncheckedUpdateWithoutVideoProjectInputSchema) ]),
}).strict();

export const VideoDesignUpdateWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignUpdateWithoutVideoProjectInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => EnumVideoDesignStatusFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fps: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  durationMs: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  width: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  latestRenderHistoryId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  latestMp4Url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  compiledAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoDesignUncheckedUpdateWithoutVideoProjectInputSchema: z.ZodType<Prisma.VideoDesignUncheckedUpdateWithoutVideoProjectInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoDesignStatusSchema),z.lazy(() => EnumVideoDesignStatusFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fps: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  durationMs: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  width: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  latestRenderHistoryId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  latestMp4Url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  compiledAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const VideoProjectCreateWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectCreateWithoutDesignInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutVideoProjectInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutVideoProjectsInputSchema),
  createdBy: z.lazy(() => UserCreateNestedOneWithoutVideoProjectsInputSchema)
}).strict();

export const VideoProjectUncheckedCreateWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectUncheckedCreateWithoutDesignInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional()
}).strict();

export const VideoProjectCreateOrConnectWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectCreateOrConnectWithoutDesignInput> = z.object({
  where: z.lazy(() => VideoProjectWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutDesignInputSchema) ]),
}).strict();

export const VideoProjectUpsertWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectUpsertWithoutDesignInput> = z.object({
  update: z.union([ z.lazy(() => VideoProjectUpdateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutDesignInputSchema) ]),
  create: z.union([ z.lazy(() => VideoProjectCreateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedCreateWithoutDesignInputSchema) ]),
  where: z.lazy(() => VideoProjectWhereInputSchema).optional()
}).strict();

export const VideoProjectUpdateToOneWithWhereWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectUpdateToOneWithWhereWithoutDesignInput> = z.object({
  where: z.lazy(() => VideoProjectWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => VideoProjectUpdateWithoutDesignInputSchema),z.lazy(() => VideoProjectUncheckedUpdateWithoutDesignInputSchema) ]),
}).strict();

export const VideoProjectUpdateWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectUpdateWithoutDesignInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutVideoProjectNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  createdBy: z.lazy(() => UserUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateWithoutDesignInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateWithoutDesignInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdByUserId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const UserCreateWithoutFilesInputSchema: z.ZodType<Prisma.UserCreateWithoutFilesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutFilesInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutFilesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutFilesInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutFilesInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutFilesInputSchema),z.lazy(() => UserUncheckedCreateWithoutFilesInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutFilesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutFilesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutFilesInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutFilesInputSchema) ]),
}).strict();

export const JobCreateWithoutFilesInputSchema: z.ZodType<Prisma.JobCreateWithoutFilesInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutFilesInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutFilesInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutFilesInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutFilesInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutFilesInputSchema),z.lazy(() => JobUncheckedCreateWithoutFilesInputSchema) ]),
}).strict();

export const RenderHistoryCreateWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryCreateWithoutAppFileInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutRenderHistoryInputSchema),
  user: z.lazy(() => UserCreateNestedOneWithoutRenderHistoryInputSchema).optional(),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutRenderHistoriesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutRenderHistoriesInputSchema)
}).strict();

export const RenderHistoryUncheckedCreateWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedCreateWithoutAppFileInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateOrConnectWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryCreateOrConnectWithoutAppFileInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema) ]),
}).strict();

export const RenderHistoryCreateManyAppFileInputEnvelopeSchema: z.ZodType<Prisma.RenderHistoryCreateManyAppFileInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => RenderHistoryCreateManyAppFileInputSchema),z.lazy(() => RenderHistoryCreateManyAppFileInputSchema).array() ]),
}).strict();

export const UserUpsertWithoutFilesInputSchema: z.ZodType<Prisma.UserUpsertWithoutFilesInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutFilesInputSchema),z.lazy(() => UserUncheckedUpdateWithoutFilesInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutFilesInputSchema),z.lazy(() => UserUncheckedCreateWithoutFilesInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutFilesInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutFilesInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutFilesInputSchema),z.lazy(() => UserUncheckedUpdateWithoutFilesInputSchema) ]),
}).strict();

export const UserUpdateWithoutFilesInputSchema: z.ZodType<Prisma.UserUpdateWithoutFilesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutFilesInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutFilesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const OrganizationUpsertWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutFilesInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutFilesInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutFilesInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutFilesInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutFilesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutFilesInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutFilesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutFilesInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutFilesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const JobUpsertWithoutFilesInputSchema: z.ZodType<Prisma.JobUpsertWithoutFilesInput> = z.object({
  update: z.union([ z.lazy(() => JobUpdateWithoutFilesInputSchema),z.lazy(() => JobUncheckedUpdateWithoutFilesInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutFilesInputSchema),z.lazy(() => JobUncheckedCreateWithoutFilesInputSchema) ]),
  where: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const JobUpdateToOneWithWhereWithoutFilesInputSchema: z.ZodType<Prisma.JobUpdateToOneWithWhereWithoutFilesInput> = z.object({
  where: z.lazy(() => JobWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => JobUpdateWithoutFilesInputSchema),z.lazy(() => JobUncheckedUpdateWithoutFilesInputSchema) ]),
}).strict();

export const JobUpdateWithoutFilesInputSchema: z.ZodType<Prisma.JobUpdateWithoutFilesInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutFilesInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutFilesInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const RenderHistoryUpsertWithWhereUniqueWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUpsertWithWhereUniqueWithoutAppFileInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => RenderHistoryUpdateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutAppFileInputSchema) ]),
  create: z.union([ z.lazy(() => RenderHistoryCreateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedCreateWithoutAppFileInputSchema) ]),
}).strict();

export const RenderHistoryUpdateWithWhereUniqueWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithWhereUniqueWithoutAppFileInput> = z.object({
  where: z.lazy(() => RenderHistoryWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateWithoutAppFileInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateWithoutAppFileInputSchema) ]),
}).strict();

export const RenderHistoryUpdateManyWithWhereWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUpdateManyWithWhereWithoutAppFileInput> = z.object({
  where: z.lazy(() => RenderHistoryScalarWhereInputSchema),
  data: z.union([ z.lazy(() => RenderHistoryUpdateManyMutationInputSchema),z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutAppFileInputSchema) ]),
}).strict();

export const OrganizationCreateWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutCaptionStylesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutCaptionStylesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutCaptionStylesInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutCaptionStylesInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutCaptionStylesInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutCaptionStylesInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutCaptionStylesInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutCaptionStylesInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutCaptionStylesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutCaptionStylesInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutCaptionStylesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutCaptionStylesInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutCaptionStylesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutBrandingsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutBrandingsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutBrandingsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutBrandingsInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutBrandingsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutBrandingsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutBrandingsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutBrandingsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutBrandingsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutBrandingsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutBrandingsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutBrandingsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutBrandingsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutNarratorProfilesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutNarratorProfilesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutNarratorProfilesInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutNarratorProfilesInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutNarratorProfilesInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutNarratorProfilesInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutNarratorProfilesInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutNarratorProfilesInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutNarratorProfilesInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutNarratorProfilesInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutNarratorProfilesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutNarratorProfilesInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutNarratorProfilesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutSocialSettingsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutSocialSettingsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutSocialSettingsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialSettingsInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutSocialSettingsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutSocialSettingsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialSettingsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutSocialSettingsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutSocialSettingsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutSocialSettingsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutSocialSettingsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutSocialSettingsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutSocialSettingsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutSocialPostsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutSocialPostsInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutSocialPostsInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialPostsInputSchema) ]),
}).strict();

export const JobCreateWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobCreateWithoutSocialPostsInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutSocialPostsInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutSocialPostsInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedCreateWithoutSocialPostsInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutSocialPostsInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutSocialPostsInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutSocialPostsInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutSocialPostsInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutSocialPostsInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutSocialPostsInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutSocialPostsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutSocialPostsInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutSocialPostsInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  renderHistory: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const JobUpsertWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobUpsertWithoutSocialPostsInput> = z.object({
  update: z.union([ z.lazy(() => JobUpdateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedUpdateWithoutSocialPostsInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedCreateWithoutSocialPostsInputSchema) ]),
  where: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const JobUpdateToOneWithWhereWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobUpdateToOneWithWhereWithoutSocialPostsInput> = z.object({
  where: z.lazy(() => JobWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => JobUpdateWithoutSocialPostsInputSchema),z.lazy(() => JobUncheckedUpdateWithoutSocialPostsInputSchema) ]),
}).strict();

export const JobUpdateWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobUpdateWithoutSocialPostsInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutSocialPostsInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutSocialPostsInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const OrganizationCreateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationCreateWithoutRenderHistoryInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationUncheckedCreateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationUncheckedCreateWithoutRenderHistoryInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  slug: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  metadata: z.string().optional().nullable(),
  paymentsCustomerId: z.string().optional().nullable(),
  AutoArchiveDurationDays: z.number().int().optional().nullable(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedCreateNestedOneWithoutOrganizationInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutOrganizationInputSchema).optional()
}).strict();

export const OrganizationCreateOrConnectWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationCreateOrConnectWithoutRenderHistoryInput> = z.object({
  where: z.lazy(() => OrganizationWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutRenderHistoryInputSchema) ]),
}).strict();

export const UserCreateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserCreateWithoutRenderHistoryInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserUncheckedCreateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserUncheckedCreateWithoutRenderHistoryInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  username: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.coerce.date().optional().nullable(),
  onboardingComplete: z.boolean().optional(),
  paymentsCustomerId: z.string().optional().nullable(),
  locale: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneVerified: z.boolean().optional(),
  smsVerificationCode: z.string().optional().nullable(),
  smsVerificationExpiresAt: z.coerce.date().optional().nullable(),
  smsVerificationAttempts: z.number().int().optional(),
  sessions: z.lazy(() => SessionUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutUserInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedCreateNestedManyWithoutCreatedByInputSchema).optional()
}).strict();

export const UserCreateOrConnectWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserCreateOrConnectWithoutRenderHistoryInput> = z.object({
  where: z.lazy(() => UserWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => UserCreateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedCreateWithoutRenderHistoryInputSchema) ]),
}).strict();

export const ApiKeyCreateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyCreateWithoutRenderHistoriesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutApiKeysInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutApiKeysInputSchema).optional(),
  jobs: z.lazy(() => JobCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyUncheckedCreateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyUncheckedCreateWithoutRenderHistoriesInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  jobs: z.lazy(() => JobUncheckedCreateNestedManyWithoutKeyInputSchema).optional()
}).strict();

export const ApiKeyCreateOrConnectWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyCreateOrConnectWithoutRenderHistoriesInput> = z.object({
  where: z.lazy(() => ApiKeyWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutRenderHistoriesInputSchema) ]),
}).strict();

export const JobCreateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobCreateWithoutRenderHistoriesInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  user: z.lazy(() => UserCreateNestedOneWithoutJobsInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutJobsInputSchema),
  key: z.lazy(() => ApiKeyCreateNestedOneWithoutJobsInputSchema).optional(),
  purchase: z.lazy(() => PurchaseCreateNestedOneWithoutJobsInputSchema).optional(),
  video: z.lazy(() => VideoCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobUncheckedCreateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobUncheckedCreateWithoutRenderHistoriesInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable(),
  video: z.lazy(() => VideoUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedCreateNestedOneWithoutJobInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedCreateNestedManyWithoutJobInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedCreateNestedManyWithoutJobInputSchema).optional()
}).strict();

export const JobCreateOrConnectWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobCreateOrConnectWithoutRenderHistoriesInput> = z.object({
  where: z.lazy(() => JobWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => JobCreateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedCreateWithoutRenderHistoriesInputSchema) ]),
}).strict();

export const AppFileCreateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileCreateWithoutRenderHistoriesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  user: z.lazy(() => UserCreateNestedOneWithoutFilesInputSchema),
  organization: z.lazy(() => OrganizationCreateNestedOneWithoutFilesInputSchema).optional(),
  job: z.lazy(() => JobCreateNestedOneWithoutFilesInputSchema).optional()
}).strict();

export const AppFileUncheckedCreateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileUncheckedCreateWithoutRenderHistoriesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional()
}).strict();

export const AppFileCreateOrConnectWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileCreateOrConnectWithoutRenderHistoriesInput> = z.object({
  where: z.lazy(() => AppFileWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => AppFileCreateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutRenderHistoriesInputSchema) ]),
}).strict();

export const OrganizationUpsertWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationUpsertWithoutRenderHistoryInput> = z.object({
  update: z.union([ z.lazy(() => OrganizationUpdateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutRenderHistoryInputSchema) ]),
  create: z.union([ z.lazy(() => OrganizationCreateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedCreateWithoutRenderHistoryInputSchema) ]),
  where: z.lazy(() => OrganizationWhereInputSchema).optional()
}).strict();

export const OrganizationUpdateToOneWithWhereWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationUpdateToOneWithWhereWithoutRenderHistoryInput> = z.object({
  where: z.lazy(() => OrganizationWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => OrganizationUpdateWithoutRenderHistoryInputSchema),z.lazy(() => OrganizationUncheckedUpdateWithoutRenderHistoryInputSchema) ]),
}).strict();

export const OrganizationUpdateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationUpdateWithoutRenderHistoryInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const OrganizationUncheckedUpdateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.OrganizationUncheckedUpdateWithoutRenderHistoryInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  slug: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  logo: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  metadata: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  AutoArchiveDurationDays: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  captionStyles: z.lazy(() => CaptionStyleUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  brandings: z.lazy(() => OrganizationBrandingUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  narratorProfiles: z.lazy(() => OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  socialSettings: z.lazy(() => OrganizationSocialSettingsUncheckedUpdateOneWithoutOrganizationNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutOrganizationNestedInputSchema).optional()
}).strict();

export const UserUpsertWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserUpsertWithoutRenderHistoryInput> = z.object({
  update: z.union([ z.lazy(() => UserUpdateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedUpdateWithoutRenderHistoryInputSchema) ]),
  create: z.union([ z.lazy(() => UserCreateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedCreateWithoutRenderHistoryInputSchema) ]),
  where: z.lazy(() => UserWhereInputSchema).optional()
}).strict();

export const UserUpdateToOneWithWhereWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserUpdateToOneWithWhereWithoutRenderHistoryInput> = z.object({
  where: z.lazy(() => UserWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => UserUpdateWithoutRenderHistoryInputSchema),z.lazy(() => UserUncheckedUpdateWithoutRenderHistoryInputSchema) ]),
}).strict();

export const UserUpdateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserUpdateWithoutRenderHistoryInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const UserUncheckedUpdateWithoutRenderHistoryInputSchema: z.ZodType<Prisma.UserUncheckedUpdateWithoutRenderHistoryInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  emailVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  image: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  username: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banned: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banReason: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  banExpires: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  onboardingComplete: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  paymentsCustomerId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  locale: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneNumber: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  phoneVerified: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  smsVerificationCode: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smsVerificationAttempts: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  sessions: z.lazy(() => SessionUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  accounts: z.lazy(() => AccountUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  apiKeys: z.lazy(() => ApiKeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  passkeys: z.lazy(() => PasskeyUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  invitations: z.lazy(() => InvitationUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  purchases: z.lazy(() => PurchaseUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  members: z.lazy(() => MemberUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  twofactors: z.lazy(() => TwoFactorUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  aiChats: z.lazy(() => AiChatUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutUserNestedInputSchema).optional(),
  videoProjects: z.lazy(() => VideoProjectUncheckedUpdateManyWithoutCreatedByNestedInputSchema).optional()
}).strict();

export const ApiKeyUpsertWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyUpsertWithoutRenderHistoriesInput> = z.object({
  update: z.union([ z.lazy(() => ApiKeyUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutRenderHistoriesInputSchema) ]),
  create: z.union([ z.lazy(() => ApiKeyCreateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedCreateWithoutRenderHistoriesInputSchema) ]),
  where: z.lazy(() => ApiKeyWhereInputSchema).optional()
}).strict();

export const ApiKeyUpdateToOneWithWhereWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyUpdateToOneWithWhereWithoutRenderHistoriesInput> = z.object({
  where: z.lazy(() => ApiKeyWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => ApiKeyUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => ApiKeyUncheckedUpdateWithoutRenderHistoriesInputSchema) ]),
}).strict();

export const ApiKeyUpdateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyUpdateWithoutRenderHistoriesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutApiKeysNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutApiKeysNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateWithoutRenderHistoriesInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const JobUpsertWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobUpsertWithoutRenderHistoriesInput> = z.object({
  update: z.union([ z.lazy(() => JobUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedUpdateWithoutRenderHistoriesInputSchema) ]),
  create: z.union([ z.lazy(() => JobCreateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedCreateWithoutRenderHistoriesInputSchema) ]),
  where: z.lazy(() => JobWhereInputSchema).optional()
}).strict();

export const JobUpdateToOneWithWhereWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobUpdateToOneWithWhereWithoutRenderHistoriesInput> = z.object({
  where: z.lazy(() => JobWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => JobUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => JobUncheckedUpdateWithoutRenderHistoriesInputSchema) ]),
}).strict();

export const JobUpdateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobUpdateWithoutRenderHistoriesInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutRenderHistoriesInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const AppFileUpsertWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileUpsertWithoutRenderHistoriesInput> = z.object({
  update: z.union([ z.lazy(() => AppFileUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutRenderHistoriesInputSchema) ]),
  create: z.union([ z.lazy(() => AppFileCreateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedCreateWithoutRenderHistoriesInputSchema) ]),
  where: z.lazy(() => AppFileWhereInputSchema).optional()
}).strict();

export const AppFileUpdateToOneWithWhereWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileUpdateToOneWithWhereWithoutRenderHistoriesInput> = z.object({
  where: z.lazy(() => AppFileWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => AppFileUpdateWithoutRenderHistoriesInputSchema),z.lazy(() => AppFileUncheckedUpdateWithoutRenderHistoriesInputSchema) ]),
}).strict();

export const AppFileUpdateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileUpdateWithoutRenderHistoriesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutFilesNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutFilesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneWithoutFilesNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateWithoutRenderHistoriesInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateWithoutRenderHistoriesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityCreateWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityCreateWithoutProxyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.coerce.date().optional().nullable(),
  cookieContent: z.string(),
  useCount: z.number().int().optional(),
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CookieIdentityUncheckedCreateWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedCreateWithoutProxyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.coerce.date().optional().nullable(),
  cookieContent: z.string(),
  useCount: z.number().int().optional(),
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CookieIdentityCreateOrConnectWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityCreateOrConnectWithoutProxyInput> = z.object({
  where: z.lazy(() => CookieIdentityWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema) ]),
}).strict();

export const CookieIdentityCreateManyProxyInputEnvelopeSchema: z.ZodType<Prisma.CookieIdentityCreateManyProxyInputEnvelope> = z.object({
  data: z.union([ z.lazy(() => CookieIdentityCreateManyProxyInputSchema),z.lazy(() => CookieIdentityCreateManyProxyInputSchema).array() ]),
}).strict();

export const CookieIdentityUpsertWithWhereUniqueWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUpsertWithWhereUniqueWithoutProxyInput> = z.object({
  where: z.lazy(() => CookieIdentityWhereUniqueInputSchema),
  update: z.union([ z.lazy(() => CookieIdentityUpdateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedUpdateWithoutProxyInputSchema) ]),
  create: z.union([ z.lazy(() => CookieIdentityCreateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedCreateWithoutProxyInputSchema) ]),
}).strict();

export const CookieIdentityUpdateWithWhereUniqueWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUpdateWithWhereUniqueWithoutProxyInput> = z.object({
  where: z.lazy(() => CookieIdentityWhereUniqueInputSchema),
  data: z.union([ z.lazy(() => CookieIdentityUpdateWithoutProxyInputSchema),z.lazy(() => CookieIdentityUncheckedUpdateWithoutProxyInputSchema) ]),
}).strict();

export const CookieIdentityUpdateManyWithWhereWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUpdateManyWithWhereWithoutProxyInput> = z.object({
  where: z.lazy(() => CookieIdentityScalarWhereInputSchema),
  data: z.union([ z.lazy(() => CookieIdentityUpdateManyMutationInputSchema),z.lazy(() => CookieIdentityUncheckedUpdateManyWithoutProxyInputSchema) ]),
}).strict();

export const CookieIdentityScalarWhereInputSchema: z.ZodType<Prisma.CookieIdentityScalarWhereInput> = z.object({
  AND: z.union([ z.lazy(() => CookieIdentityScalarWhereInputSchema),z.lazy(() => CookieIdentityScalarWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => CookieIdentityScalarWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => CookieIdentityScalarWhereInputSchema),z.lazy(() => CookieIdentityScalarWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  name: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  isActive: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  lastRequestTime: z.union([ z.lazy(() => DateTimeNullableFilterSchema),z.coerce.date() ]).optional().nullable(),
  proxyId: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  cookieContent: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  useCount: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  errors: z.lazy(() => JsonFilterSchema).optional(),
  createdAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
  updatedAt: z.union([ z.lazy(() => DateTimeFilterSchema),z.coerce.date() ]).optional(),
}).strict();

export const ProxyCreateWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyCreateWithoutCookieIdentitiesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  proxyUrl: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const ProxyUncheckedCreateWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyUncheckedCreateWithoutCookieIdentitiesInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  proxyUrl: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const ProxyCreateOrConnectWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyCreateOrConnectWithoutCookieIdentitiesInput> = z.object({
  where: z.lazy(() => ProxyWhereUniqueInputSchema),
  create: z.union([ z.lazy(() => ProxyCreateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedCreateWithoutCookieIdentitiesInputSchema) ]),
}).strict();

export const ProxyUpsertWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyUpsertWithoutCookieIdentitiesInput> = z.object({
  update: z.union([ z.lazy(() => ProxyUpdateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedUpdateWithoutCookieIdentitiesInputSchema) ]),
  create: z.union([ z.lazy(() => ProxyCreateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedCreateWithoutCookieIdentitiesInputSchema) ]),
  where: z.lazy(() => ProxyWhereInputSchema).optional()
}).strict();

export const ProxyUpdateToOneWithWhereWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyUpdateToOneWithWhereWithoutCookieIdentitiesInput> = z.object({
  where: z.lazy(() => ProxyWhereInputSchema).optional(),
  data: z.union([ z.lazy(() => ProxyUpdateWithoutCookieIdentitiesInputSchema),z.lazy(() => ProxyUncheckedUpdateWithoutCookieIdentitiesInputSchema) ]),
}).strict();

export const ProxyUpdateWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyUpdateWithoutCookieIdentitiesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  proxyUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ProxyUncheckedUpdateWithoutCookieIdentitiesInputSchema: z.ZodType<Prisma.ProxyUncheckedUpdateWithoutCookieIdentitiesInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  proxyUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PredefinedShortsCompositeListFilterSchema: z.ZodType<Prisma.PredefinedShortsCompositeListFilter> = z.object({
  equals: z.lazy(() => PredefinedShortsObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => PredefinedShortsWhereInputSchema).optional(),
  some: z.lazy(() => PredefinedShortsWhereInputSchema).optional(),
  none: z.lazy(() => PredefinedShortsWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const PredictionCompositeListFilterSchema: z.ZodType<Prisma.PredictionCompositeListFilter> = z.object({
  equals: z.lazy(() => PredictionObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => PredictionWhereInputSchema).optional(),
  some: z.lazy(() => PredictionWhereInputSchema).optional(),
  none: z.lazy(() => PredictionWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const WordObjectEqualityInputSchema: z.ZodType<Prisma.WordObjectEqualityInput> = z.object({
  word: z.string(),
  time_begin: z.number(),
  time_end: z.number(),
  confidence: z.number()
}).strict();

export const EnumShortStatusFilterSchema: z.ZodType<Prisma.EnumShortStatusFilter> = z.object({
  equals: z.lazy(() => ShortStatusSchema).optional(),
  in: z.lazy(() => ShortStatusSchema).array().optional(),
  notIn: z.lazy(() => ShortStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => ShortStatusSchema),z.lazy(() => NestedEnumShortStatusFilterSchema) ]).optional(),
}).strict();

export const ShortScoreNullableCompositeFilterSchema: z.ZodType<Prisma.ShortScoreNullableCompositeFilter> = z.object({
  equals: z.lazy(() => ShortScoreObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => ShortScoreWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => ShortScoreWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const AsdRootNullableCompositeFilterSchema: z.ZodType<Prisma.AsdRootNullableCompositeFilter> = z.object({
  equals: z.lazy(() => AsdRootObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => AsdRootWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AsdRootWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const BRollItemCompositeListFilterSchema: z.ZodType<Prisma.BRollItemCompositeListFilter> = z.object({
  equals: z.lazy(() => BRollItemObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => BRollItemWhereInputSchema).optional(),
  some: z.lazy(() => BRollItemWhereInputSchema).optional(),
  none: z.lazy(() => BRollItemWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const AsdResultObjectEqualityInputSchema: z.ZodType<Prisma.AsdResultObjectEqualityInput> = z.object({
  detection_results: z.lazy(() => AsdDetectionResultObjectEqualityInputSchema).array().optional(),
  video_info: z.lazy(() => AsdVideoInfoObjectEqualityInputSchema),
  scenes: z.lazy(() => AsdSceneObjectEqualityInputSchema).array().optional(),
  rendered_video_path: z.string().optional().nullable(),
  rendered_video_url: z.string().optional().nullable()
}).strict();

export const AsdVideoInfoObjectEqualityInputSchema: z.ZodType<Prisma.AsdVideoInfoObjectEqualityInput> = z.object({
  fps: z.number(),
  processing_fps: z.number(),
  total_frames: z.number(),
  duration: z.number(),
  start_time: z.number(),
  end_time: z.number(),
  frame_numbers_start_from: z.number(),
  resolution: z.lazy(() => AsdResolutionObjectEqualityInputSchema)
}).strict();

export const AsdConfigObjectEqualityInputSchema: z.ZodType<Prisma.AsdConfigObjectEqualityInput> = z.object({
  camera: z.lazy(() => AsdCameraConfigObjectEqualityInputSchema).optional().nullable(),
  layout: z.lazy(() => AsdLayoutConfigObjectEqualityInputSchema).optional().nullable(),
  intelligence: z.lazy(() => AsdIntelligenceConfigObjectEqualityInputSchema).optional().nullable(),
  overlaysAndTiming: z.lazy(() => AsdOverlaysAndTimingConfigObjectEqualityInputSchema).optional().nullable()
}).strict();

export const SessionCreateManyUserInputSchema: z.ZodType<Prisma.SessionCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  expiresAt: z.coerce.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  impersonatedBy: z.string().optional().nullable(),
  activeOrganizationId: z.string().optional().nullable(),
  token: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const AccountCreateManyUserInputSchema: z.ZodType<Prisma.AccountCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  accountId: z.string(),
  providerId: z.string(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  idToken: z.string().optional().nullable(),
  expiresAt: z.coerce.date().optional().nullable(),
  password: z.string().optional().nullable(),
  accessTokenExpiresAt: z.coerce.date().optional().nullable(),
  refreshTokenExpiresAt: z.coerce.date().optional().nullable(),
  scope: z.string().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
}).strict();

export const ApiKeyCreateManyUserInputSchema: z.ZodType<Prisma.ApiKeyCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const PasskeyCreateManyUserInputSchema: z.ZodType<Prisma.PasskeyCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string().optional().nullable(),
  publicKey: z.string(),
  credentialID: z.string(),
  counter: z.number().int(),
  deviceType: z.string(),
  backedUp: z.boolean(),
  transports: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const InvitationCreateManyUserInputSchema: z.ZodType<Prisma.InvitationCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date()
}).strict();

export const PurchaseCreateManyUserInputSchema: z.ZodType<Prisma.PurchaseCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const MemberCreateManyUserInputSchema: z.ZodType<Prisma.MemberCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date()
}).strict();

export const TwoFactorCreateManyUserInputSchema: z.ZodType<Prisma.TwoFactorCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  secret: z.string(),
  backupCodes: z.string()
}).strict();

export const AiChatCreateManyUserInputSchema: z.ZodType<Prisma.AiChatCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AppFileCreateManyUserInputSchema: z.ZodType<Prisma.AppFileCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  organizationId: z.string().optional().nullable(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateManyUserInputSchema: z.ZodType<Prisma.RenderHistoryCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const JobCreateManyUserInputSchema: z.ZodType<Prisma.JobCreateManyUserInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable()
}).strict();

export const VideoProjectCreateManyCreatedByInputSchema: z.ZodType<Prisma.VideoProjectCreateManyCreatedByInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  organizationId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional()
}).strict();

export const SessionUpdateWithoutUserInputSchema: z.ZodType<Prisma.SessionUpdateWithoutUserInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const SessionUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.SessionUncheckedUpdateWithoutUserInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const SessionUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.SessionUncheckedUpdateManyWithoutUserInput> = z.object({
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  ipAddress: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userAgent: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  impersonatedBy: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  activeOrganizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  token: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AccountUpdateWithoutUserInputSchema: z.ZodType<Prisma.AccountUpdateWithoutUserInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AccountUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.AccountUncheckedUpdateWithoutUserInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AccountUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.AccountUncheckedUpdateManyWithoutUserInput> = z.object({
  accountId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  providerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  accessToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  idToken: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  password: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  accessTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  refreshTokenExpiresAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scope: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ApiKeyUpdateWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUpdateWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutApiKeysNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutKeyNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutKeyNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateManyWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const PasskeyUpdateWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUpdateWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const PasskeyUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUncheckedUpdateWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const PasskeyUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.PasskeyUncheckedUpdateManyWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publicKey: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  credentialID: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  counter: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  deviceType: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backedUp: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  transports: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const InvitationUpdateWithoutUserInputSchema: z.ZodType<Prisma.InvitationUpdateWithoutUserInput> = z.object({
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutInvitationsNestedInputSchema).optional()
}).strict();

export const InvitationUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const InvitationUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateManyWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PurchaseUpdateWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUpdateWithoutUserInput> = z.object({
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutPurchasesNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutPurchaseNestedInputSchema).optional()
}).strict();

export const PurchaseUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutPurchaseNestedInputSchema).optional()
}).strict();

export const PurchaseUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateManyWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const MemberUpdateWithoutUserInputSchema: z.ZodType<Prisma.MemberUpdateWithoutUserInput> = z.object({
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutMembersNestedInputSchema).optional()
}).strict();

export const MemberUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const MemberUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateManyWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const TwoFactorUpdateWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUpdateWithoutUserInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const TwoFactorUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUncheckedUpdateWithoutUserInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const TwoFactorUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.TwoFactorUncheckedUpdateManyWithoutUserInput> = z.object({
  secret: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  backupCodes: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatUpdateWithoutUserInputSchema: z.ZodType<Prisma.AiChatUpdateWithoutUserInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutAiChatsNestedInputSchema).optional()
}).strict();

export const AiChatUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateManyWithoutUserInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AppFileUpdateWithoutUserInputSchema: z.ZodType<Prisma.AppFileUpdateWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutFilesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneWithoutFilesNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyWithoutUserInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUpdateWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithoutUserInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutRenderHistoriesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutRenderHistoriesNestedInputSchema).optional(),
  appFile: z.lazy(() => AppFileUpdateOneWithoutRenderHistoriesNestedInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateWithoutUserInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutUserInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const JobUpdateWithoutUserInputSchema: z.ZodType<Prisma.JobUpdateWithoutUserInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutUserInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutUserInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateManyWithoutUserInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutUserInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VideoProjectUpdateWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUpdateWithoutCreatedByInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutVideoProjectNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  design: z.lazy(() => VideoDesignUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateWithoutCreatedByInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.lazy(() => VideoDesignUncheckedUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateManyWithoutCreatedByInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateManyWithoutCreatedByInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const MemberCreateManyOrganizationInputSchema: z.ZodType<Prisma.MemberCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.coerce.date()
}).strict();

export const ApiKeyCreateManyOrganizationInputSchema: z.ZodType<Prisma.ApiKeyCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string(),
  name: z.string(),
  key: z.string(),
  isDeleted: z.boolean().optional(),
  createdAt: z.coerce.date().optional().nullable()
}).strict();

export const InvitationCreateManyOrganizationInputSchema: z.ZodType<Prisma.InvitationCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  email: z.string(),
  role: z.string().optional().nullable(),
  status: z.string(),
  expiresAt: z.coerce.date(),
  inviterId: z.string()
}).strict();

export const PurchaseCreateManyOrganizationInputSchema: z.ZodType<Prisma.PurchaseCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string().optional().nullable(),
  type: z.lazy(() => PurchaseTypeSchema),
  customerId: z.string(),
  isDeleted: z.boolean().optional(),
  subscriptionId: z.string().optional().nullable(),
  credit: z.number().optional().nullable(),
  productId: z.string(),
  status: z.string().optional().nullable(),
  startDate: z.coerce.date().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AiChatCreateManyOrganizationInputSchema: z.ZodType<Prisma.AiChatCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  userId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  messages: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const AppFileCreateManyOrganizationInputSchema: z.ZodType<Prisma.AppFileCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  jobId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional()
}).strict();

export const CaptionStyleCreateManyOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  style: z.record(z.string(), z.any()).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationBrandingCreateManyOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  preset: z.string().optional().nullable(),
  size: InputJsonValueSchema.optional().nullable(),
  options: InputJsonValueSchema.optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationNarratorProfileCreateManyOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  narratorName: z.string(),
  gender: z.string(),
  provider: z.string(),
  voiceId: z.string(),
  personality: z.string(),
  styleNotes: z.string().optional().nullable(),
  ttsStyle: z.string().optional(),
  ttsPace: z.string().optional(),
  ttsAccent: z.string().optional(),
  ttsNotes: z.string().optional().nullable(),
  channelName: z.string().optional().nullable(),
  channelSignOff: z.string().optional().nullable(),
  overlayText: z.string().optional().nullable(),
  overlayTextDelayMs: z.number().int().optional().nullable(),
  overlayTextDurationMs: z.number().int().optional().nullable(),
  visualDefaults: InputJsonValueSchema.optional().nullable(),
  speechSpeed: z.number().optional(),
  avatarFileAppId: z.string().optional().nullable(),
  avatarPhotos: InputJsonValueSchema.optional().nullable(),
  avatarMinDurationMs: z.number().int().optional().nullable(),
  avatarMaxDurationMs: z.number().int().optional().nullable(),
  avatarAppearEveryMs: z.number().int().optional().nullable(),
  avatarIntroHoldMs: z.number().int().optional().nullable(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialPostCreateManyOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string().optional().nullable(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateManyOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const JobCreateManyOrganizationInputSchema: z.ZodType<Prisma.JobCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  keyId: z.string().optional().nullable(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable()
}).strict();

export const VideoProjectCreateManyOrganizationInputSchema: z.ZodType<Prisma.VideoProjectCreateManyOrganizationInput> = z.object({
  id: z.string().cuid().optional(),
  jobId: z.string(),
  createdByUserId: z.string(),
  title: z.string(),
  status: z.lazy(() => VideoProjectStatusSchema).optional(),
  inputBrief: z.record(z.string(), z.any()).optional(),
  document: z.record(z.string(), z.any()).nullable().optional().nullable(),
  validation: z.record(z.string(), z.any()).nullable().optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.number().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  publishTag: InputJsonValueSchema.optional().nullable(),
  episodeSummary: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().optional()
}).strict();

export const MemberUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUpdateWithoutOrganizationInput> = z.object({
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutMembersNestedInputSchema).optional()
}).strict();

export const MemberUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const MemberUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.MemberUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ApiKeyUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutApiKeysNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutKeyNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutKeyNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutKeyNestedInputSchema).optional()
}).strict();

export const ApiKeyUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.ApiKeyUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const InvitationUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUpdateWithoutOrganizationInput> = z.object({
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutInvitationsNestedInputSchema).optional()
}).strict();

export const InvitationUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateWithoutOrganizationInput> = z.object({
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  inviterId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const InvitationUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.InvitationUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  email: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  role: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  expiresAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  inviterId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PurchaseUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUpdateWithoutOrganizationInput> = z.object({
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneWithoutPurchasesNestedInputSchema).optional(),
  jobs: z.lazy(() => JobUpdateManyWithoutPurchaseNestedInputSchema).optional()
}).strict();

export const PurchaseUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  jobs: z.lazy(() => JobUncheckedUpdateManyWithoutPurchaseNestedInputSchema).optional()
}).strict();

export const PurchaseUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.PurchaseUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => PurchaseTypeSchema),z.lazy(() => EnumPurchaseTypeFieldUpdateOperationsInputSchema) ]).optional(),
  customerId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  subscriptionId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  credit: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  productId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startDate: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUpdateWithoutOrganizationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneWithoutAiChatsNestedInputSchema).optional()
}).strict();

export const AiChatUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AiChatUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.AiChatUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  messages: z.union([ z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })),z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AppFileUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutFilesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneWithoutFilesNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CaptionStyleUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CaptionStyleUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CaptionStyleUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.CaptionStyleUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  style: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationBrandingUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationBrandingUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preset: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  size: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  options: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedUpdateWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationNarratorProfileUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  narratorName: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  gender: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  voiceId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  personality: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  styleNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  ttsStyle: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsPace: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsAccent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  ttsNotes: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelName: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  channelSignOff: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayText: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDelayMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  overlayTextDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  visualDefaults: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  speechSpeed: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  avatarFileAppId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarPhotos: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  avatarMinDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarMaxDurationMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarAppearEveryMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  avatarIntroHoldMs: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  isDefault: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateWithoutOrganizationInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  job: z.lazy(() => JobUpdateOneWithoutSocialPostsNestedInputSchema).optional()
}).strict();

export const OrganizationSocialPostUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateWithoutOrganizationInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithoutOrganizationInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneWithoutRenderHistoryNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutRenderHistoriesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutRenderHistoriesNestedInputSchema).optional(),
  appFile: z.lazy(() => AppFileUpdateOneWithoutRenderHistoriesNestedInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateWithoutOrganizationInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const JobUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUpdateWithoutOrganizationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutOrganizationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VideoProjectUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUpdateWithoutOrganizationInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutVideoProjectNestedInputSchema).optional(),
  createdBy: z.lazy(() => UserUpdateOneRequiredWithoutVideoProjectsNestedInputSchema).optional(),
  design: z.lazy(() => VideoDesignUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateWithoutOrganizationInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdByUserId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  design: z.lazy(() => VideoDesignUncheckedUpdateOneWithoutVideoProjectNestedInputSchema).optional()
}).strict();

export const VideoProjectUncheckedUpdateManyWithoutOrganizationInputSchema: z.ZodType<Prisma.VideoProjectUncheckedUpdateManyWithoutOrganizationInput> = z.object({
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  createdByUserId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => VideoProjectStatusSchema),z.lazy(() => EnumVideoProjectStatusFieldUpdateOperationsInputSchema) ]).optional(),
  inputBrief: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  document: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  validation: z.union([ z.record(z.string(), z.any()).nullable(),z.record(z.string(), z.any()).nullable() ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => VideoProjectUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishTag: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
  episodeSummary: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const JobCreateManyPurchaseInputSchema: z.ZodType<Prisma.JobCreateManyPurchaseInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  keyId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable()
}).strict();

export const JobUpdateWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUpdateWithoutPurchaseInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutPurchaseInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateManyWithoutPurchaseInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutPurchaseInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const JobCreateManyKeyInputSchema: z.ZodType<Prisma.JobCreateManyKeyInput> = z.object({
  id: z.string().cuid().optional(),
  title: z.string(),
  description: z.string().optional().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  purchaseId: z.string().optional().nullable(),
  cost: z.number(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableCreateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.lazy(() => CreditUsageStatusSchema).optional(),
  status: z.lazy(() => JobStatusSchema).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableCreateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobCreateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.lazy(() => JobFunctionNameSchema).optional(),
  source: z.lazy(() => JobSourceSchema).optional(),
  createdAt: z.coerce.date().optional(),
  startTime: z.coerce.date().optional().nullable(),
  endTime: z.coerce.date().optional().nullable(),
  isDeleted: z.boolean().optional(),
  archivedAt: z.coerce.date().optional().nullable()
}).strict();

export const RenderHistoryCreateManyKeyInputSchema: z.ZodType<Prisma.RenderHistoryCreateManyKeyInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  jobId: z.string(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const JobUpdateWithoutKeyInputSchema: z.ZodType<Prisma.JobUpdateWithoutKeyInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutJobsNestedInputSchema).optional(),
  purchase: z.lazy(() => PurchaseUpdateOneWithoutJobsNestedInputSchema).optional(),
  video: z.lazy(() => VideoUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateWithoutKeyInputSchema: z.ZodType<Prisma.JobUncheckedUpdateWithoutKeyInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  video: z.lazy(() => VideoUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  videoProject: z.lazy(() => VideoProjectUncheckedUpdateOneWithoutJobNestedInputSchema).optional(),
  files: z.lazy(() => AppFileUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutJobNestedInputSchema).optional(),
  socialPosts: z.lazy(() => OrganizationSocialPostUncheckedUpdateManyWithoutJobNestedInputSchema).optional()
}).strict();

export const JobUncheckedUpdateManyWithoutKeyInputSchema: z.ZodType<Prisma.JobUncheckedUpdateManyWithoutKeyInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  purchaseId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  costBreakDown: z.union([ z.lazy(() => CostBreakdownNullableUpdateEnvelopeInputSchema),z.lazy(() => CostBreakdownCreateInputSchema) ]).optional().nullable(),
  creditUsageStatus: z.union([ z.lazy(() => CreditUsageStatusSchema),z.lazy(() => EnumCreditUsageStatusFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => JobStatusSchema),z.lazy(() => NullableEnumJobStatusFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  webhook: z.union([ z.lazy(() => WebhookNullableUpdateEnvelopeInputSchema),z.lazy(() => WebhookCreateInputSchema) ]).optional().nullable(),
  errorMessage: z.union([ z.lazy(() => JobUpdateerrorMessageInputSchema),z.string().array() ]).optional(),
  functionName: z.union([ z.lazy(() => JobFunctionNameSchema),z.lazy(() => EnumJobFunctionNameFieldUpdateOperationsInputSchema) ]).optional(),
  source: z.union([ z.lazy(() => JobSourceSchema),z.lazy(() => EnumJobSourceFieldUpdateOperationsInputSchema) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  endTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  isDeleted: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  archivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const RenderHistoryUpdateWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithoutKeyInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutRenderHistoryNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutRenderHistoriesNestedInputSchema).optional(),
  appFile: z.lazy(() => AppFileUpdateOneWithoutRenderHistoriesNestedInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedUpdateWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateWithoutKeyInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutKeyInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutKeyInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AppFileCreateManyJobInputSchema: z.ZodType<Prisma.AppFileCreateManyJobInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  userId: z.string(),
  organizationId: z.string().optional().nullable(),
  type: z.lazy(() => AppFileTypeSchema),
  status: z.lazy(() => AppFileStatusSchema),
  description: z.string().optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileCreatetagsInputSchema),z.string().array() ]).optional(),
  size: z.number().int().optional(),
  length: z.number().int().optional(),
  key: z.string(),
  thumbnailUrl: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryCreateManyJobInputSchema: z.ZodType<Prisma.RenderHistoryCreateManyJobInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  appFileId: z.string().optional().nullable(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const OrganizationSocialPostCreateManyJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostCreateManyJobInput> = z.object({
  id: z.string().cuid().optional(),
  organizationId: z.string(),
  provider: z.lazy(() => OrganizationSocialProviderSchema).optional(),
  status: z.lazy(() => OrganizationSocialPostStatusSchema).optional(),
  providerJobId: z.string().optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostCreateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  videoUrl: z.string().optional().nullable(),
  scheduledFor: z.coerce.date().optional().nullable(),
  publishedAt: z.coerce.date().optional().nullable(),
  lastError: z.string().optional().nullable(),
  metadata: InputJsonValueSchema.optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CostBreakdownUpdateInputSchema: z.ZodType<Prisma.CostBreakdownUpdateInput> = z.object({
  shortGen: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  youtubeProcessing: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  render: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  bRoll: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const WebhookUpdateInputSchema: z.ZodType<Prisma.WebhookUpdateInput> = z.object({
  url: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  error: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const AppFileUpdateWithoutJobInputSchema: z.ZodType<Prisma.AppFileUpdateWithoutJobInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  user: z.lazy(() => UserUpdateOneRequiredWithoutFilesNestedInputSchema).optional(),
  organization: z.lazy(() => OrganizationUpdateOneWithoutFilesNestedInputSchema).optional(),
  renderHistories: z.lazy(() => RenderHistoryUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateWithoutJobInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateWithoutJobInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  renderHistories: z.lazy(() => RenderHistoryUncheckedUpdateManyWithoutAppFileNestedInputSchema).optional()
}).strict();

export const AppFileUncheckedUpdateManyWithoutJobInputSchema: z.ZodType<Prisma.AppFileUncheckedUpdateManyWithoutJobInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  organizationId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  type: z.union([ z.lazy(() => AppFileTypeSchema),z.lazy(() => EnumAppFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => AppFileStatusSchema),z.lazy(() => EnumAppFileStatusFieldUpdateOperationsInputSchema) ]).optional(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  tags: z.union([ z.lazy(() => AppFileUpdatetagsInputSchema),z.string().array() ]).optional(),
  size: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  length: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  key: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  thumbnailUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUpdateWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithoutJobInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutRenderHistoryNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutRenderHistoriesNestedInputSchema).optional(),
  appFile: z.lazy(() => AppFileUpdateOneWithoutRenderHistoriesNestedInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedUpdateWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateWithoutJobInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutJobInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutJobInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUpdateWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateWithoutJobInput> = z.object({
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutSocialPostsNestedInputSchema).optional()
}).strict();

export const OrganizationSocialPostUncheckedUpdateWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateWithoutJobInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const OrganizationSocialPostUncheckedUpdateManyWithoutJobInputSchema: z.ZodType<Prisma.OrganizationSocialPostUncheckedUpdateManyWithoutJobInput> = z.object({
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  provider: z.union([ z.lazy(() => OrganizationSocialProviderSchema),z.lazy(() => EnumOrganizationSocialProviderFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => OrganizationSocialPostStatusSchema),z.lazy(() => EnumOrganizationSocialPostStatusFieldUpdateOperationsInputSchema) ]).optional(),
  providerJobId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  platforms: z.union([ z.lazy(() => OrganizationSocialPostUpdateplatformsInputSchema),z.string().array() ]).optional(),
  title: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  caption: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  videoUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  scheduledFor: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  publishedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  lastError: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const WordCreateInputSchema: z.ZodType<Prisma.WordCreateInput> = z.object({
  word: z.string(),
  time_begin: z.number(),
  time_end: z.number(),
  confidence: z.number()
}).strict();

export const AsdResultCreateInputSchema: z.ZodType<Prisma.AsdResultCreateInput> = z.object({
  detection_results: z.union([ z.lazy(() => AsdDetectionResultCreateInputSchema),z.lazy(() => AsdDetectionResultCreateInputSchema).array() ]).optional(),
  video_info: z.lazy(() => AsdVideoInfoCreateInputSchema),
  scenes: z.union([ z.lazy(() => AsdSceneCreateInputSchema),z.lazy(() => AsdSceneCreateInputSchema).array() ]).optional(),
  rendered_video_path: z.string().optional().nullable(),
  rendered_video_url: z.string().optional().nullable()
}).strict();

export const AsdVideoInfoCreateInputSchema: z.ZodType<Prisma.AsdVideoInfoCreateInput> = z.object({
  fps: z.number(),
  processing_fps: z.number(),
  total_frames: z.number(),
  duration: z.number(),
  start_time: z.number(),
  end_time: z.number(),
  frame_numbers_start_from: z.number(),
  resolution: z.lazy(() => AsdResolutionCreateInputSchema)
}).strict();

export const AsdConfigCreateInputSchema: z.ZodType<Prisma.AsdConfigCreateInput> = z.object({
  camera: z.lazy(() => AsdCameraConfigCreateInputSchema).optional().nullable(),
  layout: z.lazy(() => AsdLayoutConfigCreateInputSchema).optional().nullable(),
  intelligence: z.lazy(() => AsdIntelligenceConfigCreateInputSchema).optional().nullable(),
  overlaysAndTiming: z.lazy(() => AsdOverlaysAndTimingConfigCreateInputSchema).optional().nullable()
}).strict();

export const BRollItemCreatesentenceIdsInputSchema: z.ZodType<Prisma.BRollItemCreatesentenceIdsInput> = z.object({
  set: z.number().array()
}).strict();

export const SourceFilesUpdateInputSchema: z.ZodType<Prisma.SourceFilesUpdateInput> = z.object({
  isInS3: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  targetFPS: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  preview: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  previewFPS: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const VideoShortsOptionsUpdateInputSchema: z.ZodType<Prisma.VideoShortsOptionsUpdateInput> = z.object({
  predefinedShorts: z.union([ z.lazy(() => PredefinedShortsListUpdateEnvelopeInputSchema),z.lazy(() => PredefinedShortsCreateInputSchema),z.lazy(() => PredefinedShortsCreateInputSchema).array() ]).optional(),
  isRTL: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const ITranscriptionUpdateInputSchema: z.ZodType<Prisma.ITranscriptionUpdateInput> = z.object({
  prediction: z.union([ z.lazy(() => PredictionListUpdateEnvelopeInputSchema),z.lazy(() => PredictionCreateInputSchema),z.lazy(() => PredictionCreateInputSchema).array() ]).optional(),
}).strict();

export const ShortUpdateInputSchema: z.ZodType<Prisma.ShortUpdateInput> = z.object({
  id: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  reason: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  startId: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  endId: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  endTime: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  status: z.union([ z.lazy(() => ShortStatusSchema),z.lazy(() => EnumShortStatusFieldUpdateOperationsInputSchema) ]).optional(),
  text: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  cropFileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cropFilekey: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  description: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  score: z.union([ z.lazy(() => ShortScoreNullableUpdateEnvelopeInputSchema),z.lazy(() => ShortScoreCreateInputSchema) ]).optional().nullable(),
  appFileId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  asd: z.union([ z.lazy(() => AsdRootNullableUpdateEnvelopeInputSchema),z.lazy(() => AsdRootCreateInputSchema) ]).optional().nullable(),
  bRolls: z.union([ z.lazy(() => BRollItemListUpdateEnvelopeInputSchema),z.lazy(() => BRollItemCreateInputSchema),z.lazy(() => BRollItemCreateInputSchema).array() ]).optional(),
}).strict();

export const StatusHistoryUpdateInputSchema: z.ZodType<Prisma.StatusHistoryUpdateInput> = z.object({
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  startTime: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  endTime: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const RenderHistoryCreateManyAppFileInputSchema: z.ZodType<Prisma.RenderHistoryCreateManyAppFileInput> = z.object({
  id: z.string().cuid().optional(),
  renderId: z.string().optional().nullable(),
  organizationId: z.string(),
  userId: z.string().optional().nullable(),
  keyId: z.string().optional().nullable(),
  jobId: z.string(),
  target: z.number().int(),
  data: z.record(z.string(), z.any()).optional(),
  fileUrl: z.string().optional().nullable(),
  fileSize: z.string().optional().nullable(),
  cost: z.number().optional().nullable(),
  status: z.lazy(() => RenderHistoryStatusSchema).optional(),
  errorMessage: z.string().optional().nullable(),
  priority: z.number().int().optional().nullable(),
  retryCount: z.number().int().optional(),
  maxRetries: z.number().int().optional(),
  webhookReceived: z.boolean().optional(),
  webhookReceivedAt: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const RenderHistoryUpdateWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUpdateWithoutAppFileInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  organization: z.lazy(() => OrganizationUpdateOneRequiredWithoutRenderHistoryNestedInputSchema).optional(),
  user: z.lazy(() => UserUpdateOneWithoutRenderHistoryNestedInputSchema).optional(),
  key: z.lazy(() => ApiKeyUpdateOneWithoutRenderHistoriesNestedInputSchema).optional(),
  job: z.lazy(() => JobUpdateOneRequiredWithoutRenderHistoriesNestedInputSchema).optional()
}).strict();

export const RenderHistoryUncheckedUpdateWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateWithoutAppFileInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const RenderHistoryUncheckedUpdateManyWithoutAppFileInputSchema: z.ZodType<Prisma.RenderHistoryUncheckedUpdateManyWithoutAppFileInput> = z.object({
  renderId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  organizationId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  userId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  keyId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  jobId: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  target: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  data: z.union([ z.record(z.string(), z.any()),z.record(z.string(), z.any()) ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  fileSize: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cost: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  status: z.union([ z.lazy(() => RenderHistoryStatusSchema),z.lazy(() => EnumRenderHistoryStatusFieldUpdateOperationsInputSchema) ]).optional(),
  errorMessage: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  priority: z.union([ z.number().int(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  retryCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  maxRetries: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceived: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  webhookReceivedAt: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityCreateManyProxyInputSchema: z.ZodType<Prisma.CookieIdentityCreateManyProxyInput> = z.object({
  id: z.string().cuid().optional(),
  name: z.string(),
  isActive: z.boolean().optional(),
  lastRequestTime: z.coerce.date().optional().nullable(),
  cookieContent: z.string(),
  useCount: z.number().int().optional(),
  errors: z.array(z.object({ message: z.string(), timestamp: z.string() })).optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional()
}).strict();

export const CookieIdentityUpdateWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUpdateWithoutProxyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityUncheckedUpdateWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedUpdateWithoutProxyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const CookieIdentityUncheckedUpdateManyWithoutProxyInputSchema: z.ZodType<Prisma.CookieIdentityUncheckedUpdateManyWithoutProxyInput> = z.object({
  name: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  isActive: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  lastRequestTime: z.union([ z.coerce.date(),z.lazy(() => NullableDateTimeFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  cookieContent: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  useCount: z.union([ z.number().int(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  errors: z.union([ z.array(z.object({ message: z.string(), timestamp: z.string() })),z.array(z.object({ message: z.string(), timestamp: z.string() })) ]).optional(),
  createdAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
  updatedAt: z.union([ z.coerce.date(),z.lazy(() => DateTimeFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PredefinedShortsWhereInputSchema: z.ZodType<Prisma.PredefinedShortsWhereInput> = z.object({
  AND: z.union([ z.lazy(() => PredefinedShortsWhereInputSchema),z.lazy(() => PredefinedShortsWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PredefinedShortsWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PredefinedShortsWhereInputSchema),z.lazy(() => PredefinedShortsWhereInputSchema).array() ]).optional(),
  title: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  start: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  end: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
}).strict();

export const PredictionWhereInputSchema: z.ZodType<Prisma.PredictionWhereInput> = z.object({
  AND: z.union([ z.lazy(() => PredictionWhereInputSchema),z.lazy(() => PredictionWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => PredictionWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => PredictionWhereInputSchema),z.lazy(() => PredictionWhereInputSchema).array() ]).optional(),
  words: z.union([ z.lazy(() => WordCompositeListFilterSchema),z.lazy(() => WordObjectEqualityInputSchema).array() ]).optional(),
  language: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  transcription: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  confidence: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  time_begin: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  time_end: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  speaker: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  channel: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
}).strict();

export const NestedEnumShortStatusFilterSchema: z.ZodType<Prisma.NestedEnumShortStatusFilter> = z.object({
  equals: z.lazy(() => ShortStatusSchema).optional(),
  in: z.lazy(() => ShortStatusSchema).array().optional(),
  notIn: z.lazy(() => ShortStatusSchema).array().optional(),
  not: z.union([ z.lazy(() => ShortStatusSchema),z.lazy(() => NestedEnumShortStatusFilterSchema) ]).optional(),
}).strict();

export const ShortScoreWhereInputSchema: z.ZodType<Prisma.ShortScoreWhereInput> = z.object({
  AND: z.union([ z.lazy(() => ShortScoreWhereInputSchema),z.lazy(() => ShortScoreWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => ShortScoreWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => ShortScoreWhereInputSchema),z.lazy(() => ShortScoreWhereInputSchema).array() ]).optional(),
  final_creative_score: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  narrative_payoff_score: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  hook_quality_score: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  engagement_potential_score: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  brand_alignment_score: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
}).strict();

export const AsdRootWhereInputSchema: z.ZodType<Prisma.AsdRootWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdRootWhereInputSchema),z.lazy(() => AsdRootWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdRootWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdRootWhereInputSchema),z.lazy(() => AsdRootWhereInputSchema).array() ]).optional(),
  status: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  success: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  segment_id: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  timestamp: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  result: z.union([ z.lazy(() => AsdResultCompositeFilterSchema),z.lazy(() => AsdResultObjectEqualityInputSchema) ]).optional(),
  video_info: z.union([ z.lazy(() => AsdVideoInfoCompositeFilterSchema),z.lazy(() => AsdVideoInfoObjectEqualityInputSchema) ]).optional(),
  detection_count: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  scenes_count: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  has_rendered_video: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  config: z.union([ z.lazy(() => AsdConfigNullableCompositeFilterSchema),z.lazy(() => AsdConfigObjectEqualityInputSchema) ]).optional().nullable(),
}).strict();

export const BRollItemWhereInputSchema: z.ZodType<Prisma.BRollItemWhereInput> = z.object({
  AND: z.union([ z.lazy(() => BRollItemWhereInputSchema),z.lazy(() => BRollItemWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => BRollItemWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => BRollItemWhereInputSchema),z.lazy(() => BRollItemWhereInputSchema).array() ]).optional(),
  id: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  sentenceIds: z.lazy(() => IntNullableListFilterSchema).optional(),
  fileUrl: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  fileType: z.union([ z.lazy(() => EnumBRollFileTypeFilterSchema),z.lazy(() => BRollFileTypeSchema) ]).optional(),
  pexelsId: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  startTime: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  endTime: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  position: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  metadata: z.lazy(() => JsonNullableFilterSchema).optional()
}).strict();

export const AsdDetectionResultObjectEqualityInputSchema: z.ZodType<Prisma.AsdDetectionResultObjectEqualityInput> = z.object({
  frame_number: z.number(),
  faces: z.lazy(() => AsdFaceObjectEqualityInputSchema).array().optional()
}).strict();

export const AsdSceneObjectEqualityInputSchema: z.ZodType<Prisma.AsdSceneObjectEqualityInput> = z.object({
  scene_number: z.number(),
  start_seconds: z.number(),
  end_seconds: z.number(),
  start_timecode: z.string(),
  end_timecode: z.string(),
  start_frame: z.number(),
  end_frame: z.number()
}).strict();

export const AsdResolutionObjectEqualityInputSchema: z.ZodType<Prisma.AsdResolutionObjectEqualityInput> = z.object({
  width: z.number(),
  height: z.number()
}).strict();

export const AsdCameraConfigObjectEqualityInputSchema: z.ZodType<Prisma.AsdCameraConfigObjectEqualityInput> = z.object({
  facePaddingFactor: z.number().optional().nullable(),
  maxZoomFactor: z.number().optional().nullable(),
  smoothingFactor: z.number().optional().nullable(),
  predictionFrames: z.number().optional().nullable(),
  predictionWeight: z.number().optional().nullable(),
  movementThresholdFactor: z.number().optional().nullable()
}).strict();

export const AsdLayoutConfigObjectEqualityInputSchema: z.ZodType<Prisma.AsdLayoutConfigObjectEqualityInput> = z.object({
  minIndicatorDurationFrames: z.number().optional().nullable(),
  speakerLingerFrames: z.number().optional().nullable()
}).strict();

export const AsdIntelligenceConfigObjectEqualityInputSchema: z.ZodType<Prisma.AsdIntelligenceConfigObjectEqualityInput> = z.object({
  focusSpeakingScoreThreshold: z.number().optional().nullable(),
  maxSpeakersToFocus: z.number().optional().nullable(),
  disableFocusThreshold: z.number().optional().nullable(),
  speakerDominanceWindowFrames: z.number().optional().nullable()
}).strict();

export const AsdOverlaysAndTimingConfigObjectEqualityInputSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigObjectEqualityInput> = z.object({
  showFaceBoundingBoxes: z.boolean().optional().nullable(),
  asdFrameOffset: z.number().optional().nullable()
}).strict();

export const AsdDetectionResultCreateInputSchema: z.ZodType<Prisma.AsdDetectionResultCreateInput> = z.object({
  frame_number: z.number(),
  faces: z.union([ z.lazy(() => AsdFaceCreateInputSchema),z.lazy(() => AsdFaceCreateInputSchema).array() ]).optional(),
}).strict();

export const AsdSceneCreateInputSchema: z.ZodType<Prisma.AsdSceneCreateInput> = z.object({
  scene_number: z.number(),
  start_seconds: z.number(),
  end_seconds: z.number(),
  start_timecode: z.string(),
  end_timecode: z.string(),
  start_frame: z.number(),
  end_frame: z.number()
}).strict();

export const AsdResolutionCreateInputSchema: z.ZodType<Prisma.AsdResolutionCreateInput> = z.object({
  width: z.number(),
  height: z.number()
}).strict();

export const AsdCameraConfigCreateInputSchema: z.ZodType<Prisma.AsdCameraConfigCreateInput> = z.object({
  facePaddingFactor: z.number().optional().nullable(),
  maxZoomFactor: z.number().optional().nullable(),
  smoothingFactor: z.number().optional().nullable(),
  predictionFrames: z.number().optional().nullable(),
  predictionWeight: z.number().optional().nullable(),
  movementThresholdFactor: z.number().optional().nullable()
}).strict();

export const AsdLayoutConfigCreateInputSchema: z.ZodType<Prisma.AsdLayoutConfigCreateInput> = z.object({
  minIndicatorDurationFrames: z.number().optional().nullable(),
  speakerLingerFrames: z.number().optional().nullable()
}).strict();

export const AsdIntelligenceConfigCreateInputSchema: z.ZodType<Prisma.AsdIntelligenceConfigCreateInput> = z.object({
  focusSpeakingScoreThreshold: z.number().optional().nullable(),
  maxSpeakersToFocus: z.number().optional().nullable(),
  disableFocusThreshold: z.number().optional().nullable(),
  speakerDominanceWindowFrames: z.number().optional().nullable()
}).strict();

export const AsdOverlaysAndTimingConfigCreateInputSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigCreateInput> = z.object({
  showFaceBoundingBoxes: z.boolean().optional().nullable(),
  asdFrameOffset: z.number().optional().nullable()
}).strict();

export const PredefinedShortsListUpdateEnvelopeInputSchema: z.ZodType<Prisma.PredefinedShortsListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => PredefinedShortsCreateInputSchema),z.lazy(() => PredefinedShortsCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => PredefinedShortsCreateInputSchema),z.lazy(() => PredefinedShortsCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => PredefinedShortsUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => PredefinedShortsDeleteManyInputSchema).optional()
}).strict();

export const PredictionListUpdateEnvelopeInputSchema: z.ZodType<Prisma.PredictionListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => PredictionCreateInputSchema),z.lazy(() => PredictionCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => PredictionCreateInputSchema),z.lazy(() => PredictionCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => PredictionUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => PredictionDeleteManyInputSchema).optional()
}).strict();

export const EnumShortStatusFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumShortStatusFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => ShortStatusSchema).optional()
}).strict();

export const ShortScoreNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.ShortScoreNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => ShortScoreCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => ShortScoreUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const AsdRootNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdRootNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdRootCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => AsdRootUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const BRollItemListUpdateEnvelopeInputSchema: z.ZodType<Prisma.BRollItemListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => BRollItemCreateInputSchema),z.lazy(() => BRollItemCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => BRollItemCreateInputSchema),z.lazy(() => BRollItemCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => BRollItemUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => BRollItemDeleteManyInputSchema).optional()
}).strict();

export const WordCompositeListFilterSchema: z.ZodType<Prisma.WordCompositeListFilter> = z.object({
  equals: z.lazy(() => WordObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => WordWhereInputSchema).optional(),
  some: z.lazy(() => WordWhereInputSchema).optional(),
  none: z.lazy(() => WordWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const AsdResultCompositeFilterSchema: z.ZodType<Prisma.AsdResultCompositeFilter> = z.object({
  equals: z.lazy(() => AsdResultObjectEqualityInputSchema).optional(),
  is: z.lazy(() => AsdResultWhereInputSchema).optional(),
  isNot: z.lazy(() => AsdResultWhereInputSchema).optional()
}).strict();

export const AsdVideoInfoCompositeFilterSchema: z.ZodType<Prisma.AsdVideoInfoCompositeFilter> = z.object({
  equals: z.lazy(() => AsdVideoInfoObjectEqualityInputSchema).optional(),
  is: z.lazy(() => AsdVideoInfoWhereInputSchema).optional(),
  isNot: z.lazy(() => AsdVideoInfoWhereInputSchema).optional()
}).strict();

export const AsdConfigNullableCompositeFilterSchema: z.ZodType<Prisma.AsdConfigNullableCompositeFilter> = z.object({
  equals: z.lazy(() => AsdConfigObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => AsdConfigWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AsdConfigWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const IntNullableListFilterSchema: z.ZodType<Prisma.IntNullableListFilter> = z.object({
  equals: z.number().array().optional().nullable(),
  has: z.number().optional().nullable(),
  hasEvery: z.number().array().optional(),
  hasSome: z.number().array().optional(),
  isEmpty: z.boolean().optional()
}).strict();

export const EnumBRollFileTypeFilterSchema: z.ZodType<Prisma.EnumBRollFileTypeFilter> = z.object({
  equals: z.lazy(() => BRollFileTypeSchema).optional(),
  in: z.lazy(() => BRollFileTypeSchema).array().optional(),
  notIn: z.lazy(() => BRollFileTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => BRollFileTypeSchema),z.lazy(() => NestedEnumBRollFileTypeFilterSchema) ]).optional(),
}).strict();

export const AsdFaceObjectEqualityInputSchema: z.ZodType<Prisma.AsdFaceObjectEqualityInput> = z.object({
  x1: z.number(),
  y1: z.number(),
  x2: z.number(),
  y2: z.number(),
  speaking_score: z.number(),
  active: z.boolean(),
  track_id: z.number()
}).strict();

export const AsdFaceCreateInputSchema: z.ZodType<Prisma.AsdFaceCreateInput> = z.object({
  x1: z.number(),
  y1: z.number(),
  x2: z.number(),
  y2: z.number(),
  speaking_score: z.number(),
  active: z.boolean(),
  track_id: z.number()
}).strict();

export const PredefinedShortsUpdateManyInputSchema: z.ZodType<Prisma.PredefinedShortsUpdateManyInput> = z.object({
  where: z.lazy(() => PredefinedShortsWhereInputSchema),
  data: z.lazy(() => PredefinedShortsUpdateInputSchema)
}).strict();

export const PredefinedShortsDeleteManyInputSchema: z.ZodType<Prisma.PredefinedShortsDeleteManyInput> = z.object({
  where: z.lazy(() => PredefinedShortsWhereInputSchema)
}).strict();

export const PredictionUpdateManyInputSchema: z.ZodType<Prisma.PredictionUpdateManyInput> = z.object({
  where: z.lazy(() => PredictionWhereInputSchema),
  data: z.lazy(() => PredictionUpdateInputSchema)
}).strict();

export const PredictionDeleteManyInputSchema: z.ZodType<Prisma.PredictionDeleteManyInput> = z.object({
  where: z.lazy(() => PredictionWhereInputSchema)
}).strict();

export const ShortScoreUpsertInputSchema: z.ZodType<Prisma.ShortScoreUpsertInput> = z.object({
  set: z.lazy(() => ShortScoreCreateInputSchema).nullable(),
  update: z.lazy(() => ShortScoreUpdateInputSchema)
}).strict();

export const AsdRootUpsertInputSchema: z.ZodType<Prisma.AsdRootUpsertInput> = z.object({
  set: z.lazy(() => AsdRootCreateInputSchema).nullable(),
  update: z.lazy(() => AsdRootUpdateInputSchema)
}).strict();

export const BRollItemUpdateManyInputSchema: z.ZodType<Prisma.BRollItemUpdateManyInput> = z.object({
  where: z.lazy(() => BRollItemWhereInputSchema),
  data: z.lazy(() => BRollItemUpdateInputSchema)
}).strict();

export const BRollItemDeleteManyInputSchema: z.ZodType<Prisma.BRollItemDeleteManyInput> = z.object({
  where: z.lazy(() => BRollItemWhereInputSchema)
}).strict();

export const WordWhereInputSchema: z.ZodType<Prisma.WordWhereInput> = z.object({
  AND: z.union([ z.lazy(() => WordWhereInputSchema),z.lazy(() => WordWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => WordWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => WordWhereInputSchema),z.lazy(() => WordWhereInputSchema).array() ]).optional(),
  word: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  time_begin: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  time_end: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  confidence: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
}).strict();

export const AsdResultWhereInputSchema: z.ZodType<Prisma.AsdResultWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdResultWhereInputSchema),z.lazy(() => AsdResultWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdResultWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdResultWhereInputSchema),z.lazy(() => AsdResultWhereInputSchema).array() ]).optional(),
  detection_results: z.union([ z.lazy(() => AsdDetectionResultCompositeListFilterSchema),z.lazy(() => AsdDetectionResultObjectEqualityInputSchema).array() ]).optional(),
  video_info: z.union([ z.lazy(() => AsdVideoInfoCompositeFilterSchema),z.lazy(() => AsdVideoInfoObjectEqualityInputSchema) ]).optional(),
  scenes: z.union([ z.lazy(() => AsdSceneCompositeListFilterSchema),z.lazy(() => AsdSceneObjectEqualityInputSchema).array() ]).optional(),
  rendered_video_path: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
  rendered_video_url: z.union([ z.lazy(() => StringNullableFilterSchema),z.string() ]).optional().nullable(),
}).strict();

export const AsdVideoInfoWhereInputSchema: z.ZodType<Prisma.AsdVideoInfoWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdVideoInfoWhereInputSchema),z.lazy(() => AsdVideoInfoWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdVideoInfoWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdVideoInfoWhereInputSchema),z.lazy(() => AsdVideoInfoWhereInputSchema).array() ]).optional(),
  fps: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  processing_fps: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  total_frames: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  duration: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  start_time: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  end_time: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  frame_numbers_start_from: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  resolution: z.union([ z.lazy(() => AsdResolutionCompositeFilterSchema),z.lazy(() => AsdResolutionObjectEqualityInputSchema) ]).optional(),
}).strict();

export const AsdConfigWhereInputSchema: z.ZodType<Prisma.AsdConfigWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdConfigWhereInputSchema),z.lazy(() => AsdConfigWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdConfigWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdConfigWhereInputSchema),z.lazy(() => AsdConfigWhereInputSchema).array() ]).optional(),
  camera: z.union([ z.lazy(() => AsdCameraConfigNullableCompositeFilterSchema),z.lazy(() => AsdCameraConfigObjectEqualityInputSchema) ]).optional().nullable(),
  layout: z.union([ z.lazy(() => AsdLayoutConfigNullableCompositeFilterSchema),z.lazy(() => AsdLayoutConfigObjectEqualityInputSchema) ]).optional().nullable(),
  intelligence: z.union([ z.lazy(() => AsdIntelligenceConfigNullableCompositeFilterSchema),z.lazy(() => AsdIntelligenceConfigObjectEqualityInputSchema) ]).optional().nullable(),
  overlaysAndTiming: z.union([ z.lazy(() => AsdOverlaysAndTimingConfigNullableCompositeFilterSchema),z.lazy(() => AsdOverlaysAndTimingConfigObjectEqualityInputSchema) ]).optional().nullable(),
}).strict();

export const NestedEnumBRollFileTypeFilterSchema: z.ZodType<Prisma.NestedEnumBRollFileTypeFilter> = z.object({
  equals: z.lazy(() => BRollFileTypeSchema).optional(),
  in: z.lazy(() => BRollFileTypeSchema).array().optional(),
  notIn: z.lazy(() => BRollFileTypeSchema).array().optional(),
  not: z.union([ z.lazy(() => BRollFileTypeSchema),z.lazy(() => NestedEnumBRollFileTypeFilterSchema) ]).optional(),
}).strict();

export const PredefinedShortsUpdateInputSchema: z.ZodType<Prisma.PredefinedShortsUpdateInput> = z.object({
  title: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  start: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  end: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const PredictionUpdateInputSchema: z.ZodType<Prisma.PredictionUpdateInput> = z.object({
  words: z.union([ z.lazy(() => WordListUpdateEnvelopeInputSchema),z.lazy(() => WordCreateInputSchema),z.lazy(() => WordCreateInputSchema).array() ]).optional(),
  language: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  transcription: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  confidence: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  time_begin: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  time_end: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  speaker: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  channel: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const ShortScoreUpdateInputSchema: z.ZodType<Prisma.ShortScoreUpdateInput> = z.object({
  final_creative_score: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  narrative_payoff_score: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  hook_quality_score: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  engagement_potential_score: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  brand_alignment_score: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AsdRootUpdateInputSchema: z.ZodType<Prisma.AsdRootUpdateInput> = z.object({
  status: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  success: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  segment_id: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  timestamp: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  result: z.union([ z.lazy(() => AsdResultUpdateEnvelopeInputSchema),z.lazy(() => AsdResultCreateInputSchema) ]).optional(),
  video_info: z.union([ z.lazy(() => AsdVideoInfoUpdateEnvelopeInputSchema),z.lazy(() => AsdVideoInfoCreateInputSchema) ]).optional(),
  detection_count: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  scenes_count: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  has_rendered_video: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  config: z.union([ z.lazy(() => AsdConfigNullableUpdateEnvelopeInputSchema),z.lazy(() => AsdConfigCreateInputSchema) ]).optional().nullable(),
}).strict();

export const BRollItemUpdateInputSchema: z.ZodType<Prisma.BRollItemUpdateInput> = z.object({
  id: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  sentenceIds: z.union([ z.lazy(() => BRollItemUpdatesentenceIdsInputSchema),z.number().array() ]).optional(),
  fileUrl: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  fileType: z.union([ z.lazy(() => BRollFileTypeSchema),z.lazy(() => EnumBRollFileTypeFieldUpdateOperationsInputSchema) ]).optional(),
  pexelsId: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  startTime: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  endTime: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  position: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  metadata: z.union([ InputJsonValueSchema,InputJsonValueSchema ]).optional().nullable(),
}).strict();

export const AsdDetectionResultCompositeListFilterSchema: z.ZodType<Prisma.AsdDetectionResultCompositeListFilter> = z.object({
  equals: z.lazy(() => AsdDetectionResultObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => AsdDetectionResultWhereInputSchema).optional(),
  some: z.lazy(() => AsdDetectionResultWhereInputSchema).optional(),
  none: z.lazy(() => AsdDetectionResultWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const AsdSceneCompositeListFilterSchema: z.ZodType<Prisma.AsdSceneCompositeListFilter> = z.object({
  equals: z.lazy(() => AsdSceneObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => AsdSceneWhereInputSchema).optional(),
  some: z.lazy(() => AsdSceneWhereInputSchema).optional(),
  none: z.lazy(() => AsdSceneWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const AsdResolutionCompositeFilterSchema: z.ZodType<Prisma.AsdResolutionCompositeFilter> = z.object({
  equals: z.lazy(() => AsdResolutionObjectEqualityInputSchema).optional(),
  is: z.lazy(() => AsdResolutionWhereInputSchema).optional(),
  isNot: z.lazy(() => AsdResolutionWhereInputSchema).optional()
}).strict();

export const AsdCameraConfigNullableCompositeFilterSchema: z.ZodType<Prisma.AsdCameraConfigNullableCompositeFilter> = z.object({
  equals: z.lazy(() => AsdCameraConfigObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => AsdCameraConfigWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AsdCameraConfigWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const AsdLayoutConfigNullableCompositeFilterSchema: z.ZodType<Prisma.AsdLayoutConfigNullableCompositeFilter> = z.object({
  equals: z.lazy(() => AsdLayoutConfigObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => AsdLayoutConfigWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AsdLayoutConfigWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const AsdIntelligenceConfigNullableCompositeFilterSchema: z.ZodType<Prisma.AsdIntelligenceConfigNullableCompositeFilter> = z.object({
  equals: z.lazy(() => AsdIntelligenceConfigObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => AsdIntelligenceConfigWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AsdIntelligenceConfigWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const AsdOverlaysAndTimingConfigNullableCompositeFilterSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigNullableCompositeFilter> = z.object({
  equals: z.lazy(() => AsdOverlaysAndTimingConfigObjectEqualityInputSchema).optional().nullable(),
  is: z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema).optional().nullable(),
  isNot: z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema).optional().nullable(),
  isSet: z.boolean().optional()
}).strict();

export const WordListUpdateEnvelopeInputSchema: z.ZodType<Prisma.WordListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => WordCreateInputSchema),z.lazy(() => WordCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => WordCreateInputSchema),z.lazy(() => WordCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => WordUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => WordDeleteManyInputSchema).optional()
}).strict();

export const AsdResultUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdResultUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdResultCreateInputSchema).optional(),
  update: z.lazy(() => AsdResultUpdateInputSchema).optional()
}).strict();

export const AsdVideoInfoUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdVideoInfoUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdVideoInfoCreateInputSchema).optional(),
  update: z.lazy(() => AsdVideoInfoUpdateInputSchema).optional()
}).strict();

export const AsdConfigNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdConfigNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdConfigCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => AsdConfigUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const BRollItemUpdatesentenceIdsInputSchema: z.ZodType<Prisma.BRollItemUpdatesentenceIdsInput> = z.object({
  set: z.number().array().optional(),
  push: z.union([ z.number(),z.number().array() ]).optional(),
}).strict();

export const EnumBRollFileTypeFieldUpdateOperationsInputSchema: z.ZodType<Prisma.EnumBRollFileTypeFieldUpdateOperationsInput> = z.object({
  set: z.lazy(() => BRollFileTypeSchema).optional()
}).strict();

export const AsdDetectionResultWhereInputSchema: z.ZodType<Prisma.AsdDetectionResultWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdDetectionResultWhereInputSchema),z.lazy(() => AsdDetectionResultWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdDetectionResultWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdDetectionResultWhereInputSchema),z.lazy(() => AsdDetectionResultWhereInputSchema).array() ]).optional(),
  frame_number: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  faces: z.union([ z.lazy(() => AsdFaceCompositeListFilterSchema),z.lazy(() => AsdFaceObjectEqualityInputSchema).array() ]).optional(),
}).strict();

export const AsdSceneWhereInputSchema: z.ZodType<Prisma.AsdSceneWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdSceneWhereInputSchema),z.lazy(() => AsdSceneWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdSceneWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdSceneWhereInputSchema),z.lazy(() => AsdSceneWhereInputSchema).array() ]).optional(),
  scene_number: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  start_seconds: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  end_seconds: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  start_timecode: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  end_timecode: z.union([ z.lazy(() => StringFilterSchema),z.string() ]).optional(),
  start_frame: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  end_frame: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
}).strict();

export const AsdResolutionWhereInputSchema: z.ZodType<Prisma.AsdResolutionWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdResolutionWhereInputSchema),z.lazy(() => AsdResolutionWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdResolutionWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdResolutionWhereInputSchema),z.lazy(() => AsdResolutionWhereInputSchema).array() ]).optional(),
  width: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
  height: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
}).strict();

export const AsdCameraConfigWhereInputSchema: z.ZodType<Prisma.AsdCameraConfigWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdCameraConfigWhereInputSchema),z.lazy(() => AsdCameraConfigWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdCameraConfigWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdCameraConfigWhereInputSchema),z.lazy(() => AsdCameraConfigWhereInputSchema).array() ]).optional(),
  facePaddingFactor: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  maxZoomFactor: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  smoothingFactor: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  predictionFrames: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  predictionWeight: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  movementThresholdFactor: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const AsdLayoutConfigWhereInputSchema: z.ZodType<Prisma.AsdLayoutConfigWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdLayoutConfigWhereInputSchema),z.lazy(() => AsdLayoutConfigWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdLayoutConfigWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdLayoutConfigWhereInputSchema),z.lazy(() => AsdLayoutConfigWhereInputSchema).array() ]).optional(),
  minIndicatorDurationFrames: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  speakerLingerFrames: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const AsdIntelligenceConfigWhereInputSchema: z.ZodType<Prisma.AsdIntelligenceConfigWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdIntelligenceConfigWhereInputSchema),z.lazy(() => AsdIntelligenceConfigWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdIntelligenceConfigWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdIntelligenceConfigWhereInputSchema),z.lazy(() => AsdIntelligenceConfigWhereInputSchema).array() ]).optional(),
  focusSpeakingScoreThreshold: z.union([ z.lazy(() => FloatNullableFilterSchema),z.number() ]).optional().nullable(),
  maxSpeakersToFocus: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  disableFocusThreshold: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
  speakerDominanceWindowFrames: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const AsdOverlaysAndTimingConfigWhereInputSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema),z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema),z.lazy(() => AsdOverlaysAndTimingConfigWhereInputSchema).array() ]).optional(),
  showFaceBoundingBoxes: z.union([ z.lazy(() => BoolNullableFilterSchema),z.boolean() ]).optional().nullable(),
  asdFrameOffset: z.union([ z.lazy(() => IntNullableFilterSchema),z.number() ]).optional().nullable(),
}).strict();

export const WordUpdateManyInputSchema: z.ZodType<Prisma.WordUpdateManyInput> = z.object({
  where: z.lazy(() => WordWhereInputSchema),
  data: z.lazy(() => WordUpdateInputSchema)
}).strict();

export const WordDeleteManyInputSchema: z.ZodType<Prisma.WordDeleteManyInput> = z.object({
  where: z.lazy(() => WordWhereInputSchema)
}).strict();

export const AsdResultUpdateInputSchema: z.ZodType<Prisma.AsdResultUpdateInput> = z.object({
  detection_results: z.union([ z.lazy(() => AsdDetectionResultListUpdateEnvelopeInputSchema),z.lazy(() => AsdDetectionResultCreateInputSchema),z.lazy(() => AsdDetectionResultCreateInputSchema).array() ]).optional(),
  video_info: z.union([ z.lazy(() => AsdVideoInfoUpdateEnvelopeInputSchema),z.lazy(() => AsdVideoInfoCreateInputSchema) ]).optional(),
  scenes: z.union([ z.lazy(() => AsdSceneListUpdateEnvelopeInputSchema),z.lazy(() => AsdSceneCreateInputSchema),z.lazy(() => AsdSceneCreateInputSchema).array() ]).optional(),
  rendered_video_path: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  rendered_video_url: z.union([ z.string(),z.lazy(() => NullableStringFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const AsdVideoInfoUpdateInputSchema: z.ZodType<Prisma.AsdVideoInfoUpdateInput> = z.object({
  fps: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  processing_fps: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  total_frames: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  duration: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  start_time: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  end_time: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  frame_numbers_start_from: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  resolution: z.union([ z.lazy(() => AsdResolutionUpdateEnvelopeInputSchema),z.lazy(() => AsdResolutionCreateInputSchema) ]).optional(),
}).strict();

export const AsdConfigUpsertInputSchema: z.ZodType<Prisma.AsdConfigUpsertInput> = z.object({
  set: z.lazy(() => AsdConfigCreateInputSchema).nullable(),
  update: z.lazy(() => AsdConfigUpdateInputSchema)
}).strict();

export const AsdFaceCompositeListFilterSchema: z.ZodType<Prisma.AsdFaceCompositeListFilter> = z.object({
  equals: z.lazy(() => AsdFaceObjectEqualityInputSchema).array().optional(),
  every: z.lazy(() => AsdFaceWhereInputSchema).optional(),
  some: z.lazy(() => AsdFaceWhereInputSchema).optional(),
  none: z.lazy(() => AsdFaceWhereInputSchema).optional(),
  isEmpty: z.boolean().optional(),
  isSet: z.boolean().optional()
}).strict();

export const WordUpdateInputSchema: z.ZodType<Prisma.WordUpdateInput> = z.object({
  word: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  time_begin: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  time_end: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  confidence: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AsdDetectionResultListUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdDetectionResultListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => AsdDetectionResultCreateInputSchema),z.lazy(() => AsdDetectionResultCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => AsdDetectionResultCreateInputSchema),z.lazy(() => AsdDetectionResultCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => AsdDetectionResultUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => AsdDetectionResultDeleteManyInputSchema).optional()
}).strict();

export const AsdSceneListUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdSceneListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => AsdSceneCreateInputSchema),z.lazy(() => AsdSceneCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => AsdSceneCreateInputSchema),z.lazy(() => AsdSceneCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => AsdSceneUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => AsdSceneDeleteManyInputSchema).optional()
}).strict();

export const AsdResolutionUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdResolutionUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdResolutionCreateInputSchema).optional(),
  update: z.lazy(() => AsdResolutionUpdateInputSchema).optional()
}).strict();

export const AsdConfigUpdateInputSchema: z.ZodType<Prisma.AsdConfigUpdateInput> = z.object({
  camera: z.union([ z.lazy(() => AsdCameraConfigNullableUpdateEnvelopeInputSchema),z.lazy(() => AsdCameraConfigCreateInputSchema) ]).optional().nullable(),
  layout: z.union([ z.lazy(() => AsdLayoutConfigNullableUpdateEnvelopeInputSchema),z.lazy(() => AsdLayoutConfigCreateInputSchema) ]).optional().nullable(),
  intelligence: z.union([ z.lazy(() => AsdIntelligenceConfigNullableUpdateEnvelopeInputSchema),z.lazy(() => AsdIntelligenceConfigCreateInputSchema) ]).optional().nullable(),
  overlaysAndTiming: z.union([ z.lazy(() => AsdOverlaysAndTimingConfigNullableUpdateEnvelopeInputSchema),z.lazy(() => AsdOverlaysAndTimingConfigCreateInputSchema) ]).optional().nullable(),
}).strict();

export const AsdFaceWhereInputSchema: z.ZodType<Prisma.AsdFaceWhereInput> = z.object({
  AND: z.union([ z.lazy(() => AsdFaceWhereInputSchema),z.lazy(() => AsdFaceWhereInputSchema).array() ]).optional(),
  OR: z.lazy(() => AsdFaceWhereInputSchema).array().optional(),
  NOT: z.union([ z.lazy(() => AsdFaceWhereInputSchema),z.lazy(() => AsdFaceWhereInputSchema).array() ]).optional(),
  x1: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  y1: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  x2: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  y2: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  speaking_score: z.union([ z.lazy(() => FloatFilterSchema),z.number() ]).optional(),
  active: z.union([ z.lazy(() => BoolFilterSchema),z.boolean() ]).optional(),
  track_id: z.union([ z.lazy(() => IntFilterSchema),z.number() ]).optional(),
}).strict();

export const AsdDetectionResultUpdateManyInputSchema: z.ZodType<Prisma.AsdDetectionResultUpdateManyInput> = z.object({
  where: z.lazy(() => AsdDetectionResultWhereInputSchema),
  data: z.lazy(() => AsdDetectionResultUpdateInputSchema)
}).strict();

export const AsdDetectionResultDeleteManyInputSchema: z.ZodType<Prisma.AsdDetectionResultDeleteManyInput> = z.object({
  where: z.lazy(() => AsdDetectionResultWhereInputSchema)
}).strict();

export const AsdSceneUpdateManyInputSchema: z.ZodType<Prisma.AsdSceneUpdateManyInput> = z.object({
  where: z.lazy(() => AsdSceneWhereInputSchema),
  data: z.lazy(() => AsdSceneUpdateInputSchema)
}).strict();

export const AsdSceneDeleteManyInputSchema: z.ZodType<Prisma.AsdSceneDeleteManyInput> = z.object({
  where: z.lazy(() => AsdSceneWhereInputSchema)
}).strict();

export const AsdResolutionUpdateInputSchema: z.ZodType<Prisma.AsdResolutionUpdateInput> = z.object({
  width: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  height: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AsdCameraConfigNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdCameraConfigNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdCameraConfigCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => AsdCameraConfigUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const AsdLayoutConfigNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdLayoutConfigNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdLayoutConfigCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => AsdLayoutConfigUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const AsdIntelligenceConfigNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdIntelligenceConfigNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdIntelligenceConfigCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => AsdIntelligenceConfigUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const AsdOverlaysAndTimingConfigNullableUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigNullableUpdateEnvelopeInput> = z.object({
  set: z.lazy(() => AsdOverlaysAndTimingConfigCreateInputSchema).optional().nullable(),
  upsert: z.lazy(() => AsdOverlaysAndTimingConfigUpsertInputSchema).optional(),
  unset: z.boolean().optional()
}).strict();

export const AsdDetectionResultUpdateInputSchema: z.ZodType<Prisma.AsdDetectionResultUpdateInput> = z.object({
  frame_number: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  faces: z.union([ z.lazy(() => AsdFaceListUpdateEnvelopeInputSchema),z.lazy(() => AsdFaceCreateInputSchema),z.lazy(() => AsdFaceCreateInputSchema).array() ]).optional(),
}).strict();

export const AsdSceneUpdateInputSchema: z.ZodType<Prisma.AsdSceneUpdateInput> = z.object({
  scene_number: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  start_seconds: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  end_seconds: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  start_timecode: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  end_timecode: z.union([ z.string(),z.lazy(() => StringFieldUpdateOperationsInputSchema) ]).optional(),
  start_frame: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
  end_frame: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

export const AsdCameraConfigUpsertInputSchema: z.ZodType<Prisma.AsdCameraConfigUpsertInput> = z.object({
  set: z.lazy(() => AsdCameraConfigCreateInputSchema).nullable(),
  update: z.lazy(() => AsdCameraConfigUpdateInputSchema)
}).strict();

export const AsdLayoutConfigUpsertInputSchema: z.ZodType<Prisma.AsdLayoutConfigUpsertInput> = z.object({
  set: z.lazy(() => AsdLayoutConfigCreateInputSchema).nullable(),
  update: z.lazy(() => AsdLayoutConfigUpdateInputSchema)
}).strict();

export const AsdIntelligenceConfigUpsertInputSchema: z.ZodType<Prisma.AsdIntelligenceConfigUpsertInput> = z.object({
  set: z.lazy(() => AsdIntelligenceConfigCreateInputSchema).nullable(),
  update: z.lazy(() => AsdIntelligenceConfigUpdateInputSchema)
}).strict();

export const AsdOverlaysAndTimingConfigUpsertInputSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigUpsertInput> = z.object({
  set: z.lazy(() => AsdOverlaysAndTimingConfigCreateInputSchema).nullable(),
  update: z.lazy(() => AsdOverlaysAndTimingConfigUpdateInputSchema)
}).strict();

export const AsdFaceListUpdateEnvelopeInputSchema: z.ZodType<Prisma.AsdFaceListUpdateEnvelopeInput> = z.object({
  set: z.union([ z.lazy(() => AsdFaceCreateInputSchema),z.lazy(() => AsdFaceCreateInputSchema).array() ]).optional(),
  push: z.union([ z.lazy(() => AsdFaceCreateInputSchema),z.lazy(() => AsdFaceCreateInputSchema).array() ]).optional(),
  updateMany: z.lazy(() => AsdFaceUpdateManyInputSchema).optional(),
  deleteMany: z.lazy(() => AsdFaceDeleteManyInputSchema).optional()
}).strict();

export const AsdCameraConfigUpdateInputSchema: z.ZodType<Prisma.AsdCameraConfigUpdateInput> = z.object({
  facePaddingFactor: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  maxZoomFactor: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  smoothingFactor: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  predictionFrames: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  predictionWeight: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  movementThresholdFactor: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const AsdLayoutConfigUpdateInputSchema: z.ZodType<Prisma.AsdLayoutConfigUpdateInput> = z.object({
  minIndicatorDurationFrames: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  speakerLingerFrames: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const AsdIntelligenceConfigUpdateInputSchema: z.ZodType<Prisma.AsdIntelligenceConfigUpdateInput> = z.object({
  focusSpeakingScoreThreshold: z.union([ z.number(),z.lazy(() => NullableFloatFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  maxSpeakersToFocus: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  disableFocusThreshold: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  speakerDominanceWindowFrames: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const AsdOverlaysAndTimingConfigUpdateInputSchema: z.ZodType<Prisma.AsdOverlaysAndTimingConfigUpdateInput> = z.object({
  showFaceBoundingBoxes: z.union([ z.boolean(),z.lazy(() => NullableBoolFieldUpdateOperationsInputSchema) ]).optional().nullable(),
  asdFrameOffset: z.union([ z.number(),z.lazy(() => NullableIntFieldUpdateOperationsInputSchema) ]).optional().nullable(),
}).strict();

export const AsdFaceUpdateManyInputSchema: z.ZodType<Prisma.AsdFaceUpdateManyInput> = z.object({
  where: z.lazy(() => AsdFaceWhereInputSchema),
  data: z.lazy(() => AsdFaceUpdateInputSchema)
}).strict();

export const AsdFaceDeleteManyInputSchema: z.ZodType<Prisma.AsdFaceDeleteManyInput> = z.object({
  where: z.lazy(() => AsdFaceWhereInputSchema)
}).strict();

export const AsdFaceUpdateInputSchema: z.ZodType<Prisma.AsdFaceUpdateInput> = z.object({
  x1: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  y1: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  x2: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  y2: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  speaking_score: z.union([ z.number(),z.lazy(() => FloatFieldUpdateOperationsInputSchema) ]).optional(),
  active: z.union([ z.boolean(),z.lazy(() => BoolFieldUpdateOperationsInputSchema) ]).optional(),
  track_id: z.union([ z.number(),z.lazy(() => IntFieldUpdateOperationsInputSchema) ]).optional(),
}).strict();

/////////////////////////////////////////
// ARGS
/////////////////////////////////////////

export const UserFindFirstArgsSchema: z.ZodType<Omit<Prisma.UserFindFirstArgs, "select">> = z.object({
  where: UserWhereInputSchema.optional(),
  orderBy: z.union([ UserOrderByWithRelationInputSchema.array(),UserOrderByWithRelationInputSchema ]).optional(),
  cursor: UserWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ UserScalarFieldEnumSchema,UserScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const UserFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.UserFindFirstOrThrowArgs, "select">> = z.object({
  where: UserWhereInputSchema.optional(),
  orderBy: z.union([ UserOrderByWithRelationInputSchema.array(),UserOrderByWithRelationInputSchema ]).optional(),
  cursor: UserWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ UserScalarFieldEnumSchema,UserScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const UserFindManyArgsSchema: z.ZodType<Omit<Prisma.UserFindManyArgs, "select">> = z.object({
  where: UserWhereInputSchema.optional(),
  orderBy: z.union([ UserOrderByWithRelationInputSchema.array(),UserOrderByWithRelationInputSchema ]).optional(),
  cursor: UserWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ UserScalarFieldEnumSchema,UserScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const UserAggregateArgsSchema: z.ZodType<Prisma.UserAggregateArgs> = z.object({
  where: UserWhereInputSchema.optional(),
  orderBy: z.union([ UserOrderByWithRelationInputSchema.array(),UserOrderByWithRelationInputSchema ]).optional(),
  cursor: UserWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const UserGroupByArgsSchema: z.ZodType<Prisma.UserGroupByArgs> = z.object({
  where: UserWhereInputSchema.optional(),
  orderBy: z.union([ UserOrderByWithAggregationInputSchema.array(),UserOrderByWithAggregationInputSchema ]).optional(),
  by: UserScalarFieldEnumSchema.array(),
  having: UserScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const UserFindUniqueArgsSchema: z.ZodType<Omit<Prisma.UserFindUniqueArgs, "select">> = z.object({
  where: UserWhereUniqueInputSchema,
}).strict() ;

export const UserFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.UserFindUniqueOrThrowArgs, "select">> = z.object({
  where: UserWhereUniqueInputSchema,
}).strict() ;

export const SessionFindFirstArgsSchema: z.ZodType<Omit<Prisma.SessionFindFirstArgs, "select">> = z.object({
  where: SessionWhereInputSchema.optional(),
  orderBy: z.union([ SessionOrderByWithRelationInputSchema.array(),SessionOrderByWithRelationInputSchema ]).optional(),
  cursor: SessionWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ SessionScalarFieldEnumSchema,SessionScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const SessionFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.SessionFindFirstOrThrowArgs, "select">> = z.object({
  where: SessionWhereInputSchema.optional(),
  orderBy: z.union([ SessionOrderByWithRelationInputSchema.array(),SessionOrderByWithRelationInputSchema ]).optional(),
  cursor: SessionWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ SessionScalarFieldEnumSchema,SessionScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const SessionFindManyArgsSchema: z.ZodType<Omit<Prisma.SessionFindManyArgs, "select">> = z.object({
  where: SessionWhereInputSchema.optional(),
  orderBy: z.union([ SessionOrderByWithRelationInputSchema.array(),SessionOrderByWithRelationInputSchema ]).optional(),
  cursor: SessionWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ SessionScalarFieldEnumSchema,SessionScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const SessionAggregateArgsSchema: z.ZodType<Prisma.SessionAggregateArgs> = z.object({
  where: SessionWhereInputSchema.optional(),
  orderBy: z.union([ SessionOrderByWithRelationInputSchema.array(),SessionOrderByWithRelationInputSchema ]).optional(),
  cursor: SessionWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const SessionGroupByArgsSchema: z.ZodType<Prisma.SessionGroupByArgs> = z.object({
  where: SessionWhereInputSchema.optional(),
  orderBy: z.union([ SessionOrderByWithAggregationInputSchema.array(),SessionOrderByWithAggregationInputSchema ]).optional(),
  by: SessionScalarFieldEnumSchema.array(),
  having: SessionScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const SessionFindUniqueArgsSchema: z.ZodType<Omit<Prisma.SessionFindUniqueArgs, "select">> = z.object({
  where: SessionWhereUniqueInputSchema,
}).strict() ;

export const SessionFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.SessionFindUniqueOrThrowArgs, "select">> = z.object({
  where: SessionWhereUniqueInputSchema,
}).strict() ;

export const AccountFindFirstArgsSchema: z.ZodType<Omit<Prisma.AccountFindFirstArgs, "select">> = z.object({
  where: AccountWhereInputSchema.optional(),
  orderBy: z.union([ AccountOrderByWithRelationInputSchema.array(),AccountOrderByWithRelationInputSchema ]).optional(),
  cursor: AccountWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AccountScalarFieldEnumSchema,AccountScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AccountFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.AccountFindFirstOrThrowArgs, "select">> = z.object({
  where: AccountWhereInputSchema.optional(),
  orderBy: z.union([ AccountOrderByWithRelationInputSchema.array(),AccountOrderByWithRelationInputSchema ]).optional(),
  cursor: AccountWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AccountScalarFieldEnumSchema,AccountScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AccountFindManyArgsSchema: z.ZodType<Omit<Prisma.AccountFindManyArgs, "select">> = z.object({
  where: AccountWhereInputSchema.optional(),
  orderBy: z.union([ AccountOrderByWithRelationInputSchema.array(),AccountOrderByWithRelationInputSchema ]).optional(),
  cursor: AccountWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AccountScalarFieldEnumSchema,AccountScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AccountAggregateArgsSchema: z.ZodType<Prisma.AccountAggregateArgs> = z.object({
  where: AccountWhereInputSchema.optional(),
  orderBy: z.union([ AccountOrderByWithRelationInputSchema.array(),AccountOrderByWithRelationInputSchema ]).optional(),
  cursor: AccountWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const AccountGroupByArgsSchema: z.ZodType<Prisma.AccountGroupByArgs> = z.object({
  where: AccountWhereInputSchema.optional(),
  orderBy: z.union([ AccountOrderByWithAggregationInputSchema.array(),AccountOrderByWithAggregationInputSchema ]).optional(),
  by: AccountScalarFieldEnumSchema.array(),
  having: AccountScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const AccountFindUniqueArgsSchema: z.ZodType<Omit<Prisma.AccountFindUniqueArgs, "select">> = z.object({
  where: AccountWhereUniqueInputSchema,
}).strict() ;

export const AccountFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.AccountFindUniqueOrThrowArgs, "select">> = z.object({
  where: AccountWhereUniqueInputSchema,
}).strict() ;

export const VerificationFindFirstArgsSchema: z.ZodType<Omit<Prisma.VerificationFindFirstArgs, "select">> = z.object({
  where: VerificationWhereInputSchema.optional(),
  orderBy: z.union([ VerificationOrderByWithRelationInputSchema.array(),VerificationOrderByWithRelationInputSchema ]).optional(),
  cursor: VerificationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VerificationScalarFieldEnumSchema,VerificationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VerificationFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.VerificationFindFirstOrThrowArgs, "select">> = z.object({
  where: VerificationWhereInputSchema.optional(),
  orderBy: z.union([ VerificationOrderByWithRelationInputSchema.array(),VerificationOrderByWithRelationInputSchema ]).optional(),
  cursor: VerificationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VerificationScalarFieldEnumSchema,VerificationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VerificationFindManyArgsSchema: z.ZodType<Omit<Prisma.VerificationFindManyArgs, "select">> = z.object({
  where: VerificationWhereInputSchema.optional(),
  orderBy: z.union([ VerificationOrderByWithRelationInputSchema.array(),VerificationOrderByWithRelationInputSchema ]).optional(),
  cursor: VerificationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VerificationScalarFieldEnumSchema,VerificationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VerificationAggregateArgsSchema: z.ZodType<Prisma.VerificationAggregateArgs> = z.object({
  where: VerificationWhereInputSchema.optional(),
  orderBy: z.union([ VerificationOrderByWithRelationInputSchema.array(),VerificationOrderByWithRelationInputSchema ]).optional(),
  cursor: VerificationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VerificationGroupByArgsSchema: z.ZodType<Prisma.VerificationGroupByArgs> = z.object({
  where: VerificationWhereInputSchema.optional(),
  orderBy: z.union([ VerificationOrderByWithAggregationInputSchema.array(),VerificationOrderByWithAggregationInputSchema ]).optional(),
  by: VerificationScalarFieldEnumSchema.array(),
  having: VerificationScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VerificationFindUniqueArgsSchema: z.ZodType<Omit<Prisma.VerificationFindUniqueArgs, "select">> = z.object({
  where: VerificationWhereUniqueInputSchema,
}).strict() ;

export const VerificationFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.VerificationFindUniqueOrThrowArgs, "select">> = z.object({
  where: VerificationWhereUniqueInputSchema,
}).strict() ;

export const PasskeyFindFirstArgsSchema: z.ZodType<Omit<Prisma.PasskeyFindFirstArgs, "select">> = z.object({
  where: PasskeyWhereInputSchema.optional(),
  orderBy: z.union([ PasskeyOrderByWithRelationInputSchema.array(),PasskeyOrderByWithRelationInputSchema ]).optional(),
  cursor: PasskeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ PasskeyScalarFieldEnumSchema,PasskeyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const PasskeyFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.PasskeyFindFirstOrThrowArgs, "select">> = z.object({
  where: PasskeyWhereInputSchema.optional(),
  orderBy: z.union([ PasskeyOrderByWithRelationInputSchema.array(),PasskeyOrderByWithRelationInputSchema ]).optional(),
  cursor: PasskeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ PasskeyScalarFieldEnumSchema,PasskeyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const PasskeyFindManyArgsSchema: z.ZodType<Omit<Prisma.PasskeyFindManyArgs, "select">> = z.object({
  where: PasskeyWhereInputSchema.optional(),
  orderBy: z.union([ PasskeyOrderByWithRelationInputSchema.array(),PasskeyOrderByWithRelationInputSchema ]).optional(),
  cursor: PasskeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ PasskeyScalarFieldEnumSchema,PasskeyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const PasskeyAggregateArgsSchema: z.ZodType<Prisma.PasskeyAggregateArgs> = z.object({
  where: PasskeyWhereInputSchema.optional(),
  orderBy: z.union([ PasskeyOrderByWithRelationInputSchema.array(),PasskeyOrderByWithRelationInputSchema ]).optional(),
  cursor: PasskeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const PasskeyGroupByArgsSchema: z.ZodType<Prisma.PasskeyGroupByArgs> = z.object({
  where: PasskeyWhereInputSchema.optional(),
  orderBy: z.union([ PasskeyOrderByWithAggregationInputSchema.array(),PasskeyOrderByWithAggregationInputSchema ]).optional(),
  by: PasskeyScalarFieldEnumSchema.array(),
  having: PasskeyScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const PasskeyFindUniqueArgsSchema: z.ZodType<Omit<Prisma.PasskeyFindUniqueArgs, "select">> = z.object({
  where: PasskeyWhereUniqueInputSchema,
}).strict() ;

export const PasskeyFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.PasskeyFindUniqueOrThrowArgs, "select">> = z.object({
  where: PasskeyWhereUniqueInputSchema,
}).strict() ;

export const TwoFactorFindFirstArgsSchema: z.ZodType<Omit<Prisma.TwoFactorFindFirstArgs, "select">> = z.object({
  where: TwoFactorWhereInputSchema.optional(),
  orderBy: z.union([ TwoFactorOrderByWithRelationInputSchema.array(),TwoFactorOrderByWithRelationInputSchema ]).optional(),
  cursor: TwoFactorWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ TwoFactorScalarFieldEnumSchema,TwoFactorScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const TwoFactorFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.TwoFactorFindFirstOrThrowArgs, "select">> = z.object({
  where: TwoFactorWhereInputSchema.optional(),
  orderBy: z.union([ TwoFactorOrderByWithRelationInputSchema.array(),TwoFactorOrderByWithRelationInputSchema ]).optional(),
  cursor: TwoFactorWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ TwoFactorScalarFieldEnumSchema,TwoFactorScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const TwoFactorFindManyArgsSchema: z.ZodType<Omit<Prisma.TwoFactorFindManyArgs, "select">> = z.object({
  where: TwoFactorWhereInputSchema.optional(),
  orderBy: z.union([ TwoFactorOrderByWithRelationInputSchema.array(),TwoFactorOrderByWithRelationInputSchema ]).optional(),
  cursor: TwoFactorWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ TwoFactorScalarFieldEnumSchema,TwoFactorScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const TwoFactorAggregateArgsSchema: z.ZodType<Prisma.TwoFactorAggregateArgs> = z.object({
  where: TwoFactorWhereInputSchema.optional(),
  orderBy: z.union([ TwoFactorOrderByWithRelationInputSchema.array(),TwoFactorOrderByWithRelationInputSchema ]).optional(),
  cursor: TwoFactorWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const TwoFactorGroupByArgsSchema: z.ZodType<Prisma.TwoFactorGroupByArgs> = z.object({
  where: TwoFactorWhereInputSchema.optional(),
  orderBy: z.union([ TwoFactorOrderByWithAggregationInputSchema.array(),TwoFactorOrderByWithAggregationInputSchema ]).optional(),
  by: TwoFactorScalarFieldEnumSchema.array(),
  having: TwoFactorScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const TwoFactorFindUniqueArgsSchema: z.ZodType<Omit<Prisma.TwoFactorFindUniqueArgs, "select">> = z.object({
  where: TwoFactorWhereUniqueInputSchema,
}).strict() ;

export const TwoFactorFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.TwoFactorFindUniqueOrThrowArgs, "select">> = z.object({
  where: TwoFactorWhereUniqueInputSchema,
}).strict() ;

export const OrganizationFindFirstArgsSchema: z.ZodType<Omit<Prisma.OrganizationFindFirstArgs, "select">> = z.object({
  where: OrganizationWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationOrderByWithRelationInputSchema.array(),OrganizationOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationScalarFieldEnumSchema,OrganizationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationFindFirstOrThrowArgs, "select">> = z.object({
  where: OrganizationWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationOrderByWithRelationInputSchema.array(),OrganizationOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationScalarFieldEnumSchema,OrganizationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationFindManyArgsSchema: z.ZodType<Omit<Prisma.OrganizationFindManyArgs, "select">> = z.object({
  where: OrganizationWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationOrderByWithRelationInputSchema.array(),OrganizationOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationScalarFieldEnumSchema,OrganizationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationAggregateArgsSchema: z.ZodType<Prisma.OrganizationAggregateArgs> = z.object({
  where: OrganizationWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationOrderByWithRelationInputSchema.array(),OrganizationOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationGroupByArgsSchema: z.ZodType<Prisma.OrganizationGroupByArgs> = z.object({
  where: OrganizationWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationOrderByWithAggregationInputSchema.array(),OrganizationOrderByWithAggregationInputSchema ]).optional(),
  by: OrganizationScalarFieldEnumSchema.array(),
  having: OrganizationScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationFindUniqueArgsSchema: z.ZodType<Omit<Prisma.OrganizationFindUniqueArgs, "select">> = z.object({
  where: OrganizationWhereUniqueInputSchema,
}).strict() ;

export const OrganizationFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationFindUniqueOrThrowArgs, "select">> = z.object({
  where: OrganizationWhereUniqueInputSchema,
}).strict() ;

export const MemberFindFirstArgsSchema: z.ZodType<Omit<Prisma.MemberFindFirstArgs, "select">> = z.object({
  where: MemberWhereInputSchema.optional(),
  orderBy: z.union([ MemberOrderByWithRelationInputSchema.array(),MemberOrderByWithRelationInputSchema ]).optional(),
  cursor: MemberWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ MemberScalarFieldEnumSchema,MemberScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const MemberFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.MemberFindFirstOrThrowArgs, "select">> = z.object({
  where: MemberWhereInputSchema.optional(),
  orderBy: z.union([ MemberOrderByWithRelationInputSchema.array(),MemberOrderByWithRelationInputSchema ]).optional(),
  cursor: MemberWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ MemberScalarFieldEnumSchema,MemberScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const MemberFindManyArgsSchema: z.ZodType<Omit<Prisma.MemberFindManyArgs, "select">> = z.object({
  where: MemberWhereInputSchema.optional(),
  orderBy: z.union([ MemberOrderByWithRelationInputSchema.array(),MemberOrderByWithRelationInputSchema ]).optional(),
  cursor: MemberWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ MemberScalarFieldEnumSchema,MemberScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const MemberAggregateArgsSchema: z.ZodType<Prisma.MemberAggregateArgs> = z.object({
  where: MemberWhereInputSchema.optional(),
  orderBy: z.union([ MemberOrderByWithRelationInputSchema.array(),MemberOrderByWithRelationInputSchema ]).optional(),
  cursor: MemberWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const MemberGroupByArgsSchema: z.ZodType<Prisma.MemberGroupByArgs> = z.object({
  where: MemberWhereInputSchema.optional(),
  orderBy: z.union([ MemberOrderByWithAggregationInputSchema.array(),MemberOrderByWithAggregationInputSchema ]).optional(),
  by: MemberScalarFieldEnumSchema.array(),
  having: MemberScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const MemberFindUniqueArgsSchema: z.ZodType<Omit<Prisma.MemberFindUniqueArgs, "select">> = z.object({
  where: MemberWhereUniqueInputSchema,
}).strict() ;

export const MemberFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.MemberFindUniqueOrThrowArgs, "select">> = z.object({
  where: MemberWhereUniqueInputSchema,
}).strict() ;

export const InvitationFindFirstArgsSchema: z.ZodType<Omit<Prisma.InvitationFindFirstArgs, "select">> = z.object({
  where: InvitationWhereInputSchema.optional(),
  orderBy: z.union([ InvitationOrderByWithRelationInputSchema.array(),InvitationOrderByWithRelationInputSchema ]).optional(),
  cursor: InvitationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ InvitationScalarFieldEnumSchema,InvitationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const InvitationFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.InvitationFindFirstOrThrowArgs, "select">> = z.object({
  where: InvitationWhereInputSchema.optional(),
  orderBy: z.union([ InvitationOrderByWithRelationInputSchema.array(),InvitationOrderByWithRelationInputSchema ]).optional(),
  cursor: InvitationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ InvitationScalarFieldEnumSchema,InvitationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const InvitationFindManyArgsSchema: z.ZodType<Omit<Prisma.InvitationFindManyArgs, "select">> = z.object({
  where: InvitationWhereInputSchema.optional(),
  orderBy: z.union([ InvitationOrderByWithRelationInputSchema.array(),InvitationOrderByWithRelationInputSchema ]).optional(),
  cursor: InvitationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ InvitationScalarFieldEnumSchema,InvitationScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const InvitationAggregateArgsSchema: z.ZodType<Prisma.InvitationAggregateArgs> = z.object({
  where: InvitationWhereInputSchema.optional(),
  orderBy: z.union([ InvitationOrderByWithRelationInputSchema.array(),InvitationOrderByWithRelationInputSchema ]).optional(),
  cursor: InvitationWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const InvitationGroupByArgsSchema: z.ZodType<Prisma.InvitationGroupByArgs> = z.object({
  where: InvitationWhereInputSchema.optional(),
  orderBy: z.union([ InvitationOrderByWithAggregationInputSchema.array(),InvitationOrderByWithAggregationInputSchema ]).optional(),
  by: InvitationScalarFieldEnumSchema.array(),
  having: InvitationScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const InvitationFindUniqueArgsSchema: z.ZodType<Omit<Prisma.InvitationFindUniqueArgs, "select">> = z.object({
  where: InvitationWhereUniqueInputSchema,
}).strict() ;

export const InvitationFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.InvitationFindUniqueOrThrowArgs, "select">> = z.object({
  where: InvitationWhereUniqueInputSchema,
}).strict() ;

export const PurchaseFindFirstArgsSchema: z.ZodType<Omit<Prisma.PurchaseFindFirstArgs, "select">> = z.object({
  where: PurchaseWhereInputSchema.optional(),
  orderBy: z.union([ PurchaseOrderByWithRelationInputSchema.array(),PurchaseOrderByWithRelationInputSchema ]).optional(),
  cursor: PurchaseWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ PurchaseScalarFieldEnumSchema,PurchaseScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const PurchaseFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.PurchaseFindFirstOrThrowArgs, "select">> = z.object({
  where: PurchaseWhereInputSchema.optional(),
  orderBy: z.union([ PurchaseOrderByWithRelationInputSchema.array(),PurchaseOrderByWithRelationInputSchema ]).optional(),
  cursor: PurchaseWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ PurchaseScalarFieldEnumSchema,PurchaseScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const PurchaseFindManyArgsSchema: z.ZodType<Omit<Prisma.PurchaseFindManyArgs, "select">> = z.object({
  where: PurchaseWhereInputSchema.optional(),
  orderBy: z.union([ PurchaseOrderByWithRelationInputSchema.array(),PurchaseOrderByWithRelationInputSchema ]).optional(),
  cursor: PurchaseWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ PurchaseScalarFieldEnumSchema,PurchaseScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const PurchaseAggregateArgsSchema: z.ZodType<Prisma.PurchaseAggregateArgs> = z.object({
  where: PurchaseWhereInputSchema.optional(),
  orderBy: z.union([ PurchaseOrderByWithRelationInputSchema.array(),PurchaseOrderByWithRelationInputSchema ]).optional(),
  cursor: PurchaseWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const PurchaseGroupByArgsSchema: z.ZodType<Prisma.PurchaseGroupByArgs> = z.object({
  where: PurchaseWhereInputSchema.optional(),
  orderBy: z.union([ PurchaseOrderByWithAggregationInputSchema.array(),PurchaseOrderByWithAggregationInputSchema ]).optional(),
  by: PurchaseScalarFieldEnumSchema.array(),
  having: PurchaseScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const PurchaseFindUniqueArgsSchema: z.ZodType<Omit<Prisma.PurchaseFindUniqueArgs, "select">> = z.object({
  where: PurchaseWhereUniqueInputSchema,
}).strict() ;

export const PurchaseFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.PurchaseFindUniqueOrThrowArgs, "select">> = z.object({
  where: PurchaseWhereUniqueInputSchema,
}).strict() ;

export const AiChatFindFirstArgsSchema: z.ZodType<Omit<Prisma.AiChatFindFirstArgs, "select">> = z.object({
  where: AiChatWhereInputSchema.optional(),
  orderBy: z.union([ AiChatOrderByWithRelationInputSchema.array(),AiChatOrderByWithRelationInputSchema ]).optional(),
  cursor: AiChatWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AiChatScalarFieldEnumSchema,AiChatScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AiChatFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.AiChatFindFirstOrThrowArgs, "select">> = z.object({
  where: AiChatWhereInputSchema.optional(),
  orderBy: z.union([ AiChatOrderByWithRelationInputSchema.array(),AiChatOrderByWithRelationInputSchema ]).optional(),
  cursor: AiChatWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AiChatScalarFieldEnumSchema,AiChatScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AiChatFindManyArgsSchema: z.ZodType<Omit<Prisma.AiChatFindManyArgs, "select">> = z.object({
  where: AiChatWhereInputSchema.optional(),
  orderBy: z.union([ AiChatOrderByWithRelationInputSchema.array(),AiChatOrderByWithRelationInputSchema ]).optional(),
  cursor: AiChatWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AiChatScalarFieldEnumSchema,AiChatScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AiChatAggregateArgsSchema: z.ZodType<Prisma.AiChatAggregateArgs> = z.object({
  where: AiChatWhereInputSchema.optional(),
  orderBy: z.union([ AiChatOrderByWithRelationInputSchema.array(),AiChatOrderByWithRelationInputSchema ]).optional(),
  cursor: AiChatWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const AiChatGroupByArgsSchema: z.ZodType<Prisma.AiChatGroupByArgs> = z.object({
  where: AiChatWhereInputSchema.optional(),
  orderBy: z.union([ AiChatOrderByWithAggregationInputSchema.array(),AiChatOrderByWithAggregationInputSchema ]).optional(),
  by: AiChatScalarFieldEnumSchema.array(),
  having: AiChatScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const AiChatFindUniqueArgsSchema: z.ZodType<Omit<Prisma.AiChatFindUniqueArgs, "select">> = z.object({
  where: AiChatWhereUniqueInputSchema,
}).strict() ;

export const AiChatFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.AiChatFindUniqueOrThrowArgs, "select">> = z.object({
  where: AiChatWhereUniqueInputSchema,
}).strict() ;

export const ApiKeyFindFirstArgsSchema: z.ZodType<Omit<Prisma.ApiKeyFindFirstArgs, "select">> = z.object({
  where: ApiKeyWhereInputSchema.optional(),
  orderBy: z.union([ ApiKeyOrderByWithRelationInputSchema.array(),ApiKeyOrderByWithRelationInputSchema ]).optional(),
  cursor: ApiKeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ApiKeyScalarFieldEnumSchema,ApiKeyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ApiKeyFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.ApiKeyFindFirstOrThrowArgs, "select">> = z.object({
  where: ApiKeyWhereInputSchema.optional(),
  orderBy: z.union([ ApiKeyOrderByWithRelationInputSchema.array(),ApiKeyOrderByWithRelationInputSchema ]).optional(),
  cursor: ApiKeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ApiKeyScalarFieldEnumSchema,ApiKeyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ApiKeyFindManyArgsSchema: z.ZodType<Omit<Prisma.ApiKeyFindManyArgs, "select">> = z.object({
  where: ApiKeyWhereInputSchema.optional(),
  orderBy: z.union([ ApiKeyOrderByWithRelationInputSchema.array(),ApiKeyOrderByWithRelationInputSchema ]).optional(),
  cursor: ApiKeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ApiKeyScalarFieldEnumSchema,ApiKeyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ApiKeyAggregateArgsSchema: z.ZodType<Prisma.ApiKeyAggregateArgs> = z.object({
  where: ApiKeyWhereInputSchema.optional(),
  orderBy: z.union([ ApiKeyOrderByWithRelationInputSchema.array(),ApiKeyOrderByWithRelationInputSchema ]).optional(),
  cursor: ApiKeyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const ApiKeyGroupByArgsSchema: z.ZodType<Prisma.ApiKeyGroupByArgs> = z.object({
  where: ApiKeyWhereInputSchema.optional(),
  orderBy: z.union([ ApiKeyOrderByWithAggregationInputSchema.array(),ApiKeyOrderByWithAggregationInputSchema ]).optional(),
  by: ApiKeyScalarFieldEnumSchema.array(),
  having: ApiKeyScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const ApiKeyFindUniqueArgsSchema: z.ZodType<Omit<Prisma.ApiKeyFindUniqueArgs, "select">> = z.object({
  where: ApiKeyWhereUniqueInputSchema,
}).strict() ;

export const ApiKeyFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.ApiKeyFindUniqueOrThrowArgs, "select">> = z.object({
  where: ApiKeyWhereUniqueInputSchema,
}).strict() ;

export const JobFindFirstArgsSchema: z.ZodType<Omit<Prisma.JobFindFirstArgs, "select">> = z.object({
  where: JobWhereInputSchema.optional(),
  orderBy: z.union([ JobOrderByWithRelationInputSchema.array(),JobOrderByWithRelationInputSchema ]).optional(),
  cursor: JobWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ JobScalarFieldEnumSchema,JobScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const JobFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.JobFindFirstOrThrowArgs, "select">> = z.object({
  where: JobWhereInputSchema.optional(),
  orderBy: z.union([ JobOrderByWithRelationInputSchema.array(),JobOrderByWithRelationInputSchema ]).optional(),
  cursor: JobWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ JobScalarFieldEnumSchema,JobScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const JobFindManyArgsSchema: z.ZodType<Omit<Prisma.JobFindManyArgs, "select">> = z.object({
  where: JobWhereInputSchema.optional(),
  orderBy: z.union([ JobOrderByWithRelationInputSchema.array(),JobOrderByWithRelationInputSchema ]).optional(),
  cursor: JobWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ JobScalarFieldEnumSchema,JobScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const JobAggregateArgsSchema: z.ZodType<Prisma.JobAggregateArgs> = z.object({
  where: JobWhereInputSchema.optional(),
  orderBy: z.union([ JobOrderByWithRelationInputSchema.array(),JobOrderByWithRelationInputSchema ]).optional(),
  cursor: JobWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const JobGroupByArgsSchema: z.ZodType<Prisma.JobGroupByArgs> = z.object({
  where: JobWhereInputSchema.optional(),
  orderBy: z.union([ JobOrderByWithAggregationInputSchema.array(),JobOrderByWithAggregationInputSchema ]).optional(),
  by: JobScalarFieldEnumSchema.array(),
  having: JobScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const JobFindUniqueArgsSchema: z.ZodType<Omit<Prisma.JobFindUniqueArgs, "select">> = z.object({
  where: JobWhereUniqueInputSchema,
}).strict() ;

export const JobFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.JobFindUniqueOrThrowArgs, "select">> = z.object({
  where: JobWhereUniqueInputSchema,
}).strict() ;

export const VideoFindFirstArgsSchema: z.ZodType<Omit<Prisma.VideoFindFirstArgs, "select">> = z.object({
  where: VideoWhereInputSchema.optional(),
  orderBy: z.union([ VideoOrderByWithRelationInputSchema.array(),VideoOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoScalarFieldEnumSchema,VideoScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.VideoFindFirstOrThrowArgs, "select">> = z.object({
  where: VideoWhereInputSchema.optional(),
  orderBy: z.union([ VideoOrderByWithRelationInputSchema.array(),VideoOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoScalarFieldEnumSchema,VideoScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoFindManyArgsSchema: z.ZodType<Omit<Prisma.VideoFindManyArgs, "select">> = z.object({
  where: VideoWhereInputSchema.optional(),
  orderBy: z.union([ VideoOrderByWithRelationInputSchema.array(),VideoOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoScalarFieldEnumSchema,VideoScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoAggregateArgsSchema: z.ZodType<Prisma.VideoAggregateArgs> = z.object({
  where: VideoWhereInputSchema.optional(),
  orderBy: z.union([ VideoOrderByWithRelationInputSchema.array(),VideoOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VideoGroupByArgsSchema: z.ZodType<Prisma.VideoGroupByArgs> = z.object({
  where: VideoWhereInputSchema.optional(),
  orderBy: z.union([ VideoOrderByWithAggregationInputSchema.array(),VideoOrderByWithAggregationInputSchema ]).optional(),
  by: VideoScalarFieldEnumSchema.array(),
  having: VideoScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VideoFindUniqueArgsSchema: z.ZodType<Omit<Prisma.VideoFindUniqueArgs, "select">> = z.object({
  where: VideoWhereUniqueInputSchema,
}).strict() ;

export const VideoFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.VideoFindUniqueOrThrowArgs, "select">> = z.object({
  where: VideoWhereUniqueInputSchema,
}).strict() ;

export const VideoProjectFindFirstArgsSchema: z.ZodType<Omit<Prisma.VideoProjectFindFirstArgs, "select">> = z.object({
  where: VideoProjectWhereInputSchema.optional(),
  orderBy: z.union([ VideoProjectOrderByWithRelationInputSchema.array(),VideoProjectOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoProjectWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoProjectScalarFieldEnumSchema,VideoProjectScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoProjectFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.VideoProjectFindFirstOrThrowArgs, "select">> = z.object({
  where: VideoProjectWhereInputSchema.optional(),
  orderBy: z.union([ VideoProjectOrderByWithRelationInputSchema.array(),VideoProjectOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoProjectWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoProjectScalarFieldEnumSchema,VideoProjectScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoProjectFindManyArgsSchema: z.ZodType<Omit<Prisma.VideoProjectFindManyArgs, "select">> = z.object({
  where: VideoProjectWhereInputSchema.optional(),
  orderBy: z.union([ VideoProjectOrderByWithRelationInputSchema.array(),VideoProjectOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoProjectWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoProjectScalarFieldEnumSchema,VideoProjectScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoProjectAggregateArgsSchema: z.ZodType<Prisma.VideoProjectAggregateArgs> = z.object({
  where: VideoProjectWhereInputSchema.optional(),
  orderBy: z.union([ VideoProjectOrderByWithRelationInputSchema.array(),VideoProjectOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoProjectWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VideoProjectGroupByArgsSchema: z.ZodType<Prisma.VideoProjectGroupByArgs> = z.object({
  where: VideoProjectWhereInputSchema.optional(),
  orderBy: z.union([ VideoProjectOrderByWithAggregationInputSchema.array(),VideoProjectOrderByWithAggregationInputSchema ]).optional(),
  by: VideoProjectScalarFieldEnumSchema.array(),
  having: VideoProjectScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VideoProjectFindUniqueArgsSchema: z.ZodType<Omit<Prisma.VideoProjectFindUniqueArgs, "select">> = z.object({
  where: VideoProjectWhereUniqueInputSchema,
}).strict() ;

export const VideoProjectFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.VideoProjectFindUniqueOrThrowArgs, "select">> = z.object({
  where: VideoProjectWhereUniqueInputSchema,
}).strict() ;

export const VideoDesignFindFirstArgsSchema: z.ZodType<Omit<Prisma.VideoDesignFindFirstArgs, "select">> = z.object({
  where: VideoDesignWhereInputSchema.optional(),
  orderBy: z.union([ VideoDesignOrderByWithRelationInputSchema.array(),VideoDesignOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoDesignWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoDesignScalarFieldEnumSchema,VideoDesignScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoDesignFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.VideoDesignFindFirstOrThrowArgs, "select">> = z.object({
  where: VideoDesignWhereInputSchema.optional(),
  orderBy: z.union([ VideoDesignOrderByWithRelationInputSchema.array(),VideoDesignOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoDesignWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoDesignScalarFieldEnumSchema,VideoDesignScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoDesignFindManyArgsSchema: z.ZodType<Omit<Prisma.VideoDesignFindManyArgs, "select">> = z.object({
  where: VideoDesignWhereInputSchema.optional(),
  orderBy: z.union([ VideoDesignOrderByWithRelationInputSchema.array(),VideoDesignOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoDesignWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ VideoDesignScalarFieldEnumSchema,VideoDesignScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const VideoDesignAggregateArgsSchema: z.ZodType<Prisma.VideoDesignAggregateArgs> = z.object({
  where: VideoDesignWhereInputSchema.optional(),
  orderBy: z.union([ VideoDesignOrderByWithRelationInputSchema.array(),VideoDesignOrderByWithRelationInputSchema ]).optional(),
  cursor: VideoDesignWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VideoDesignGroupByArgsSchema: z.ZodType<Prisma.VideoDesignGroupByArgs> = z.object({
  where: VideoDesignWhereInputSchema.optional(),
  orderBy: z.union([ VideoDesignOrderByWithAggregationInputSchema.array(),VideoDesignOrderByWithAggregationInputSchema ]).optional(),
  by: VideoDesignScalarFieldEnumSchema.array(),
  having: VideoDesignScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const VideoDesignFindUniqueArgsSchema: z.ZodType<Omit<Prisma.VideoDesignFindUniqueArgs, "select">> = z.object({
  where: VideoDesignWhereUniqueInputSchema,
}).strict() ;

export const VideoDesignFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.VideoDesignFindUniqueOrThrowArgs, "select">> = z.object({
  where: VideoDesignWhereUniqueInputSchema,
}).strict() ;

export const AppFileFindFirstArgsSchema: z.ZodType<Omit<Prisma.AppFileFindFirstArgs, "select">> = z.object({
  where: AppFileWhereInputSchema.optional(),
  orderBy: z.union([ AppFileOrderByWithRelationInputSchema.array(),AppFileOrderByWithRelationInputSchema ]).optional(),
  cursor: AppFileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AppFileScalarFieldEnumSchema,AppFileScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AppFileFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.AppFileFindFirstOrThrowArgs, "select">> = z.object({
  where: AppFileWhereInputSchema.optional(),
  orderBy: z.union([ AppFileOrderByWithRelationInputSchema.array(),AppFileOrderByWithRelationInputSchema ]).optional(),
  cursor: AppFileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AppFileScalarFieldEnumSchema,AppFileScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AppFileFindManyArgsSchema: z.ZodType<Omit<Prisma.AppFileFindManyArgs, "select">> = z.object({
  where: AppFileWhereInputSchema.optional(),
  orderBy: z.union([ AppFileOrderByWithRelationInputSchema.array(),AppFileOrderByWithRelationInputSchema ]).optional(),
  cursor: AppFileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ AppFileScalarFieldEnumSchema,AppFileScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const AppFileAggregateArgsSchema: z.ZodType<Prisma.AppFileAggregateArgs> = z.object({
  where: AppFileWhereInputSchema.optional(),
  orderBy: z.union([ AppFileOrderByWithRelationInputSchema.array(),AppFileOrderByWithRelationInputSchema ]).optional(),
  cursor: AppFileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const AppFileGroupByArgsSchema: z.ZodType<Prisma.AppFileGroupByArgs> = z.object({
  where: AppFileWhereInputSchema.optional(),
  orderBy: z.union([ AppFileOrderByWithAggregationInputSchema.array(),AppFileOrderByWithAggregationInputSchema ]).optional(),
  by: AppFileScalarFieldEnumSchema.array(),
  having: AppFileScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const AppFileFindUniqueArgsSchema: z.ZodType<Omit<Prisma.AppFileFindUniqueArgs, "select">> = z.object({
  where: AppFileWhereUniqueInputSchema,
}).strict() ;

export const AppFileFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.AppFileFindUniqueOrThrowArgs, "select">> = z.object({
  where: AppFileWhereUniqueInputSchema,
}).strict() ;

export const CaptionStyleFindFirstArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleFindFirstArgs, "select">> = z.object({
  where: CaptionStyleWhereInputSchema.optional(),
  orderBy: z.union([ CaptionStyleOrderByWithRelationInputSchema.array(),CaptionStyleOrderByWithRelationInputSchema ]).optional(),
  cursor: CaptionStyleWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ CaptionStyleScalarFieldEnumSchema,CaptionStyleScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const CaptionStyleFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleFindFirstOrThrowArgs, "select">> = z.object({
  where: CaptionStyleWhereInputSchema.optional(),
  orderBy: z.union([ CaptionStyleOrderByWithRelationInputSchema.array(),CaptionStyleOrderByWithRelationInputSchema ]).optional(),
  cursor: CaptionStyleWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ CaptionStyleScalarFieldEnumSchema,CaptionStyleScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const CaptionStyleFindManyArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleFindManyArgs, "select">> = z.object({
  where: CaptionStyleWhereInputSchema.optional(),
  orderBy: z.union([ CaptionStyleOrderByWithRelationInputSchema.array(),CaptionStyleOrderByWithRelationInputSchema ]).optional(),
  cursor: CaptionStyleWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ CaptionStyleScalarFieldEnumSchema,CaptionStyleScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const CaptionStyleAggregateArgsSchema: z.ZodType<Prisma.CaptionStyleAggregateArgs> = z.object({
  where: CaptionStyleWhereInputSchema.optional(),
  orderBy: z.union([ CaptionStyleOrderByWithRelationInputSchema.array(),CaptionStyleOrderByWithRelationInputSchema ]).optional(),
  cursor: CaptionStyleWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const CaptionStyleGroupByArgsSchema: z.ZodType<Prisma.CaptionStyleGroupByArgs> = z.object({
  where: CaptionStyleWhereInputSchema.optional(),
  orderBy: z.union([ CaptionStyleOrderByWithAggregationInputSchema.array(),CaptionStyleOrderByWithAggregationInputSchema ]).optional(),
  by: CaptionStyleScalarFieldEnumSchema.array(),
  having: CaptionStyleScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const CaptionStyleFindUniqueArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleFindUniqueArgs, "select">> = z.object({
  where: CaptionStyleWhereUniqueInputSchema,
}).strict() ;

export const CaptionStyleFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleFindUniqueOrThrowArgs, "select">> = z.object({
  where: CaptionStyleWhereUniqueInputSchema,
}).strict() ;

export const OrganizationBrandingFindFirstArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingFindFirstArgs, "select">> = z.object({
  where: OrganizationBrandingWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationBrandingOrderByWithRelationInputSchema.array(),OrganizationBrandingOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationBrandingWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationBrandingScalarFieldEnumSchema,OrganizationBrandingScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationBrandingFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingFindFirstOrThrowArgs, "select">> = z.object({
  where: OrganizationBrandingWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationBrandingOrderByWithRelationInputSchema.array(),OrganizationBrandingOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationBrandingWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationBrandingScalarFieldEnumSchema,OrganizationBrandingScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationBrandingFindManyArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingFindManyArgs, "select">> = z.object({
  where: OrganizationBrandingWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationBrandingOrderByWithRelationInputSchema.array(),OrganizationBrandingOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationBrandingWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationBrandingScalarFieldEnumSchema,OrganizationBrandingScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationBrandingAggregateArgsSchema: z.ZodType<Prisma.OrganizationBrandingAggregateArgs> = z.object({
  where: OrganizationBrandingWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationBrandingOrderByWithRelationInputSchema.array(),OrganizationBrandingOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationBrandingWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationBrandingGroupByArgsSchema: z.ZodType<Prisma.OrganizationBrandingGroupByArgs> = z.object({
  where: OrganizationBrandingWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationBrandingOrderByWithAggregationInputSchema.array(),OrganizationBrandingOrderByWithAggregationInputSchema ]).optional(),
  by: OrganizationBrandingScalarFieldEnumSchema.array(),
  having: OrganizationBrandingScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationBrandingFindUniqueArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingFindUniqueArgs, "select">> = z.object({
  where: OrganizationBrandingWhereUniqueInputSchema,
}).strict() ;

export const OrganizationBrandingFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingFindUniqueOrThrowArgs, "select">> = z.object({
  where: OrganizationBrandingWhereUniqueInputSchema,
}).strict() ;

export const OrganizationNarratorProfileFindFirstArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileFindFirstArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationNarratorProfileOrderByWithRelationInputSchema.array(),OrganizationNarratorProfileOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationNarratorProfileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationNarratorProfileScalarFieldEnumSchema,OrganizationNarratorProfileScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationNarratorProfileFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileFindFirstOrThrowArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationNarratorProfileOrderByWithRelationInputSchema.array(),OrganizationNarratorProfileOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationNarratorProfileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationNarratorProfileScalarFieldEnumSchema,OrganizationNarratorProfileScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationNarratorProfileFindManyArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileFindManyArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationNarratorProfileOrderByWithRelationInputSchema.array(),OrganizationNarratorProfileOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationNarratorProfileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationNarratorProfileScalarFieldEnumSchema,OrganizationNarratorProfileScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationNarratorProfileAggregateArgsSchema: z.ZodType<Prisma.OrganizationNarratorProfileAggregateArgs> = z.object({
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationNarratorProfileOrderByWithRelationInputSchema.array(),OrganizationNarratorProfileOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationNarratorProfileWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationNarratorProfileGroupByArgsSchema: z.ZodType<Prisma.OrganizationNarratorProfileGroupByArgs> = z.object({
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationNarratorProfileOrderByWithAggregationInputSchema.array(),OrganizationNarratorProfileOrderByWithAggregationInputSchema ]).optional(),
  by: OrganizationNarratorProfileScalarFieldEnumSchema.array(),
  having: OrganizationNarratorProfileScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationNarratorProfileFindUniqueArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileFindUniqueArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereUniqueInputSchema,
}).strict() ;

export const OrganizationNarratorProfileFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileFindUniqueOrThrowArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialSettingsFindFirstArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsFindFirstArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialSettingsOrderByWithRelationInputSchema.array(),OrganizationSocialSettingsOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialSettingsWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationSocialSettingsScalarFieldEnumSchema,OrganizationSocialSettingsScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationSocialSettingsFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsFindFirstOrThrowArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialSettingsOrderByWithRelationInputSchema.array(),OrganizationSocialSettingsOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialSettingsWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationSocialSettingsScalarFieldEnumSchema,OrganizationSocialSettingsScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationSocialSettingsFindManyArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsFindManyArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialSettingsOrderByWithRelationInputSchema.array(),OrganizationSocialSettingsOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialSettingsWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationSocialSettingsScalarFieldEnumSchema,OrganizationSocialSettingsScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationSocialSettingsAggregateArgsSchema: z.ZodType<Prisma.OrganizationSocialSettingsAggregateArgs> = z.object({
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialSettingsOrderByWithRelationInputSchema.array(),OrganizationSocialSettingsOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialSettingsWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationSocialSettingsGroupByArgsSchema: z.ZodType<Prisma.OrganizationSocialSettingsGroupByArgs> = z.object({
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialSettingsOrderByWithAggregationInputSchema.array(),OrganizationSocialSettingsOrderByWithAggregationInputSchema ]).optional(),
  by: OrganizationSocialSettingsScalarFieldEnumSchema.array(),
  having: OrganizationSocialSettingsScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationSocialSettingsFindUniqueArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsFindUniqueArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialSettingsFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsFindUniqueOrThrowArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialPostFindFirstArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostFindFirstArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialPostOrderByWithRelationInputSchema.array(),OrganizationSocialPostOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialPostWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationSocialPostScalarFieldEnumSchema,OrganizationSocialPostScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationSocialPostFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostFindFirstOrThrowArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialPostOrderByWithRelationInputSchema.array(),OrganizationSocialPostOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialPostWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationSocialPostScalarFieldEnumSchema,OrganizationSocialPostScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationSocialPostFindManyArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostFindManyArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialPostOrderByWithRelationInputSchema.array(),OrganizationSocialPostOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialPostWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ OrganizationSocialPostScalarFieldEnumSchema,OrganizationSocialPostScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const OrganizationSocialPostAggregateArgsSchema: z.ZodType<Prisma.OrganizationSocialPostAggregateArgs> = z.object({
  where: OrganizationSocialPostWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialPostOrderByWithRelationInputSchema.array(),OrganizationSocialPostOrderByWithRelationInputSchema ]).optional(),
  cursor: OrganizationSocialPostWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationSocialPostGroupByArgsSchema: z.ZodType<Prisma.OrganizationSocialPostGroupByArgs> = z.object({
  where: OrganizationSocialPostWhereInputSchema.optional(),
  orderBy: z.union([ OrganizationSocialPostOrderByWithAggregationInputSchema.array(),OrganizationSocialPostOrderByWithAggregationInputSchema ]).optional(),
  by: OrganizationSocialPostScalarFieldEnumSchema.array(),
  having: OrganizationSocialPostScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const OrganizationSocialPostFindUniqueArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostFindUniqueArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialPostFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostFindUniqueOrThrowArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereUniqueInputSchema,
}).strict() ;

export const RenderHistoryFindFirstArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryFindFirstArgs, "select">> = z.object({
  where: RenderHistoryWhereInputSchema.optional(),
  orderBy: z.union([ RenderHistoryOrderByWithRelationInputSchema.array(),RenderHistoryOrderByWithRelationInputSchema ]).optional(),
  cursor: RenderHistoryWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ RenderHistoryScalarFieldEnumSchema,RenderHistoryScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const RenderHistoryFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryFindFirstOrThrowArgs, "select">> = z.object({
  where: RenderHistoryWhereInputSchema.optional(),
  orderBy: z.union([ RenderHistoryOrderByWithRelationInputSchema.array(),RenderHistoryOrderByWithRelationInputSchema ]).optional(),
  cursor: RenderHistoryWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ RenderHistoryScalarFieldEnumSchema,RenderHistoryScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const RenderHistoryFindManyArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryFindManyArgs, "select">> = z.object({
  where: RenderHistoryWhereInputSchema.optional(),
  orderBy: z.union([ RenderHistoryOrderByWithRelationInputSchema.array(),RenderHistoryOrderByWithRelationInputSchema ]).optional(),
  cursor: RenderHistoryWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ RenderHistoryScalarFieldEnumSchema,RenderHistoryScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const RenderHistoryAggregateArgsSchema: z.ZodType<Prisma.RenderHistoryAggregateArgs> = z.object({
  where: RenderHistoryWhereInputSchema.optional(),
  orderBy: z.union([ RenderHistoryOrderByWithRelationInputSchema.array(),RenderHistoryOrderByWithRelationInputSchema ]).optional(),
  cursor: RenderHistoryWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const RenderHistoryGroupByArgsSchema: z.ZodType<Prisma.RenderHistoryGroupByArgs> = z.object({
  where: RenderHistoryWhereInputSchema.optional(),
  orderBy: z.union([ RenderHistoryOrderByWithAggregationInputSchema.array(),RenderHistoryOrderByWithAggregationInputSchema ]).optional(),
  by: RenderHistoryScalarFieldEnumSchema.array(),
  having: RenderHistoryScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const RenderHistoryFindUniqueArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryFindUniqueArgs, "select">> = z.object({
  where: RenderHistoryWhereUniqueInputSchema,
}).strict() ;

export const RenderHistoryFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryFindUniqueOrThrowArgs, "select">> = z.object({
  where: RenderHistoryWhereUniqueInputSchema,
}).strict() ;

export const ExternalKeysFindFirstArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysFindFirstArgs, "select">> = z.object({
  where: ExternalKeysWhereInputSchema.optional(),
  orderBy: z.union([ ExternalKeysOrderByWithRelationInputSchema.array(),ExternalKeysOrderByWithRelationInputSchema ]).optional(),
  cursor: ExternalKeysWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ExternalKeysScalarFieldEnumSchema,ExternalKeysScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ExternalKeysFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysFindFirstOrThrowArgs, "select">> = z.object({
  where: ExternalKeysWhereInputSchema.optional(),
  orderBy: z.union([ ExternalKeysOrderByWithRelationInputSchema.array(),ExternalKeysOrderByWithRelationInputSchema ]).optional(),
  cursor: ExternalKeysWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ExternalKeysScalarFieldEnumSchema,ExternalKeysScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ExternalKeysFindManyArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysFindManyArgs, "select">> = z.object({
  where: ExternalKeysWhereInputSchema.optional(),
  orderBy: z.union([ ExternalKeysOrderByWithRelationInputSchema.array(),ExternalKeysOrderByWithRelationInputSchema ]).optional(),
  cursor: ExternalKeysWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ExternalKeysScalarFieldEnumSchema,ExternalKeysScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ExternalKeysAggregateArgsSchema: z.ZodType<Prisma.ExternalKeysAggregateArgs> = z.object({
  where: ExternalKeysWhereInputSchema.optional(),
  orderBy: z.union([ ExternalKeysOrderByWithRelationInputSchema.array(),ExternalKeysOrderByWithRelationInputSchema ]).optional(),
  cursor: ExternalKeysWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const ExternalKeysGroupByArgsSchema: z.ZodType<Prisma.ExternalKeysGroupByArgs> = z.object({
  where: ExternalKeysWhereInputSchema.optional(),
  orderBy: z.union([ ExternalKeysOrderByWithAggregationInputSchema.array(),ExternalKeysOrderByWithAggregationInputSchema ]).optional(),
  by: ExternalKeysScalarFieldEnumSchema.array(),
  having: ExternalKeysScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const ExternalKeysFindUniqueArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysFindUniqueArgs, "select">> = z.object({
  where: ExternalKeysWhereUniqueInputSchema,
}).strict() ;

export const ExternalKeysFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysFindUniqueOrThrowArgs, "select">> = z.object({
  where: ExternalKeysWhereUniqueInputSchema,
}).strict() ;

export const ProxyFindFirstArgsSchema: z.ZodType<Omit<Prisma.ProxyFindFirstArgs, "select">> = z.object({
  where: ProxyWhereInputSchema.optional(),
  orderBy: z.union([ ProxyOrderByWithRelationInputSchema.array(),ProxyOrderByWithRelationInputSchema ]).optional(),
  cursor: ProxyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ProxyScalarFieldEnumSchema,ProxyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ProxyFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.ProxyFindFirstOrThrowArgs, "select">> = z.object({
  where: ProxyWhereInputSchema.optional(),
  orderBy: z.union([ ProxyOrderByWithRelationInputSchema.array(),ProxyOrderByWithRelationInputSchema ]).optional(),
  cursor: ProxyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ProxyScalarFieldEnumSchema,ProxyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ProxyFindManyArgsSchema: z.ZodType<Omit<Prisma.ProxyFindManyArgs, "select">> = z.object({
  where: ProxyWhereInputSchema.optional(),
  orderBy: z.union([ ProxyOrderByWithRelationInputSchema.array(),ProxyOrderByWithRelationInputSchema ]).optional(),
  cursor: ProxyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ ProxyScalarFieldEnumSchema,ProxyScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const ProxyAggregateArgsSchema: z.ZodType<Prisma.ProxyAggregateArgs> = z.object({
  where: ProxyWhereInputSchema.optional(),
  orderBy: z.union([ ProxyOrderByWithRelationInputSchema.array(),ProxyOrderByWithRelationInputSchema ]).optional(),
  cursor: ProxyWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const ProxyGroupByArgsSchema: z.ZodType<Prisma.ProxyGroupByArgs> = z.object({
  where: ProxyWhereInputSchema.optional(),
  orderBy: z.union([ ProxyOrderByWithAggregationInputSchema.array(),ProxyOrderByWithAggregationInputSchema ]).optional(),
  by: ProxyScalarFieldEnumSchema.array(),
  having: ProxyScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const ProxyFindUniqueArgsSchema: z.ZodType<Omit<Prisma.ProxyFindUniqueArgs, "select">> = z.object({
  where: ProxyWhereUniqueInputSchema,
}).strict() ;

export const ProxyFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.ProxyFindUniqueOrThrowArgs, "select">> = z.object({
  where: ProxyWhereUniqueInputSchema,
}).strict() ;

export const CookieIdentityFindFirstArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityFindFirstArgs, "select">> = z.object({
  where: CookieIdentityWhereInputSchema.optional(),
  orderBy: z.union([ CookieIdentityOrderByWithRelationInputSchema.array(),CookieIdentityOrderByWithRelationInputSchema ]).optional(),
  cursor: CookieIdentityWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ CookieIdentityScalarFieldEnumSchema,CookieIdentityScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const CookieIdentityFindFirstOrThrowArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityFindFirstOrThrowArgs, "select">> = z.object({
  where: CookieIdentityWhereInputSchema.optional(),
  orderBy: z.union([ CookieIdentityOrderByWithRelationInputSchema.array(),CookieIdentityOrderByWithRelationInputSchema ]).optional(),
  cursor: CookieIdentityWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ CookieIdentityScalarFieldEnumSchema,CookieIdentityScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const CookieIdentityFindManyArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityFindManyArgs, "select">> = z.object({
  where: CookieIdentityWhereInputSchema.optional(),
  orderBy: z.union([ CookieIdentityOrderByWithRelationInputSchema.array(),CookieIdentityOrderByWithRelationInputSchema ]).optional(),
  cursor: CookieIdentityWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
  distinct: z.union([ CookieIdentityScalarFieldEnumSchema,CookieIdentityScalarFieldEnumSchema.array() ]).optional(),
}).strict() ;

export const CookieIdentityAggregateArgsSchema: z.ZodType<Prisma.CookieIdentityAggregateArgs> = z.object({
  where: CookieIdentityWhereInputSchema.optional(),
  orderBy: z.union([ CookieIdentityOrderByWithRelationInputSchema.array(),CookieIdentityOrderByWithRelationInputSchema ]).optional(),
  cursor: CookieIdentityWhereUniqueInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const CookieIdentityGroupByArgsSchema: z.ZodType<Prisma.CookieIdentityGroupByArgs> = z.object({
  where: CookieIdentityWhereInputSchema.optional(),
  orderBy: z.union([ CookieIdentityOrderByWithAggregationInputSchema.array(),CookieIdentityOrderByWithAggregationInputSchema ]).optional(),
  by: CookieIdentityScalarFieldEnumSchema.array(),
  having: CookieIdentityScalarWhereWithAggregatesInputSchema.optional(),
  take: z.number().optional(),
  skip: z.number().optional(),
}).strict() ;

export const CookieIdentityFindUniqueArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityFindUniqueArgs, "select">> = z.object({
  where: CookieIdentityWhereUniqueInputSchema,
}).strict() ;

export const CookieIdentityFindUniqueOrThrowArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityFindUniqueOrThrowArgs, "select">> = z.object({
  where: CookieIdentityWhereUniqueInputSchema,
}).strict() ;

export const UserCreateArgsSchema: z.ZodType<Omit<Prisma.UserCreateArgs, "select">> = z.object({
  data: z.union([ UserCreateInputSchema,UserUncheckedCreateInputSchema ]),
}).strict() ;

export const UserUpsertArgsSchema: z.ZodType<Omit<Prisma.UserUpsertArgs, "select">> = z.object({
  where: UserWhereUniqueInputSchema,
  create: z.union([ UserCreateInputSchema,UserUncheckedCreateInputSchema ]),
  update: z.union([ UserUpdateInputSchema,UserUncheckedUpdateInputSchema ]),
}).strict() ;

export const UserCreateManyArgsSchema: z.ZodType<Prisma.UserCreateManyArgs> = z.object({
  data: z.union([ UserCreateManyInputSchema,UserCreateManyInputSchema.array() ]),
}).strict() ;

export const UserDeleteArgsSchema: z.ZodType<Omit<Prisma.UserDeleteArgs, "select">> = z.object({
  where: UserWhereUniqueInputSchema,
}).strict() ;

export const UserUpdateArgsSchema: z.ZodType<Omit<Prisma.UserUpdateArgs, "select">> = z.object({
  data: z.union([ UserUpdateInputSchema,UserUncheckedUpdateInputSchema ]),
  where: UserWhereUniqueInputSchema,
}).strict() ;

export const UserUpdateManyArgsSchema: z.ZodType<Prisma.UserUpdateManyArgs> = z.object({
  data: z.union([ UserUpdateManyMutationInputSchema,UserUncheckedUpdateManyInputSchema ]),
  where: UserWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const UserDeleteManyArgsSchema: z.ZodType<Prisma.UserDeleteManyArgs> = z.object({
  where: UserWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const SessionCreateArgsSchema: z.ZodType<Omit<Prisma.SessionCreateArgs, "select">> = z.object({
  data: z.union([ SessionCreateInputSchema,SessionUncheckedCreateInputSchema ]),
}).strict() ;

export const SessionUpsertArgsSchema: z.ZodType<Omit<Prisma.SessionUpsertArgs, "select">> = z.object({
  where: SessionWhereUniqueInputSchema,
  create: z.union([ SessionCreateInputSchema,SessionUncheckedCreateInputSchema ]),
  update: z.union([ SessionUpdateInputSchema,SessionUncheckedUpdateInputSchema ]),
}).strict() ;

export const SessionCreateManyArgsSchema: z.ZodType<Prisma.SessionCreateManyArgs> = z.object({
  data: z.union([ SessionCreateManyInputSchema,SessionCreateManyInputSchema.array() ]),
}).strict() ;

export const SessionDeleteArgsSchema: z.ZodType<Omit<Prisma.SessionDeleteArgs, "select">> = z.object({
  where: SessionWhereUniqueInputSchema,
}).strict() ;

export const SessionUpdateArgsSchema: z.ZodType<Omit<Prisma.SessionUpdateArgs, "select">> = z.object({
  data: z.union([ SessionUpdateInputSchema,SessionUncheckedUpdateInputSchema ]),
  where: SessionWhereUniqueInputSchema,
}).strict() ;

export const SessionUpdateManyArgsSchema: z.ZodType<Prisma.SessionUpdateManyArgs> = z.object({
  data: z.union([ SessionUpdateManyMutationInputSchema,SessionUncheckedUpdateManyInputSchema ]),
  where: SessionWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const SessionDeleteManyArgsSchema: z.ZodType<Prisma.SessionDeleteManyArgs> = z.object({
  where: SessionWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const AccountCreateArgsSchema: z.ZodType<Omit<Prisma.AccountCreateArgs, "select">> = z.object({
  data: z.union([ AccountCreateInputSchema,AccountUncheckedCreateInputSchema ]),
}).strict() ;

export const AccountUpsertArgsSchema: z.ZodType<Omit<Prisma.AccountUpsertArgs, "select">> = z.object({
  where: AccountWhereUniqueInputSchema,
  create: z.union([ AccountCreateInputSchema,AccountUncheckedCreateInputSchema ]),
  update: z.union([ AccountUpdateInputSchema,AccountUncheckedUpdateInputSchema ]),
}).strict() ;

export const AccountCreateManyArgsSchema: z.ZodType<Prisma.AccountCreateManyArgs> = z.object({
  data: z.union([ AccountCreateManyInputSchema,AccountCreateManyInputSchema.array() ]),
}).strict() ;

export const AccountDeleteArgsSchema: z.ZodType<Omit<Prisma.AccountDeleteArgs, "select">> = z.object({
  where: AccountWhereUniqueInputSchema,
}).strict() ;

export const AccountUpdateArgsSchema: z.ZodType<Omit<Prisma.AccountUpdateArgs, "select">> = z.object({
  data: z.union([ AccountUpdateInputSchema,AccountUncheckedUpdateInputSchema ]),
  where: AccountWhereUniqueInputSchema,
}).strict() ;

export const AccountUpdateManyArgsSchema: z.ZodType<Prisma.AccountUpdateManyArgs> = z.object({
  data: z.union([ AccountUpdateManyMutationInputSchema,AccountUncheckedUpdateManyInputSchema ]),
  where: AccountWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const AccountDeleteManyArgsSchema: z.ZodType<Prisma.AccountDeleteManyArgs> = z.object({
  where: AccountWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VerificationCreateArgsSchema: z.ZodType<Omit<Prisma.VerificationCreateArgs, "select">> = z.object({
  data: z.union([ VerificationCreateInputSchema,VerificationUncheckedCreateInputSchema ]),
}).strict() ;

export const VerificationUpsertArgsSchema: z.ZodType<Omit<Prisma.VerificationUpsertArgs, "select">> = z.object({
  where: VerificationWhereUniqueInputSchema,
  create: z.union([ VerificationCreateInputSchema,VerificationUncheckedCreateInputSchema ]),
  update: z.union([ VerificationUpdateInputSchema,VerificationUncheckedUpdateInputSchema ]),
}).strict() ;

export const VerificationCreateManyArgsSchema: z.ZodType<Prisma.VerificationCreateManyArgs> = z.object({
  data: z.union([ VerificationCreateManyInputSchema,VerificationCreateManyInputSchema.array() ]),
}).strict() ;

export const VerificationDeleteArgsSchema: z.ZodType<Omit<Prisma.VerificationDeleteArgs, "select">> = z.object({
  where: VerificationWhereUniqueInputSchema,
}).strict() ;

export const VerificationUpdateArgsSchema: z.ZodType<Omit<Prisma.VerificationUpdateArgs, "select">> = z.object({
  data: z.union([ VerificationUpdateInputSchema,VerificationUncheckedUpdateInputSchema ]),
  where: VerificationWhereUniqueInputSchema,
}).strict() ;

export const VerificationUpdateManyArgsSchema: z.ZodType<Prisma.VerificationUpdateManyArgs> = z.object({
  data: z.union([ VerificationUpdateManyMutationInputSchema,VerificationUncheckedUpdateManyInputSchema ]),
  where: VerificationWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VerificationDeleteManyArgsSchema: z.ZodType<Prisma.VerificationDeleteManyArgs> = z.object({
  where: VerificationWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const PasskeyCreateArgsSchema: z.ZodType<Omit<Prisma.PasskeyCreateArgs, "select">> = z.object({
  data: z.union([ PasskeyCreateInputSchema,PasskeyUncheckedCreateInputSchema ]),
}).strict() ;

export const PasskeyUpsertArgsSchema: z.ZodType<Omit<Prisma.PasskeyUpsertArgs, "select">> = z.object({
  where: PasskeyWhereUniqueInputSchema,
  create: z.union([ PasskeyCreateInputSchema,PasskeyUncheckedCreateInputSchema ]),
  update: z.union([ PasskeyUpdateInputSchema,PasskeyUncheckedUpdateInputSchema ]),
}).strict() ;

export const PasskeyCreateManyArgsSchema: z.ZodType<Prisma.PasskeyCreateManyArgs> = z.object({
  data: z.union([ PasskeyCreateManyInputSchema,PasskeyCreateManyInputSchema.array() ]),
}).strict() ;

export const PasskeyDeleteArgsSchema: z.ZodType<Omit<Prisma.PasskeyDeleteArgs, "select">> = z.object({
  where: PasskeyWhereUniqueInputSchema,
}).strict() ;

export const PasskeyUpdateArgsSchema: z.ZodType<Omit<Prisma.PasskeyUpdateArgs, "select">> = z.object({
  data: z.union([ PasskeyUpdateInputSchema,PasskeyUncheckedUpdateInputSchema ]),
  where: PasskeyWhereUniqueInputSchema,
}).strict() ;

export const PasskeyUpdateManyArgsSchema: z.ZodType<Prisma.PasskeyUpdateManyArgs> = z.object({
  data: z.union([ PasskeyUpdateManyMutationInputSchema,PasskeyUncheckedUpdateManyInputSchema ]),
  where: PasskeyWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const PasskeyDeleteManyArgsSchema: z.ZodType<Prisma.PasskeyDeleteManyArgs> = z.object({
  where: PasskeyWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const TwoFactorCreateArgsSchema: z.ZodType<Omit<Prisma.TwoFactorCreateArgs, "select">> = z.object({
  data: z.union([ TwoFactorCreateInputSchema,TwoFactorUncheckedCreateInputSchema ]),
}).strict() ;

export const TwoFactorUpsertArgsSchema: z.ZodType<Omit<Prisma.TwoFactorUpsertArgs, "select">> = z.object({
  where: TwoFactorWhereUniqueInputSchema,
  create: z.union([ TwoFactorCreateInputSchema,TwoFactorUncheckedCreateInputSchema ]),
  update: z.union([ TwoFactorUpdateInputSchema,TwoFactorUncheckedUpdateInputSchema ]),
}).strict() ;

export const TwoFactorCreateManyArgsSchema: z.ZodType<Prisma.TwoFactorCreateManyArgs> = z.object({
  data: z.union([ TwoFactorCreateManyInputSchema,TwoFactorCreateManyInputSchema.array() ]),
}).strict() ;

export const TwoFactorDeleteArgsSchema: z.ZodType<Omit<Prisma.TwoFactorDeleteArgs, "select">> = z.object({
  where: TwoFactorWhereUniqueInputSchema,
}).strict() ;

export const TwoFactorUpdateArgsSchema: z.ZodType<Omit<Prisma.TwoFactorUpdateArgs, "select">> = z.object({
  data: z.union([ TwoFactorUpdateInputSchema,TwoFactorUncheckedUpdateInputSchema ]),
  where: TwoFactorWhereUniqueInputSchema,
}).strict() ;

export const TwoFactorUpdateManyArgsSchema: z.ZodType<Prisma.TwoFactorUpdateManyArgs> = z.object({
  data: z.union([ TwoFactorUpdateManyMutationInputSchema,TwoFactorUncheckedUpdateManyInputSchema ]),
  where: TwoFactorWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const TwoFactorDeleteManyArgsSchema: z.ZodType<Prisma.TwoFactorDeleteManyArgs> = z.object({
  where: TwoFactorWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationCreateArgsSchema: z.ZodType<Omit<Prisma.OrganizationCreateArgs, "select">> = z.object({
  data: z.union([ OrganizationCreateInputSchema,OrganizationUncheckedCreateInputSchema ]),
}).strict() ;

export const OrganizationUpsertArgsSchema: z.ZodType<Omit<Prisma.OrganizationUpsertArgs, "select">> = z.object({
  where: OrganizationWhereUniqueInputSchema,
  create: z.union([ OrganizationCreateInputSchema,OrganizationUncheckedCreateInputSchema ]),
  update: z.union([ OrganizationUpdateInputSchema,OrganizationUncheckedUpdateInputSchema ]),
}).strict() ;

export const OrganizationCreateManyArgsSchema: z.ZodType<Prisma.OrganizationCreateManyArgs> = z.object({
  data: z.union([ OrganizationCreateManyInputSchema,OrganizationCreateManyInputSchema.array() ]),
}).strict() ;

export const OrganizationDeleteArgsSchema: z.ZodType<Omit<Prisma.OrganizationDeleteArgs, "select">> = z.object({
  where: OrganizationWhereUniqueInputSchema,
}).strict() ;

export const OrganizationUpdateArgsSchema: z.ZodType<Omit<Prisma.OrganizationUpdateArgs, "select">> = z.object({
  data: z.union([ OrganizationUpdateInputSchema,OrganizationUncheckedUpdateInputSchema ]),
  where: OrganizationWhereUniqueInputSchema,
}).strict() ;

export const OrganizationUpdateManyArgsSchema: z.ZodType<Prisma.OrganizationUpdateManyArgs> = z.object({
  data: z.union([ OrganizationUpdateManyMutationInputSchema,OrganizationUncheckedUpdateManyInputSchema ]),
  where: OrganizationWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationDeleteManyArgsSchema: z.ZodType<Prisma.OrganizationDeleteManyArgs> = z.object({
  where: OrganizationWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const MemberCreateArgsSchema: z.ZodType<Omit<Prisma.MemberCreateArgs, "select">> = z.object({
  data: z.union([ MemberCreateInputSchema,MemberUncheckedCreateInputSchema ]),
}).strict() ;

export const MemberUpsertArgsSchema: z.ZodType<Omit<Prisma.MemberUpsertArgs, "select">> = z.object({
  where: MemberWhereUniqueInputSchema,
  create: z.union([ MemberCreateInputSchema,MemberUncheckedCreateInputSchema ]),
  update: z.union([ MemberUpdateInputSchema,MemberUncheckedUpdateInputSchema ]),
}).strict() ;

export const MemberCreateManyArgsSchema: z.ZodType<Prisma.MemberCreateManyArgs> = z.object({
  data: z.union([ MemberCreateManyInputSchema,MemberCreateManyInputSchema.array() ]),
}).strict() ;

export const MemberDeleteArgsSchema: z.ZodType<Omit<Prisma.MemberDeleteArgs, "select">> = z.object({
  where: MemberWhereUniqueInputSchema,
}).strict() ;

export const MemberUpdateArgsSchema: z.ZodType<Omit<Prisma.MemberUpdateArgs, "select">> = z.object({
  data: z.union([ MemberUpdateInputSchema,MemberUncheckedUpdateInputSchema ]),
  where: MemberWhereUniqueInputSchema,
}).strict() ;

export const MemberUpdateManyArgsSchema: z.ZodType<Prisma.MemberUpdateManyArgs> = z.object({
  data: z.union([ MemberUpdateManyMutationInputSchema,MemberUncheckedUpdateManyInputSchema ]),
  where: MemberWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const MemberDeleteManyArgsSchema: z.ZodType<Prisma.MemberDeleteManyArgs> = z.object({
  where: MemberWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const InvitationCreateArgsSchema: z.ZodType<Omit<Prisma.InvitationCreateArgs, "select">> = z.object({
  data: z.union([ InvitationCreateInputSchema,InvitationUncheckedCreateInputSchema ]),
}).strict() ;

export const InvitationUpsertArgsSchema: z.ZodType<Omit<Prisma.InvitationUpsertArgs, "select">> = z.object({
  where: InvitationWhereUniqueInputSchema,
  create: z.union([ InvitationCreateInputSchema,InvitationUncheckedCreateInputSchema ]),
  update: z.union([ InvitationUpdateInputSchema,InvitationUncheckedUpdateInputSchema ]),
}).strict() ;

export const InvitationCreateManyArgsSchema: z.ZodType<Prisma.InvitationCreateManyArgs> = z.object({
  data: z.union([ InvitationCreateManyInputSchema,InvitationCreateManyInputSchema.array() ]),
}).strict() ;

export const InvitationDeleteArgsSchema: z.ZodType<Omit<Prisma.InvitationDeleteArgs, "select">> = z.object({
  where: InvitationWhereUniqueInputSchema,
}).strict() ;

export const InvitationUpdateArgsSchema: z.ZodType<Omit<Prisma.InvitationUpdateArgs, "select">> = z.object({
  data: z.union([ InvitationUpdateInputSchema,InvitationUncheckedUpdateInputSchema ]),
  where: InvitationWhereUniqueInputSchema,
}).strict() ;

export const InvitationUpdateManyArgsSchema: z.ZodType<Prisma.InvitationUpdateManyArgs> = z.object({
  data: z.union([ InvitationUpdateManyMutationInputSchema,InvitationUncheckedUpdateManyInputSchema ]),
  where: InvitationWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const InvitationDeleteManyArgsSchema: z.ZodType<Prisma.InvitationDeleteManyArgs> = z.object({
  where: InvitationWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const PurchaseCreateArgsSchema: z.ZodType<Omit<Prisma.PurchaseCreateArgs, "select">> = z.object({
  data: z.union([ PurchaseCreateInputSchema,PurchaseUncheckedCreateInputSchema ]),
}).strict() ;

export const PurchaseUpsertArgsSchema: z.ZodType<Omit<Prisma.PurchaseUpsertArgs, "select">> = z.object({
  where: PurchaseWhereUniqueInputSchema,
  create: z.union([ PurchaseCreateInputSchema,PurchaseUncheckedCreateInputSchema ]),
  update: z.union([ PurchaseUpdateInputSchema,PurchaseUncheckedUpdateInputSchema ]),
}).strict() ;

export const PurchaseCreateManyArgsSchema: z.ZodType<Prisma.PurchaseCreateManyArgs> = z.object({
  data: z.union([ PurchaseCreateManyInputSchema,PurchaseCreateManyInputSchema.array() ]),
}).strict() ;

export const PurchaseDeleteArgsSchema: z.ZodType<Omit<Prisma.PurchaseDeleteArgs, "select">> = z.object({
  where: PurchaseWhereUniqueInputSchema,
}).strict() ;

export const PurchaseUpdateArgsSchema: z.ZodType<Omit<Prisma.PurchaseUpdateArgs, "select">> = z.object({
  data: z.union([ PurchaseUpdateInputSchema,PurchaseUncheckedUpdateInputSchema ]),
  where: PurchaseWhereUniqueInputSchema,
}).strict() ;

export const PurchaseUpdateManyArgsSchema: z.ZodType<Prisma.PurchaseUpdateManyArgs> = z.object({
  data: z.union([ PurchaseUpdateManyMutationInputSchema,PurchaseUncheckedUpdateManyInputSchema ]),
  where: PurchaseWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const PurchaseDeleteManyArgsSchema: z.ZodType<Prisma.PurchaseDeleteManyArgs> = z.object({
  where: PurchaseWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const AiChatCreateArgsSchema: z.ZodType<Omit<Prisma.AiChatCreateArgs, "select">> = z.object({
  data: z.union([ AiChatCreateInputSchema,AiChatUncheckedCreateInputSchema ]),
}).strict() ;

export const AiChatUpsertArgsSchema: z.ZodType<Omit<Prisma.AiChatUpsertArgs, "select">> = z.object({
  where: AiChatWhereUniqueInputSchema,
  create: z.union([ AiChatCreateInputSchema,AiChatUncheckedCreateInputSchema ]),
  update: z.union([ AiChatUpdateInputSchema,AiChatUncheckedUpdateInputSchema ]),
}).strict() ;

export const AiChatCreateManyArgsSchema: z.ZodType<Prisma.AiChatCreateManyArgs> = z.object({
  data: z.union([ AiChatCreateManyInputSchema,AiChatCreateManyInputSchema.array() ]),
}).strict() ;

export const AiChatDeleteArgsSchema: z.ZodType<Omit<Prisma.AiChatDeleteArgs, "select">> = z.object({
  where: AiChatWhereUniqueInputSchema,
}).strict() ;

export const AiChatUpdateArgsSchema: z.ZodType<Omit<Prisma.AiChatUpdateArgs, "select">> = z.object({
  data: z.union([ AiChatUpdateInputSchema,AiChatUncheckedUpdateInputSchema ]),
  where: AiChatWhereUniqueInputSchema,
}).strict() ;

export const AiChatUpdateManyArgsSchema: z.ZodType<Prisma.AiChatUpdateManyArgs> = z.object({
  data: z.union([ AiChatUpdateManyMutationInputSchema,AiChatUncheckedUpdateManyInputSchema ]),
  where: AiChatWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const AiChatDeleteManyArgsSchema: z.ZodType<Prisma.AiChatDeleteManyArgs> = z.object({
  where: AiChatWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const ApiKeyCreateArgsSchema: z.ZodType<Omit<Prisma.ApiKeyCreateArgs, "select">> = z.object({
  data: z.union([ ApiKeyCreateInputSchema,ApiKeyUncheckedCreateInputSchema ]),
}).strict() ;

export const ApiKeyUpsertArgsSchema: z.ZodType<Omit<Prisma.ApiKeyUpsertArgs, "select">> = z.object({
  where: ApiKeyWhereUniqueInputSchema,
  create: z.union([ ApiKeyCreateInputSchema,ApiKeyUncheckedCreateInputSchema ]),
  update: z.union([ ApiKeyUpdateInputSchema,ApiKeyUncheckedUpdateInputSchema ]),
}).strict() ;

export const ApiKeyCreateManyArgsSchema: z.ZodType<Prisma.ApiKeyCreateManyArgs> = z.object({
  data: z.union([ ApiKeyCreateManyInputSchema,ApiKeyCreateManyInputSchema.array() ]),
}).strict() ;

export const ApiKeyDeleteArgsSchema: z.ZodType<Omit<Prisma.ApiKeyDeleteArgs, "select">> = z.object({
  where: ApiKeyWhereUniqueInputSchema,
}).strict() ;

export const ApiKeyUpdateArgsSchema: z.ZodType<Omit<Prisma.ApiKeyUpdateArgs, "select">> = z.object({
  data: z.union([ ApiKeyUpdateInputSchema,ApiKeyUncheckedUpdateInputSchema ]),
  where: ApiKeyWhereUniqueInputSchema,
}).strict() ;

export const ApiKeyUpdateManyArgsSchema: z.ZodType<Prisma.ApiKeyUpdateManyArgs> = z.object({
  data: z.union([ ApiKeyUpdateManyMutationInputSchema,ApiKeyUncheckedUpdateManyInputSchema ]),
  where: ApiKeyWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const ApiKeyDeleteManyArgsSchema: z.ZodType<Prisma.ApiKeyDeleteManyArgs> = z.object({
  where: ApiKeyWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const JobCreateArgsSchema: z.ZodType<Omit<Prisma.JobCreateArgs, "select">> = z.object({
  data: z.union([ JobCreateInputSchema,JobUncheckedCreateInputSchema ]),
}).strict() ;

export const JobUpsertArgsSchema: z.ZodType<Omit<Prisma.JobUpsertArgs, "select">> = z.object({
  where: JobWhereUniqueInputSchema,
  create: z.union([ JobCreateInputSchema,JobUncheckedCreateInputSchema ]),
  update: z.union([ JobUpdateInputSchema,JobUncheckedUpdateInputSchema ]),
}).strict() ;

export const JobCreateManyArgsSchema: z.ZodType<Prisma.JobCreateManyArgs> = z.object({
  data: z.union([ JobCreateManyInputSchema,JobCreateManyInputSchema.array() ]),
}).strict() ;

export const JobDeleteArgsSchema: z.ZodType<Omit<Prisma.JobDeleteArgs, "select">> = z.object({
  where: JobWhereUniqueInputSchema,
}).strict() ;

export const JobUpdateArgsSchema: z.ZodType<Omit<Prisma.JobUpdateArgs, "select">> = z.object({
  data: z.union([ JobUpdateInputSchema,JobUncheckedUpdateInputSchema ]),
  where: JobWhereUniqueInputSchema,
}).strict() ;

export const JobUpdateManyArgsSchema: z.ZodType<Prisma.JobUpdateManyArgs> = z.object({
  data: z.union([ JobUpdateManyMutationInputSchema,JobUncheckedUpdateManyInputSchema ]),
  where: JobWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const JobDeleteManyArgsSchema: z.ZodType<Prisma.JobDeleteManyArgs> = z.object({
  where: JobWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VideoCreateArgsSchema: z.ZodType<Omit<Prisma.VideoCreateArgs, "select">> = z.object({
  data: z.union([ VideoCreateInputSchema,VideoUncheckedCreateInputSchema ]),
}).strict() ;

export const VideoUpsertArgsSchema: z.ZodType<Omit<Prisma.VideoUpsertArgs, "select">> = z.object({
  where: VideoWhereUniqueInputSchema,
  create: z.union([ VideoCreateInputSchema,VideoUncheckedCreateInputSchema ]),
  update: z.union([ VideoUpdateInputSchema,VideoUncheckedUpdateInputSchema ]),
}).strict() ;

export const VideoCreateManyArgsSchema: z.ZodType<Prisma.VideoCreateManyArgs> = z.object({
  data: z.union([ VideoCreateManyInputSchema,VideoCreateManyInputSchema.array() ]),
}).strict() ;

export const VideoDeleteArgsSchema: z.ZodType<Omit<Prisma.VideoDeleteArgs, "select">> = z.object({
  where: VideoWhereUniqueInputSchema,
}).strict() ;

export const VideoUpdateArgsSchema: z.ZodType<Omit<Prisma.VideoUpdateArgs, "select">> = z.object({
  data: z.union([ VideoUpdateInputSchema,VideoUncheckedUpdateInputSchema ]),
  where: VideoWhereUniqueInputSchema,
}).strict() ;

export const VideoUpdateManyArgsSchema: z.ZodType<Prisma.VideoUpdateManyArgs> = z.object({
  data: z.union([ VideoUpdateManyMutationInputSchema,VideoUncheckedUpdateManyInputSchema ]),
  where: VideoWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VideoDeleteManyArgsSchema: z.ZodType<Prisma.VideoDeleteManyArgs> = z.object({
  where: VideoWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VideoProjectCreateArgsSchema: z.ZodType<Omit<Prisma.VideoProjectCreateArgs, "select">> = z.object({
  data: z.union([ VideoProjectCreateInputSchema,VideoProjectUncheckedCreateInputSchema ]),
}).strict() ;

export const VideoProjectUpsertArgsSchema: z.ZodType<Omit<Prisma.VideoProjectUpsertArgs, "select">> = z.object({
  where: VideoProjectWhereUniqueInputSchema,
  create: z.union([ VideoProjectCreateInputSchema,VideoProjectUncheckedCreateInputSchema ]),
  update: z.union([ VideoProjectUpdateInputSchema,VideoProjectUncheckedUpdateInputSchema ]),
}).strict() ;

export const VideoProjectCreateManyArgsSchema: z.ZodType<Prisma.VideoProjectCreateManyArgs> = z.object({
  data: z.union([ VideoProjectCreateManyInputSchema,VideoProjectCreateManyInputSchema.array() ]),
}).strict() ;

export const VideoProjectDeleteArgsSchema: z.ZodType<Omit<Prisma.VideoProjectDeleteArgs, "select">> = z.object({
  where: VideoProjectWhereUniqueInputSchema,
}).strict() ;

export const VideoProjectUpdateArgsSchema: z.ZodType<Omit<Prisma.VideoProjectUpdateArgs, "select">> = z.object({
  data: z.union([ VideoProjectUpdateInputSchema,VideoProjectUncheckedUpdateInputSchema ]),
  where: VideoProjectWhereUniqueInputSchema,
}).strict() ;

export const VideoProjectUpdateManyArgsSchema: z.ZodType<Prisma.VideoProjectUpdateManyArgs> = z.object({
  data: z.union([ VideoProjectUpdateManyMutationInputSchema,VideoProjectUncheckedUpdateManyInputSchema ]),
  where: VideoProjectWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VideoProjectDeleteManyArgsSchema: z.ZodType<Prisma.VideoProjectDeleteManyArgs> = z.object({
  where: VideoProjectWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VideoDesignCreateArgsSchema: z.ZodType<Omit<Prisma.VideoDesignCreateArgs, "select">> = z.object({
  data: z.union([ VideoDesignCreateInputSchema,VideoDesignUncheckedCreateInputSchema ]),
}).strict() ;

export const VideoDesignUpsertArgsSchema: z.ZodType<Omit<Prisma.VideoDesignUpsertArgs, "select">> = z.object({
  where: VideoDesignWhereUniqueInputSchema,
  create: z.union([ VideoDesignCreateInputSchema,VideoDesignUncheckedCreateInputSchema ]),
  update: z.union([ VideoDesignUpdateInputSchema,VideoDesignUncheckedUpdateInputSchema ]),
}).strict() ;

export const VideoDesignCreateManyArgsSchema: z.ZodType<Prisma.VideoDesignCreateManyArgs> = z.object({
  data: z.union([ VideoDesignCreateManyInputSchema,VideoDesignCreateManyInputSchema.array() ]),
}).strict() ;

export const VideoDesignDeleteArgsSchema: z.ZodType<Omit<Prisma.VideoDesignDeleteArgs, "select">> = z.object({
  where: VideoDesignWhereUniqueInputSchema,
}).strict() ;

export const VideoDesignUpdateArgsSchema: z.ZodType<Omit<Prisma.VideoDesignUpdateArgs, "select">> = z.object({
  data: z.union([ VideoDesignUpdateInputSchema,VideoDesignUncheckedUpdateInputSchema ]),
  where: VideoDesignWhereUniqueInputSchema,
}).strict() ;

export const VideoDesignUpdateManyArgsSchema: z.ZodType<Prisma.VideoDesignUpdateManyArgs> = z.object({
  data: z.union([ VideoDesignUpdateManyMutationInputSchema,VideoDesignUncheckedUpdateManyInputSchema ]),
  where: VideoDesignWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const VideoDesignDeleteManyArgsSchema: z.ZodType<Prisma.VideoDesignDeleteManyArgs> = z.object({
  where: VideoDesignWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const AppFileCreateArgsSchema: z.ZodType<Omit<Prisma.AppFileCreateArgs, "select">> = z.object({
  data: z.union([ AppFileCreateInputSchema,AppFileUncheckedCreateInputSchema ]),
}).strict() ;

export const AppFileUpsertArgsSchema: z.ZodType<Omit<Prisma.AppFileUpsertArgs, "select">> = z.object({
  where: AppFileWhereUniqueInputSchema,
  create: z.union([ AppFileCreateInputSchema,AppFileUncheckedCreateInputSchema ]),
  update: z.union([ AppFileUpdateInputSchema,AppFileUncheckedUpdateInputSchema ]),
}).strict() ;

export const AppFileCreateManyArgsSchema: z.ZodType<Prisma.AppFileCreateManyArgs> = z.object({
  data: z.union([ AppFileCreateManyInputSchema,AppFileCreateManyInputSchema.array() ]),
}).strict() ;

export const AppFileDeleteArgsSchema: z.ZodType<Omit<Prisma.AppFileDeleteArgs, "select">> = z.object({
  where: AppFileWhereUniqueInputSchema,
}).strict() ;

export const AppFileUpdateArgsSchema: z.ZodType<Omit<Prisma.AppFileUpdateArgs, "select">> = z.object({
  data: z.union([ AppFileUpdateInputSchema,AppFileUncheckedUpdateInputSchema ]),
  where: AppFileWhereUniqueInputSchema,
}).strict() ;

export const AppFileUpdateManyArgsSchema: z.ZodType<Prisma.AppFileUpdateManyArgs> = z.object({
  data: z.union([ AppFileUpdateManyMutationInputSchema,AppFileUncheckedUpdateManyInputSchema ]),
  where: AppFileWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const AppFileDeleteManyArgsSchema: z.ZodType<Prisma.AppFileDeleteManyArgs> = z.object({
  where: AppFileWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const CaptionStyleCreateArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleCreateArgs, "select">> = z.object({
  data: z.union([ CaptionStyleCreateInputSchema,CaptionStyleUncheckedCreateInputSchema ]),
}).strict() ;

export const CaptionStyleUpsertArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleUpsertArgs, "select">> = z.object({
  where: CaptionStyleWhereUniqueInputSchema,
  create: z.union([ CaptionStyleCreateInputSchema,CaptionStyleUncheckedCreateInputSchema ]),
  update: z.union([ CaptionStyleUpdateInputSchema,CaptionStyleUncheckedUpdateInputSchema ]),
}).strict() ;

export const CaptionStyleCreateManyArgsSchema: z.ZodType<Prisma.CaptionStyleCreateManyArgs> = z.object({
  data: z.union([ CaptionStyleCreateManyInputSchema,CaptionStyleCreateManyInputSchema.array() ]),
}).strict() ;

export const CaptionStyleDeleteArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleDeleteArgs, "select">> = z.object({
  where: CaptionStyleWhereUniqueInputSchema,
}).strict() ;

export const CaptionStyleUpdateArgsSchema: z.ZodType<Omit<Prisma.CaptionStyleUpdateArgs, "select">> = z.object({
  data: z.union([ CaptionStyleUpdateInputSchema,CaptionStyleUncheckedUpdateInputSchema ]),
  where: CaptionStyleWhereUniqueInputSchema,
}).strict() ;

export const CaptionStyleUpdateManyArgsSchema: z.ZodType<Prisma.CaptionStyleUpdateManyArgs> = z.object({
  data: z.union([ CaptionStyleUpdateManyMutationInputSchema,CaptionStyleUncheckedUpdateManyInputSchema ]),
  where: CaptionStyleWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const CaptionStyleDeleteManyArgsSchema: z.ZodType<Prisma.CaptionStyleDeleteManyArgs> = z.object({
  where: CaptionStyleWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationBrandingCreateArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingCreateArgs, "select">> = z.object({
  data: z.union([ OrganizationBrandingCreateInputSchema,OrganizationBrandingUncheckedCreateInputSchema ]),
}).strict() ;

export const OrganizationBrandingUpsertArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingUpsertArgs, "select">> = z.object({
  where: OrganizationBrandingWhereUniqueInputSchema,
  create: z.union([ OrganizationBrandingCreateInputSchema,OrganizationBrandingUncheckedCreateInputSchema ]),
  update: z.union([ OrganizationBrandingUpdateInputSchema,OrganizationBrandingUncheckedUpdateInputSchema ]),
}).strict() ;

export const OrganizationBrandingCreateManyArgsSchema: z.ZodType<Prisma.OrganizationBrandingCreateManyArgs> = z.object({
  data: z.union([ OrganizationBrandingCreateManyInputSchema,OrganizationBrandingCreateManyInputSchema.array() ]),
}).strict() ;

export const OrganizationBrandingDeleteArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingDeleteArgs, "select">> = z.object({
  where: OrganizationBrandingWhereUniqueInputSchema,
}).strict() ;

export const OrganizationBrandingUpdateArgsSchema: z.ZodType<Omit<Prisma.OrganizationBrandingUpdateArgs, "select">> = z.object({
  data: z.union([ OrganizationBrandingUpdateInputSchema,OrganizationBrandingUncheckedUpdateInputSchema ]),
  where: OrganizationBrandingWhereUniqueInputSchema,
}).strict() ;

export const OrganizationBrandingUpdateManyArgsSchema: z.ZodType<Prisma.OrganizationBrandingUpdateManyArgs> = z.object({
  data: z.union([ OrganizationBrandingUpdateManyMutationInputSchema,OrganizationBrandingUncheckedUpdateManyInputSchema ]),
  where: OrganizationBrandingWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationBrandingDeleteManyArgsSchema: z.ZodType<Prisma.OrganizationBrandingDeleteManyArgs> = z.object({
  where: OrganizationBrandingWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationNarratorProfileCreateArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileCreateArgs, "select">> = z.object({
  data: z.union([ OrganizationNarratorProfileCreateInputSchema,OrganizationNarratorProfileUncheckedCreateInputSchema ]),
}).strict() ;

export const OrganizationNarratorProfileUpsertArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileUpsertArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereUniqueInputSchema,
  create: z.union([ OrganizationNarratorProfileCreateInputSchema,OrganizationNarratorProfileUncheckedCreateInputSchema ]),
  update: z.union([ OrganizationNarratorProfileUpdateInputSchema,OrganizationNarratorProfileUncheckedUpdateInputSchema ]),
}).strict() ;

export const OrganizationNarratorProfileCreateManyArgsSchema: z.ZodType<Prisma.OrganizationNarratorProfileCreateManyArgs> = z.object({
  data: z.union([ OrganizationNarratorProfileCreateManyInputSchema,OrganizationNarratorProfileCreateManyInputSchema.array() ]),
}).strict() ;

export const OrganizationNarratorProfileDeleteArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileDeleteArgs, "select">> = z.object({
  where: OrganizationNarratorProfileWhereUniqueInputSchema,
}).strict() ;

export const OrganizationNarratorProfileUpdateArgsSchema: z.ZodType<Omit<Prisma.OrganizationNarratorProfileUpdateArgs, "select">> = z.object({
  data: z.union([ OrganizationNarratorProfileUpdateInputSchema,OrganizationNarratorProfileUncheckedUpdateInputSchema ]),
  where: OrganizationNarratorProfileWhereUniqueInputSchema,
}).strict() ;

export const OrganizationNarratorProfileUpdateManyArgsSchema: z.ZodType<Prisma.OrganizationNarratorProfileUpdateManyArgs> = z.object({
  data: z.union([ OrganizationNarratorProfileUpdateManyMutationInputSchema,OrganizationNarratorProfileUncheckedUpdateManyInputSchema ]),
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationNarratorProfileDeleteManyArgsSchema: z.ZodType<Prisma.OrganizationNarratorProfileDeleteManyArgs> = z.object({
  where: OrganizationNarratorProfileWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationSocialSettingsCreateArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsCreateArgs, "select">> = z.object({
  data: z.union([ OrganizationSocialSettingsCreateInputSchema,OrganizationSocialSettingsUncheckedCreateInputSchema ]),
}).strict() ;

export const OrganizationSocialSettingsUpsertArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsUpsertArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereUniqueInputSchema,
  create: z.union([ OrganizationSocialSettingsCreateInputSchema,OrganizationSocialSettingsUncheckedCreateInputSchema ]),
  update: z.union([ OrganizationSocialSettingsUpdateInputSchema,OrganizationSocialSettingsUncheckedUpdateInputSchema ]),
}).strict() ;

export const OrganizationSocialSettingsCreateManyArgsSchema: z.ZodType<Prisma.OrganizationSocialSettingsCreateManyArgs> = z.object({
  data: z.union([ OrganizationSocialSettingsCreateManyInputSchema,OrganizationSocialSettingsCreateManyInputSchema.array() ]),
}).strict() ;

export const OrganizationSocialSettingsDeleteArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsDeleteArgs, "select">> = z.object({
  where: OrganizationSocialSettingsWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialSettingsUpdateArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialSettingsUpdateArgs, "select">> = z.object({
  data: z.union([ OrganizationSocialSettingsUpdateInputSchema,OrganizationSocialSettingsUncheckedUpdateInputSchema ]),
  where: OrganizationSocialSettingsWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialSettingsUpdateManyArgsSchema: z.ZodType<Prisma.OrganizationSocialSettingsUpdateManyArgs> = z.object({
  data: z.union([ OrganizationSocialSettingsUpdateManyMutationInputSchema,OrganizationSocialSettingsUncheckedUpdateManyInputSchema ]),
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationSocialSettingsDeleteManyArgsSchema: z.ZodType<Prisma.OrganizationSocialSettingsDeleteManyArgs> = z.object({
  where: OrganizationSocialSettingsWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationSocialPostCreateArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostCreateArgs, "select">> = z.object({
  data: z.union([ OrganizationSocialPostCreateInputSchema,OrganizationSocialPostUncheckedCreateInputSchema ]),
}).strict() ;

export const OrganizationSocialPostUpsertArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostUpsertArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereUniqueInputSchema,
  create: z.union([ OrganizationSocialPostCreateInputSchema,OrganizationSocialPostUncheckedCreateInputSchema ]),
  update: z.union([ OrganizationSocialPostUpdateInputSchema,OrganizationSocialPostUncheckedUpdateInputSchema ]),
}).strict() ;

export const OrganizationSocialPostCreateManyArgsSchema: z.ZodType<Prisma.OrganizationSocialPostCreateManyArgs> = z.object({
  data: z.union([ OrganizationSocialPostCreateManyInputSchema,OrganizationSocialPostCreateManyInputSchema.array() ]),
}).strict() ;

export const OrganizationSocialPostDeleteArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostDeleteArgs, "select">> = z.object({
  where: OrganizationSocialPostWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialPostUpdateArgsSchema: z.ZodType<Omit<Prisma.OrganizationSocialPostUpdateArgs, "select">> = z.object({
  data: z.union([ OrganizationSocialPostUpdateInputSchema,OrganizationSocialPostUncheckedUpdateInputSchema ]),
  where: OrganizationSocialPostWhereUniqueInputSchema,
}).strict() ;

export const OrganizationSocialPostUpdateManyArgsSchema: z.ZodType<Prisma.OrganizationSocialPostUpdateManyArgs> = z.object({
  data: z.union([ OrganizationSocialPostUpdateManyMutationInputSchema,OrganizationSocialPostUncheckedUpdateManyInputSchema ]),
  where: OrganizationSocialPostWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const OrganizationSocialPostDeleteManyArgsSchema: z.ZodType<Prisma.OrganizationSocialPostDeleteManyArgs> = z.object({
  where: OrganizationSocialPostWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const RenderHistoryCreateArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryCreateArgs, "select">> = z.object({
  data: z.union([ RenderHistoryCreateInputSchema,RenderHistoryUncheckedCreateInputSchema ]),
}).strict() ;

export const RenderHistoryUpsertArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryUpsertArgs, "select">> = z.object({
  where: RenderHistoryWhereUniqueInputSchema,
  create: z.union([ RenderHistoryCreateInputSchema,RenderHistoryUncheckedCreateInputSchema ]),
  update: z.union([ RenderHistoryUpdateInputSchema,RenderHistoryUncheckedUpdateInputSchema ]),
}).strict() ;

export const RenderHistoryCreateManyArgsSchema: z.ZodType<Prisma.RenderHistoryCreateManyArgs> = z.object({
  data: z.union([ RenderHistoryCreateManyInputSchema,RenderHistoryCreateManyInputSchema.array() ]),
}).strict() ;

export const RenderHistoryDeleteArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryDeleteArgs, "select">> = z.object({
  where: RenderHistoryWhereUniqueInputSchema,
}).strict() ;

export const RenderHistoryUpdateArgsSchema: z.ZodType<Omit<Prisma.RenderHistoryUpdateArgs, "select">> = z.object({
  data: z.union([ RenderHistoryUpdateInputSchema,RenderHistoryUncheckedUpdateInputSchema ]),
  where: RenderHistoryWhereUniqueInputSchema,
}).strict() ;

export const RenderHistoryUpdateManyArgsSchema: z.ZodType<Prisma.RenderHistoryUpdateManyArgs> = z.object({
  data: z.union([ RenderHistoryUpdateManyMutationInputSchema,RenderHistoryUncheckedUpdateManyInputSchema ]),
  where: RenderHistoryWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const RenderHistoryDeleteManyArgsSchema: z.ZodType<Prisma.RenderHistoryDeleteManyArgs> = z.object({
  where: RenderHistoryWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const ExternalKeysCreateArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysCreateArgs, "select">> = z.object({
  data: z.union([ ExternalKeysCreateInputSchema,ExternalKeysUncheckedCreateInputSchema ]),
}).strict() ;

export const ExternalKeysUpsertArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysUpsertArgs, "select">> = z.object({
  where: ExternalKeysWhereUniqueInputSchema,
  create: z.union([ ExternalKeysCreateInputSchema,ExternalKeysUncheckedCreateInputSchema ]),
  update: z.union([ ExternalKeysUpdateInputSchema,ExternalKeysUncheckedUpdateInputSchema ]),
}).strict() ;

export const ExternalKeysCreateManyArgsSchema: z.ZodType<Prisma.ExternalKeysCreateManyArgs> = z.object({
  data: z.union([ ExternalKeysCreateManyInputSchema,ExternalKeysCreateManyInputSchema.array() ]),
}).strict() ;

export const ExternalKeysDeleteArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysDeleteArgs, "select">> = z.object({
  where: ExternalKeysWhereUniqueInputSchema,
}).strict() ;

export const ExternalKeysUpdateArgsSchema: z.ZodType<Omit<Prisma.ExternalKeysUpdateArgs, "select">> = z.object({
  data: z.union([ ExternalKeysUpdateInputSchema,ExternalKeysUncheckedUpdateInputSchema ]),
  where: ExternalKeysWhereUniqueInputSchema,
}).strict() ;

export const ExternalKeysUpdateManyArgsSchema: z.ZodType<Prisma.ExternalKeysUpdateManyArgs> = z.object({
  data: z.union([ ExternalKeysUpdateManyMutationInputSchema,ExternalKeysUncheckedUpdateManyInputSchema ]),
  where: ExternalKeysWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const ExternalKeysDeleteManyArgsSchema: z.ZodType<Prisma.ExternalKeysDeleteManyArgs> = z.object({
  where: ExternalKeysWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const ProxyCreateArgsSchema: z.ZodType<Omit<Prisma.ProxyCreateArgs, "select">> = z.object({
  data: z.union([ ProxyCreateInputSchema,ProxyUncheckedCreateInputSchema ]),
}).strict() ;

export const ProxyUpsertArgsSchema: z.ZodType<Omit<Prisma.ProxyUpsertArgs, "select">> = z.object({
  where: ProxyWhereUniqueInputSchema,
  create: z.union([ ProxyCreateInputSchema,ProxyUncheckedCreateInputSchema ]),
  update: z.union([ ProxyUpdateInputSchema,ProxyUncheckedUpdateInputSchema ]),
}).strict() ;

export const ProxyCreateManyArgsSchema: z.ZodType<Prisma.ProxyCreateManyArgs> = z.object({
  data: z.union([ ProxyCreateManyInputSchema,ProxyCreateManyInputSchema.array() ]),
}).strict() ;

export const ProxyDeleteArgsSchema: z.ZodType<Omit<Prisma.ProxyDeleteArgs, "select">> = z.object({
  where: ProxyWhereUniqueInputSchema,
}).strict() ;

export const ProxyUpdateArgsSchema: z.ZodType<Omit<Prisma.ProxyUpdateArgs, "select">> = z.object({
  data: z.union([ ProxyUpdateInputSchema,ProxyUncheckedUpdateInputSchema ]),
  where: ProxyWhereUniqueInputSchema,
}).strict() ;

export const ProxyUpdateManyArgsSchema: z.ZodType<Prisma.ProxyUpdateManyArgs> = z.object({
  data: z.union([ ProxyUpdateManyMutationInputSchema,ProxyUncheckedUpdateManyInputSchema ]),
  where: ProxyWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const ProxyDeleteManyArgsSchema: z.ZodType<Prisma.ProxyDeleteManyArgs> = z.object({
  where: ProxyWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const CookieIdentityCreateArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityCreateArgs, "select">> = z.object({
  data: z.union([ CookieIdentityCreateInputSchema,CookieIdentityUncheckedCreateInputSchema ]),
}).strict() ;

export const CookieIdentityUpsertArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityUpsertArgs, "select">> = z.object({
  where: CookieIdentityWhereUniqueInputSchema,
  create: z.union([ CookieIdentityCreateInputSchema,CookieIdentityUncheckedCreateInputSchema ]),
  update: z.union([ CookieIdentityUpdateInputSchema,CookieIdentityUncheckedUpdateInputSchema ]),
}).strict() ;

export const CookieIdentityCreateManyArgsSchema: z.ZodType<Prisma.CookieIdentityCreateManyArgs> = z.object({
  data: z.union([ CookieIdentityCreateManyInputSchema,CookieIdentityCreateManyInputSchema.array() ]),
}).strict() ;

export const CookieIdentityDeleteArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityDeleteArgs, "select">> = z.object({
  where: CookieIdentityWhereUniqueInputSchema,
}).strict() ;

export const CookieIdentityUpdateArgsSchema: z.ZodType<Omit<Prisma.CookieIdentityUpdateArgs, "select">> = z.object({
  data: z.union([ CookieIdentityUpdateInputSchema,CookieIdentityUncheckedUpdateInputSchema ]),
  where: CookieIdentityWhereUniqueInputSchema,
}).strict() ;

export const CookieIdentityUpdateManyArgsSchema: z.ZodType<Prisma.CookieIdentityUpdateManyArgs> = z.object({
  data: z.union([ CookieIdentityUpdateManyMutationInputSchema,CookieIdentityUncheckedUpdateManyInputSchema ]),
  where: CookieIdentityWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;

export const CookieIdentityDeleteManyArgsSchema: z.ZodType<Prisma.CookieIdentityDeleteManyArgs> = z.object({
  where: CookieIdentityWhereInputSchema.optional(),
  limit: z.number().optional(),
}).strict() ;